import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  useNavigate, useLocation, useParams, Navigate,
} from 'react-router';
import ContentHeader from '../../components/content-header/content-header.component';
import PageTabs from '../../components/page-tabs/page-tabs.component';
import WidgetRow from '../../components/widget-row/widget-row.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import CheckAccess from '../../components/check-access/check-access.component';
import PageError from '../../components/page-error/page-error.component';
import FlexContainer from '../../components/flex-container/flex-container.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import Widget from '../../components/widget/widget.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import NoContentBlock from '../../components/no-content-block/no-content-block.component';
import Form from '../../components/form/form.component';
import FormError from '../../components/form-error/form-error.component';
import FormContainer from '../../components/form-container/form-container.component';
import FilterPanel from '../../components/filter-panel/filter-panel.component';
import PageTitleSeparator from '../../components/page-title-separator/page-title-separator.component';
import ExportModal from '../app/controls/export-modal/export-modal.component';
import StatusModal from '../app/controls/status-modal/status-modal.component';
import ActivityModal from '../activity-list/activity-modal/activity-modal.component';
import ActivityListFilters from '../activity-list/activity-list-filters/activity-list-filters.component';
import ActivityListInternal from '../activity-list/activity-list-internal/activity-list-internal.component';
import DealTab from './deal-tab/deal-tab.component';
import DealComments from './deal-comments/deal-comments.component';
import DealAllocation from './deal-allocation/deal-allocation.component';
import DealStatusDropdown from './deal-status-dropdown/deal-status-dropdown.component';
import DealCommonData from './deal-common-data/deal-common-data.component';
import DealDescription from './deal-description/deal-description.component';
import DealPhotos from './deal-photos/deal-photos.component';
import DealPropertyDetails from './deal-property-details/deal-property-details.component';
import DealInvestmentDetails from './deal-investment-details/deal-investment-details.component';
import DealMilestones from './deal-milestones/deal-milestones.component';
import DealContacts from './deal-contacts/deal-contacts.component';
import DealCustomSection from './deal-custom-section/deal-custom-section.component';
import DealExternalLinks from './deal-external-links/deal-external-links.component';
import DealCapitalization from './deal-capitalization/deal-capitalization.component';
import DealCapitalizationFile from './deal-capitalization-file/deal-capitalization-file.component';
import DealCapitalizationModal from './deal-capitalization-modal/deal-capitalization-modal.component';
import DealCapitalizationCommonData from './deal-capitalization-common-data/deal-capitalization-common-data.component';
import DealEquityOrganizationChart from './deal-equity-organization-chart/deal-equity-organization-chart.component';
import DealMetrics from './deal-metrics/deal-metrics.component';
import DealHistoricalValuations from './deal-historical-valuations/deal-historical-valuations.component';
import DealTopLevelMetrics from './deal-top-level-metrics/deal-top-level-metrics.component';
import DealValuationFiles from './deal-valuation-files/deal-valuation-files.component';
import DealValuationFileModal from './deal-valuation-file-modal/deal-valuation-file-modal.component';
import DealTasks from './deal-tasks/deal-tasks.component';
import DealTasksFilters from './deal-tasks-filters/deal-tasks-filters.component';
import DealTaskModal from './deal-task-modal/deal-task-modal.component';
import DealTaskGroupModal from './deal-task-group-modal/deal-task-group-modal.component';
import DealTerms from './deal-terms/deal-terms.component';
import DealTermGroupModal from './deal-term-group-modal/deal-term-group-modal.component';
import DealParticipants from './deal-participants/deal-participants.component';
import DealParticipantModal from './deal-participant-modal/deal-participant-modal.component';
import DealParticipantListModal from './deal-participant-list-modal/deal-participant-list-modal.component';
import DealParticipantsFilters from './deal-participants-filters/deal-participants-filters.component';
import DealFiles from './deal-files/deal-files.component';
import DealFileModal from './deal-file-modal/deal-file-modal.component';
import DealFileGroupModal from './deal-file-group-modal/deal-file-group-modal.component';
import DealFileChatModal from './deal-file-chat-modal/deal-file-chat-modal.component';
import DealSchedules from './deal-schedules/deal-schedules.component';
import DealScheduleModal from './deal-schedule-modal/deal-schedule-modal.component';
import DealTaskListModal from './deal-task-list-modal/deal-task-list-modal.component';
import DealTaskTemplateSaveModal from './deal-task-template-save-modal/deal-task-template-save-modal.component';
import DealTaskTemplateLoadModal from './deal-task-template-load-modal/deal-task-template-load-modal.component';
import DealStatusChanges from './deal-status-changes/deal-status-changes.component';
import EditIcon from '../../icons/edit-icon';
import SaveTemplateIcon from '../../icons/save-template-icon';
import LoadTemplateIcon from '../../icons/load-template-icon';
import DealValidator from './deal.validator';
import GoogleMapService from '../../services/google-map-service';
import FormatService from '../../services/format-service';
import StructureService from '../../services/structure-service';
import ChartService from '../../services/chart-service';
import DateService from '../../services/date-service';
import FormService from '../../services/form-service';
import FileService from '../../services/file-service';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useScreenSmall from '../../hooks/use-screen-small';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import {
  toggleEditMode, toggleCustomizeMode, toggleModalEditMode, toggleExportMode, toggleSelectMode, saveTableColumnSettings,
} from '../app/controls/controls.action';
import { getAssetData, resetAssetData } from '../asset/asset.action';
import {
  getDealData, getDealDataByFile, getDealTaskAttachmentTextData, getDealFileTextData, viewDealFile, saveDealData,
  saveDealStatus, saveDealTabSettings, resetDealData, resetNewDealId, resetDealFileAnalyzed,
  resetDealFileChatResponseText,
} from './deal.action';
import { deleteDealList } from '../deal-list/deal-list.action';
import {
  saveDealTask, deleteDealTaskList, deleteDealTaskGroup, saveDealTaskStatus, saveDealTaskReviewStatus,
  deleteDealTaskComment, saveDealTaskComment, getDealTaskListData, saveDealTaskList,
  loadDealTaskTemplateData, saveDealTaskTemplateData,
} from './deal-tasks/deal-tasks.action';
import { deleteDealTerm, deleteDealTermGroup } from './deal-terms/deal-terms.action';
import { saveDealValuationFile, deleteDealValuationFile } from './deal-valuation-files/deal-valuation-files.action';
import {
  getDealParticipantListData, saveDealParticipant, saveDealParticipantList, deleteDealParticipantList,
  saveDealParticipantComment, deleteDealParticipantComment,
} from './deal-participants/deal-participants.action';
import {
  saveDealFile, deleteDealFileList, deleteDealFileGroup, getDealFileListData,
} from './deal-files/deal-files.action';
import {
  deleteActivityList, getActivityListData, saveActivityListData, saveActivityStatusList,
} from '../activity-list/activity-list.action';
import { deleteActivityComment, saveActivityComment } from '../activity/activity.action';
import { getCommentListData } from '../app/comments/comments.action';
import { getNotificationListData } from '../app/notifications/notifications.action';
import { saveDealSchedule, deleteDealSchedule } from './deal-schedules/deal-schedules.action';
import {
  getListActivityDealData, getListContactCompanyData, getListContactData, getListContactNotParticipantData,
  getListContactPersonData, getListContactPersonInternalData, getListDealAssetData, getListDealTaskContactPersonData,
  getListTagData,
} from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import setDealScheduleZoom from './deal-schedule-tasks/deal-schedule-tasks.action';
import {
  FORMAT_LIST_EXPORT_DEAL_TABLE, FORMAT_LIST_IMPORT_DEAL_TABLE, FORMAT_LIST_EXPORT_ACTIVITY,
  FORMAT_LIST_IMPORT_ACTIVITY, FORMAT_LIST_EXPORT_DEAL_FILES_TABLE,
} from '../../constants/system/format-lists';
import { FORM_ID_DEAL } from '../../constants/system/form-ids';
import { TIMEOUT_SHORT } from '../../constants/system/timeouts';
import { PAGE_LIST_DEFAULT, PAGE_DEFAULT } from '../../constants/system/pages';
import { TABLE_LIMIT_DEFAULT } from '../../constants/system/table-limits';
import { FILTER_VALUE_EMPTY } from '../../constants/system/filter-values';
import { TAB_INDEX_DEFAULT } from '../../constants/system/tab-indexes';
import {
  DEAL_VIEW, DEAL_CAPITALIZATION_VIEW, DEAL_DUE_DILIGENCE_VIEW, DEAL_FILES_VIEW, DEAL_TASK_STATUS_EDIT,
  DEAL_PARTICIPANTS_VIEW, DEAL_SCHEDULE_VIEW, DEAL_SUMMARY_VIEW, DEAL_TERMS_VIEW, DEAL_PRO_FORMA_VIEW,
  DEAL_CAPITALIZATION_IMPORT, DEAL_CAPITALIZATION_EXPORT, DEAL_TERMS_IMPORT, DEAL_FILES_EXPORT, DEAL_TERMS_EXPORT,
  DEAL_PRO_FORMA_EXPORT, DEAL_DUE_DILIGENCE_IMPORT, DEAL_DUE_DILIGENCE_EXPORT,
  DEAL_PARTICIPANTS_IMPORT, DEAL_PARTICIPANTS_EXPORT, DEAL_PHOTO_LIST_VIEW, DEAL_COMMENTS_VIEW,
  DEAL_BUILDING_DETAILS_LIST_VIEW, DEAL_CONTACTS_GROUP_LIST_VIEW, DEAL_SOURCES_LIST_VIEW, DEAL_USES_LIST_VIEW,
  DEAL_EQUITY_ORGANIZATION_CHART_VIEW, DEAL_METRICS_STATISTICS_LIST_VIEW, DEAL_METRICS_CAPITAL_STACK_LIST_VIEW,
  DEAL_TERM_GROUP_LIST_VIEW, DEAL_HISTORICAL_VALUATIONS_LIST_VIEW, DEAL_TOP_LEVEL_METRICS_LIST_VIEW,
  DEAL_VALUATION_FILE_GROUP_LIST_VIEW, DEAL_TASK_GROUP_LIST_VIEW, DEAL_SCHEDULE_TASK_LIST_VIEW,
  DEAL_PARTICIPANT_GROUP_LIST_VIEW, DEAL_FILE_GROUP_LIST_VIEW, DEAL_TERM_GROUP_CREATE, DEAL_TASK_CREATE,
  DEAL_TASK_GROUP_CREATE, DEAL_PARTICIPANT_CREATE, DEAL_FILE_GROUP_CREATE, DEAL_FILE_CREATE,
  DEAL_SOURCES_ITEM_CREATE, DEAL_USES_ITEM_CREATE, DEAL_SOURCES_ITEM_EDIT, DEAL_USES_ITEM_EDIT,
  ACTIVITY_LIST_VIEW, ACTIVITY_LIST_IMPORT, ACTIVITY_LIST_EXPORT, ACTIVITY_CREATE, DEAL_STATUS_EDIT,
  DEAL_COMMON_EDIT, DEAL_HISTORICAL_VALUATIONS_EDIT, DEAL_TOP_LEVEL_METRICS_EDIT, DEAL_VALUATION_FILE_EDIT,
  DEAL_SCHEDULE_TASK_EDIT, DEAL_PARTICIPANT_EDIT, DEAL_PHOTO_EDIT, DEAL_BUILDING_DETAILS_ITEM_EDIT,
  DEAL_CONTACTS_GROUP_ITEM_EDIT, DEAL_CONTACTS_ITEM_EDIT, DEAL_MILESTONES_ITEM_EDIT, DEAL_EXTERNAL_LINKS_ITEM_EDIT,
  DEAL_FILE_EDIT, DEAL_FILE_GROUP_EDIT, DEAL_TASK_EDIT, DEAL_METRICS_CAPITAL_STACK_ITEM_EDIT,
  DEAL_METRICS_STATISTICS_ITEM_EDIT, DEAL_TERM_GROUP_EDIT, DEAL_TERM_EDIT, DEAL_TASK_GROUP_EDIT,
  ACTIVITY_EDIT, ACTIVITY_STATUS_EDIT, ACTIVITY_DELETE, DEAL_FILE_DELETE, DEAL_TASK_DELETE, DEAL_PARTICIPANT_DELETE,
  DEAL_SCHEDULE_CREATE, DEAL_VALUATION_FILE_CREATE, DEAL_TERM_CREATE, DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW,
  DEAL_PARTICIPANT_COMMENT_LIST_VIEW, DEAL_USES_ITEM_DELETE, DEAL_SOURCES_ITEM_DELETE, DEAL_TEMPLATE_CREATE,
  DEAL_DELETE,
} from '../../constants/entity/permissions';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../constants/system/sort-types';
import {
  ROUTE_DEAL, ROUTE_DEAL_LIST, ROUTE_NOT_FOUND, ROUTE_PARAM_ID, ROUTE_SEARCH, ROUTE_CONTACT, ROUTE_PARAM_ID_NEW,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_TAG_ID, QUERY_PARAM_DEAL_TASK_ID, QUERY_PARAM_COMMENT_ID, QUERY_PARAM_TAB, QUERY_PARAM_TEMPLATE,
  QUERY_PARAM_DEAL_FILE_ID, QUERY_PARAM_CONTACT_ID, QUERY_PARAM_STATUS, QUERY_PARAM_BROKER_ID, QUERY_PARAM_INVESTOR_ID,
  QUERY_PARAM_DEAL_CONTACT_ID, QUERY_PARAM_CATEGORY, QUERY_PARAM_LENDER_ID, QUERY_PARAM_TYPE, QUERY_PARAM_ACCESS_LEVEL,
  QUERY_PARAM_COMPANY_ID, QUERY_PARAM_DEAL_PARTICIPANT_ID,
} from '../../constants/system/query-params';
import {
  DEAL_CAPITALIZATION_GROUP_COSTS, DEAL_CAPITALIZATION_GROUP_DEBT, DEAL_CAPITALIZATION_GROUP_EQUITY,
} from '../../constants/entity/deal-capitalization-groups';
import { FILTER_KEY_NAME } from '../../constants/system/filter-keys';
import { FILTER_LABEL_KEY_FULL_NAME } from '../../constants/system/filter-label-keys';
import {
  FILTER_TYPE_BROKER_ID, FILTER_TYPE_CATEGORY, FILTER_TYPE_DEAL_CONTACT_ID, FILTER_TYPE_INVESTOR_ID,
  FILTER_TYPE_LENDER_ID, FILTER_TYPE_TYPE, FILTER_TYPE_CONTACT_ID, FILTER_TYPE_ACCESS_LEVEL, FILTER_TYPE_STATUS,
  FILTER_TYPE_COMPANY_ID,
} from '../../constants/system/filter-types';
import { DEAL_STATUS_DEAD_DEAL, DEAL_STATUS_DRAFT, DEAL_STATUS_INITIAL } from '../../constants/entity/deal-statuses';
import {
  TAB_NAME_DEAL_ACTIVITY, TAB_NAME_DEAL_COMMENTS, TAB_NAME_DEAL_FILES, TAB_NAME_DEAL_TERMS, TAB_NAME_CAPITALIZATION,
  TAB_NAME_DUE_DILIGENCE, TAB_NAME_PARTICIPANTS, TAB_NAME_PRO_FORMA, TAB_NAME_SUMMARY, TAB_NAME_SCHEDULE,
} from '../../constants/system/tab-names';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../constants/entity/contact-types';
import {
  TABLE_NAME_ACTIVITY_LIST_DEAL, TABLE_NAME_DEAL_CAPITALIZATION, TABLE_NAME_DEAL_PARTICIPANT_LIST,
  TABLE_NAME_DEAL_SCHEDULE_TASK_LIST,
} from '../../constants/system/table-names';
import {
  DEAL_CAPITALIZATION_CATEGORY_SOURCES, DEAL_CAPITALIZATION_CATEGORY_USES,
} from '../../constants/entity/deal-capitalization-categories';
import { DEAL_TASK_STATUS_INITIAL } from '../../constants/entity/deal-task-statuses';
import {
  DEAL_TEMPLATE_KEY_DEAL_ALLOCATION, DEAL_TEMPLATE_KEY_DEAL_CONTACTS, DEAL_TEMPLATE_KEY_DEAL_EXTERNAL_LINKS,
  DEAL_TEMPLATE_KEY_DEAL_FILES, DEAL_TEMPLATE_KEY_DEAL_MILESTONES, DEAL_TEMPLATE_KEY_DEAL_OVERVIEW,
  DEAL_TEMPLATE_KEY_DEAL_PHOTOS,
} from '../../constants/entity/deal-template-keys';
import {
  IMPORT_EXPORT_ENTITY_TYPE_DEAL_FILE_LIST, IMPORT_EXPORT_ENTITY_TYPE_DEAL_TASK_LIST,
} from '../../constants/system/import-export-entity-types';
import { ACTIVITY_STATUS_INITIAL } from '../../constants/entity/activity-statuses';
import {
  ACTIVITY_CATEGORY_CAPITAL_RAISE, ACTIVITY_CATEGORY_NEW_DEAL,
} from '../../constants/entity/activity-categories';
import { DEAL_TEMPLATE_TYPE_DUE_DILIGENCE_TEMPLATE } from '../../constants/entity/deal-template-types';
import { FILE_EXTENSION_LIST_ANALYZABLE } from '../../constants/system/file-extensions';
import {
  FIELD_TYPE_TEXT, FIELD_TYPE_TEXT_MULTILINE, FIELD_TYPE_NUMBER, FIELD_TYPE_NUMBER_CURRENCY, FIELD_TYPE_NUMBER_DOUBLE,
  FIELD_TYPE_NUMBER_PERCENT, FIELD_TYPE_DATE, FIELD_TYPE_DROPDOWN, FIELD_TYPE_BOOLEAN,
} from '../../constants/entity/field-types';
import ROUTER_INDEX_BACK from '../../constants/system/router-indexes';
import ASSET_STATUS_UNREALIZED from '../../constants/entity/asset-statuses';
import ACTIVITY_TYPE_DEBT from '../../constants/entity/activity-types';
import POINTER_KEY_PRIMARY from '../../constants/system/pointer-keys';

const Deal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const DEAL_FILES_WIDGET_TITLE_DEFAULT: string = 'Deal Files';
  const DEAL_GROUP_DEFAULT_COLLAPSE_LIMIT: number = 5;
  const DEAL_SUBTASK_DEFAULT_LIMIT: number = 5;
  const ROW_INDEX_FROM_DEFAULT: number = 0;
  const DEAL_DRAFT_POSTFIX: string = '(Draft)';

  const {
    isEditMode,
    isSelectMode,
    isExportMode,
    isCustomizeMode,
    isModalEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    isDealTasksLoading,
    isDealFilesLoading,
    isDealFileChatResponseLoading,
    isDealTaskListSaving,
    isDealActivityListSaving,
    isDealTaskCommentSaving,
    isDealParticipantListSaving,
    isDealParticipantCommentSaving,
    isDealActivityCommentSaving,
    isDealFileAnalyzed,
    dealCommonData,
    dealTabSettings,
    dealPhotosData,
    dealMainPhotoData,
    dealBuildingDetailsData,
    dealInvestmentReturnsData,
    dealMilestonesData,
    dealExternalLinksData,
    dealContactsData,
    dealCustomSectionsData,
    dealEquityOrganizationChartData,
    dealMetricsStatisticsData,
    dealMetricsCapitalStackData,
    dealTermsData,
    dealHistoricalValuationsData,
    dealCapitalizationSourcesData,
    dealCapitalizationSourcesSummaryData,
    dealCapitalizationUsesData,
    dealCapitalizationUsesSummaryData,
    dealCapitalizationFileData,
    dealTopLevelMetricsData,
    dealValuationFilesData,
    dealTasksData,
    dealTasksTotal,
    dealParticipantsData,
    dealParticipantsTotal,
    dealSchedulesData,
    dealCommentsData,
    dealStatusChangeListData,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
    currentUser,
    newDealId,
    dealAsset,
    dealScheduleZoom,
    dealTaskNewCommentList,
    dealParticipantNewCommentList,
    dealActivityNewCommentList,
    dealFileChatResponseText,
    accessLevelList,
    roleList,
    accountUserTypeList,
    contactPersonList,
    contactPersonInternalList,
    contactNotParticipantList,
    contactGroupList,
    contactCompanyList,
    contactList,
    contactTypeList,
    dealStatusList,
    dealTypeList,
    assetTypeList,
    assetStatusList,
    assetList,
    portfolioList,
    hiddenPortfolioList,
    measureUnitList,
    activityInterestLevelList,
    activityTypeList,
    pricingTypeList,
    tagList,
    contactLinkedUserList,
    dealCustomListTypeList,
    dealValuationFileGroupList,
    dealTaskGroupList,
    dealTaskPlainList,
    dealTaskStatusList,
    dealTaskContactPersonList,
    dealParticipantGroupList,
    assetOwnershipStructureTypeList,
    dealLoanTypeList,
    dealCapitalizationTypeList,
    dealCapitalizationGroupList,
    dealRejectReasonList,
    dealTaskTemplateList,
    dealHiddenFieldList,
    dealFilesData,
    dealFileGroupList,
    dealFilesTotal,
    dealActivityList,
    dealActivityTotal,
    dealActivityListSummaryData,
    activityStatusList,
    activityCategoryList,
    filterDealTaskContactList,
    filterActivityCategoryList,
    filterActivityTypeList,
    filterBrokerList,
    filterLenderList,
    filterInvestorList,
    filterDealContactList,
    filterDealParticipantCompanyList,
    filterDealParticipantAccessLevelList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    initialValues: initialFormValues,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);
  const dispatch: ReduxDispatch = useDispatch();

  const isDraft: boolean = !!dealCommonData && dealCommonData.status === DEAL_STATUS_DRAFT;
  const isNewDeal: boolean = routeParams[ROUTE_PARAM_ID] === ROUTE_PARAM_ID_NEW || isDraft;
  const dealId: number | null = isNewDeal && !isDraft ? null : FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryTemplate: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TEMPLATE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TEMPLATE].toString()) : null;
  const queryDealTaskId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_DEAL_TASK_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_DEAL_TASK_ID].toString()) : null;
  const queryDealParticipantId: number | null = locationQueryParams
  && locationQueryParams[QUERY_PARAM_DEAL_PARTICIPANT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_DEAL_PARTICIPANT_ID].toString()) : null;
  const queryDealFileId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_DEAL_FILE_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_DEAL_FILE_ID].toString()) : null;
  const queryCommentId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_COMMENT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_COMMENT_ID].toString()) : null;
  const queryFilterContactId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CONTACT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_CONTACT_ID].toString()) : null;
  const queryFilterStatus: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_STATUS]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_STATUS].toString()) : null;
  const queryFilterCompanyId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_COMPANY_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_COMPANY_ID].toString()) : null;
  const queryFilterAccessLevel: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_ACCESS_LEVEL]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_ACCESS_LEVEL].toString()) : null;
  const queryFilterCategory: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CATEGORY]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_CATEGORY].toString()) : null;
  const queryFilterBrokerId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_BROKER_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_BROKER_ID].toString()) : null;
  const queryFilterLenderId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_LENDER_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_LENDER_ID].toString()) : null;
  const queryFilterInvestorId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_INVESTOR_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_INVESTOR_ID].toString()) : null;
  const queryFilterDealContactId: number | null = locationQueryParams
  && locationQueryParams[QUERY_PARAM_DEAL_CONTACT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_DEAL_CONTACT_ID].toString()) : null;
  const queryFilterType: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TYPE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TYPE].toString()) : null;
  const isCustomizable: boolean = !isSmallScreen && !isNewDeal;

  const DEFAULT_DEAL_PARTICIPANT_SORT_STATE: SortParams = useMemo((): SortParams => ({
    field: 'group.title',
    type: SORT_TYPE_DESC,
  }), []);

  const DEFAULT_DEAL_ACTIVITY_SORT_STATE: SortParams = useMemo((): SortParams => ({
    field: 'deal.name',
    type: SORT_TYPE_ASC,
  }), []);

  const DEAL_TASK_DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_CONTACT_ID]: queryFilterContactId || null,
    [FILTER_TYPE_STATUS]: queryFilterStatus || null,
  }), [queryFilterContactId, queryFilterStatus]);

  const DEAL_TASK_FILTER_OPTION_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CONTACT_ID]: filterDealTaskContactList,
    [FILTER_TYPE_STATUS]: dealTaskStatusList,
  }), [filterDealTaskContactList, dealTaskStatusList]);

  const DEAL_TASK_FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_STATUS]: FILTER_KEY_NAME,
  }), []);

  const DEAL_TASK_FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CONTACT_ID]: 'Assigned To',
    [FILTER_TYPE_STATUS]: 'Status',
  }), []);

  const DEAL_TASK_FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CONTACT_ID]: FILTER_LABEL_KEY_FULL_NAME,
  }), []);

  const DEAL_TASK_FILTER_DEFAULT_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CONTACT_ID]: 'Assigned to me',
  }), []);

  const DEAL_PARTICIPANT_DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_COMPANY_ID]: queryFilterCompanyId || null,
    [FILTER_TYPE_ACCESS_LEVEL]: queryFilterAccessLevel || null,
  }), [queryFilterCompanyId, queryFilterAccessLevel]);

  const DEAL_PARTICIPANT_FILTER_OPTION_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_COMPANY_ID]: filterDealParticipantCompanyList,
    [FILTER_TYPE_ACCESS_LEVEL]: filterDealParticipantAccessLevelList,
  }), [filterDealParticipantCompanyList, filterDealParticipantAccessLevelList]);

  const DEAL_PARTICIPANT_FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_ACCESS_LEVEL]: FILTER_KEY_NAME,
  }), []);

  const DEAL_PARTICIPANT_FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_COMPANY_ID]: 'Company',
    [FILTER_TYPE_ACCESS_LEVEL]: 'Access Level',
  }), []);

  const DEAL_PARTICIPANT_FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_COMPANY_ID]: FILTER_LABEL_KEY_FULL_NAME,
  }), []);

  const DEAL_ACTIVITY_DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_CATEGORY]: null,
    [FILTER_TYPE_BROKER_ID]: null,
    [FILTER_TYPE_LENDER_ID]: null,
  }), []);

  const DEAL_ACTIVITY_FILTER_OPTION_MAP: Record<string, FilterOption[]>
    = useMemo((): Record<string, FilterOption[]> => ({
    [FILTER_TYPE_CATEGORY]: filterActivityCategoryList,
    [FILTER_TYPE_TYPE]: filterActivityTypeList,
    [FILTER_TYPE_BROKER_ID]: filterBrokerList,
    [FILTER_TYPE_LENDER_ID]: filterLenderList,
    [FILTER_TYPE_INVESTOR_ID]: filterInvestorList,
    [FILTER_TYPE_DEAL_CONTACT_ID]: filterDealContactList,
  }), [
    filterActivityCategoryList, filterActivityTypeList, filterBrokerList,
    filterLenderList, filterInvestorList, filterDealContactList,
  ]);

  const DEAL_ACTIVITY_FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CATEGORY]: FILTER_KEY_NAME,
    [FILTER_TYPE_TYPE]: FILTER_KEY_NAME,
  }), []);

  const DEAL_ACTIVITY_FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_CATEGORY]: 'Category',
    [FILTER_TYPE_TYPE]: 'Type',
    [FILTER_TYPE_BROKER_ID]: 'Broker',
    [FILTER_TYPE_LENDER_ID]: 'Lender',
    [FILTER_TYPE_INVESTOR_ID]: 'Investor',
    [FILTER_TYPE_DEAL_CONTACT_ID]: 'Deal Contact',
  }), []);

  const DEAL_ACTIVITY_FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_DEAL_CONTACT_ID]: FILTER_LABEL_KEY_FULL_NAME,
  }), []);

  const DEAL_TAB_PERMISSION_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [TAB_NAME_PARTICIPANTS]: DEAL_PARTICIPANTS_VIEW,
    [TAB_NAME_DEAL_COMMENTS]: DEAL_COMMENTS_VIEW,
  }), []);

  const EDIT_PERMISSION_LIST_SUMMARY: string[] = useMemo((): string[] => [
    DEAL_COMMON_EDIT,
    DEAL_PHOTO_EDIT,
    DEAL_BUILDING_DETAILS_ITEM_EDIT,
    DEAL_SOURCES_ITEM_EDIT,
    DEAL_CONTACTS_GROUP_ITEM_EDIT,
    DEAL_CONTACTS_ITEM_EDIT,
    DEAL_MILESTONES_ITEM_EDIT,
    DEAL_EXTERNAL_LINKS_ITEM_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_DEAL_CAPITALIZATION: string[] = useMemo((): string[] => [
    DEAL_COMMON_EDIT,
    DEAL_SOURCES_ITEM_EDIT,
    DEAL_USES_ITEM_EDIT,
    DEAL_METRICS_CAPITAL_STACK_ITEM_EDIT,
    DEAL_METRICS_STATISTICS_ITEM_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_PRO_FORMA: string[] = useMemo((): string[] => [
    DEAL_HISTORICAL_VALUATIONS_EDIT,
    DEAL_TOP_LEVEL_METRICS_EDIT,
    DEAL_VALUATION_FILE_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_DUE_DILIGENCE: string[] = useMemo((): string[] => [
    DEAL_TASK_GROUP_EDIT,
    DEAL_TASK_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_DEAL_FILES: string[] = useMemo((): string[] => [
    DEAL_FILE_GROUP_EDIT,
    DEAL_FILE_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_DEAL_TERMS: string[] = useMemo((): string[] => [
    DEAL_TERM_GROUP_EDIT,
    DEAL_TERM_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_SCHEDULE: string[] = useMemo((): string[] => [
    DEAL_SCHEDULE_TASK_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_PARTICIPANTS: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_EDIT,
  ], []);

  const EDIT_PERMISSION_LIST_DEAL_ACTIVITY: string[] = useMemo((): string[] => [
    ACTIVITY_EDIT,
  ], []);

  const [tabIndex, setTabIndex]: [number | null, Function] = useState(null);
  const [subTabIndex, setSubTabIndex]: [number | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [createModalErrorMessage, setCreateModalErrorMessage]: [string | null, Function] = useState(null);
  const [statusModalErrorMessage, setStatusModalErrorMessage]: [string | null, Function] = useState(null);
  const [isFormProcessed, setIsFormProcessed]: [boolean, Function] = useState(false);
  const [isNewRow, setIsNewRow]: [boolean, Function] = useState(false);
  const [currentDealTabSettings, setCurrentDealTabSettings]: [TabItem | null, Function] = useState(null);
  const [inlineSaveIsConfirm, setInlineSaveIsConfirm]: [boolean | null, Function] = useState(null);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);
  const [isDealCapitalizationSourcesModalOpen, setIsDealCapitalizationSourcesModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealCapitalizationUsesModalOpen, setIsDealCapitalizationUsesModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealTermGroupModalOpen, setIsDealTermGroupModalOpen]: [boolean, Function] = useState(false);
  const [isDealValuationFileModalOpen, setIsDealValuationFileModalOpen]: [boolean, Function] = useState(false);
  const [isDealTaskModalOpen, setIsDealTaskModalOpen]: [boolean, Function] = useState(false);
  const [isDealTaskGroupModalOpen, setIsDealTaskGroupModalOpen]: [boolean, Function] = useState(false);
  const [isDealScheduleModalOpen, setIsDealScheduleModalOpen]: [boolean, Function] = useState(false);
  const [isDealParticipantModalOpen, setIsDealParticipantModalOpen]: [boolean, Function] = useState(false);
  const [isDealParticipantListModalOpen, setIsDealParticipantListModalOpen]: [boolean, Function] = useState(false);
  const [isDealFileModalOpen, setIsDealFileModalOpen]: [boolean, Function] = useState(false);
  const [isDealFileGroupModalOpen, setIsDealFileGroupModalOpen]: [boolean, Function] = useState(false);
  const [isDealActivityModalOpen, setIsDealActivityModalOpen]: [boolean, Function] = useState(false);
  const [isDealTermDeleteConfirmModalOpen, setIsDealTermDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealTermGroupDeleteConfirmModalOpen, setIsDealTermGroupDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealValuationFileDeleteConfirmModalOpen, setIsDealValuationFileDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealTaskDeleteConfirmModalOpen, setIsDealTaskDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealTaskGroupDeleteConfirmModalOpen, setIsDealTaskGroupDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealScheduleDeleteConfirmModalOpen, setIsDealScheduleDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealParticipantDeleteConfirmModalOpen, setIsDealParticipantDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealFileDeleteConfirmModalOpen, setIsDealFileDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealFileGroupDeleteConfirmModalOpen, setIsDealFileGroupDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealTaskAttachmentFileChatModalOpen, setIsDealTaskAttachmentFileChatModalOpen]: [boolean, Function]
    = useState(false);
  const [isDealFileChatModalOpen, setIsDealFileChatModalOpen]: [boolean, Function] = useState(false);
  const [isDealActivityDeleteConfirmModalOpen, setIsDealActivityDeleteConfirmModalOpen]: [boolean, Function]
    = useState(false);
  const [isNewDealTermGroup, setIsNewDealTermGroup]: [boolean, Function] = useState(false);
  const [isNewDealTaskGroup, setIsNewDealTaskGroup]: [boolean, Function] = useState(false);
  const [isNewDealFileGroup, setIsNewDealFileGroup]: [boolean, Function] = useState(false);
  const [dealStatusErrorMessage, setDealStatusErrorMessage]: [string | null, Function] = useState(null);
  const [dealFileChatData, setDealFileChatData]: [DealChatFile | null, Function] = useState<StateValueDefault>(null);
  const [dealAssetPhotoFile, setDealAssetPhotoFile]: [File | null, Function] = useState(null);
  const [dealAssetAddress, setDealAssetAddress]: [JsonObject | null, Function] = useState(null);
  const [dealAssetIsPhotoDeleted, setDealAssetIsPhotoDeleted]: [boolean, Function] = useState(false);
  const [dealPhotoFiles, setDealPhotoFiles]: [Record<string, File>, Function] = useState({});
  const [dealPhotoNewItems, setDealPhotoNewItems]: [DealPhotoInput[], Function] = useState([]);
  const [dealPhotoList, setDealPhotoList]: [DealPhoto[], Function] = useState([]);
  const [dealPhotoIsDataChanged, setDealPhotoIsDataChanged]: [boolean, Function] = useState(false);
  const [dealAllocationItems, setDealAllocationItems]: [Record<string, DealAllocationItemInput> | null, Function]
    = useState<StateValueDefault>(null);
  const [dealAllocationNewItemIds, setDealAllocationNewItemIds]: [number[], Function] = useState([]);
  const [dealPropertyDetailsDealAssetNewFieldItems, setDealPropertyDetailsDealAssetNewFieldItems]
    : [AssetFieldInput[], Function] = useState([]);
  const [dealPropertyDetailsAssetTypes, setDealPropertyDetailsAssetTypes]: [AssetType[], Function] = useState([]);
  const [dealPropertyDetailsAssetSellers, setDealPropertyDetailsAssetSellers]: [Contact[], Function] = useState([]);
  const [dealAssetUseBreakdownNewItems, setDealAssetUseBreakdownNewItems]: [DealAssetUseBreakdownItemInput[], Function]
    = useState([]);
  const [dealAssetUseBreakdownNewFieldItems, setDealAssetUseBreakdownNewFieldItems]
    : [DealAssetUseBreakdownHeaderInput[], Function] = useState([]);
  const [dealAssetUseBreakdownDefaultProperties, setDealAssetUseBreakdownDefaultProperties]
    : [DealAssetUseBreakdownPropertyInput[], Function] = useState([]);
  const [dealInvestmentReturnsNewItems, setDealInvestmentReturnsNewItems]
    : [DealInvestmentReturnsItemInput[], Function] = useState([]);
  const [dealMilestonesNewItems, setDealMilestonesNewItems]: [DealMilestonesItemInput[], Function] = useState([]);
  const [dealExternalLinksNewItems, setDealExternalLinksNewItems]: [DealExternalLinksItemInput[], Function]
    = useState([]);
  const [dealContactNewItems, setDealContactNewItems]: [Record<string, DealContactInput[]>, Function] = useState({});
  const [dealContactNewGroupItems, setDealContactNewGroupItems]: [DealContactGroupInput[], Function] = useState([]);
  const [dealCapitalizationSourcesItemData, setDealCapitalizationSourcesItemData]
    : [DealCapitalizationItem | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationSourcesGroupData, setDealCapitalizationSourcesGroupData]
    : [DealCapitalizationGroup | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationUsesItemData, setDealCapitalizationUsesItemData]
    : [DealCapitalizationItem | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationUsesGroupData, setDealCapitalizationUsesGroupData]
    : [DealCapitalizationGroup | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationSourceNewItems, setDealCapitalizationSourceNewItems]
    : [Record<string, DealCapitalizationItemInput[]>, Function] = useState({});
  const [dealCapitalizationUseNewItems, setDealCapitalizationUseNewItems]
    : [Record<string, DealCapitalizationItemInput[]>, Function] = useState({});
  const [dealCapitalizationTableColumnSettings, setDealCapitalizationTableColumnSettings]
    : [TableField[] | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationSourceItemActiveIds, setDealCapitalizationSourceItemActiveIds]
    : [Record<string, number[]> | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationUseItemActiveIds, setDealCapitalizationUseItemActiveIds]
    : [Record<string, number[]> | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationSourceItemInlineSaveId, setDealCapitalizationSourceItemInlineSaveId]
    : [number | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationSourceGroupInlineSaveId, setDealCapitalizationSourceGroupInlineSaveId]
    : [number | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationUseItemInlineSaveId, setDealCapitalizationUseItemInlineSaveId]
    : [number | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationUseGroupInlineSaveId, setDealCapitalizationUseGroupInlineSaveId]
    : [number | null, Function] = useState<StateValueDefault>(null);
  const [dealCapitalizationFileFile, setDealCapitalizationFileFile]: [File | null, Function]
    = useState<StateValueDefault>(null);
  const [dealIsDealCapitalizationFileDeleted, setDealIsDealCapitalizationFileDeleted]: [boolean, Function]
    = useState(false);
  const [dealEquityOrganizationChartFile, setDealEquityOrganizationChartFile]: [File | null, Function]
    = useState<StateValueDefault>(null);
  const [dealIsEquityOrganizationChartDeleted, setDealIsEquityOrganizationChartDeleted]: [boolean, Function]
    = useState(false);
  const [dealMetricsStatisticsNewItems, setDealMetricsStatisticsNewItems]
    : [DealMetricsStatisticsItemInput[], Function] = useState([]);
  const [dealTermNewItems, setDealTermNewItems]: [Record<string, DealTermInput[]>, Function] = useState({});
  const [dealTermNewGroupItems, setDealTermNewGroupItems]
    : [DealTermGroupInput[], Function] = useState<StateValueDefault>([]);
  const [dealTermActiveId, setDealTermActiveId]: [number | null, Function] = useState<StateValueDefault>(null);
  const [dealTermGroupActiveId, setDealTermGroupActiveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealTermExpandedGroups, setDealTermExpandedGroups]: [number[], Function] = useState([]);
  const [dealHistoricalValuationsNewItems, setDealHistoricalValuationsNewItems]
    : [DealHistoricalValuationsItemInput[], Function] = useState([]);
  const [dealTopLevelMetricsNewItems, setDealTopLevelMetricsNewItems]
    : [DealTopLevelMetricsItem[], Function] = useState([]);
  const [dealValuationFileData, setDealValuationFileData]: [DealFile | null, Function]
    = useState<StateValueDefault>(null);
  const [dealValuationFileGroupData, setDealValuationFileGroupData]: [DealFileGroup | null, Function]
    = useState<StateValueDefault>(null);
  const [dealValuationFileFiles, setDealValuationFileFiles]
    : [Record<string, Record<string, DealFile>>, Function] = useState({});
  const [dealValuationFileNewItems, setDealValuationFileNewItems]: [Record<string, DealFileInput[]>, Function]
    = useState({});
  const [dealValuationFileActiveId, setDealValuationFileActiveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealValuationFileActiveIds, setDealValuationFileActiveIds]: [number[], Function] = useState([]);
  const [dealValuationFileInlineSaveId, setDealValuationFileInlineSaveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealValuationFileGroupInlineSaveId, setDealValuationFileGroupInlineSaveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealTaskData, setDealTaskData]: [DealTask | null, Function] = useState<StateValueDefault>(null);
  const [dealTaskGroupData, setDealTaskGroupData]: [DealTaskGroup | null, Function]
    = useState<StateValueDefault>(null);
  const [dealTaskIsCommentFormActive, setDealTaskIsCommentFormActive]: [boolean, Function] = useState(false);
  const [dealTaskGroupActiveId, setDealTaskGroupActiveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealTaskNewItems, setDealTaskNewItems]: [Record<string, DealTaskInput[]>, Function] = useState({});
  const [dealTaskExpandedGroups, setDealTaskExpandedGroups]: [number[], Function] = useState([]);
  const [dealTaskRowsSelectedDefault, setDealTaskRowsSelectedDefault]
    : [Record<string, number[]>, Function] = useState({});
  const [dealTaskRowsSelected, setDealTaskRowsSelected]
    : [Record<string, number[]> | null, Function] = useState<StateValueDefault>(null);
  const [dealTaskActiveIds, setDealTaskActiveIds]: [number[], Function] = useState([]);
  const [dealTaskGroupActiveIds, setDealTaskGroupActiveIds]: [number[], Function] = useState([]);
  const [dealTaskIsExportModalOpen, setDealTaskIsExportModalOpen]: [boolean, Function] = useState(false);
  const [dealTaskIsStatusChangeModalOpen, setDealTaskIsStatusChangeModalOpen]: [boolean, Function] = useState(false);
  const [dealTaskIsListModalOpen, setDealTaskIsListModalOpen]: [boolean, Function] = useState(false);
  const [dealTaskIsTemplateSaveModalOpen, setDealTaskIsTemplateSaveModalOpen]: [boolean, Function] = useState(false);
  const [dealTaskIsTemplateLoadModalOpen, setDealTaskIsTemplateLoadModalOpen]: [boolean, Function] = useState(false);
  const [dealTaskListModalErrorMessage, setDealTaskListModalErrorMessage]: [string | null, Function]
    = useState<StateValueDefault>(null);
  const [dealTaskTemplateSaveModalErrorMessage, setDealTaskTemplateSaveModalErrorMessage]: [string | null, Function]
    = useState<StateValueDefault>(null);
  const [dealTaskTemplateLoadModalErrorMessage, setDealTaskTemplateLoadModalErrorMessage]: [string | null, Function]
    = useState<StateValueDefault>(null);
  const [dealTaskAllRowsSelected, setDealTaskAllRowsSelected]: [boolean, Function] = useState(false);
  const [dealTaskCollapsedParents, setDealTaskCollapsedParents]
    : [Record<string, boolean> | null, Function] = useState<StateValueDefault>(null);
  const [dealTaskPage, setDealTaskPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [dealTaskFilters, setDealTaskFilters]: [Record<string, FilterValue>, Function]
    = useState(DEAL_TASK_DEFAULT_FILTERS);
  const [dealTaskCurrentFilters, setDealTaskCurrentFilters]: [Record<string, FilterValue>, Function]
    = useState(DEAL_TASK_DEFAULT_FILTERS);
  const [dealTaskInlineSaveId, setDealTaskInlineSaveId]: [number | null, Function] = useState<StateValueDefault>(null);
  const [dealTaskGroupInlineSaveId, setDealTaskGroupInlineSaveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealScheduleIsTaskDataChanged, setDealScheduleIsTaskDataChanged]: [boolean, Function] = useState(false);
  const [dealScheduleTableColumnSettings, setDealScheduleTableColumnSettings]
    : [TableField[] | null, Function] = useState<StateValueDefault>(null);
  const [dealScheduleActiveId, setDealScheduleActiveId]: [number | null, Function] = useState<StateValueDefault>(null);
  const [dealParticipantData, setDealParticipantData]: [DealParticipant | null, Function]
    = useState<StateValueDefault>(null);
  const [dealParticipantPage, setDealParticipantPage]: [number, Function] = useState(PAGE_DEFAULT);
  const [dealParticipantLimit, setDealParticipantLimit]: [number, Function] = useState(TABLE_LIMIT_DEFAULT);
  const [dealParticipantSortParams, setDealParticipantSortParams]: [SortParams, Function]
    = useState(DEFAULT_DEAL_PARTICIPANT_SORT_STATE);
  const [dealParticipantNewItems, setDealParticipantNewItems]: [DealParticipantInput[], Function] = useState([]);
  const [dealParticipantActiveIds, setDealParticipantActiveIds]: [number[], Function] = useState([]);
  const [dealParticipantTableColumnSettings, setDealParticipantTableColumnSettings]
    : [TableField[] | null, Function] = useState<StateValueDefault>(null);
  const [dealParticipantRowsSelected, setDealParticipantRowsSelected]: [number[], Function] = useState([]);
  const [dealParticipantAllRowsSelected, setDealParticipantAllRowsSelected]: [boolean, Function] = useState(false);
  const [dealParticipantInlineSaveId, setDealParticipantInlineSaveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealParticipantListModalErrorMessage, setDealParticipantListModalErrorMessage]: [string | null, Function]
    = useState<StateValueDefault>(null);
  const [dealParticipantFilters, setDealParticipantFilters]: [Record<string, FilterValue>, Function]
    = useState(DEAL_PARTICIPANT_DEFAULT_FILTERS);
  const [dealParticipantCurrentFilters, setDealParticipantCurrentFilters]: [Record<string, FilterValue>, Function]
    = useState(DEAL_PARTICIPANT_DEFAULT_FILTERS);
  const [dealParticipantIsCommentFormActive, setDealParticipantIsCommentFormActive]: [boolean, Function]
    = useState(false);
  const [dealParticipantNdaFiles, setDealParticipantNdaFiles]: [Record<string, File>, Function] = useState({});
  const [dealFileData, setDealFileData]: [DealFile | null, Function] = useState<StateValueDefault>(null);
  const [dealFileGroupData, setDealFileGroupData]: [DealFileGroup | null, Function] = useState<StateValueDefault>(null);
  const [dealFileFiles, setDealFileFiles]: [Record<string, Record<string, File>>, Function] = useState({});
  const [dealFileGroupActiveId, setDealFileGroupActiveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealFileNewItems, setDealFileNewItems]: [Record<string, DealFileInput[]>, Function] = useState({});
  const [dealFileNewGroupItems, setDealFileNewGroupItems]: [DealFileGroupInput[], Function] = useState([]);
  const [dealFileExpandedGroups, setDealFileExpandedGroups]: [number[], Function] = useState([]);
  const [dealFileRowsSelectedDefault, setDealFileRowsSelectedDefault]
    : [Record<string, number[]>, Function] = useState({});
  const [dealFileRowsSelected, setDealFileRowsSelected]: [Record<string, number[]> | null, Function]
    = useState<StateValueDefault>(null);
  const [dealFileActiveIds, setDealFileActiveIds]: [number[], Function] = useState([]);
  const [dealFileGroupActiveIds, setDealFileGroupActiveIds]: [number[], Function] = useState([]);
  const [dealFileIsExportModalOpen, setDealFileIsExportModalOpen]: [boolean, Function] = useState(false);
  const [dealFileAllRowsSelected, setDealFileAllRowsSelected]: [boolean, Function] = useState(false);
  const [dealFilePage, setDealFilePage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [dealFileInlineSaveId, setDealFileInlineSaveId]: [number | null, Function] = useState<StateValueDefault>(null);
  const [dealFileGroupInlineSaveId, setDealFileGroupInlineSaveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [dealFileChatModalErrorMessage, setDealFileChatModalErrorMessage]: [string | null, Function]
    = useState(null);
  const [dealActivityPage, setDealActivityPage]: [number, Function] = useState(PAGE_DEFAULT);
  const [dealActivityLimit, setDealActivityLimit]: [number, Function] = useState(TABLE_LIMIT_DEFAULT);
  const [dealActivitySortParams, setDealActivitySortParams]: [SortParams, Function]
    = useState(DEFAULT_DEAL_ACTIVITY_SORT_STATE);
  const [dealActivityFilters, setDealActivityFilters]: [Record<string, FilterValue>, Function]
    = useState(DEAL_ACTIVITY_DEFAULT_FILTERS);
  const [dealActivityCurrentFilters, setDealActivityCurrentFilters]: [Record<string, FilterValue>, Function]
    = useState(DEAL_ACTIVITY_DEFAULT_FILTERS);
  const [dealActivityActiveIds, setDealActivityActiveIds]: [number[], Function] = useState([]);
  const [dealActivityItemData, setDealActivityItemData]: [Activity | null, Function]
    = useState<StateValueDefault>(null);
  const [dealActivityIsCommentFormActive, setDealActivityIsCommentFormActive]: [boolean, Function] = useState(false);
  const [dealActivityIsNewDealSourceFormVisible, setDealActivityIsNewDealSourceFormVisible]: [boolean, Function]
    = useState(false);
  const [dealActivityIsNewDealContactFormVisible, setDealActivityIsNewDealContactFormVisible]: [boolean, Function]
    = useState(false);
  const [dealActivityFieldNewItems, setDealActivityFieldNewItems]
    : [Record<string, ActivityField[]>, Function] = useState({});
  const [dealActivityTableColumnSettings, setDealActivityTableColumnSettings]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);
  const [dealActivityTags, setDealActivityTags]: [Record<string, Tag[]>, Function] = useState({});
  const [dealActivityIsStatusChangeModalOpen, setDealActivityIsStatusChangeModalOpen]: [boolean, Function]
    = useState(false);
  const [dealActivityRowsSelected, setDealActivityRowsSelected]: [number[], Function] = useState([]);
  const [dealActivityAllRowsSelected, setDealActivityAllRowsSelected]: [boolean, Function] = useState(false);
  const [dealActivityInlineSaveId, setDealActivityInlineSaveId]: [number | null, Function]
    = useState<StateValueDefault>(null);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState<StateValueDefault>(null);
  const [lastSubTabIndex, setLastSubTabIndex]: [number | null, Function] = useState(null);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState<StateValueDefault>(null);

  const createButtonTitleDealCapitalization: string | null = !isCustomizeMode ? 'New Source' : null;
  const createAdditionalItemButtonTitleDealCapitalization: string | null = !isCustomizeMode ? 'New Use' : null;
  const createButtonTitleProForma: string = 'New File';
  const createButtonTitleDueDiligence: string | null = !isSelectMode && !isExportMode ? 'New Task' : null;
  const createGroupButtonTitleDueDiligence: string | null = !isSelectMode && !isExportMode && !isEditMode
    ? 'New Group' : null;
  const createButtonTitleDealFiles: string | null = !isSelectMode && !isExportMode ? 'New File' : null;
  const createGroupButtonTitleDealFiles: string | null = !isSelectMode && !isExportMode && !isEditMode
    ? 'New Group' : null;
  const createGroupButtonTitleDealTerms: string = 'New Group';
  const createButtonTitleDealSchedule: string | null = !isCustomizeMode && !isEditMode ? 'New Schedule' : null;
  const createButtonTitleDealParticipants: string | null = !isCustomizeMode && !isSelectMode
    ? 'New Participant' : null;
  const createAdditionalItemButtonTitleDealParticipants: string | null = !isSelectMode && !isCustomizeMode
    ? 'New Participants' : null;
  const createButtonTitleDealActivity: string | null = !isSelectMode && !isExportMode && !isCustomizeMode
    ? 'New Activity' : null;
  const dealTermDeleteConfirmModalTitle: string = 'Delete Term';
  const dealTermDeleteConfirmModalText: string = 'Are you sure you want to delete this term?';
  const dealTermGroupDeleteConfirmModalTitle: string = 'Delete Term Group';
  const dealTermGroupDeleteConfirmModalText: string = 'Are you sure you want to delete this term group?';
  const dealValuationFileDeleteConfirmModalTitle: string = 'Delete File';
  const dealValuationFileDeleteConfirmModalText: string = 'Are you sure you want to delete this file?';
  const dealTaskDeleteConfirmModalTitle: string = dealTaskActiveIds && dealTaskActiveIds.length > 1
    ? 'Delete Tasks' : 'Delete Task';
  const dealTaskDeleteConfirmModalText: string = dealTaskActiveIds && dealTaskActiveIds.length > 1
    ? 'Are you sure you want to delete these tasks?'
    : 'Are you sure you want to delete this task?';
  const dealTaskGroupDeleteConfirmModalTitle: string = 'Delete Task Group';
  const dealTaskGroupDeleteConfirmModalText: string = 'Are you sure you want to delete this task group?';
  const dealParticipantDeleteConfirmModalTitle: string = dealParticipantActiveIds && dealParticipantActiveIds.length > 1
    ? 'Delete Participants' : 'Delete Participant';
  const dealParticipantDeleteConfirmModalText: string = dealParticipantActiveIds && dealParticipantActiveIds.length > 1
    ? 'Are you sure you want to delete these participants?'
    : 'Are you sure you want to delete this participant?';
  const dealScheduleDeleteConfirmModalTitle: string = 'Delete Schedule';
  const dealScheduleDeleteConfirmModalText: string = 'Are you sure you want to delete this schedule?';
  const dealFileDeleteConfirmModalTitle: string = dealFileActiveIds && dealFileActiveIds.length > 1
    ? 'Delete Files' : 'Delete File';
  const dealFileDeleteConfirmModalText: string = dealFileActiveIds && dealFileActiveIds.length > 1
    ? 'Are you sure you want to delete these files?'
    : 'Are you sure you want to delete this file?';
  const dealFileGroupDeleteConfirmModalTitle: string = 'Delete File Group';
  const dealFileGroupDeleteConfirmModalText: string = 'Are you sure you want to delete this file group?';
  const dealActivityDeleteConfirmModalTitle: string = dealActivityActiveIds && dealActivityActiveIds.length > 1
    ? 'Delete Activities' : 'Delete Activity';
  const dealActivityDeleteConfirmModalText: string = dealActivityActiveIds && dealActivityActiveIds.length > 1
    ? 'Are you sure you want to delete these activities?'
    : 'Are you sure you want to delete this activity?';
  const deleteConfirmModalTitle: string = 'Delete Deal';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this deal?';
  const isDealTaskGroupExpanded: boolean = dealTaskExpandedGroups && !!dealTaskExpandedGroups.length;
  const isDealFileGroupExpanded: boolean = dealFileExpandedGroups && !!dealFileExpandedGroups.length;
  const isDealTermGroupExpanded: boolean = dealTermExpandedGroups && !!dealTermExpandedGroups.length;
  const isDealValuationFilesVisible: boolean = queryTab === TAB_NAME_PRO_FORMA;
  const isDealSchedulesVisible: boolean = queryTab === TAB_NAME_SCHEDULE;

  const dealBuildingDetailsListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_BUILDING_DETAILS_LIST_VIEW,
  ], []);
  const dealPhotoListViewPermissions: string[] = useMemo((): string[] => [DEAL_PHOTO_LIST_VIEW], []);
  const dealFileGroupListViewPermissions: string[] = useMemo((): string[] => [DEAL_FILE_GROUP_LIST_VIEW], []);
  const dealContactsGroupListViewPermissions: string[] = useMemo((): string[] => [DEAL_CONTACTS_GROUP_LIST_VIEW], []);
  const dealSourcesListViewPermissions: string[] = useMemo((): string[] => [DEAL_SOURCES_LIST_VIEW], []);
  const dealUsesListViewPermissions: string[] = useMemo((): string[] => [DEAL_USES_LIST_VIEW], []);
  const dealEquityOrganizationChartViewPermissions: string[] = useMemo((): string[] => [
    DEAL_EQUITY_ORGANIZATION_CHART_VIEW,
  ], []);
  const dealHistoricalValuationsListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_HISTORICAL_VALUATIONS_LIST_VIEW,
  ], []);
  const dealTopLevelMetricsListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_TOP_LEVEL_METRICS_LIST_VIEW,
  ], []);
  const dealValuationFileGroupListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_VALUATION_FILE_GROUP_LIST_VIEW,
  ], []);
  const dealTaskGroupListViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_GROUP_LIST_VIEW], []);
  const dealTermGroupListViewPermissions: string[] = useMemo((): string[] => [DEAL_TERM_GROUP_LIST_VIEW], []);
  const dealScheduleTaskListViewPermissions: string[] = useMemo((): string[] => [DEAL_SCHEDULE_TASK_LIST_VIEW], []);
  const dealParticipantGroupListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_GROUP_LIST_VIEW,
  ], []);
  const activityListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_LIST_VIEW], []);
  const dealViewPermissions: string[] = useMemo((): string[] => [DEAL_VIEW], []);
  const dealStatusEditPermissions: string[] = useMemo((): string[] => [DEAL_STATUS_EDIT], []);
  const dealCapitalizationViewPermissions: string[] = useMemo((): string[] => [
    DEAL_BUILDING_DETAILS_LIST_VIEW,
    DEAL_CAPITALIZATION_VIEW,
  ], []);
  const dealMetricsViewPermissions: string[] = useMemo((): string[] => [
    DEAL_METRICS_CAPITAL_STACK_LIST_VIEW,
    DEAL_METRICS_STATISTICS_LIST_VIEW,
  ], []);
  const dealTabPageMap: Record<string, number> = useMemo((): Record<string, number> => ({
    [TAB_NAME_DUE_DILIGENCE]: dealTaskPage,
    [TAB_NAME_PARTICIPANTS]: dealParticipantPage + 1,
    [TAB_NAME_DEAL_FILES]: dealFilePage,
    [TAB_NAME_DEAL_ACTIVITY]: dealActivityPage + 1,
  }), [dealTaskPage, dealParticipantPage, dealFilePage, dealActivityPage]);
  const dealTabLimitMap: Record<string, number> = useMemo((): Record<string, number> => ({
    [TAB_NAME_PARTICIPANTS]: dealParticipantLimit,
    [TAB_NAME_DEAL_ACTIVITY]: dealActivityLimit,
  }), [dealTaskPage, dealParticipantPage, dealFilePage, dealActivityPage]);

  const dealPhotoListDefault: DealPhoto[] = useMemo((): DealPhoto[] => ([
    ...(dealMainPhotoData ? [dealMainPhotoData] : []),
    ...((dealPhotosData || []).filter((dealPhoto: DealPhoto): boolean => !!dealPhoto)),
  ]), [dealMainPhotoData, dealPhotosData]);
  const isCapitalStackChartEmpty: boolean = useMemo((): boolean => (
    dealMetricsCapitalStackData && dealMetricsCapitalStackData.length ? !dealMetricsCapitalStackData.reduce((
      isNotEmptyResult: boolean, dealMetricsCapitalStackItem: DealMetricsCapitalStackItem,
    ): boolean => (
      isNotEmptyResult || !!FormatService.parseInt(dealMetricsCapitalStackItem.value)
    ), false) : true
  ), [dealMetricsCapitalStackData]);
  const dealCapitalizationSourcesEmpty: boolean = useMemo((): boolean => (
    dealCapitalizationSourcesData && dealCapitalizationSourcesData.length ? !dealCapitalizationSourcesData.reduce((
      isNotEmptyResult: boolean, dealCapitalizationGroup: DealCapitalizationGroup,
    ): boolean => (
      isNotEmptyResult || ((dealCapitalizationGroup.items && !!dealCapitalizationGroup.items.length) || false)
    ), false) : true
  ), [dealCapitalizationSourcesData]);
  const dealCapitalizationSourcesTotal: number = useMemo((): number => (
    dealCapitalizationSourcesData && dealCapitalizationSourcesData.length ? dealCapitalizationSourcesData.reduce((
      itemsTotal: number, dealCapitalizationGroup: DealCapitalizationGroup,
    ): number => (
      itemsTotal + ((dealCapitalizationGroup.items && dealCapitalizationGroup.items.length) || 0)
    ), 0) : 0
  ), [dealCapitalizationSourcesData]);
  const dealCapitalizationSourcesNewItemsTotal: number = useMemo((): number => (
    dealCapitalizationSourceNewItems && Object.keys(dealCapitalizationSourceNewItems).length
      ? Object.values(dealCapitalizationSourceNewItems).reduce(
        (itemsTotal: number, dealCapitalizationItems: DealCapitalizationItemInput[]): number => (
          itemsTotal + (dealCapitalizationItems.length || 0)
        ), 0) : 0
  ), [dealCapitalizationSourceNewItems]);
  const dealCapitalizationUsesEmpty: boolean = useMemo((): boolean => (
    dealCapitalizationUsesData && dealCapitalizationUsesData.length ? !dealCapitalizationUsesData.reduce((
      isNotEmptyResult: boolean, dealCapitalizationGroup: DealCapitalizationGroup,
    ): boolean => (
      isNotEmptyResult || ((dealCapitalizationGroup.items && !!dealCapitalizationGroup.items.length) || false)
    ), false) : true
  ), [dealCapitalizationUsesData]);
  const dealTasksSelectedTotal: number = useMemo((): number => (
    dealTaskRowsSelected ? Object.values(dealTaskRowsSelected)
      .reduce((resultTotal: number, dealTaskGroupList: number[]): number => (
        resultTotal + dealTaskGroupList.length
      ), 0) : 0
  ), [dealTaskRowsSelected]);
  const dealFilesSelectedTotal: number = useMemo((): number => (
    dealFileRowsSelected ? Object.values(dealFileRowsSelected)
      .reduce((resultTotal: number, dealFileGroupList: number[]): number => (
        resultTotal + dealFileGroupList.length
      ), 0) : 0
  ), [dealFileRowsSelected]);
  const commonDealStatusList: DealStatus[] = useMemo((): DealStatus[] => (
    dealStatusList && dealStatusList.length
      ? dealStatusList.filter((dealStatusItem: DealStatus): boolean => dealStatusItem.name !== DEAL_STATUS_DRAFT) : []
  ), [dealStatusList]);
  const filteredDealStatusList: DealStatus[] = useMemo((): DealStatus[] => (
    (commonDealStatusList && commonDealStatusList.length && (
      dealCommonData && dealCommonData.status !== DEAL_STATUS_DEAD_DEAL
        ? commonDealStatusList.filter((dealStatusItem: DealStatus): boolean => (
          dealStatusItem.name !== DEAL_STATUS_DEAD_DEAL
        )) : commonDealStatusList
    )) || []
  ), [commonDealStatusList, dealCommonData]);
  const dealFilesLoadedTotal: number = useMemo((): number => (
    dealFilesData && dealFilesData.length
      ? dealFilesData.reduce((dealFilesTotalResult: number, dealFileGroup: DealFileGroup): number => (
        dealFilesTotalResult + (dealFileGroup.files ? dealFileGroup.files.length : 0)
      ), 0) : 0
  ), [dealFilesData]);
  const saveFormDeps: DepsItem[] = useMemo((): DepsItem[] => [formValues], [formValues]);

  const isFormDataPristine: boolean = isFormPristine && !Object.keys(dealFileFiles).length
    && !dealPhotoIsDataChanged && !Object.keys(dealValuationFileFiles).length
    && !Object.keys(dealParticipantNdaFiles).length && !dealEquityOrganizationChartFile
    && !Object.keys(dealPhotoFiles).length && !dealIsEquityOrganizationChartDeleted
    && !dealPropertyDetailsAssetTypes.length && !dealPropertyDetailsAssetSellers.length && !dealAssetPhotoFile
    && !dealScheduleIsTaskDataChanged && !Object.keys(dealActivityTags).length;
  const isFormChanged: boolean = (isEditMode || isModalEditMode || (isNewDeal && !isFormProcessed))
    && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode || isExportMode || isSelectMode || isCustomizeMode || isModalEditMode
    || !!dealId;
  const isSaveDisabled: boolean = (isFormSaving || (isFormDataPristine && !isFormStructureModified)) && !isDraft;
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));
  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;
  const dealTaskAllFiltersEmpty: boolean = !dealTaskFilters[FILTER_TYPE_CONTACT_ID]
    && !dealTaskFilters[FILTER_TYPE_STATUS];
  const dealParticipantAllFiltersEmpty: boolean = !dealParticipantFilters[FILTER_TYPE_COMPANY_ID]
    && !dealParticipantFilters[FILTER_TYPE_ACCESS_LEVEL];
  const dealActivityAllFiltersEmpty: boolean = !dealActivityFilters[FILTER_TYPE_CATEGORY]
    && !dealActivityFilters[FILTER_TYPE_BROKER_ID] && !dealActivityFilters[FILTER_TYPE_DEAL_CONTACT_ID]
    && !dealActivityFilters[FILTER_TYPE_INVESTOR_ID] && !dealActivityFilters[FILTER_TYPE_LENDER_ID]
    && !dealActivityFilters[FILTER_TYPE_TYPE];
  const dealValuationFileId: number = (dealValuationFileData && dealValuationFileData.id) || FormService.FORM_KEY_NEW;
  const dealValuationFileGroupId: number = (dealValuationFileGroupData && dealValuationFileGroupData.id)
    || FormService.FORM_KEY_NEW;
  const dealTaskId: number = (dealTaskData && dealTaskData.id) || FormService.FORM_KEY_NEW;
  const dealTaskGroupId: number = (dealTaskGroupData && dealTaskGroupData.id) || FormService.FORM_KEY_NEW;
  const dealParticipantId: number = (dealParticipantData && dealParticipantData.id) || FormService.FORM_KEY_NEW;
  const dealFileId: number = (dealFileData && dealFileData.id) || FormService.FORM_KEY_NEW;
  const dealFileGroupId: number = (dealFileGroupData && dealFileGroupData.id) || FormService.FORM_KEY_NEW;
  const dealActivityId: number = (dealActivityItemData && dealActivityItemData.id) || FormService.FORM_KEY_NEW;
  const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(dealValuationFileGroupId);
  const formKeyDealValuationFile: string = FormService.getIndexedFormFieldKey(dealValuationFileId);
  const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);
  const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);
  const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);
  const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileId);
  const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);
  const dealAssetId: number = dealAsset && dealAsset.id;
  const formDealId: number | null = dealCommonData ? dealCommonData.id : null;
  const dealName: string | null = dealCommonData ? dealCommonData.name : null;
  const dealDraftLabel: string = isDraft ? ` ${DEAL_DRAFT_POSTFIX}` : '';
  const pageTitle: string = isNewDeal && !isDraft ? 'New Deal' : `Deal: ${dealName || '...'}${dealDraftLabel}`;
  const currentDealStatus: string | null = dealCommonData ? dealCommonData.status : null;
  const currentDealRejectReason: string | null = dealCommonData ? dealCommonData.rejectReason : null;
  const isDealPhotoListChanged: boolean = isDataLoaded && (
    !formDealId || (formDealId && dealId && formDealId !== dealId)
    || (!isEditMode && !isNewDeal && (
      dealPhotoList.length !== dealPhotoListDefault.length
      || (dealPhotoListDefault.length
        && !dealPhotoListDefault.every((dealPhotoItem: DealPhoto, dealPhotoIndex: number): boolean => (
          dealPhotoItem.link === dealPhotoList[dealPhotoIndex].link
        )))
    ))
  );
  const isSummaryTabEmpty: boolean = dealCommonData && !Object.keys(dealCommonData).length && dealPhotosData
    && !dealPhotosData.length && dealMilestonesData && !dealMilestonesData.length && dealExternalLinksData
    && !dealExternalLinksData.length && !isNewDeal;
  const isCapitalizationTabEmpty: boolean = !isEditMode && !isNewDeal && isCapitalStackChartEmpty
    && !dealEquityOrganizationChartData && (!dealMetricsStatisticsData || !dealMetricsStatisticsData.length)
    && dealCapitalizationSourcesEmpty && dealCapitalizationUsesEmpty;
  const isDealTermsTabEmpty: boolean = (dealTermsData && !dealTermsData.length)
    && (dealTermNewItems && !Object.keys(dealTermNewItems).length);
  const isProFormaTabEmpty: boolean = (!dealValuationFilesData || !dealValuationFilesData.length)
    && (!dealTopLevelMetricsData || !dealTopLevelMetricsData.length)
    && (!dealHistoricalValuationsData || !dealHistoricalValuationsData.length)
    && !isEditMode && !isNewDeal;
  const isDueDiligenceTabEmpty: boolean = (dealTasksData && !dealTasksData.length)
    && (dealTaskNewItems && !Object.keys(dealTaskNewItems).length);
  const isScheduleTabEmpty: boolean = (dealSchedulesData && !dealSchedulesData.length) && !isEditMode && !isNewDeal;
  const isDealParticipantsTabEmpty: boolean = (dealParticipantsData && !dealParticipantsData.length)
    && (dealParticipantNewItems && !dealParticipantNewItems.length);
  const isDealFilesTabEmpty: boolean = (dealFilesData && !dealFilesData.length)
    && (dealFileNewItems && !Object.keys(dealFileNewItems).length);
  const isDealActivityTabEmpty: boolean = dealActivityList && !dealActivityList.length;
  const extendedDealTabSettings: TabSetting[] = dealTabSettings && dealTabSettings.length
    ? dealTabSettings.map((tabSettingItem: TabSetting): TabItem => ({
      ...tabSettingItem,
      permission: (tabSettingItem.name && DEAL_TAB_PERMISSION_MAP[tabSettingItem.name]) || undefined,
    })) : [];
  const currentUserDealParticipantId: number | null = (dealCommonData && dealCommonData.dealParticipantId) || null;
  const dealCapitalizationSourcesFormValues: FormValueItems = formValues && formValues.dealData
    && formValues.dealData.dealCapitalizationData
    && { ...formValues.dealData.dealCapitalizationData.sources };

  const dealCapitalizationTableFieldLabels: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      title: 'Name',
      group: 'default',
    },
    {
      id: 2,
      name: 'type.title',
      title: 'Type',
      group: 'default',
    },
    {
      id: 3,
      name: 'atClosingAmount',
      title: 'At Closing, $',
      group: 'default',
    },
    {
      id: 4,
      name: 'futureAmount',
      title: 'Future, $',
      group: 'default',
    },
    {
      id: 5,
      name: 'amount',
      title: 'Amount',
      group: 'default',
    },
    {
      id: 6,
      name: 'gsfAmount',
      title: '$ / GSF',
      group: 'default',
    },
    {
      id: 7,
      name: 'nsfAmount',
      title: '$ / NSF',
      group: 'default',
    },
    {
      id: 8,
      name: 'zfaAmount',
      title: '$ / ZFA',
      group: 'default',
    },
    {
      id: 9,
      name: 'unitsAmount',
      title: '$ / Unit',
      group: 'default',
    },
    {
      id: 10,
      name: 'keysAmount',
      title: '$ / Key',
      group: 'default',
    },
    {
      id: 11,
      name: 'acresAmount',
      title: '$ / Acre',
      group: 'default',
    },
    {
      id: 12,
      name: 'lotsfAmount',
      title: '$ / Lot SF',
      group: 'default',
    },
    {
      id: 13,
      name: 'sharePercent',
      title: '% Share',
      group: 'default',
    },
    {
      id: 14,
      name: 'notes',
      title: 'Notes',
      group: 'default',
    },
  ], []);

  const dealParticipantTableFieldLabels: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'group.title',
      title: 'Group',
      group: 'default',
    },
    {
      id: 2,
      name: 'contact.fullName',
      title: 'Contact',
      group: 'default',
    },
    {
      id: 3,
      name: 'contact.company.name',
      title: 'Company',
      group: 'default',
    },
    {
      id: 4,
      name: 'contact.email',
      title: 'Email',
      group: 'default',
    },
    {
      id: 5,
      name: 'contact.phone',
      title: 'Phone',
      group: 'default',
    },
    {
      id: 6,
      name: 'name',
      title: 'Title',
      group: 'default',
    },
    {
      id: 7,
      name: 'createdDate',
      title: 'Created Date',
      group: 'default',
    },
    {
      id: 8,
      name: 'accessLevel.title',
      title: 'Access Level',
      group: 'default',
      permission: DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW,
    },
    {
      id: 9,
      name: 'isApproved',
      title: 'Is Approved',
      group: 'default',
    },
    {
      id: 10,
      name: 'ndaFile',
      title: 'NDA File',
      group: 'default',
    },
    {
      id: 11,
      name: 'updatedDate',
      title: 'Last Update',
      group: 'default',
      permission: DEAL_PARTICIPANT_COMMENT_LIST_VIEW,
    },
  ], []);

  const dealScheduleTableFieldLabels: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      title: 'Task Name',
      group: 'default',
    },
    {
      id: 2,
      name: 'assignedTo',
      title: 'Assigned To',
      group: 'default',
    },
    {
      id: 3,
      name: 'progress',
      title: '% Complete',
      group: 'default',
    },
    {
      id: 4,
      name: 'startDate',
      title: 'Start Date',
      group: 'default',
    },
    {
      id: 5,
      name: 'endDate',
      title: 'Due Date',
      group: 'default',
    },
    {
      id: 6,
      name: 'duration',
      title: 'Duration',
      group: 'default',
    },
  ], []);

  const dealActivityTableFieldLabels: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'category',
      title: 'Category',
      group: 'generalInformation',
    },
    {
      id: 2,
      name: 'status',
      title: 'Status',
      group: 'generalInformation',
    },
    {
      id: 3,
      name: 'type',
      title: 'Type',
      group: 'generalInformation',
    },
    {
      id: 4,
      name: 'dealSource.name',
      title: 'Deal Source',
      group: 'generalInformation',
    },
    {
      id: 5,
      name: 'sponsor.fullName',
      title: 'Sponsor',
      group: 'generalInformation',
    },
    {
      id: 6,
      name: 'broker.fullName',
      title: 'Broker',
      group: 'generalInformation',
    },
    {
      id: 7,
      name: 'lender.fullName',
      title: 'Lender',
      group: 'generalInformation',
    },
    {
      id: 8,
      name: 'amount',
      title: 'Total Capitalization',
      group: 'generalInformation',
    },
    {
      id: 9,
      name: 'pricing',
      title: 'Price Guidance',
      group: 'generalInformation',
    },
    {
      id: 10,
      name: 'size',
      title: 'Size',
      group: 'generalInformation',
    },
    {
      id: 11,
      name: 'dealContacts.fullName',
      title: 'Deal Contacts',
      group: 'generalInformation',
    },
    {
      id: 12,
      name: 'dealContacts.phone',
      title: 'Deal Contact Phones',
      group: 'generalInformation',
    },
    {
      id: 13,
      name: 'dealContacts.email',
      title: 'Deal Contact Emails',
      group: 'generalInformation',
    },
    {
      id: 14,
      name: 'purchasePrice',
      title: 'Purchase Price',
      group: 'generalInformation',
    },
    {
      id: 15,
      name: 'capitalExpenditure',
      title: 'CapEx',
      group: 'generalInformation',
    },
    {
      id: 16,
      name: 'oneYearNoi',
      title: '1 Year NOI',
      group: 'investmentInformation',
    },
    {
      id: 17,
      name: 'yieldOnCost',
      title: 'Yield On Cost',
      group: 'investmentInformation',
    },
    {
      id: 18,
      name: 'debtProceeds',
      title: 'Debt Proceeds',
      group: 'investmentInformation',
    },
    {
      id: 19,
      name: 'requiredEquityAmount',
      title: 'Required Equity',
      group: 'investmentInformation',
    },
    {
      id: 20,
      name: 'interestLevel',
      title: 'Level of Interest',
      group: 'investmentInformation',
    },
    {
      id: 21,
      name: 'ltc',
      title: 'LTC',
      group: 'investmentInformation',
    },
    {
      id: 22,
      name: 'equityProvider.fullName',
      title: 'Equity Provider',
      group: 'investmentInformation',
    },
    {
      id: 23,
      name: 'investor.fullName',
      title: 'Investor',
      group: 'investmentInformation',
    },
    {
      id: 24,
      name: 'lenderRole',
      title: 'Lender Role',
      group: 'investmentInformation',
    },
    {
      id: 25,
      name: 'maxAmount',
      title: 'Max Amount',
      group: 'investmentInformation',
    },
    {
      id: 26,
      name: 'loanRecourse',
      title: 'Loan Recourse',
      group: 'investmentInformation',
    },
    {
      id: 27,
      name: 'additionalParty.fullName',
      title: 'Additional Party',
      group: 'investmentInformation',
    },
    {
      id: 28,
      name: 'tags',
      title: 'Tags',
      group: 'other',
    },
    {
      id: 29,
      name: 'updatedDate',
      title: 'Last Update',
      group: 'other',
    },
  ], []);

  const dealActivityTableFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'generalInformation',
      title: 'General Information',
    },
    {
      id: 2,
      name: 'investmentInformation',
      title: 'Investment Information',
    },
    {
      id: 3,
      name: 'other',
      title: 'Other',
    },
  ], []);

  const dealCapitalizationTableFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'default',
      title: 'All Columns',
    },
  ], []);

  const dealScheduleTableFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'default',
      title: 'All Columns',
    },
  ], []);

  const dealParticipantsTableFieldGroups: TableFieldGroup[] = useMemo((): TableFieldGroup[] => [
    {
      id: 1,
      name: 'default',
      title: 'All Columns',
    },
  ], []);

  const onEditModeButtonClick: Function = useCallback((): void => {
    dispatch(toggleEditMode(!isEditMode));
  }, [isEditMode]);

  const onDealAssetSearch: Function = useCallback((query: string, searchOptions: SearchOptions): void => {
    dispatch(getListDealAssetData({
      query,
      dealId: (searchOptions && searchOptions.dealId) || null,
    }));
  }, []);

  const onDealAssetSearchClose: Function = useCallback((searchOptions: SearchOptions): void => {
    dispatch(getListDealAssetData({
      query: null,
      dealId: (searchOptions && searchOptions.dealId) || null,
    }));
  }, []);

  const onActivityDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListActivityDealData({
      query,
    }));
  }, []);

  const onActivityDealSearchClose: Function = useCallback((): void => {
    dispatch(getListActivityDealData({
      query: null,
    }));
  }, []);

  const onDealTaskContactPersonSearch: Function = useCallback((query: string): void => {
    dispatch(getListDealTaskContactPersonData({
      query,
    }));
  }, []);

  const onDealTaskContactPersonSearchClose: Function = useCallback((): void => {
    dispatch(getListDealTaskContactPersonData({
      query: null,
    }));
  }, []);

  const onContactSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactData({
      query,
    }));
  }, []);

  const onContactSearchClose: Function = useCallback((): void => {
    dispatch(getListContactData({
      query: null,
    }));
  }, []);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onContactPersonSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonData({
      query,
    }));
  }, []);

  const onContactPersonSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonData({
      query: null,
    }));
  }, []);

  const onContactNotParticipantSearch: Function = useCallback((query: string, searchOptions: SearchOptions): void => {
    dispatch(getListContactNotParticipantData({
      query,
      dealId: (searchOptions && searchOptions.dealId) || null,
    }));
  }, []);

  const onContactNotParticipantSearchClose: Function = useCallback((searchOptions: SearchOptions): void => {
    dispatch(getListContactNotParticipantData({
      query: null,
      dealId: (searchOptions && searchOptions.dealId) || null,
    }));
  }, []);

  const onContactPersonInternalSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonInternalData({
      query,
    }));
  }, []);

  const onContactPersonInternalSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonInternalData({
      query: null,
    }));
  }, []);

  const onCommentEntityLinkClick: Function = useCallback((link: string): void => {
    navigate(link);
  }, []);

  const onCancelForm: Function = useCallback((
    event?: React.MouseEvent<Element, MouseEvent>,
    withNoRedirect?: boolean,
  ): void => {
    if (dealCapitalizationSourceItemInlineSaveId !== null || dealCapitalizationUseItemInlineSaveId !== null
      || dealValuationFileInlineSaveId !== null || dealTaskInlineSaveId !== null
      || dealParticipantInlineSaveId !== null || dealFileInlineSaveId !== null || dealActivityInlineSaveId !== null) {
      inlineCancel();
    } else {
      fullResetForm();
      closeAllModals();

      setDealTaskRowsSelected(dealTaskRowsSelectedDefault);
      setDealTaskAllRowsSelected(false);
      setDealFileRowsSelected(dealFileRowsSelectedDefault);
      setDealFileAllRowsSelected(false);
      setDealParticipantRowsSelected([]);
      setDealParticipantAllRowsSelected(false);
      setDealActivityRowsSelected([]);
      setDealActivityAllRowsSelected(false);

      if (!isNewDeal) {
        dispatch(toggleEditMode(false));
        dispatch(toggleModalEditMode(false));
        dispatch(toggleExportMode(false));
        dispatch(toggleCustomizeMode(false));
        dispatch(toggleSelectMode(false));
      }
    }

    if (isNewDeal && !withNoRedirect) {
      navigate(ROUTER_INDEX_BACK);
    }
  }, [
    dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onExportSuccess: Function = useCallback((): void => {
    if (!isNewDeal) {
      onCancelForm();

      setStatusMessage('Exported successfully');
      setIsOpenMessage(true);
    }
  }, [
    isNewDeal, dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId,
  ]);

  const onImportSuccess: Function = useCallback((): void => {
    dispatch(getDealData({
      dealId,
      tab: queryTab || TAB_NAME_SUMMARY,
      filters: {
        dealTaskListFilters: DEAL_TASK_DEFAULT_FILTERS,
        dealParticipantListFilters: DEAL_PARTICIPANT_DEFAULT_FILTERS,
        activityListFilters: DEAL_ACTIVITY_DEFAULT_FILTERS,
      },
    }, (): void => {
      setDealTaskPage(PAGE_LIST_DEFAULT);
      setDealParticipantPage(PAGE_DEFAULT);
      setDealFilePage(PAGE_LIST_DEFAULT);
      setDealActivityPage(PAGE_DEFAULT);
    }, (exception: ServerError): void => {
      if (exception) {
        setErrorMessage(exception.message);
      }
    }));
  }, [queryTab, dealId]);

  const onResetDealPhotos: Function = useCallback((): void => {
    if (isDealPhotoListChanged) {
      setDealPhotoList([...(dealPhotoListDefault || [])]);
    }
  }, [isDealPhotoListChanged, dealPhotoListDefault]);

  const fullResetForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    if ((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) {
      onResetDealPhotos();
      onResetAssetData();
      setDealAssetPhotoFile(null);
      setDealAssetIsPhotoDeleted(false);
      setDealAssetAddress(null);
      setDealAllocationNewItemIds([]);
      setDealPhotoNewItems([]);
      setDealPhotoFiles({});
      setDealPhotoIsDataChanged(false);
      setDealPropertyDetailsDealAssetNewFieldItems([]);
      setDealPropertyDetailsAssetTypes([]);
      setDealPropertyDetailsAssetSellers([]);
      setDealAssetUseBreakdownNewItems([]);
      setDealAssetUseBreakdownNewFieldItems([]);
      setDealInvestmentReturnsNewItems([]);
      setDealMilestonesNewItems([]);
      setDealExternalLinksNewItems([]);
      setDealContactNewItems({});
      setDealContactNewGroupItems([]);
    }

    if ((queryTab && (queryTab === TAB_NAME_SUMMARY || queryTab === TAB_NAME_CAPITALIZATION)) || !queryTab) {
      setDealCapitalizationSourceNewItems({});
      setDealCapitalizationUseNewItems({});
      setDealCapitalizationSourceItemActiveIds(null);
      setDealCapitalizationUseItemActiveIds(null);
    }

    if (queryTab && queryTab === TAB_NAME_CAPITALIZATION) {
      setDealCapitalizationFileFile(null);
      setDealIsDealCapitalizationFileDeleted(false);
      setDealEquityOrganizationChartFile(null);
      setDealIsEquityOrganizationChartDeleted(false);
      setDealMetricsStatisticsNewItems([]);
    }

    if (queryTab && queryTab === TAB_NAME_DEAL_TERMS) {
      setDealTermNewItems({});
      setDealTermNewGroupItems([]);
      setIsNewDealTermGroup(false);
    }

    if (queryTab && queryTab === TAB_NAME_PRO_FORMA) {
      setDealHistoricalValuationsNewItems([]);
      setDealTopLevelMetricsNewItems([]);
      setDealValuationFileNewItems({});
      setDealValuationFileFiles({});
      setDealValuationFileActiveIds([]);
    }

    if (queryTab && queryTab === TAB_NAME_DUE_DILIGENCE) {
      setDealTaskNewItems({});
      setDealTaskActiveIds([]);
      setDealTaskGroupActiveIds([]);
      setIsNewDealTaskGroup(false);
    }

    if (queryTab && queryTab === TAB_NAME_SCHEDULE) {
      setDealScheduleIsTaskDataChanged(false);
    }

    if (queryTab && queryTab === TAB_NAME_PARTICIPANTS) {
      setDealParticipantNewItems([]);
      setDealParticipantActiveIds([]);
      setDealParticipantNdaFiles({});
    }

    if ((queryTab && queryTab === TAB_NAME_DEAL_FILES) || !queryTab) {
      setDealFileNewItems({});
      setDealFileNewGroupItems([]);
      setDealFileFiles({});
      setDealFileActiveIds([]);
      setDealFileGroupActiveIds([]);
      setIsNewDealFileGroup(false);
    }

    if (queryTab && queryTab === TAB_NAME_DEAL_ACTIVITY) {
      setDealActivityFieldNewItems({});
      setDealActivityTags({});
      setDealActivityIsNewDealContactFormVisible(false);
      setDealActivityIsNewDealSourceFormVisible(false);
      setDealActivityActiveIds([]);
    }
  }, [queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const resetInlineIds: Function = useCallback((): void => {
    setDealCapitalizationSourceItemInlineSaveId(null);
    setDealCapitalizationSourceGroupInlineSaveId(null);
    setDealCapitalizationUseItemInlineSaveId(null);
    setDealCapitalizationUseGroupInlineSaveId(null);
    setDealValuationFileInlineSaveId(null);
    setDealValuationFileGroupInlineSaveId(null);
    setDealTaskInlineSaveId(null);
    setDealTaskGroupInlineSaveId(null);
    setDealParticipantInlineSaveId(null);
    setDealFileInlineSaveId(null);
    setDealFileGroupInlineSaveId(null);
    setDealActivityInlineSaveId(null);
    setInlineSaveIsConfirm(null);
  }, []);

  const onSwitchModalEditMode: Function = useCallback((): void => {
    dispatch(toggleModalEditMode(!isModalEditMode));
    fullResetForm();
  }, [isModalEditMode, isDealPhotoListChanged, dealPhotoListDefault, queryTab]);

  const checkIsSectionVisible: Function = useCallback((dealFieldName: string): boolean => (
    isNewDeal && dealHiddenFieldList && dealHiddenFieldList.length
      ? !dealHiddenFieldList.includes(dealFieldName) : true
  ), [isNewDeal, dealHiddenFieldList]);

  const populateDealCapitalizationSourceEquity: Function = useCallback((contactId: number): void => {
    if (isNewDeal && dealCapitalizationSourcesData && dealCapitalizationSourcesData.length) {
      dealCapitalizationSourcesData.forEach((dealCapitalizationSourceGroupData: DealCapitalizationGroup): void => {
        const dealCapitalizationGroup: DealCapitalizationGroup = dealCapitalizationGroupList
          && dealCapitalizationGroupList.find((dealCapitalizationGroupItem: DealCapitalizationGroup): boolean => (
            dealCapitalizationGroupItem.title === dealCapitalizationSourceGroupData.name.trim()
          ));

        if (dealCapitalizationGroup && dealCapitalizationSourceGroupData.items.length
          && dealCapitalizationGroup.name === DEAL_CAPITALIZATION_GROUP_EQUITY) {
          const dealCapitalizationGroupId: number = dealCapitalizationSourceGroupData.id;
          const dealCapitalizationItemId: number = dealCapitalizationSourceGroupData.items[0]
            && dealCapitalizationSourceGroupData.items[0].id;
          const dealCapitalizationGroupFormKey: string = FormService.getIndexedFormFieldKey(
            dealCapitalizationGroupId,
          );
          const dealCapitalizationItemFormKey: string = FormService.getIndexedFormFieldKey(
            dealCapitalizationItemId,
          );
          const contact: Contact = contactList.find((contactItem: Contact): boolean => contactItem.id === contactId);

          if (contact) {
            changeForm(`dealData.dealCapitalizationData.sources[${dealCapitalizationGroupFormKey}].items`
              + `[${dealCapitalizationItemFormKey}].name`, contact.fullName);
          }
        }
      });
    }
  }, [isNewDeal, dealCapitalizationSourcesData, dealCapitalizationGroupList, contactList]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number, isSubTabs?: boolean): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      if (isSubTabs) {
        setLastSubTabIndex(newTabIndex);
      } else {
        setLastTabIndex(newTabIndex);
      }
    }
  }, [isFormChanged]);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const onDealStatusChange: Function = useCallback((
    status: string, rejectReason: string, rejectReasonComment: string,
  ): Promise<ActionCallback> | null => {
    if (!status || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealStatus({
        dealId,
        newStatus: status,
        rejectReason,
        rejectReasonComment,
      }, (response: ActionResponse): void => {
        setStatusMessage('Deal status was changed successfully');
        setIsOpenMessage(true);

        dispatch(getDealData({
          dealId,
          tab: queryTab || TAB_NAME_SUMMARY,
          filters: {
            dealTaskListFilters: DEAL_TASK_DEFAULT_FILTERS,
            dealParticipantListFilters: DEAL_PARTICIPANT_DEFAULT_FILTERS,
            activityListFilters: DEAL_ACTIVITY_DEFAULT_FILTERS,
          },
          page: (queryTab && dealTabPageMap[queryTab]) || null,
          limit: (queryTab && dealTabLimitMap[queryTab]) || null,
          allRows: true,
        }, undefined, (exception: ServerError): void => {
          if (exception) {
            setErrorMessage(exception.message);
          }
        }));

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setDealStatusErrorMessage(exception.message);
        }
      }));
    });
  }, [dealId, queryTab]);

  const onDealDescriptionFieldChange: Function = useCallback((
    event: React.ChangeEvent, descriptionValue: string | null,
  ): void => {
    changeForm('dealData.dealCommonData.description', descriptionValue);
  }, []);

  const onDealSponsorOpenNewContactForm: Function = useCallback((dealSponsorIndex: number): void => {
    const formKeyDealSponsorIndex: string = FormService.getIndexedFormFieldKey(dealSponsorIndex);

    changeForm(`dealData.dealCommonData.sponsors[${formKeyDealSponsorIndex}]`, {});
    changeForm(`dealData.dealCommonData.sponsors[${formKeyDealSponsorIndex}].type`, CONTACT_TYPE_COMPANY);
  }, []);

  const onDealSponsorCloseNewContactForm: Function = useCallback((dealSponsorIndex: number): void => {
    const formKeyDealSponsorIndex: string = FormService.getIndexedFormFieldKey(dealSponsorIndex);

    changeForm(`dealData.dealCommonData.sponsors[${formKeyDealSponsorIndex}]`, null);
  }, []);

  const onDealSponsorsChange: Function = useCallback((contacts: Contact[]): void => {
    changeForm('dealData.dealCommonData.sponsorIds', contacts);

    if (contacts && contacts.length) {
      populateDealCapitalizationSourceEquity(contacts[0].id);
    }
  }, [isNewDeal, dealCapitalizationSourcesData, dealCapitalizationGroupList, contactList]);

  const onDealSponsorContactsChange: Function = useCallback((contacts: Contact[]): void => {
    changeForm('dealData.dealCommonData.sponsorContactIds', contacts);
  }, []);

  const onDealBrokerOpenNewContactForm: Function = useCallback((dealBrokerIndex: number): void => {
    const formKeyDealBrokerIndex: string = FormService.getIndexedFormFieldKey(dealBrokerIndex);

    changeForm(`dealData.dealCommonData.brokers[${formKeyDealBrokerIndex}]`, {});
    changeForm(`dealData.dealCommonData.brokers[${formKeyDealBrokerIndex}].type`, CONTACT_TYPE_COMPANY);
  }, []);

  const onDealBrokerCloseNewContactForm: Function = useCallback((dealBrokerIndex: number): void => {
    const formKeyDealBrokerIndex: string = FormService.getIndexedFormFieldKey(dealBrokerIndex);

    changeForm(`dealData.dealCommonData.brokers[${formKeyDealBrokerIndex}]`, null);
  }, []);

  const onDealBrokersChange: Function = useCallback((contacts: Contact[]): void => {
    changeForm('dealData.dealCommonData.brokerIds', contacts);
  }, []);

  const onDealBrokerContactsChange: Function = useCallback((contacts: Contact[]): void => {
    changeForm('dealData.dealCommonData.brokerContactIds', contacts);
  }, []);

  const onDealLenderOpenNewContactForm: Function = useCallback((dealLenderIndex: number): void => {
    const formKeyDealLenderIndex: string = FormService.getIndexedFormFieldKey(dealLenderIndex);

    changeForm(`dealData.dealCommonData.lenders[${formKeyDealLenderIndex}]`, {});
    changeForm(`dealData.dealCommonData.lenders[${formKeyDealLenderIndex}].type`, CONTACT_TYPE_COMPANY);
  }, []);

  const onDealLenderCloseNewContactForm: Function = useCallback((dealLenderIndex: number): void => {
    const formKeyDealLenderIndex: string = FormService.getIndexedFormFieldKey(dealLenderIndex);

    changeForm(`dealData.dealCommonData.lenders[${formKeyDealLenderIndex}]`, null);
  }, []);

  const onDealLendersChange: Function = useCallback((contacts: Contact[]): void => {
    changeForm('dealData.dealCommonData.lenderIds', contacts);
  }, []);

  const onDealLenderContactsChange: Function = useCallback((contacts: Contact[]): void => {
    changeForm('dealData.dealCommonData.lenderContactIds', contacts);
  }, []);

  const onDealNewDealActivityDealContactsChange: Function = useCallback((contacts: Contact[]): void => {
    changeForm('dealData.dealCommonData.newDealActivity.dealContactIds', contacts);
  }, []);

  const onAddNewDealAllocationItem: Function = useCallback((): void => {
    const newDealAllocationItemId: number = StructureService.getNewItemId(
      [],
      (dealAllocationItems && Object.values(dealAllocationItems)) || [],
    );
    const formKeyDealAllocationItem: string = FormService.getIndexedFormFieldKey(newDealAllocationItemId);

    const newDealAllocationItem: DealAllocationItemInput = {
      id: newDealAllocationItemId,
      portfolioId: null,
      value: FormatService.NUMBER_VALUE_DEFAULT.toString(),
    };

    setDealAllocationItems({
      ...(dealAllocationItems || {}),
      [newDealAllocationItem.id]: newDealAllocationItem,
    });

    setDealAllocationNewItemIds([
      ...dealAllocationNewItemIds,
      newDealAllocationItem.id,
    ]);

    changeForm(`dealData.dealCommonData.allocationItems[${formKeyDealAllocationItem}]`, newDealAllocationItem);
  }, [dealAllocationItems, dealAllocationNewItemIds]);

  const onDeleteDealAllocationItemClick: Function = useCallback((dealAllocationItemId: number): void => {
    const formKeyDealAllocationItem: string = FormService.getIndexedFormFieldKey(dealAllocationItemId);

    if (dealAllocationItems) {
      const newDealAllocationItems: Record<string, DealAllocationItemInput> = { ...dealAllocationItems };
      delete newDealAllocationItems[dealAllocationItemId];
      setDealAllocationItems({ ...newDealAllocationItems });
    }

    if (dealAllocationNewItemIds.length) {
      const dealAllocationNewItemIdIndex: number = dealAllocationNewItemIds.indexOf(dealAllocationItemId);

      if (dealAllocationNewItemIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(dealAllocationNewItemIds, dealAllocationNewItemIdIndex);
      }

      setDealAllocationNewItemIds([...dealAllocationNewItemIds]);
    }

    changeForm(`dealData.dealCommonData.allocationItems[${formKeyDealAllocationItem}]`, null);
  }, [dealAllocationItems, dealAllocationNewItemIds]);

  const onDealAllocationPortfolioChange: Function = useCallback((
    portfolioId: number, dealAllocatedItemId: number,
  ): void => {
    if (dealAllocationItems) {
      setDealAllocationItems({
        ...dealAllocationItems,
        [dealAllocatedItemId]: {
          ...dealAllocationItems[dealAllocatedItemId],
          portfolioId: portfolioId,
        },
      });
    }
  }, [dealAllocationItems]);

  const onDealPropertyDetailsAssetFileChange: Function = useCallback((file: File): void => {
    setDealAssetPhotoFile(file);
    changeForm('dealData.dealCommonData.asset.mainPhoto', file);
  }, []);

  const onDealPropertyDetailsAssetFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setDealAssetPhotoFile(null);
    changeForm('dealData.dealCommonData.asset.mainPhoto', null);

    event.stopPropagation();
  }, []);

  const onDealPropertyDetailsAssetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    changeForm('dealData.dealCommonData.asset.types', assetTypeValues);
    setDealPropertyDetailsAssetTypes([...assetTypeValues]);
  }, []);

  const onDealPropertyDetailsAssetSellersChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    changeForm('dealData.dealCommonData.asset.sellerIds', assetTypeValues);
    setDealPropertyDetailsAssetSellers([...assetTypeValues]);
  }, []);

  const checkIsRowsSelected: Function = useCallback((selectedRows: Record<string, StructureItem[]>): boolean => {
    let result: boolean = false;
    Object.keys(selectedRows).forEach((groupItemId: string): void => {
      if (selectedRows[groupItemId].length > 0) {
        result = true;
      }
    });

    return result;
  }, []);

  const isExportDisabled: boolean = useMemo((): boolean => {
    if (queryTab === TAB_NAME_DUE_DILIGENCE && dealTaskRowsSelected) {
      return !dealTaskRowsSelected || !checkIsRowsSelected(dealTaskRowsSelected);
    }

    if (queryTab === TAB_NAME_DEAL_FILES && dealFileRowsSelected) {
      return !dealFileRowsSelected || !checkIsRowsSelected(dealFileRowsSelected);
    }

    return true;
  }, [queryTab, dealTaskRowsSelected, dealFileRowsSelected]);

  const onDealTabSettingsChange: Function = useCallback((tabSettings: TabItem[]): void => {
    dispatch(saveDealTabSettings({
      dealId,
      tabSettings,
    }));
  }, [dealId]);

  const onDealTaskSelectAllGroupRows: Function = useCallback((
    event: React.ChangeEvent, dealTaskGroupId: number, selectedRows: DealTask[], onSelectRows: Function,
    dealTaskGroupList: DealTaskGroup[],
  ): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    dealTaskGroupList.forEach((dealTaskGroupItem: DealTaskGroup): void => {
      if (dealTaskGroupItem.id === dealTaskGroupId) {
        onSelectRows({
          ...selectedRows,
          [dealTaskGroupId]: isSelected ? dealTaskGroupItem.tasks.map((dealTaskItem: DealTask): number[] => ([
            dealTaskItem.id,
            ...(dealTaskItem.subTasks && !!dealTaskItem.subTasks.length
              ? dealTaskItem.subTasks.map((dealSubTaskItem: DealTask): number => dealSubTaskItem.id) : []),
          ])).flat() : [],
        });
      }
    });

    if (!isSelected) {
      setDealTaskAllRowsSelected(false);
    }
  }, []);

  const onDealFileSelectAllGroupRows: Function = useCallback((
    event: React.ChangeEvent, dealFileGroupId: number, selectedRows: DealFileGroup[], onSelectRows: Function,
    dealFileGroupList: DealFileGroup[],
  ): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    dealFileGroupList.forEach((dealFileGroupItem: DealFileGroup): void => {
      if (dealFileGroupItem.id === dealFileGroupId) {
        onSelectRows({
          ...selectedRows,
          [dealFileGroupId]: isSelected ? dealFileGroupItem.files.map((dealFileItem: DealFile): number[] => ([
            dealFileItem.id,
          ])).flat() : [],
        });
      }
    });

    if (!isSelected) {
      setDealFileAllRowsSelected(false);
    }
  }, []);

  const onDealFileSelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    const selectedRows: Record<string, number[]> = dealFilesData.reduce((
      resultDealFileList: Record<string, number[]>, dealFileGroupItem: DealFileGroup,
    ): Record<string, number[]> => ({
      ...resultDealFileList,
      [dealFileGroupItem.id]: isSelected && dealFileGroupItem.files
        ? dealFileGroupItem.files.map((dealFileItem: DealFile): number => dealFileItem.id) : [],
    }), {});

    setDealFileRowsSelected(selectedRows);
    setDealFileAllRowsSelected(!dealFileAllRowsSelected);
  }, [dealFilesData, dealFileAllRowsSelected]);

  const onDealTaskSelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;

    const selectedRows: Record<string, number[]> = dealTasksData.reduce((
      resultDealTaskMap: Record<string, number[]>, dealTaskGroupItem: DealTaskGroup,
    ): Record<string, number[]> => ({
      ...resultDealTaskMap,
      [dealTaskGroupItem.id]: isSelected ? dealTaskGroupItem.tasks.map((dealTaskItem: DealTask): number[] => ([
        dealTaskItem.id,
        dealTaskItem.subTasks && !!dealTaskItem.subTasks.length
          ? dealTaskItem.subTasks.map((dealSubTaskItem: DealTask): number => dealSubTaskItem.id) : [],
      ].flat())).flat() : [],
    }), {});

    setDealTaskRowsSelected(selectedRows);
    setDealTaskAllRowsSelected(!dealTaskAllRowsSelected);
  }, [dealTasksData, dealTaskAllRowsSelected]);

  const dealPropertyDetailsResetAssetForm: Function = useCallback((assetData?: FormValueItem): void => {
    const assetFormData: FormValueItem = {
      id: assetData ? assetData.id : null,
      name: assetData ? assetData.name : '',
      status: assetData ? assetData.status : ASSET_STATUS_UNREALIZED,
      size: assetData ? FormatService.formatNumber(assetData.size) : null,
      sizeType: assetData && assetData.sizeType ? assetData.sizeType.name : null,
      loanAmount: assetData ? FormatService.formatNumber(assetData.loanAmount) : null,
      amount: assetData ? FormatService.formatNumber(assetData.amount) : null,
      value: assetData ? FormatService.formatNumber(assetData.value) : null,
      parcelNumber: assetData ? assetData.parcelNumber : null,
      submarket: assetData ? assetData.submarket : null,
      lotSize: assetData ? assetData.lotSize : null,
      portfolioId: assetData && assetData.portfolio ? assetData.portfolio.id : null,
      streetAddress: assetData ? assetData.streetAddress : null,
      address: assetData ? assetData.address : null,
      addressDetails: assetData ? assetData.addressDetails : null,
      location: assetData ? assetData.location : null,
      postalCode: assetData ? assetData.postalCode : null,
      showInPortfolio: assetData ? assetData.showInPortfolio : false,
      assetOwnershipStructure: assetData ? assetData.assetOwnershipStructure : null,
      affordableUnits: assetData ? assetData.affordableUnits : null,
      asOfRightZoning: assetData ? assetData.asOfRightZoning : false,
      cost: assetData ? FormatService.formatNumber(assetData.cost) : null,
      occupancy: assetData ? assetData.occupancy : null,
      currentNoi: assetData ? FormatService.formatNumber(assetData.currentNoi) : null,
      ttmNoi: assetData ? FormatService.formatNumber(assetData.ttmNoi) : null,
      runRateNoi: assetData ? FormatService.formatNumber(assetData.runRateNoi) : null,
      forwardNoi: assetData ? FormatService.formatNumber(assetData.forwardNoi) : null,
      maturityDate: (assetData && (assetData.maturityDate
        ? DateService.formatDate(assetData.maturityDate) : '')) || null,
      sellerIds: assetData && assetData.sellerIds && assetData.sellerIds.length
        ? assetData.sellerIds.map((contactItem: Contact): number => contactItem.id) : [],
      portfolioInvestedEquity: assetData ? FormatService.formatNumber(assetData.portfolioInvestedEquity) : null,
      leveredIrr: assetData ? FormatService.formatNumber(assetData.leveredIrr, true) : null,
      equityMultiple: assetData ? FormatService.formatNumber(assetData.equityMultiple, true) : null,
      yardiAssetId: assetData ? assetData.yardiAssetId : null,
      types: assetData ? assetData.types : [],
      options: {},
      mainPhoto: null,
    };

    if (assetData && assetData.options && assetData.options.length) {
      assetData.options.forEach((assetFieldItem: FormValueItem): void => {
        assetFormData.options[assetFieldItem.id] = {
          name: assetFieldItem.name,
          value: assetFieldItem.value,
        };
      });
    }

    changeForm('dealData.dealCommonData.asset', assetFormData);
  }, []);

  const onResetAssetData: Function = useCallback((): void => {
    dispatch(resetAssetData());
  }, []);

  const onDealPropertyDetailsAssetChange: Function = useCallback((assetId: number | string): void => {
    if (assetId === '') {
      dealPropertyDetailsResetAssetForm();
      onResetAssetData();
      setDealAssetPhotoFile(null);
      setDealAssetIsPhotoDeleted(true);
      setDealAssetAddress(null);
      changeForm('dealData.dealCommonData.asset', null);
    } else {
      dispatch(getAssetData({
        assetId,
      }));
      setDealAssetIsPhotoDeleted(false);
    }
    setDealAssetPhotoFile(null);
  }, []);

  const onDealPropertyDetailsAddNewAsset: Function = useCallback((): void => {
    dealPropertyDetailsResetAssetForm();
    onResetAssetData();

    setDealAssetPhotoFile(null);
    setDealAssetIsPhotoDeleted(true);
    setDealAssetAddress(null);

    changeForm('dealData.dealCommonData.asset', {
      name: '',
    });
  }, []);

  const onDealPropertyDetailsCancelNewAsset: Function = useCallback((): void => {
    dealPropertyDetailsResetAssetForm();
    onResetAssetData();

    setDealAssetAddress(null);
    setDealAssetIsPhotoDeleted(true);
    setDealAssetPhotoFile(null);

    if (dealCommonData) {
      changeForm('dealData.dealCommonData.asset', null);
    }
  }, [dealCommonData]);

  const onDealPropertyDetailsAddressSearchCompleted: Function = useCallback((addressObject: JsonObject): void => {
    setDealAssetAddress({ ...addressObject });

    changeForm('dealData.dealCommonData.asset.address', addressObject);
    changeForm('dealData.dealCommonData.asset.location', GoogleMapService.extractLocation(addressObject));
    changeForm('dealData.dealCommonData.asset.postalCode', GoogleMapService.extractPostalCode(addressObject));
    changeForm('dealData.dealCommonData.asset.streetAddress', GoogleMapService.extractStreetAddress(addressObject));
  }, []);

  const onDealPropertyDetailsFieldKeyDown: Function = useCallback((): void => {
    if (dealAssetAddress) {
      changeForm('dealData.dealCommonData.asset.address', '');
      changeForm('dealData.dealCommonData.asset.streetAddress', '');
      changeForm('dealData.dealCommonData.asset.location', '');
      changeForm('dealData.dealCommonData.asset.postalCode', '');

      setDealAssetAddress(null);
    }
  }, [dealAssetAddress]);

  const onDealPhotosFilesChange: Function = useCallback((dealPhotoId: number, files: File[]): void => {
    const newDealPhotoFiles: Record<string, File> = { ...dealPhotoFiles };
    files.forEach((file: File, fileIndex: number): void => {
      newDealPhotoFiles[dealPhotoId + fileIndex] = file;
    });
    setDealPhotoFiles(newDealPhotoFiles);

    files.forEach((file: File, fileIndex: number): void => {
      const formKeyDealPhoto: string = FormService.getIndexedFormFieldKey(dealPhotoId + fileIndex);

      changeForm(`dealData.dealPhotosData[${formKeyDealPhoto}].file`, file);
    });

    const newItemId: number = StructureService.getNewItemId(dealPhotoNewItems, dealPhotoList);

    setDealPhotoNewItems([
      ...dealPhotoNewItems,
      ...files.map((fileItem: File, fileIndex: number): DealPhoto => ({
        id: newItemId + fileIndex,
      })),
    ]);
  }, [dealPhotoFiles, dealPhotoNewItems, dealPhotoList]);

  const onDealPhotosFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealPhotoId: number,
  ): void => {
    const formKeyDealPhoto: string = FormService.getIndexedFormFieldKey(dealPhotoId);

    const newDealPhotoFiles: Record<string, File> = dealPhotoFiles;

    delete newDealPhotoFiles[dealPhotoId];
    setDealPhotoFiles({ ...newDealPhotoFiles });
    setDealPhotoNewItems([...dealPhotoNewItems.filter((dealPhotoNewItem: DealPhotoInput): boolean => (
      dealPhotoNewItem.id !== dealPhotoId
    ))]);

    changeForm(`dealData.dealPhotosData[${formKeyDealPhoto}].file`, null);

    event.stopPropagation();
  }, [dealPhotoFiles, dealPhotoNewItems]);

  const onDealPhotoAddNewItem: Function = useCallback((dealPhotoItem: DealPhotoInput): void => {
    setDealPhotoNewItems([...dealPhotoNewItems, dealPhotoItem]);
  }, [dealPhotoNewItems, dealPhotoFiles]);

  const onDeleteDealPhoto: Function = useCallback((dealPhotoIndex: number): void => {
    const newDealPhotoList: DealPhoto[] = [...dealPhotoList];
    StructureService.deleteListItemByIndex(newDealPhotoList, dealPhotoIndex);
    setDealPhotoList(newDealPhotoList);
    setDealPhotoIsDataChanged(true);
  }, [dealPhotoList]);

  const onDealPhotoListChange: Function = useCallback((newDealPhotoList: DealPhoto[]): void => {
    setDealPhotoList([...newDealPhotoList]);

    if (newDealPhotoList.length !== dealPhotoListDefault.length
      || !newDealPhotoList.every((dealPhotoItem: DealPhoto, dealPhotoIndex: number): boolean => (
        dealPhotoItem === dealPhotoListDefault[dealPhotoIndex]
      ))) {
      setDealPhotoIsDataChanged(true);
    }
  }, [dealPhotoListDefault]);

  const onDealPropertyDetailsAddNewItem: Function = useCallback((assetFieldItem: AssetField): void => {
    setDealPropertyDetailsDealAssetNewFieldItems([...dealPropertyDetailsDealAssetNewFieldItems, assetFieldItem]);
  }, [dealPropertyDetailsDealAssetNewFieldItems]);

  const onDealPropertyDetailsDeleteClick: Function = useCallback((dealAssetFieldId: number): void => {
    const formKeyAssetField: string = FormService.getIndexedFormFieldKey(dealAssetFieldId);

    dealPropertyDetailsDealAssetNewFieldItems.forEach((
      dealAssetNewFieldItem: AssetFieldInput,
      dealAssetNewFieldItemIndex: number,
    ): void => {
      if (dealAssetNewFieldItem && dealAssetNewFieldItem.id === dealAssetFieldId) {
        delete dealPropertyDetailsDealAssetNewFieldItems[dealAssetNewFieldItemIndex];
      }
    });
    setDealPropertyDetailsDealAssetNewFieldItems([
      ...dealPropertyDetailsDealAssetNewFieldItems
        .filter((assetFieldItem: AssetFieldInput): boolean => !!assetFieldItem),
    ]);

    changeForm(`dealData.dealCommonData.asset.options[${formKeyAssetField}]`, null);
  }, [dealPropertyDetailsDealAssetNewFieldItems]);

  const onDealPropertyDetailsAddAssetType: Function = useCallback((assetType: AssetType): void => {
    if (assetType && assetType.name) {
      const assetUseBreakdown: DealAssetUseBreakdownItem = dealBuildingDetailsData.assetUseBreakdown
        && dealBuildingDetailsData.assetUseBreakdown.find((
          dealAssetUseBreakdownItem: DealAssetUseBreakdownItem,
        ): boolean => (
          dealAssetUseBreakdownItem.assetType && dealAssetUseBreakdownItem.assetType.name === assetType.name
        ));
      const newAssetUseBreakdown: DealAssetUseBreakdownItemInput | null = (dealAssetUseBreakdownNewItems
        && dealAssetUseBreakdownNewItems.find((
          dealAssetUseBreakdownItem: DealAssetUseBreakdownItemInput,
        ): boolean => (
          !!dealAssetUseBreakdownItem.assetType && dealAssetUseBreakdownItem.assetType.name === assetType.name
        ))) || null;

      if (!assetUseBreakdown && !newAssetUseBreakdown) {
        const newDealAssetUseBreakdownItems: DealAssetUseBreakdownItemInput[] = dealAssetUseBreakdownNewItems;
        const newDealAssetUseBreakdownItemId: number = StructureService.getNewItemId(
          newDealAssetUseBreakdownItems,
          dealBuildingDetailsData && dealBuildingDetailsData.assetUseBreakdown,
        );
        const formDealAssetUseBreakdownItem: string = FormService.getIndexedFormFieldKey(
          newDealAssetUseBreakdownItemId,
        );

        newDealAssetUseBreakdownItems.push({
          id: newDealAssetUseBreakdownItemId,
          assetType,
          breakdownProperties: dealAssetUseBreakdownDefaultProperties,
        });
        setDealAssetUseBreakdownNewItems([...newDealAssetUseBreakdownItems]);

        changeForm(
          `dealData.dealBuildingDetailsData.assetUseBreakdown[${formDealAssetUseBreakdownItem}].assetTypeId`,
          assetType.id,
        );
      }
    }
  }, [dealBuildingDetailsData, dealAssetUseBreakdownNewItems, dealAssetUseBreakdownDefaultProperties]);

  const onDealBuildingDetailsAssetUseBreakdownAddNewItem: Function = useCallback((
    dealBuildingDetailsDataItem: DealBuildingDetailsData,
  ): void => {
    setDealAssetUseBreakdownNewItems([...dealAssetUseBreakdownNewItems, dealBuildingDetailsDataItem]);
  }, [dealAssetUseBreakdownNewItems]);

  const onDealBuildingDetailsAssetUseBreakdownDeleteClick: Function = useCallback((
    dealAssetUseBreakdownItemId: number,
  ): void => {
    const formDealAssetUseBreakdownItem: string = FormService.getIndexedFormFieldKey(
      dealAssetUseBreakdownItemId,
    );

    if (dealAssetUseBreakdownNewItems) {
      dealAssetUseBreakdownNewItems.forEach((
        dealAssetUseBreakdownItem: DealAssetUseBreakdownItemInput, dealAssetUseBreakdownItemIndex: number,
      ): void => {
        if (dealAssetUseBreakdownItem && dealAssetUseBreakdownItem.id === dealAssetUseBreakdownItemId) {
          delete dealAssetUseBreakdownNewItems[dealAssetUseBreakdownItemIndex];
        }
      });
      setDealAssetUseBreakdownNewItems([
        ...dealAssetUseBreakdownNewItems
          .filter((dealAssetUseBreakdownItem: DealAssetUseBreakdownItemInput): boolean => !!dealAssetUseBreakdownItem),
      ]);
    }

    changeForm(`dealData.dealBuildingDetailsData.assetUseBreakdown[${formDealAssetUseBreakdownItem}]`, null);
  }, [dealAssetUseBreakdownNewItems]);

  const onDealBuildingDetailsAssetUseBreakdownAddNewField: Function = useCallback((
    dealAssetUseBreakdownHeaderItem: DealAssetUseBreakdownHeaderInput,
  ): void => {
    setDealAssetUseBreakdownNewFieldItems([...dealAssetUseBreakdownNewFieldItems, dealAssetUseBreakdownHeaderItem]);
  }, [dealAssetUseBreakdownNewFieldItems]);

  const onDealBuildingDetailsAssetUseBreakdownDeleteField: Function = useCallback((fieldId: number): void => {
    const formKeyDealAssetUseBreakdownField: string = FormService.getIndexedFormFieldKey(fieldId);

    if (dealBuildingDetailsData && dealBuildingDetailsData.assetUseBreakdown) {
      dealBuildingDetailsData.assetUseBreakdown.forEach((
        dealAssetUseBreakdownItem: DealAssetUseBreakdownItem,
      ): void => {
        const resultDealAssetUseBreakdownItem: DealAssetUseBreakdownItem = dealAssetUseBreakdownItem;
        const dealAssetUseBreakdownItemId: number | null = dealAssetUseBreakdownItem.id || null;
        const formKeyDealAssetUseBreakdownItem: string | null = dealAssetUseBreakdownItemId
          ? FormService.getIndexedFormFieldKey(dealAssetUseBreakdownItemId) : null;

        if (resultDealAssetUseBreakdownItem && resultDealAssetUseBreakdownItem.breakdownProperties) {
          resultDealAssetUseBreakdownItem.breakdownProperties
            .forEach((dealAssetUseBreakdownProperty: DealAssetUseBreakdownProperty, itemIndex: number): void => {
              if (dealAssetUseBreakdownProperty.id === fieldId) {
                delete resultDealAssetUseBreakdownItem.breakdownProperties[itemIndex];
              }
            });
          resultDealAssetUseBreakdownItem.breakdownProperties = [
            ...resultDealAssetUseBreakdownItem.breakdownProperties,
          ].filter((dealAssetUseBreakdownProperty: DealAssetUseBreakdownProperty): boolean => (
            !!dealAssetUseBreakdownProperty
          ));
        }

        if (formKeyDealAssetUseBreakdownItem && formKeyDealAssetUseBreakdownField) {
          changeForm(
            `dealData.dealBuildingDetailsData.assetUseBreakdown`
            + `[${formKeyDealAssetUseBreakdownItem}].breakdownProperties[${formKeyDealAssetUseBreakdownField}]`,
            null,
          );
        }
      });
    }

    dealAssetUseBreakdownNewItems.forEach((dealAssetUseBreakdownItem: DealAssetUseBreakdownItemInput): void => {
      const resultDealAssetUseBreakdownItem: DealAssetUseBreakdownItemInput = dealAssetUseBreakdownItem;
      const dealAssetUseBreakdownItemId: number | null = dealAssetUseBreakdownItem.id || null;
      const formKeyDealAssetUseBreakdownItem: string | null = dealAssetUseBreakdownItemId
        ? FormService.getIndexedFormFieldKey(dealAssetUseBreakdownItemId) : null;

      if (resultDealAssetUseBreakdownItem && resultDealAssetUseBreakdownItem.breakdownProperties) {
        resultDealAssetUseBreakdownItem.breakdownProperties.forEach((
          dealAssetUseBreakdownProperty: DealAssetUseBreakdownPropertyInput, itemIndex: number,
        ): void => {
          if (dealAssetUseBreakdownProperty.id === fieldId) {
            delete resultDealAssetUseBreakdownItem.breakdownProperties[itemIndex];
          }
        });
        resultDealAssetUseBreakdownItem.breakdownProperties = [
          ...resultDealAssetUseBreakdownItem.breakdownProperties,
        ].filter((dealAssetUseBreakdownProperty: DealAssetUseBreakdownPropertyInput): boolean => (
          !!dealAssetUseBreakdownProperty
        ));
      }

      if (formKeyDealAssetUseBreakdownItem && formKeyDealAssetUseBreakdownField) {
        changeForm(
          `dealData.dealBuildingDetailsData.assetUseBreakdown`
          + `[${formKeyDealAssetUseBreakdownItem}].breakdownProperties[${formKeyDealAssetUseBreakdownField}]`,
          null,
        );
      }
    });

    if (dealBuildingDetailsData && dealBuildingDetailsData.assetUseBreakdownHeaders) {
      dealBuildingDetailsData.assetUseBreakdownHeaders.forEach((
        dealAssetUseBreakdownFieldItem: DealAssetUseBreakdownHeader, fieldIndex: number,
      ): void => {
        if (fieldId === dealAssetUseBreakdownFieldItem.id) {
          delete dealBuildingDetailsData.assetUseBreakdownHeaders[fieldIndex];
        }
      });
      dealBuildingDetailsData.assetUseBreakdownHeaders = [
        ...dealBuildingDetailsData.assetUseBreakdownHeaders,
      ].filter((dealAssetUseBreakdownHeader: DealAssetUseBreakdownHeader): boolean => !!dealAssetUseBreakdownHeader);
    }

    dealAssetUseBreakdownNewFieldItems.forEach((
      dealAssetUseBreakdownFieldItem: DealAssetUseBreakdownHeaderInput, fieldIndex: number,
    ): void => {
      if (fieldId === dealAssetUseBreakdownFieldItem.id) {
        delete dealAssetUseBreakdownNewFieldItems[fieldIndex];
      }
    });

    setDealAssetUseBreakdownNewFieldItems([
      ...dealAssetUseBreakdownNewFieldItems.filter((
        dealAssetUseBreakdownHeader: DealAssetUseBreakdownHeaderInput,
      ): boolean => !!dealAssetUseBreakdownHeader),
    ]);
    setDealAssetUseBreakdownNewItems([...dealAssetUseBreakdownNewItems]);

    changeForm(`dealData.dealBuildingDetailsData.assetUseBreakdownHeaders[${formKeyDealAssetUseBreakdownField}]`, null);
  }, [dealBuildingDetailsData, dealAssetUseBreakdownNewItems, dealAssetUseBreakdownNewFieldItems]);

  const onDealBuildingDetailsAssetUseBreakdownAssetTypeChange: Function = useCallback((
    dealAssetUseBreakdownItemId: number, assetTypeItemId: number,
  ): void => {
    const newDealAssetUseBreakdownNewItems: DealAssetUseBreakdownItemInput[] = dealAssetUseBreakdownNewItems;
    const dealAssetUseBreakdownItemIndex: number = newDealAssetUseBreakdownNewItems
      .findIndex((dealAssetUseBreakdownItem: DealAssetUseBreakdownItemInput): boolean => (
        dealAssetUseBreakdownItem.id === dealAssetUseBreakdownItemId
      ));
    const assetType: AssetType = assetTypeList.find((assetTypeItem: AssetType): boolean => (
      assetTypeItem.id === assetTypeItemId
    ));

    if (dealAssetUseBreakdownItemIndex !== FormatService.INDEX_NOT_FOUND && assetType) {
      newDealAssetUseBreakdownNewItems[dealAssetUseBreakdownItemIndex] = {
        ...newDealAssetUseBreakdownNewItems[dealAssetUseBreakdownItemIndex],
        assetType,
      };
      setDealAssetUseBreakdownNewItems([...newDealAssetUseBreakdownNewItems]);
    }
  }, [dealAssetUseBreakdownNewItems, assetTypeList]);

  const onDealInvestmentReturnsAddNewItem: Function = useCallback((
    dealInvestmentReturnsItemItem: DealInvestmentReturnsItemInput,
  ): void => {
    setDealInvestmentReturnsNewItems([...dealInvestmentReturnsNewItems, dealInvestmentReturnsItemItem]);
  }, [dealInvestmentReturnsNewItems]);

  const onDealInvestmentReturnsDeleteClick: Function = useCallback((dealInvestmentReturnsItemId: number): void => {
    const formKeyDealInvestmentReturnsItem: string = FormService.getIndexedFormFieldKey(
      dealInvestmentReturnsItemId,
    );

    dealInvestmentReturnsNewItems.forEach((
      dealInvestmentReturnsItem: DealInvestmentReturnsItemInput, dealInvestmentReturnsItemIndex: number,
    ): void => {
      if (dealInvestmentReturnsItem && dealInvestmentReturnsItem.id === dealInvestmentReturnsItemId) {
        delete dealInvestmentReturnsNewItems[dealInvestmentReturnsItemIndex];
      }
    });
    setDealInvestmentReturnsNewItems([
      ...dealInvestmentReturnsNewItems.filter((
        dealInvestmentReturnsItem: DealInvestmentReturnsItemInput,
      ): boolean => !!dealInvestmentReturnsItem),
    ]);

    changeForm(`dealData.dealInvestmentReturnsData[${formKeyDealInvestmentReturnsItem}]`, null);
  }, [dealInvestmentReturnsNewItems]);

  const onDealMilestonesAddNewItem: Function = useCallback((dealMilestonesItem: DealMilestonesItemInput): void => {
    const formKeyDealMilestonesItem: string = FormService.getIndexedFormFieldKey(
      dealMilestonesItem.id as number,
    );

    setDealMilestonesNewItems([...dealMilestonesNewItems, dealMilestonesItem]);
    changeForm(`dealData.dealMilestonesData[${formKeyDealMilestonesItem}].id`, dealMilestonesItem.id);
    changeForm(
      `dealData.dealMilestonesData[${formKeyDealMilestonesItem}].value`,
      DateService.parseDate(DateService.getDateNow()),
    );
  }, [dealMilestonesNewItems]);

  const onDealMilestonesDeleteClick: Function = useCallback((dealMilestonesItemId: number): void => {
    const formKeyDealMilestonesItem: string = FormService.getIndexedFormFieldKey(
      dealMilestonesItemId,
    );

    dealMilestonesNewItems.forEach((
      dealMilestonesItem: DealMilestonesItemInput, dealMilestonesItemIndex: number,
    ): void => {
      if (dealMilestonesItem && dealMilestonesItem.id === dealMilestonesItemId) {
        delete dealMilestonesNewItems[dealMilestonesItemIndex];
      }
    });
    setDealMilestonesNewItems([...dealMilestonesNewItems.filter((
      dealMilestonesItem: DealMilestonesItemInput,
    ): boolean => !!dealMilestonesItem)]);

    changeForm(`dealData.dealMilestonesData[${formKeyDealMilestonesItem}]`, null);
  }, [dealMilestonesNewItems]);

  const onDealExternalLinksAddNewItem: Function = useCallback((dealExternalLinksItem: DealExternalLinksItem): void => {
    const formKeyDealExternalLinksItem: string = FormService.getIndexedFormFieldKey(
      dealExternalLinksItem.id,
    );

    setDealExternalLinksNewItems([...dealExternalLinksNewItems, dealExternalLinksItem]);
    changeForm(`dealData.dealExternalLinksData[${formKeyDealExternalLinksItem}]`, dealExternalLinksItem);
  }, [dealExternalLinksNewItems]);

  const onDealExternalLinksDeleteItem: Function = useCallback((dealExternalLinksItemId: number): void => {
    const formKeyDealExternalLinksItem: string = FormService.getIndexedFormFieldKey(
      dealExternalLinksItemId,
    );

    dealExternalLinksNewItems.forEach((
      dealExternalLinksItem: DealExternalLinksItemInput, dealExternalLinksItemIndex: number,
    ): void => {
      if (dealExternalLinksItem && dealExternalLinksItem.id === dealExternalLinksItemId) {
        delete dealExternalLinksNewItems[dealExternalLinksItemIndex];
      }
    });
    setDealExternalLinksNewItems([
      ...dealExternalLinksNewItems.filter((
        dealExternalLinksItem: DealExternalLinksItemInput,
      ): boolean => !!dealExternalLinksItem),
    ]);

    changeForm(`dealData.dealExternalLinksData[${formKeyDealExternalLinksItem}]`, null);
  }, [dealExternalLinksNewItems]);

  const onDealContactAddNewItem: Function = useCallback((
    dealContactGroupId: number, dealContactItem: DealContactInput,
  ): void => {
    setDealContactNewItems({
      ...dealContactNewItems,
      [dealContactGroupId]: [...(dealContactNewItems[dealContactGroupId] || []), dealContactItem],
    });
  }, [dealContactNewItems]);

  const onDealContactDeleteClick: Function = useCallback((
    dealContactItemId: number, dealContactGroupId: number,
  ): void => {
    const formKeyDealContactGroup: string = FormService.getIndexedFormFieldKey(
      dealContactGroupId,
    );
    const formKeyDealContactItem: string = FormService.getIndexedFormFieldKey(
      dealContactItemId,
    );

    if (dealContactNewItems[dealContactGroupId]) {
      dealContactNewItems[dealContactGroupId].forEach((
        dealContactItem: DealContactInput, dealContactItemIndex: number,
      ): void => {
        if (dealContactItem && dealContactItem.id === dealContactItemId) {
          delete dealContactNewItems[dealContactGroupId][dealContactItemIndex];
        }
      });
      setDealContactNewItems({
        ...dealContactNewItems,
        [dealContactGroupId]: dealContactNewItems[dealContactGroupId]
          .filter((dealContactItem: DealContactInput): boolean => !!dealContactItem),
      });
    }

    changeForm(`dealData.dealContactsData[${formKeyDealContactGroup}].contacts[${formKeyDealContactItem}]`, null);
  }, [dealContactNewItems]);

  const onDealContactGroupDeleteClick: Function = useCallback((dealContactGroupId: number): void => {
    const formKeyDealContactGroup: string = FormService.getIndexedFormFieldKey(
      dealContactGroupId,
    );

    if (dealContactNewGroupItems && dealContactNewGroupItems.length) {
      dealContactNewGroupItems.forEach((
        dealContactGroupItem: DealContactGroupInput, dealContactGroupItemIndex: number,
      ): void => {
        if (dealContactGroupItem && dealContactGroupItem.id === dealContactGroupId) {
          delete dealContactNewGroupItems[dealContactGroupItemIndex];
        }
      });
      setDealContactNewGroupItems([
        ...dealContactNewGroupItems.filter((dealContactGroup: DealContactGroupInput): boolean => !!dealContactGroup),
      ]);
    }

    if (dealContactNewItems[dealContactGroupId]) {
      delete dealContactNewItems[dealContactGroupId];
      setDealContactNewItems({ ...dealContactNewItems });
    }

    changeForm(`dealData.dealContactsData[${formKeyDealContactGroup}]`, null);
  }, [dealContactNewGroupItems, dealContactNewItems]);

  const onDealContactGroupAddNewItem: Function = useCallback((dealContactGroupItem: DealContactGroupInput): void => {
    setDealContactNewGroupItems([...dealContactNewGroupItems, dealContactGroupItem]);
  }, [dealContactNewGroupItems]);

  const openDealCapitalizationSourcesModal: Function = useCallback((
    dealCapitalizationItem: DealCapitalizationItem, dealCapitalizationGroup: DealCapitalizationGroup,
  ): void => {
    setDealCapitalizationSourcesItemData({ ...dealCapitalizationItem });
    setDealCapitalizationSourcesGroupData({ ...dealCapitalizationGroup });
    setIsNewRow(false);
    setIsDealCapitalizationSourcesModalOpen(true);
  }, []);

  const closeDealCapitalizationSourcesModal: Function = useCallback((): void => {
    setIsDealCapitalizationSourcesModalOpen(false);
    setDealCapitalizationSourcesItemData(null);
    setIsNewRow(false);
  }, []);

  const openDealCapitalizationUsesModal: Function = useCallback((
    dealCapitalizationItem: DealCapitalizationItem, dealCapitalizationGroup: DealCapitalizationGroup,
  ): void => {
    setDealCapitalizationUsesItemData({ ...dealCapitalizationItem });
    setDealCapitalizationUsesGroupData({ ...dealCapitalizationGroup });
    setIsNewRow(false);
    setIsDealCapitalizationUsesModalOpen(true);
  }, []);

  const closeDealCapitalizationUsesModal: Function = useCallback((): void => {
    setIsDealCapitalizationUsesModalOpen(false);
    setDealCapitalizationUsesItemData(null);
    setIsNewRow(false);
  }, []);

  const onDealCapitalizationSourceAddNewItem: Function = useCallback((
    dealCapitalizationGroupId: number, newDealCapitalizationItem: DealCapitalizationItemInput,
  ): void => {
    setDealCapitalizationSourceNewItems({
      ...dealCapitalizationSourceNewItems,
      [dealCapitalizationGroupId]: [
        ...(dealCapitalizationSourceNewItems[dealCapitalizationGroupId] || []),
        newDealCapitalizationItem,
      ],
    });
  }, [dealCapitalizationSourceNewItems]);

  const onDealCapitalizationSourceDeleteClick: Function = useCallback((
    dealCapitalizationItemId: number, dealCapitalizationGroupId: number, isNewItem?: boolean,
  ): void => {
    const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
      dealCapitalizationGroupId,
    );
    const formKeyDealCapitalizationItem: string = FormService.getIndexedFormFieldKey(
      dealCapitalizationItemId,
    );

    if (isNewItem && dealCapitalizationSourceNewItems[dealCapitalizationGroupId]) {
      dealCapitalizationSourceNewItems[dealCapitalizationGroupId].forEach((
        dealCapitalizationItem: DealCapitalizationItemInput, dealCapitalizationItemIndex: number,
      ): void => {
        if (dealCapitalizationItem && dealCapitalizationItem.id === dealCapitalizationItemId) {
          delete dealCapitalizationSourceNewItems[dealCapitalizationGroupId][dealCapitalizationItemIndex];
        }
      });
      setDealCapitalizationSourceNewItems({
        ...dealCapitalizationSourceNewItems,
        [dealCapitalizationGroupId]: dealCapitalizationSourceNewItems[dealCapitalizationGroupId]
          .filter((dealCapitalizationItem: DealCapitalizationItemInput): boolean => !!dealCapitalizationItem),
      });
    }

    if (dealCapitalizationSourceItemActiveIds && dealCapitalizationSourceItemActiveIds[dealCapitalizationGroupId]) {
      const newDealCapitalizationSourceItemActiveIds: Record<string, number[]> = {
        ...dealCapitalizationSourceItemActiveIds,
      };
      const activeItemIndex: number = newDealCapitalizationSourceItemActiveIds[dealCapitalizationGroupId]
        .findIndex((activeItemId: number): boolean => (
          activeItemId === dealCapitalizationItemId
        ));
      if (activeItemIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(
          newDealCapitalizationSourceItemActiveIds[dealCapitalizationGroupId],
          activeItemIndex,
        );
        setDealCapitalizationSourceItemActiveIds(newDealCapitalizationSourceItemActiveIds);
      }
    }

    changeForm(`dealData.dealCapitalizationData.sources[${formKeyDealCapitalizationGroup}].items`
      + `[${formKeyDealCapitalizationItem}]`, null);
  }, [dealCapitalizationSourceNewItems, dealCapitalizationSourceItemActiveIds]);

  const onDealCapitalizationUseAddNewItem: Function = useCallback((
    dealCapitalizationGroupId: number, dealCapitalizationItem: DealCapitalizationItemInput,
  ): void => {
    setDealCapitalizationUseNewItems({
      ...dealCapitalizationUseNewItems,
      [dealCapitalizationGroupId]: [
        ...(dealCapitalizationUseNewItems[dealCapitalizationGroupId] || []),
        dealCapitalizationItem,
      ],
    });
  }, [dealCapitalizationUseNewItems]);

  const onDealCapitalizationUseDeleteClick: Function = useCallback((
    dealCapitalizationItemId: number, dealCapitalizationGroupId: number, isNewItem?: boolean,
  ): void => {
    const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
      dealCapitalizationGroupId,
    );
    const formKeyDealCapitalizationItem: string = FormService.getIndexedFormFieldKey(
      dealCapitalizationItemId,
    );

    if (isNewItem && dealCapitalizationUseNewItems[dealCapitalizationGroupId]) {
      dealCapitalizationUseNewItems[dealCapitalizationGroupId].forEach((
        dealCapitalizationItem: DealCapitalizationItemInput, dealCapitalizationItemIndex: number,
      ): void => {
        if (dealCapitalizationItem && dealCapitalizationItem.id === dealCapitalizationItemId) {
          delete dealCapitalizationUseNewItems[dealCapitalizationGroupId][dealCapitalizationItemIndex];
        }
      });
      setDealCapitalizationUseNewItems({
        ...dealCapitalizationUseNewItems,
        [dealCapitalizationGroupId]: dealCapitalizationUseNewItems[dealCapitalizationGroupId]
          .filter((dealCapitalizationItem: DealCapitalizationItemInput): boolean => !!dealCapitalizationItem),
      });
    }

    if (dealCapitalizationUseItemActiveIds && dealCapitalizationUseItemActiveIds[dealCapitalizationGroupId]) {
      const newDealCapitalizationUseItemActiveIds: Record<string, number[]> = {
        ...dealCapitalizationUseItemActiveIds,
      };
      const activeItemIndex: number = newDealCapitalizationUseItemActiveIds[dealCapitalizationGroupId]
        .findIndex((activeItemId: number): boolean => (
          activeItemId === dealCapitalizationItemId
        ));
      if (activeItemIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(
          newDealCapitalizationUseItemActiveIds[dealCapitalizationGroupId],
          activeItemIndex,
        );
        setDealCapitalizationUseItemActiveIds(newDealCapitalizationUseItemActiveIds);
      }
    }

    changeForm(`dealData.dealCapitalizationData.uses[${formKeyDealCapitalizationGroup}].items`
      + `[${formKeyDealCapitalizationItem}]`, null);
  }, [dealCapitalizationUseNewItems, dealCapitalizationUseItemActiveIds]);

  const onDealCapitalizationTableColumnsChange: Function = useCallback((
    tableColumnSettings: TableField[], isDefault?: boolean,
  ): void => {
    setDealCapitalizationTableColumnSettings(tableColumnSettings);

    if (!isDefault) {
      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_DEAL_CAPITALIZATION,
        tableColumnSettings,
      }));
    }
  }, []);

  const onDealCapitalizationModalCancel: Function = useCallback((): void => {
    closeDealCapitalizationSourcesModal();
    closeDealCapitalizationUsesModal();

    if (!isNewDeal && !isEditMode) {
      fullResetForm();
    }

    dispatch(toggleModalEditMode(false));
  }, [isNewDeal, isEditMode, queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onDealCapitalizationSourceInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealCapitalizationGroupId: number, dealCapitalizationItemId: number,
  ): void => {
    const newItemActiveIds: Record<string, number[]> = dealCapitalizationSourceItemActiveIds || {};

    setDealCapitalizationSourceItemActiveIds({
      ...newItemActiveIds,
      [dealCapitalizationGroupId]: [
        ...(newItemActiveIds[dealCapitalizationGroupId] || []),
        dealCapitalizationItemId,
      ],
    });

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealCapitalizationSourceItemActiveIds]);

  const onDealCapitalizationUseInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealCapitalizationGroupId: number, dealCapitalizationItemId: number,
  ): void => {
    const newItemActiveIds: Record<string, number[]> = dealCapitalizationUseItemActiveIds || {};

    setDealCapitalizationUseItemActiveIds({
      ...newItemActiveIds,
      [dealCapitalizationGroupId]: [
        ...(newItemActiveIds[dealCapitalizationGroupId] || []),
        dealCapitalizationItemId,
      ],
    });

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealCapitalizationUseItemActiveIds]);

  const onDealCapitalizationSourceItemCreateClick: Function = useCallback((): void => {
    setDealCapitalizationSourcesItemData({});
    setDealCapitalizationSourcesGroupData({});
    setIsNewRow(true);
    setIsDealCapitalizationSourcesModalOpen(true);
  }, []);

  const onDealCapitalizationUseItemCreateClick: Function = useCallback((): void => {
    setDealCapitalizationUsesItemData({});
    setDealCapitalizationUsesGroupData({});
    setIsNewRow(true);
    setIsDealCapitalizationUsesModalOpen(true);
  }, []);

  const onDealCapitalizationFileViewClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealCapitalizationFileFileItem: FileItem,
  ): void => {
    if (dealId && dealCapitalizationFileFileItem && event.button === POINTER_KEY_PRIMARY) {
      dispatch(viewDealFile({
        dealId,
        details: 'Viewed / downloaded deal capitalization file',
      }));
    }

    event.stopPropagation();
  }, [dealId, dealCapitalizationFileData]);

  const dealCapitalizationFileFileChange: Function = useCallback((file: File): void => {
    setDealIsDealCapitalizationFileDeleted(false);
    setDealCapitalizationFileFile(file);
    changeForm('dealData.dealCapitalizationFileData.file', file);
  }, []);

  const dealCapitalizationFileFileDelete: Function = useCallback((isNew?: boolean): void => {
    if (!isNew) {
      setDealIsDealCapitalizationFileDeleted(true);
    }

    setDealCapitalizationFileFile(null);
    changeForm('dealData.dealCapitalizationFileData.file', null);
  }, []);

  const onDealEquityOrganizationChartChange: Function = useCallback((file: File): void => {
    setDealIsEquityOrganizationChartDeleted(false);
    setDealEquityOrganizationChartFile(file);
    changeForm('dealData.dealEquityOrganizationChartData.file', file);
  }, []);

  const onDealEquityOrganizationChartDelete: Function = useCallback((): void => {
    setDealIsEquityOrganizationChartDeleted(true);
    setDealEquityOrganizationChartFile(null);
    changeForm('dealData.dealEquityOrganizationChartData.file', null);
  }, []);

  const onDealMetricsStatisticsAddNewItem: Function = useCallback((
    dealMetricsStatisticsGroupItem: DealMetricsStatisticsGroupInput,
  ): void => {
    if (!dealMetricsStatisticsGroupItem || !dealMetricsStatisticsGroupItem.id) {
      return;
    }

    const formKeyDealMetricsStatisticsGroup: string = FormService.getIndexedFormFieldKey(
      dealMetricsStatisticsGroupItem.id,
    );

    setDealMetricsStatisticsNewItems([...dealMetricsStatisticsNewItems, dealMetricsStatisticsGroupItem]);

    changeForm(`dealData.dealMetricsStatisticsData[${formKeyDealMetricsStatisticsGroup}]`, {
      id: dealMetricsStatisticsGroupItem.id,
      name: dealMetricsStatisticsGroupItem.name,
      values: {},
    });

    if (dealMetricsStatisticsGroupItem.values && dealMetricsStatisticsGroupItem.values.length) {
      dealMetricsStatisticsGroupItem.values.forEach((
        dealMetricsStatisticsItem: DealMetricsStatisticsItemInput,
      ): void => {
        if (dealMetricsStatisticsItem && dealMetricsStatisticsItem.id) {
          const formKeyDealMetricsStatisticsItem: string = FormService.getIndexedFormFieldKey(
            dealMetricsStatisticsItem.id,
          );

          changeForm(`dealData.dealMetricsStatisticsData[${formKeyDealMetricsStatisticsGroup}].values`
            + `[${formKeyDealMetricsStatisticsItem}]`, {
            id: dealMetricsStatisticsItem.id,
            value: dealMetricsStatisticsItem.value || '',
          });
        }
      });
    }
  }, [dealMetricsStatisticsNewItems]);

  const onDealMetricsStatisticsDeleteClick: Function = useCallback((dealMetricsStatisticsItemId: number): void => {
    const formKeyDealMetricsStatisticsItem: string = FormService.getIndexedFormFieldKey(
      dealMetricsStatisticsItemId,
    );

    if (dealMetricsStatisticsNewItems) {
      dealMetricsStatisticsNewItems.forEach((
        dealMetricsStatisticsItem: DealMetricsStatisticsItemInput, dealMetricsStatisticsItemIndex: number,
      ): void => {
        if (dealMetricsStatisticsItem && dealMetricsStatisticsItem.id === dealMetricsStatisticsItemId) {
          delete dealMetricsStatisticsNewItems[dealMetricsStatisticsItemIndex];
        }
      });
      setDealMetricsStatisticsNewItems([...dealMetricsStatisticsNewItems
        .filter((dealMetricsStatisticsItem: DealMetricsStatisticsItemInput): boolean => !!dealMetricsStatisticsItem)]);
    }

    changeForm(`dealData.dealMetricsStatisticsData[${formKeyDealMetricsStatisticsItem}]`, null);
  }, [dealMetricsStatisticsNewItems]);

  const onDealTermAddNewItem: Function = useCallback((dealTermGroupId: number, dealTermItem: DealTermInput): void => {
    const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroupId);
    const formKeyDealTerm: string = FormService.getIndexedFormFieldKey(dealTermItem.id as number);

    setDealTermNewItems({
      ...dealTermNewItems,
      [dealTermGroupId]: [...(dealTermNewItems[dealTermGroupId] || []), dealTermItem],
    });

    changeForm(`dealData.dealTermsData[${formKeyDealTermGroup}].terms[${formKeyDealTerm}].groupId`, dealTermGroupId);
    changeForm(`dealData.dealTermsData[${formKeyDealTermGroup}].terms[${formKeyDealTerm}].name`, null);
    changeForm(`dealData.dealTermsData[${formKeyDealTermGroup}].terms[${formKeyDealTerm}].content`, null);
  }, [dealTermNewItems]);

  const openDealTermGroupModal: Function = useCallback((): void => {
    setIsDealTermGroupModalOpen(true);
    setIsNewDealTermGroup(true);
  }, []);

  const closeDealTermGroupModal: Function = useCallback((): void => {
    setIsDealTermGroupModalOpen(false);
    setIsNewDealTermGroup(false);
  }, []);

  const onDealTermCreateGroupClick: Function = useCallback((): void => {
    if (isEditMode || isNewDeal) {
      const dealTermGroupItemId: number = StructureService.getNewItemId(dealTermNewGroupItems);
      const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroupItemId);

      changeForm(`dealData.dealTermsData[${formKeyDealTermGroup}]`, {
        name: null,
        terms: {},
      });

      setDealTermNewGroupItems([...dealTermNewGroupItems, {
        id: dealTermGroupItemId,
        name: null,
        terms: [],
      }]);

      setDealTermExpandedGroups([...dealTermExpandedGroups, dealTermExpandedGroups.length + 1]);
    } else {
      openDealTermGroupModal();
    }
  }, [isEditMode, isNewDeal, dealTermsData, dealTermNewGroupItems, dealTermExpandedGroups]);

  const onDealTermItemDeleteClick: Function = useCallback((dealTermId: number, dealTermGroupId: number): void => {
    const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroupId);
    const formKeyDealTerm: string = FormService.getIndexedFormFieldKey(dealTermId);

    if (dealTermNewItems[dealTermGroupId]) {
      dealTermNewItems[dealTermGroupId].forEach((dealTermItem: DealTermInput, dealTermItemIndex: number): void => {
        if (dealTermItem && dealTermItem.id === dealTermId) {
          delete dealTermNewItems[dealTermGroupId][dealTermItemIndex];
        }
      });

      setDealTermNewItems({
        ...dealTermNewItems,
        [dealTermGroupId]: dealTermNewItems[dealTermGroupId]
          .filter((dealTermItem: DealTermInput): boolean => !!dealTermItem),
      });
    }

    changeForm(`dealData.dealTermsData[${formKeyDealTermGroup}].terms[${formKeyDealTerm}]`, null);
  }, [dealTermNewItems]);

  const onDealTermGroupDeleteClick: Function = useCallback((dealTermGroupId: number): void => {
    const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroupId);

    if (dealTermNewGroupItems && dealTermNewGroupItems.length) {
      dealTermNewGroupItems.forEach((dealTermGroupItem: DealTermGroupInput, dealTermGroupItemIndex: number): void => {
        if (dealTermGroupItem && dealTermGroupItem.id === dealTermGroupId) {
          delete dealTermNewGroupItems[dealTermGroupItemIndex];
        }
      });
      setDealTermNewGroupItems([...dealTermNewGroupItems
        .filter((dealTermGroupItem: DealTermGroupInput): boolean => !!dealTermGroupItem)]);
    }

    if (dealTermNewItems[dealTermGroupId]) {
      delete dealTermNewItems[dealTermGroupId];

      setDealTermNewItems({ ...dealTermNewItems });
    }

    changeForm(`dealData.dealTermsData[${formKeyDealTermGroup}]`, null);
  }, [dealTermNewGroupItems, dealTermNewItems]);

  const openDealTermDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealTermId: number,
  ): void => {
    setDealTermActiveId(dealTermId);
    setIsDealTermDeleteConfirmModalOpen(true);
  }, []);

  const openDealTermGroupDeleteConfirmModal: Function = useCallback((dealTermGroupId: number): void => {
    setDealTermGroupActiveId(dealTermGroupId);
    setIsDealTermGroupDeleteConfirmModalOpen(true);
  }, []);

  const closeDealTermDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealTermDeleteConfirmModalOpen(false);
    setDealTermActiveId(null);
  }, []);

  const closeDealTermGroupDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealTermGroupDeleteConfirmModalOpen(false);
    setDealTermGroupActiveId(null);
  }, []);

  const onDealTermDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealTermActiveId || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealTerm({
        dealTermId: dealTermActiveId,
        dealId,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealTermDeleteConfirmModalOpen(false);

        setDealTermGroupActiveId();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealTermActiveId, dealId]);

  const onDealTermGroupDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealTermGroupActiveId || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealTermGroup({
        dealTermGroupId: dealTermGroupActiveId,
        dealId,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealTermGroupDeleteConfirmModalOpen(false);

        setDealTermGroupActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealTermGroupActiveId, dealId]);

  const openDealTermExpansionGroups: Function = useCallback((isDefault?: boolean): void => {
    if ((isDefault && dealTermsData.length <= DEAL_GROUP_DEFAULT_COLLAPSE_LIMIT) || !isDefault) {
      setDealTermExpandedGroups(dealTermsData && dealTermsData.length
        ? dealTermsData.map((dealTermItem: DealTerm, dealTermIndex: number): number => dealTermIndex + 1) : []);
    }
  }, [dealTermsData]);

  const closeDealTermExpansionGroups: Function = useCallback((): void => {
    setDealTermExpandedGroups([]);
  }, []);

  const onDealTermExpansionGroupChange: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, groupIndex: number,
  ): void => {
    if (dealTermExpandedGroups) {
      if (dealTermExpandedGroups.includes(groupIndex)) {
        delete dealTermExpandedGroups[dealTermExpandedGroups.indexOf(groupIndex)];
        setDealTermExpandedGroups([...dealTermExpandedGroups.filter((
          expandedGroup: number,
        ): boolean => !!expandedGroup)]);
      } else {
        setDealTermExpandedGroups([...dealTermExpandedGroups, groupIndex]);
      }
    }

    event.stopPropagation();
  }, [dealTermExpandedGroups]);

  const onDealHistoricalValuationsAddNewItem: Function = useCallback((
    dealHistoricalValuationsItem: DealHistoricalValuationsItemInput,
  ): void => {
    setDealHistoricalValuationsNewItems([...dealHistoricalValuationsNewItems, dealHistoricalValuationsItem]);
  }, [dealHistoricalValuationsNewItems]);

  const onDealHistoricalValuationsDeleteClick: Function = useCallback((
    dealHistoricalValuationsItemId: number,
  ): void => {
    const formKeyDealHistoricalValuationsItem: string = FormService.getIndexedFormFieldKey(
      dealHistoricalValuationsItemId,
    );

    dealHistoricalValuationsNewItems.forEach((
      dealHistoricalValuationsItem: DealHistoricalValuationsItemInput, dealHistoricalValuationsItemIndex: number,
    ): void => {
      if (dealHistoricalValuationsItem && dealHistoricalValuationsItem.id === dealHistoricalValuationsItemId) {
        delete dealHistoricalValuationsNewItems[dealHistoricalValuationsItemIndex];
      }
    });

    setDealHistoricalValuationsNewItems([
      ...dealHistoricalValuationsNewItems.filter((
        dealHistoricalValuationsItem: DealHistoricalValuationsItemInput,
      ): boolean => !!dealHistoricalValuationsItem),
    ]);

    changeForm(`dealData.dealHistoricalValuationsData[${formKeyDealHistoricalValuationsItem}]`, null);
  }, [dealHistoricalValuationsNewItems]);

  const onDealTopLevelMetricsAddNewItem: Function = useCallback((
    dealTopLevelMetricsItem: DealTopLevelMetricsItemInput,
  ): void => {
    setDealTopLevelMetricsNewItems([...dealTopLevelMetricsNewItems, dealTopLevelMetricsItem]);
  }, [dealTopLevelMetricsNewItems]);

  const onDealTopLevelMetricsDeleteClick: Function = useCallback((dealTopLevelMetricsItemId: number): void => {
    const formKeyDealTopLevelMetricsItem: string = FormService.getIndexedFormFieldKey(
      dealTopLevelMetricsItemId,
    );

    dealTopLevelMetricsNewItems.forEach((
      dealTopLevelMetricsItem: DealTopLevelMetricsItemInput, dealTopLevelMetricsItemIndex: number,
    ): void => {
      if (dealTopLevelMetricsItem && dealTopLevelMetricsItem.id === dealTopLevelMetricsItemId) {
        delete dealTopLevelMetricsNewItems[dealTopLevelMetricsItemIndex];
      }
    });

    setDealTopLevelMetricsNewItems([
      ...dealTopLevelMetricsNewItems.filter((
        dealTopLevelMetricsItem: DealTopLevelMetricsItemInput,
      ): boolean => !!dealTopLevelMetricsItem),
    ]);

    changeForm(`dealData.dealTopLevelMetricsData[${formKeyDealTopLevelMetricsItem}]`, null);
  }, [dealTopLevelMetricsNewItems]);

  const openDealValuationFileModal: Function = useCallback((
    dealValuationFileItem: DealFile, dealValuationFileGroupItem: DealFileGroup, isNewItem?: boolean,
  ): void => {
    if (dealValuationFileItem) {
      setDealValuationFileData({ ...dealValuationFileItem });
    }

    if (dealValuationFileGroupItem) {
      setDealValuationFileGroupData({ ...dealValuationFileGroupItem });
    }

    if (isNewItem) {
      setIsNewRow(true);
    }

    setIsDealValuationFileModalOpen(true);
  }, []);

  const closeDealValuationFileModal: Function = useCallback((): void => {
    setDealValuationFileData(null);
    setDealValuationFileGroupData(null);
    setIsDealValuationFileModalOpen(false);
    setIsNewRow(false);
  }, []);

  const onDealValuationFileChange: Function = useCallback((
    file: File, dealValuationFileGroupId: number, dealValuationFileId: number,
  ): void => {
    const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(
      dealValuationFileGroupId,
    );
    const formKeyDealValuationFile: string = FormService.getIndexedFormFieldKey(dealValuationFileId);

    setDealValuationFileFiles({
      ...dealValuationFileFiles,
      [dealValuationFileGroupId]: {
        ...dealValuationFileFiles[dealValuationFileGroupId] || {},
        [dealValuationFileId]: file,
      },
    });

    changeForm(`dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].files`
      + `[${formKeyDealValuationFile}].file`, file);
  }, [dealValuationFileFiles]);

  const onDealValuationFileDelete: Function = useCallback((
    dealValuationFileGroupId: number, dealValuationFileId: number,
  ): void => {
    const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(
      dealValuationFileGroupId,
    );
    const formKeyDealValuationFile: string = FormService.getIndexedFormFieldKey(dealValuationFileId);

    const newDealValuationFileFiles: Record<string, Record<string, DealFile>> = dealValuationFileFiles;

    if (newDealValuationFileFiles[dealValuationFileGroupId]) {
      delete newDealValuationFileFiles[dealValuationFileGroupId][dealValuationFileId];
      setDealValuationFileFiles({ ...newDealValuationFileFiles });
      changeForm(
        `dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].files[${formKeyDealValuationFile}].file`,
        null,
      );
    }
  }, [dealValuationFileFiles]);

  const onDealValuationFileAddNewItem: Function = useCallback((
    dealValuationFileGroupId: number, dealFileItem: DealFileInput,
  ): void => {
    if (!dealFileItem || !dealFileItem.id) {
      return;
    }

    const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(
      dealValuationFileGroupId,
    );
    const formKeyDealValuationFile: string = FormService.getIndexedFormFieldKey(dealFileItem.id);

    changeForm(
      `dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].files[${formKeyDealValuationFile}].name`,
      null,
    );
    changeForm(
      `dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].files[${formKeyDealValuationFile}].file`,
      null,
    );
    changeForm(
      `dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].files[${formKeyDealValuationFile}].groupId`,
      dealValuationFileGroupId,
    );
    setDealValuationFileNewItems({
      ...dealValuationFileNewItems,
      [dealValuationFileGroupId]: [
        ...((dealValuationFileNewItems && dealValuationFileNewItems[dealValuationFileGroupId]) || []),
        dealFileItem,
      ],
    });
  }, [dealValuationFileNewItems]);

  const onDealValuationFileItemDeleteClick: Function = useCallback((
    dealValuationFileId: number, dealValuationFileGroupId: number,
  ): void => {
    const formKeyDealValuationFileGroup: string = FormService.getIndexedFormFieldKey(
      dealValuationFileGroupId,
    );
    const formKeyDealValuationFile: string = FormService.getIndexedFormFieldKey(dealValuationFileId);

    if (dealValuationFileNewItems[dealValuationFileGroupId]) {
      dealValuationFileNewItems[dealValuationFileGroupId].forEach((
        dealValuationFile: DealFileInput, dealValuationFileItemIndex: number,
      ): void => {
        if (dealValuationFile && dealValuationFile.id === dealValuationFileId) {
          delete dealValuationFileNewItems[dealValuationFileGroupId][dealValuationFileItemIndex];
        }
      });
      setDealValuationFileNewItems({
        ...dealValuationFileNewItems,
        [dealValuationFileGroupId]: dealValuationFileNewItems[dealValuationFileGroupId]
          .filter((dealFileItem: DealFileInput): boolean => !!dealFileItem),
      });

      onDealValuationFileDelete(dealValuationFileGroupId, dealValuationFileId);

      changeForm(`dealData.dealValuationFilesData[${formKeyDealValuationFileGroup}].files`
        + `[${formKeyDealValuationFile}]`, null);
    }
  }, [dealValuationFileNewItems]);

  const openDealValuationFileDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealValuationFileId: number,
  ): void => {
    setDealValuationFileActiveId(dealValuationFileId);
    setIsDealValuationFileDeleteConfirmModalOpen(true);
  }, []);

  const closeDealValuationFileDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealValuationFileDeleteConfirmModalOpen(false);
    setDealValuationFileActiveId(null);
  }, []);

  const onDealValuationFileInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealValuationFileId: number,
  ): void => {
    setDealValuationFileActiveIds([...(dealValuationFileActiveIds || []), dealValuationFileId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealValuationFileActiveIds]);

  const onDealValuationFileDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealValuationFileActiveId || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealValuationFile({
        dealValuationFileId: dealValuationFileActiveId,
        dealId,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealValuationFileDeleteConfirmModalOpen(false);

        setDealValuationFileActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealValuationFileActiveId, dealId]);

  const onDealValuationFileViewClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealValuationFileFile: FileItem, dealValuationFileGroupName: string,
  ): void => {
    if (dealId && dealValuationFileFile && dealValuationFileFile.name && dealValuationFileGroupName
      && event.button === POINTER_KEY_PRIMARY) {
      dispatch(viewDealFile({
        dealId,
        details: `Viewed / downloaded deal valuation file ${dealValuationFileFile.name} `
          + `of group ${dealValuationFileGroupName}`,
      }));
    }
  }, [dealId]);

  const checkIsDealValuationFileActive: Function = useCallback((dealValuationFileId: number): boolean => (
    isSmallScreen || (dealValuationFileActiveIds ? dealValuationFileActiveIds.includes(dealValuationFileId) : false)
  ), [dealValuationFileActiveIds, isSmallScreen]);

  const openDealTaskModal: Function = useCallback((
    dealTaskItem: DealTask, dealTaskGroupItem: DealTaskGroup, isCommentFormActive?: boolean, isNew?: boolean,
  ): void => {
    if (dealTaskItem) {
      setDealTaskData({ ...dealTaskItem });
    }

    if (dealTaskGroupItem) {
      setDealTaskGroupData({ ...dealTaskGroupItem });
    }

    if (isCommentFormActive) {
      setDealTaskIsCommentFormActive(isCommentFormActive);
    }

    if (isNew) {
      setIsNewRow(true);
    }

    setIsDealTaskModalOpen(true);
  }, [isEditMode]);

  const onDealValuationFileCreateClick: Function = useCallback((): void => {
    setDealValuationFileData(null);
    setDealValuationFileGroupData(null);
    openDealValuationFileModal(null, null, true);
  }, []);

  const closeDealTaskModal: Function = useCallback((): void => {
    setIsDealTaskModalOpen(false);
    setDealTaskIsCommentFormActive(false);
    setDealTaskData(null);
    setDealTaskGroupData(null);
    setIsNewRow(false);

    if (isEditMode) {
      setDealTaskInlineSaveId(FormService.FORM_KEY_NEW);
      setDealTaskGroupInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    }
  }, [isEditMode, dealTaskActiveIds]);

  const closeDealTaskGroupModal: Function = useCallback((): void => {
    setIsDealTaskGroupModalOpen(false);
    setIsNewDealTaskGroup(false);
  }, []);

  const openDealTaskNewModal: Function = useCallback((): void => {
    setDealTaskIsCommentFormActive(false);
    setDealTaskData(null);
    setDealTaskGroupData(null);
    openDealTaskModal(null, null, false, true);
  }, []);

  const openDealTaskExportModal: Function = useCallback((): void => {
    let dealTaskActiveIdList: number[] = [];
    if (dealTaskRowsSelected) {
      Object.keys(dealTaskRowsSelected).forEach((dealTaskGroupIndex: string): void => {
        const dealTaskItemIds: number[] = dealTaskRowsSelected[dealTaskGroupIndex];

        dealTaskActiveIdList = [...dealTaskActiveIdList, ...dealTaskItemIds];
      });
    }
    setDealTaskActiveIds(dealTaskActiveIdList);

    setDealTaskIsExportModalOpen(true);
  }, [dealTaskRowsSelected]);

  const closeDealTaskExportModal: Function = useCallback((): void => {
    setDealTaskIsExportModalOpen(false);

    setDealTaskActiveIds([]);

    closeMessage();
  }, []);

  const openDealTaskStatusChangeModal: Function = useCallback((
    event: React.SyntheticEvent,
    dealTaskIds: number[],
  ): void => {
    let dealTaskActiveIdList: number[] = dealTaskIds || [];
    if ((!dealTaskIds || !dealTaskIds.length) && dealTaskRowsSelected) {
      Object.keys(dealTaskRowsSelected).forEach((dealTaskGroupIndex: string): void => {
        const dealTaskItemIds: number[] = dealTaskRowsSelected[dealTaskGroupIndex];

        dealTaskActiveIdList = [...dealTaskActiveIdList, ...dealTaskItemIds];
      });
    }
    setDealTaskActiveIds(dealTaskActiveIdList);

    setDealTaskIsStatusChangeModalOpen(true);
  }, [dealTaskRowsSelected]);

  const closeDealTaskStatusModal: Function = useCallback((): void => {
    setStatusModalErrorMessage(null);
    setDealTaskIsStatusChangeModalOpen(false);

    setDealTaskActiveIds([]);

    closeMessage();
  }, []);

  const openDealTaskListModal: Function = useCallback((event: React.SyntheticEvent, dealTaskIds: number[]): void => {
    let dealTaskActiveIdList: number[] = dealTaskIds || [];
    if ((!dealTaskIds || !dealTaskIds.length) && dealTaskRowsSelected) {
      Object.keys(dealTaskRowsSelected).forEach((dealTaskGroupIndex: string): void => {
        const dealTaskItemIds: number[] = dealTaskRowsSelected[dealTaskGroupIndex];

        dealTaskActiveIdList = [...dealTaskActiveIdList, ...dealTaskItemIds];
      });
    }
    setDealTaskActiveIds(dealTaskActiveIdList);

    setDealTaskIsListModalOpen(true);
  }, [dealTaskRowsSelected]);

  const closeDealTaskListModal: Function = useCallback((): void => {
    setDealTaskListModalErrorMessage(null);
    setDealTaskIsListModalOpen(false);

    setDealTaskActiveIds([]);

    closeMessage();
  }, []);

  const openDealTaskTemplateSaveModal: Function = useCallback((): void => {
    setDealTaskIsTemplateSaveModalOpen(true);
  }, []);

  const closeDealTaskTemplateSaveModal: Function = useCallback((): void => {
    setDealTaskIsTemplateSaveModalOpen(false);

    closeMessage();
  }, []);

  const openDealTaskTemplateLoadModal: Function = useCallback((): void => {
    setDealTaskIsTemplateLoadModalOpen(true);
  }, []);

  const closeDealTaskTemplateLoadModal: Function = useCallback((): void => {
    setDealTaskIsTemplateLoadModalOpen(false);

    closeMessage();
  }, []);

  const onDealTaskTemplateSave: Function = useCallback((
    dealTemplateTitle: string, dealTemplateDescription: string, dealTemplateInitialData: DealTemplateInitialDataInput,
    onSuccess?: Function,
  ): Promise<ActionCallback> | null => {
    if (!dealTemplateTitle || !dealTemplateInitialData || !dealId) {
      return null;
    }

    const dealTemplateData: DealTemplateInput = {
      title: dealTemplateTitle,
      description: dealTemplateDescription || null,
      initialData: dealTemplateInitialData,
      type: DEAL_TEMPLATE_TYPE_DUE_DILIGENCE_TEMPLATE,
    };

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealTaskTemplateData({
        dealTemplate: dealTemplateData,
        dealId,
        filters: dealTaskFilters,
        page: dealTaskPage || null,
      }, (response: ActionResponse): void => {
        setDealTaskIsTemplateSaveModalOpen(false);

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setDealTaskTemplateSaveModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealId, dealTaskFilters, dealTaskPage]);

  const onDealTaskTemplateLoadData: Function = useCallback((
    dealTemplateId: number, onSuccess?: Function,
  ): Promise<ActionCallback> | null => {
    if (!dealId || !dealTemplateId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(loadDealTaskTemplateData({
        dealTemplateId,
        dealId,
        filters: dealTaskFilters,
        page: dealTaskPage || null,
      }, (response: ActionResponse): void => {
        setDealTaskIsTemplateLoadModalOpen(false);

        setStatusMessage('Template data loaded successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setDealTaskTemplateLoadModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealId, dealTaskFilters, dealTaskPage]);

  const onDealTaskListSaveProcess: Function = useCallback(async (
    contactId: number, dueDate: string, status: string, onSuccess?: Function,
  ): Promise<ActionCallback | null> => {
    if ((!contactId && contactId !== FILTER_VALUE_EMPTY && !dueDate && !status)
      || !dealId || !dealTaskActiveIds || !dealTaskActiveIds.length) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealTaskList({
        dealTaskListForm: {
          dealTaskIds: dealTaskActiveIds,
          dealId,
          newStatus: status || null,
          contactId: contactId || contactId === FILTER_VALUE_EMPTY ? contactId : null,
          dueDate: dueDate || null,
        },
        filters: dealTaskFilters,
        page: dealTaskPage || null,
        allRows: true,
      }, (response: ActionResponse): void => {
        setDealTaskIsListModalOpen(false);
        setDealTaskIsStatusChangeModalOpen(false);
        setDealTaskListModalErrorMessage(null);
        setStatusModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          if (status) {
            setStatusModalErrorMessage(exception.message);
          }

          if (contactId || dueDate) {
            setDealTaskListModalErrorMessage(exception.message);
          }
        }
      }));
    });
  }, [
    dealId, dealTaskActiveIds, dealTaskFilters, dealTaskRowsSelectedDefault, dealTaskPage,
    dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onDealTaskListSave: Function = useCallback(async (
    contactId: number, dueDate: string, onSuccess?: Function,
  ): Promise<void> => {
    await onDealTaskListSaveProcess(contactId, dueDate, null, onSuccess);
  }, [dealId, dealTaskActiveIds, dealTaskFilters, dealTaskRowsSelectedDefault, dealTaskPage]);

  const onDealTaskStatusModalSaveClick: Function = useCallback(async (
    status: string, statusChangeReason: string, statusChangeReasonComment: string, onSuccess?: Function,
  ): Promise<void> => {
    await onDealTaskListSaveProcess(null, null, status, onSuccess);
  }, [dealId, dealTaskActiveIds, dealTaskFilters, dealTaskRowsSelectedDefault, dealTaskPage]);

  const onDealTaskListChange: Function = useCallback((): void => {
    if (dealTaskListModalErrorMessage) {
      setDealTaskListModalErrorMessage(null);
    }
  }, [dealTaskListModalErrorMessage]);

  const onDealTaskModalExportSuccess: Function = useCallback((): void => {
    onCancelForm();

    setStatusMessage('Exported successfully');
    setIsOpenMessage(true);
  }, [
    dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onDealTaskCreateClick: Function = useCallback((): void => {
    openDealTaskNewModal();
  }, []);

  const onDealTaskAddComment: Function = useCallback((
    dealTaskId: number, comment: string, attachmentFiles: File[], mentionContactIds: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveDealTaskComment({
      comment, attachmentFiles, dealTaskId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onDealTaskUpdateComment: Function = useCallback((
    dealTaskId: number, comment: string, attachments: AttachmentInput[], attachmentFiles: File[], commentId: number,
    mentionContactIds: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveDealTaskComment({
      comment, attachments, attachmentFiles, dealTaskId, commentId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onDealTaskDeleteComment: Function = useCallback((
    dealTaskId: number,
    commentId: number,
    onSuccess?: Function,
  ): void => {
    dispatch(deleteDealTaskComment({ dealTaskId, commentId }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onDealTaskStatusChange: Function = useCallback((dealTaskId: number, newStatus: string): void => {
    dispatch(saveDealTaskStatus({
      dealTaskId,
      newStatus,
      dealId,
      filters: dealTaskFilters,
      page: dealTaskPage || null,
    }, (): void => {
      onCancelForm();
    }));
  }, [
    dealId, dealTaskFilters, dealTaskPage, dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault,
    dealCapitalizationSourceItemActiveIds, dealCapitalizationUseItemActiveIds, dealTaskActiveIds,
    dealValuationFileActiveIds, dealParticipantActiveIds, dealFileActiveIds, dealActivityActiveIds,
    dealCapitalizationSourceItemInlineSaveId, dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId,
    dealTaskInlineSaveId, queryTab, dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId,
    dealPhotoListDefault, isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onDealTaskReviewButtonClick: Function = useCallback((dealTaskReviewId: number, newStatus: string): void => {
    dispatch(saveDealTaskReviewStatus({
      dealTaskReviewId,
      newStatus,
      dealId,
      filters: dealTaskFilters,
      page: dealTaskPage || null,
    }, (): void => {
      setStatusMessage('Saved successfully');
      setIsOpenMessage(true);
    }));
  }, [dealId, dealTaskFilters, dealTaskPage]);

  const onDealTaskAddNewItem: Function = useCallback((dealTaskGroupId: number, dealTask: DealTaskInput): void => {
    const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);
    const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTask.id as number);

    changeForm(`dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].name`, null);
    changeForm(`dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].contactId`, null);
    changeForm(`dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].groupId`, dealTaskGroupId);
    changeForm(
      `dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].dueDate`,
      DateService.parseDate(DateService.getDateNow()),
    );
    changeForm(
      `dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].status`,
      DEAL_TASK_STATUS_INITIAL,
    );

    setDealTaskNewItems({
      ...dealTaskNewItems,
      [dealTaskGroupId]: [...(dealTaskNewItems[dealTaskGroupId] || []), dealTask],
    });
  }, [dealTaskNewItems]);

  const openDealTaskGroupModal: Function = useCallback((): void => {
    setIsDealTaskGroupModalOpen(true);
    setIsNewDealTaskGroup(true);
  }, []);

  const onDealTaskGroupCreateClick: Function = useCallback((): void => {
    openDealTaskGroupModal();
  }, [isEditMode, dealTasksData, dealTaskExpandedGroups]);

  const onDealTaskItemDeleteClick: Function = useCallback((dealTaskId: number, dealTaskGroupId: number): void => {
    const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);
    const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);

    if (dealTaskNewItems[dealTaskGroupId]) {
      dealTaskNewItems[dealTaskGroupId].forEach((dealTaskItem: DealTaskInput, dealTaskItemIndex: number): void => {
        if (dealTaskItem && dealTaskItem.id === dealTaskId) {
          delete dealTaskNewItems[dealTaskGroupId][dealTaskItemIndex];
        }
      });
      setDealTaskNewItems({
        ...dealTaskNewItems,
        [dealTaskGroupId]: dealTaskNewItems[dealTaskGroupId].filter((
          dealTaskItem: DealTaskInput,
        ): boolean => !!dealTaskItem),
      });

      changeForm(`dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`, null);
    }
  }, [dealTaskNewItems]);

  const onDealTaskCloseNewGroupForm: Function = useCallback((dealTaskId: number, dealTaskGroupId: number): void => {
    const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);
    const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);

    changeForm(`dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].group`, null);
  }, []);

  const onDealTaskGroupChange: Function = useCallback((dealTaskId: number, dealTaskGroupId: number): void => {
    const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);
    const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);

    changeForm(`dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].parentId`, null);
  }, []);

  const onDealTaskReviewContactsChange: Function = useCallback((
    dealTaskId: number, dealTaskGroupId: number, dealTaskReviewValues: DealTaskReview[],
  ): void => {
    const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);
    const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);

    changeForm(
      `dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}].reviewContactIds`,
      dealTaskReviewValues,
    );
  }, []);

  const openDealTaskDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealTaskId: number,
  ): void => {
    setDealTaskActiveIds(dealTaskId ? [dealTaskId] : (dealTaskRowsSelected && Object.values(dealTaskRowsSelected)
      .reduce((resultDealTaskItemIds: number[], dealTaskItemIds: number[]): number[] => ([
        ...resultDealTaskItemIds,
        ...dealTaskItemIds,
      ]), [])));
    setIsDealTaskDeleteConfirmModalOpen(true);
  }, [dealTaskRowsSelected]);

  const openDealTaskGroupDeleteConfirmModal: Function = useCallback((dealTaskGroupId: number): void => {
    setDealTaskGroupActiveId(dealTaskGroupId);
    setIsDealTaskGroupDeleteConfirmModalOpen(true);
  }, []);

  const closeDealTaskDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealTaskDeleteConfirmModalOpen(false);
    setDealTaskActiveIds([]);
    setDealTaskAllRowsSelected(false);
    setDealTaskRowsSelected(dealTaskRowsSelectedDefault);
  }, [dealTaskRowsSelectedDefault]);

  const closeDealTaskGroupDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealTaskGroupDeleteConfirmModalOpen(false);
    setDealTaskGroupActiveId(null);
  }, []);

  const onDealTaskDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealTaskActiveIds || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealTaskList({
        dealTaskIds: dealTaskActiveIds,
        dealId,
        filters: dealTaskFilters,
        page: dealTaskPage || null,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealTaskDeleteConfirmModalOpen(false);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealTaskActiveIds, dealId, dealTaskFilters, dealTaskRowsSelectedDefault, dealTaskPage]);

  const onDealTaskGroupDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealTaskGroupActiveId || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealTaskGroup({
        dealTaskGroupId: dealTaskGroupActiveId,
        dealId,
        filters: dealTaskFilters,
        page: dealTaskPage || null,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealTaskGroupDeleteConfirmModalOpen(false);

        setDealTaskGroupActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealTaskGroupActiveId, dealId, dealTaskFilters, dealTaskPage]);

  const openDealTaskExpansionGroups: Function = useCallback((
    event?: React.MouseEvent<Element, MouseEvent>, isDefault?: boolean,
  ): void => {
    if ((isDefault && dealTasksData.length <= DEAL_GROUP_DEFAULT_COLLAPSE_LIMIT) || !isDefault) {
      setDealTaskExpandedGroups(dealTasksData && dealTasksData.length
        ? dealTasksData.map((dealTaskItem: DealTask, dealTaskIndex: number): number => dealTaskIndex + 1) : []);
    }

    const initialDealTaskCollapsedParents: Record<string, boolean> = {};
    dealTasksData.forEach((dealTaskGroupItem: DealTaskGroup): void => {
      dealTaskGroupItem.tasks.forEach((dealTaskItem: DealTask): void => {
        initialDealTaskCollapsedParents[dealTaskItem.id] = isEditMode || isNewDeal
          || !dealTaskItem.subTasks || !dealTaskItem.subTasks.length || (dealTaskItem.subTasks
            && ((isDefault && dealTaskItem.subTasks.length <= DEAL_SUBTASK_DEFAULT_LIMIT) || !isDefault));
      });
    });
    setDealTaskCollapsedParents(initialDealTaskCollapsedParents);
  }, [dealTasksData, isEditMode, isNewDeal]);

  const closeDealTaskExpansionGroups: Function = useCallback((): void => {
    setDealTaskExpandedGroups([]);

    const initialDealTaskCollapsedParents: Record<string, boolean> = {};
    dealTasksData.forEach((dealTaskGroupItem: DealTaskGroup): void => {
      dealTaskGroupItem.tasks.forEach((dealTaskItem: DealTask): void => {
        initialDealTaskCollapsedParents[dealTaskItem.id] = true;
      });
    });
    setDealTaskCollapsedParents(initialDealTaskCollapsedParents);
  }, [dealTasksData]);

  const dealTaskExpansionGroupChange: Function = useCallback((groupIndex: number): void => {
    if (dealTaskExpandedGroups) {
      if (dealTaskExpandedGroups.includes(groupIndex)) {
        delete dealTaskExpandedGroups[dealTaskExpandedGroups.indexOf(groupIndex)];
        setDealTaskExpandedGroups([
          ...dealTaskExpandedGroups.filter((expandedGroup: number): boolean => !!expandedGroup),
        ]);
      } else {
        setDealTaskExpandedGroups([...dealTaskExpandedGroups, groupIndex]);
      }
    }
  }, [dealTaskExpandedGroups]);

  const onDealTaskSelectRow: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, rowGroupId: number, rowId: number,
    selectedRows: Record<string, number[]>, onSelectRows?: Function,
  ): void => {
    if (onSelectRows) {
      onSelectRows({
        ...selectedRows,
        [rowGroupId]: StructureService.selectRow(selectedRows, rowId, rowGroupId),
      });
    }

    setDealTaskAllRowsSelected(false);

    event.stopPropagation();
  }, []);

  const onDealTaskSelectRows: Function = useCallback((selectedRows: Record<string, number[]>): void => {
    setDealTaskRowsSelected(selectedRows);
  }, []);

  const onDealTaskLoadBatchData: Function = useCallback((allRows?: boolean, onSuccess?: Function): void => {
    dispatch(getDealTaskListData({
      dealId,
      page: dealTaskPage + PAGE_LIST_DEFAULT,
      filters: dealTaskFilters,
      allRows: allRows || false,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
    setDealTaskPage(dealTaskPage + PAGE_LIST_DEFAULT);
  }, [dealId, dealTaskPage, dealTaskFilters]);

  const onDealTaskExpansionGroupChange: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, groupIndex: number, withLoading?: boolean,
  ): void => {
    if (withLoading) {
      onDealTaskLoadBatchData(true, (): void => {
        dealTaskExpansionGroupChange(groupIndex);
      });
    } else {
      dealTaskExpansionGroupChange(groupIndex);
    }

    event.stopPropagation();
  }, [dealTaskExpandedGroups, dealId, dealTaskPage, dealTaskFilters]);

  const applyDealTaskUrlFilters: Function = useCallback((): Record<string, FilterValue> => {
    const filterValues: Record<string, FilterValue> = { ...dealTaskFilters };

    if (queryFilterStatus) {
      filterValues[FILTER_TYPE_STATUS] = queryFilterStatus;
    }

    if (queryFilterContactId) {
      filterValues[FILTER_TYPE_CONTACT_ID] = queryFilterContactId;
    }

    setDealTaskFilters(filterValues);

    return filterValues;
  }, [dealTaskFilters, queryFilterStatus, queryFilterContactId]);

  const onDealTaskResetFilters: Function = useCallback((): void => {
    setDealTaskCurrentFilters({ ...dealTaskFilters });
  }, [dealTaskFilters]);

  const onDealTaskApplyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || dealTaskCurrentFilters;

    setDealTaskFilters(resultNewFilters);
    setDealTaskPage(PAGE_LIST_DEFAULT);

    dispatch(getDealTaskListData({
      dealId,
      filters: resultNewFilters,
    }));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (resultNewFilters) {
      Object.entries(resultNewFilters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem || valueItem === false) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, dealTaskCurrentFilters, dealId, queryTab]);

  const dealTaskSetFilterValue: Function = useCallback((
    filterType: string, value: string,
  ): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = { ...dealTaskCurrentFilters, [filterType]: selectedValue };

    setDealTaskCurrentFilters(newFilters);

    return newFilters;
  }, [dealTaskCurrentFilters]);

  const onDealTaskDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = dealTaskSetFilterValue(filterType, null);

    onDealTaskApplyFilters(newFilters);
  }, [locationQueryParams, dealTaskCurrentFilters, dealId, queryTab]);

  const onDealTaskLongClick: Function = useCallback((): void => {
    dispatch(toggleSelectMode(true));
  }, []);

  const onDealTaskInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealTaskId: number,
  ): void => {
    setDealTaskActiveIds([...(dealTaskActiveIds || []), dealTaskId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealTaskActiveIds]);

  const onDealTaskInlineGroupEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealTaskGroupId: number,
  ): void => {
    setDealTaskGroupActiveIds([...(dealTaskGroupActiveIds || []), dealTaskGroupId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealTaskGroupActiveIds]);

  const onDealTaskSwitchParentRow: Function = useCallback((dealTaskId: number): void => {
    const newDealTaskCollapsedParents: Record<string, boolean> = { ...dealTaskCollapsedParents };
    newDealTaskCollapsedParents[dealTaskId] = !newDealTaskCollapsedParents[dealTaskId];
    setDealTaskCollapsedParents({ ...newDealTaskCollapsedParents });
  }, [dealTaskCollapsedParents]);

  const onDealTaskCommentAttachmentFileViewClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, attachmentFile: FileItem, dealTaskName: string, groupName: string,
  ): void => {
    if (dealId && event.button === POINTER_KEY_PRIMARY && attachmentFile && attachmentFile.name
      && dealTaskName && groupName) {
      dispatch(viewDealFile({
        dealId,
        details: `Viewed / downloaded attachment file ${attachmentFile.name} `
          + `of deal task ${dealTaskName}, group ${groupName}`,
      }));
    }

    event.stopPropagation();
  }, [dealId]);

  const checkIsDealTaskActive: Function = useCallback((dealTaskId: number): boolean => (
    isSmallScreen || (dealTaskActiveIds ? dealTaskActiveIds.includes(dealTaskId) : false)
  ), [dealTaskActiveIds, isSmallScreen]);

  const checkIsDealTaskGroupActive: Function = useCallback((dealTaskGroupId: number): boolean => (
    isSmallScreen || (dealTaskGroupActiveIds ? dealTaskGroupActiveIds.includes(dealTaskGroupId) : false)
  ), [dealTaskGroupActiveIds, isSmallScreen]);

  const openDealParticipantModal: Function = useCallback((
    dealParticipantItem: DealParticipant, isCommentFormActive?: boolean, isNew?: boolean,
  ): void => {
    if (dealParticipantItem) {
      setDealParticipantData({ ...dealParticipantItem });
    }

    if (isCommentFormActive) {
      setDealParticipantIsCommentFormActive(isCommentFormActive);
    }

    if (isNew) {
      setIsNewRow(true);
    }

    setIsDealParticipantModalOpen(true);
  }, []);

  const openDealParticipantListModal: Function = useCallback((): void => {
    setIsDealParticipantListModalOpen(true);
  }, []);

  const closeDealParticipantModal: Function = useCallback((): void => {
    setDealParticipantData(null);
    setDealParticipantIsCommentFormActive(false);
    setIsDealParticipantModalOpen(false);
    setIsNewRow(false);

    if (isEditMode) {
      setDealParticipantInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    }
  }, [isEditMode, dealParticipantActiveIds]);

  const closeDealParticipantListModal: Function = useCallback((): void => {
    setIsDealParticipantListModalOpen(false);
    setDealParticipantListModalErrorMessage(null);
  }, []);

  const openDealParticipantNewModal: Function = useCallback((): void => {
    setDealParticipantData(null);
    setDealParticipantIsCommentFormActive(false);
    openDealParticipantModal(null, false, true);
  }, [isEditMode]);

  const onDealParticipantCreateClick: Function = useCallback((): void => {
    openDealParticipantNewModal();
  }, []);

  const onDealParticipantListCreateClick: Function = useCallback((): void => {
    openDealParticipantListModal();
  }, []);

  const onDealParticipantListChange: Function = useCallback((): void => {
    if (dealParticipantListModalErrorMessage) {
      setDealParticipantListModalErrorMessage(null);
    }
  }, [dealParticipantListModalErrorMessage]);

  const getDealParticipantListOptions: Function = useCallback((
    pageOption: number, limitOption: number, sortOption?: SortParams, filtersOption?: Record<string, FilterValue>,
  ): RequestOptions => {
    let options: RequestOptions = { dealId, page: pageOption + 1, limit: limitOption };

    options = sortOption ? { ...options, sort: sortOption } : options;
    options = filtersOption ? { ...options, filters: { ...filtersOption } } : options;

    return options;
  }, [dealId]);

  const onDealParticipantPageChange: Function = useCallback((pageValue: number): void => {
    setDealParticipantPage(pageValue);

    if (dealId) {
      dispatch(getDealParticipantListData(
        getDealParticipantListOptions(
          pageValue,
          dealParticipantLimit,
          dealParticipantSortParams,
          dealParticipantFilters,
        ),
      ));
    }
  }, [dealId, dealParticipantLimit, dealParticipantSortParams, dealParticipantFilters]);

  const onDealParticipantLimitChange: Function = useCallback((limitValue: number): void => {
    setDealParticipantPage(PAGE_DEFAULT);
    setDealParticipantLimit(limitValue);

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (dealId) {
      dispatch(getDealParticipantListData(
        getDealParticipantListOptions(PAGE_DEFAULT, limitValue, dealParticipantSortParams, dealParticipantFilters),
      ));
    }

    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, queryTab, dealId, dealParticipantSortParams, dealParticipantFilters]);

  const onDealParticipantSortParamsChange: Function = useCallback((field: string, type: SortType): void => {
    const sortParamsValue: SortParams = {
      field,
      type,
    };

    setDealParticipantSortParams(sortParamsValue);

    if (dealId) {
      dispatch(getDealParticipantListData(
        getDealParticipantListOptions(dealParticipantPage, dealParticipantLimit, sortParamsValue),
      ));
    }
  }, [dealId, dealParticipantPage, dealParticipantLimit]);

  const onDealParticipantFileViewClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealParticipantFile: FileItem, dealParticipantName: string,
  ): void => {
    if (dealId && dealParticipantFile && dealParticipantFile.name && event.button === POINTER_KEY_PRIMARY) {
      dispatch(viewDealFile({
        dealId,
        details: `Viewed / downloaded deal participant ${dealParticipantName} NDA file ${dealParticipantFile.name}`,
      }));
    }
  }, [dealId]);

  const onDealParticipantNdaFileChange: Function = useCallback((dealParticipantId: number, file: File): void => {
    const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);

    setDealParticipantNdaFiles({
      ...(dealParticipantNdaFiles || {}),
      [dealParticipantId]: file,
    });

    changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].ndaFile`, file);
  }, [dealParticipantNdaFiles]);

  const onDealParticipantNdaFileDelete: Function = useCallback((dealParticipantId: number): void => {
    const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);

    const newDealParticipantNdaFiles: Record<string, File> | null = dealParticipantNdaFiles;

    if (newDealParticipantNdaFiles && newDealParticipantNdaFiles[dealParticipantId]) {
      delete newDealParticipantNdaFiles[dealParticipantId];
      setDealParticipantNdaFiles({ ...newDealParticipantNdaFiles });
      changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].ndaFile`, null);
    } else {
      setDealParticipantNdaFiles({
        ...(newDealParticipantNdaFiles || {}),
        [dealParticipantId]: null,
      });
    }
  }, [dealParticipantNdaFiles]);

  const onDealParticipantListSaveForm: Function = useCallback(async (
    contactIds: number[], dealParticipantGroupId: number, accessLevel?: string, onSuccess?: Function,
  ): Promise<ActionCallback | null> => {
    if (!contactIds || !contactIds.length || !dealParticipantGroupId || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealParticipantList({
        dealParticipantListForm: {
          contactIds,
          dealId,
          groupId: dealParticipantGroupId,
          accessLevel: accessLevel || null,
        },
        ...getDealParticipantListOptions(
          dealParticipantPage,
          dealParticipantLimit,
          dealParticipantSortParams,
          dealParticipantFilters,
        ),
      }, (response: ActionResponse): void => {
        closeDealParticipantListModal();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setDealParticipantListModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealId, dealParticipantPage, dealParticipantLimit, dealParticipantSortParams, dealParticipantFilters]);

  const onDealParticipantAddNewItem: Function = useCallback((dealParticipant: DealParticipantInput): void => {
    setDealParticipantNewItems([...dealParticipantNewItems, dealParticipant]);
  }, [dealParticipantNewItems]);

  const onDealParticipantItemDeleteClick: Function = useCallback((dealParticipantId: number): void => {
    const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);

    dealParticipantNewItems.forEach((
      dealParticipantItem: DealParticipantInput, dealParticipantItemIndex: number,
    ): void => {
      if (dealParticipantItem && dealParticipantItem.id === dealParticipantId) {
        delete dealParticipantNewItems[dealParticipantItemIndex];
      }
    });
    setDealParticipantNewItems([...dealParticipantNewItems]
      .filter((dealParticipantItem: DealParticipantInput): boolean => !!dealParticipantItem));

    changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}]`, null);
  }, [dealParticipantNewItems, dealParticipantNdaFiles]);

  const onDealParticipantContactChange: Function = useCallback((
    dealParticipantId: number, contact: Contact, isContactLinked?: boolean,
  ): void => {
    const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);

    if (contact && !isContactLinked) {
      changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].accessLevel`, null);
    }

    changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].name`, (contact && contact.title) || null);
  }, []);

  const onDealParticipantSwitchNewUserFormActive: Function = useCallback((
    dealParticipantId: number, isNewUserFormActiveValue?: boolean,
  ): void => {
    const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);

    if (isNewUserFormActiveValue) {
      changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].user`, {});
    } else {
      changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].user`, null);
      changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].accessLevel`, null);
    }
  }, []);

  const onDealParticipantNewUserSendInvitationCheckboxChange: Function = useCallback((
    dealParticipantId: number,
  ): void => {
    const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);

    changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].user.password`, null);
    changeForm(`dealData.dealParticipantsData[${formKeyDealParticipant}].user.passwordConfirm`, null);
  }, []);

  const onDealParticipantAccountUserTypeGroupChange: Function = useCallback((
    dealParticipantId: number, accountUserTypeGroupName: string,
  ): void => {
    const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);

    changeForm(
      `dealData.dealParticipantsData[${formKeyDealParticipant}].user.accountUserTypeGroup`,
      accountUserTypeGroupName,
    );
  }, []);

  const openDealParticipantDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealParticipantId: number,
  ): void => {
    setDealParticipantActiveIds(dealParticipantId ? [dealParticipantId] : dealParticipantRowsSelected);
    setIsDealParticipantDeleteConfirmModalOpen(true);
  }, [dealParticipantRowsSelected]);

  const closeDealParticipantDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealParticipantDeleteConfirmModalOpen(false);
    setDealParticipantActiveIds([]);
    setDealParticipantRowsSelected([]);
    setDealParticipantAllRowsSelected(false);
  }, []);

  const onDealParticipantInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealParticipantId: number,
  ): void => {
    setDealParticipantActiveIds([...(dealParticipantActiveIds || []), dealParticipantId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealParticipantActiveIds]);

  const onDealParticipantDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealParticipantActiveIds || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealParticipantList({
        dealParticipantIds: dealParticipantActiveIds,
        dealId,
        ...getDealParticipantListOptions(
          dealParticipantPage,
          dealParticipantLimit,
          dealParticipantSortParams,
          dealParticipantFilters,
        ),
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealParticipantDeleteConfirmModalOpen(false);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    dealParticipantActiveIds, dealId, dealParticipantPage, dealParticipantLimit, dealParticipantSortParams,
    dealParticipantFilters, dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault,
    dealCapitalizationSourceItemActiveIds, dealCapitalizationUseItemActiveIds, dealTaskActiveIds,
    dealValuationFileActiveIds, dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onDealParticipantChangeTableColumns: Function = useCallback((
    tableColumnSettings: TableField[], isDefault?: boolean,
  ): void => {
    setDealParticipantTableColumnSettings(tableColumnSettings);

    if (!isDefault) {
      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_DEAL_PARTICIPANT_LIST,
        tableColumnSettings,
      }));
    }
  }, []);

  const onDealParticipantSelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;
    setDealParticipantRowsSelected(isSelected
      ? dealParticipantsData.map((dealParticipantItem: DealParticipant): number => dealParticipantItem.id) : []);
    setDealParticipantAllRowsSelected(!dealParticipantAllRowsSelected);
  }, [dealParticipantsData, dealParticipantAllRowsSelected]);

  const onDealParticipantSelectRow: Function = useCallback((rowId: number): void => {
    setDealParticipantRowsSelected(StructureService.selectRow(dealParticipantRowsSelected, rowId));
    setDealParticipantAllRowsSelected(false);
  }, [dealParticipantRowsSelected]);

  const onDealParticipantLongClick: Function = useCallback((): void => {
    dispatch(toggleSelectMode(true));
  }, []);

  const onDealParticipantAddComment: Function = useCallback((
    dealParticipantId: number, comment: string, attachmentFiles: File[], mentionContactIds: number[],
    onSuccess?: Function,
  ): void => {
    dispatch(saveDealParticipantComment({
      comment, attachmentFiles, dealParticipantId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onDealParticipantUpdateComment: Function = useCallback((
    dealParticipantId: number, comment: string, attachments: AttachmentInput[], attachmentFiles: File[],
    commentId: number,
    mentionContactIds: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveDealParticipantComment({
      comment, attachments, attachmentFiles, dealParticipantId, commentId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onDealParticipantDeleteComment: Function = useCallback((
    dealParticipantId: number, commentId: number, onSuccess?: Function,
  ): void => {
    dispatch(deleteDealParticipantComment({ dealParticipantId, commentId }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const applyDealParticipantUrlFilters: Function = useCallback((): Record<string, FilterValue> => {
    const filterValues: Record<string, FilterValue> = { ...dealParticipantFilters };

    if (queryFilterAccessLevel) {
      filterValues[FILTER_TYPE_ACCESS_LEVEL] = queryFilterAccessLevel;
    }

    if (queryFilterCompanyId) {
      filterValues[FILTER_TYPE_COMPANY_ID] = queryFilterCompanyId;
    }

    setDealParticipantFilters(filterValues);

    return filterValues;
  }, [dealParticipantFilters, queryFilterAccessLevel, queryFilterCompanyId]);

  const onDealParticipantResetFilters: Function = useCallback((): void => {
    setDealParticipantCurrentFilters({ ...dealParticipantFilters });
  }, [dealParticipantFilters]);

  const onDealParticipantApplyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || dealParticipantCurrentFilters;

    setDealParticipantFilters(resultNewFilters);
    setDealParticipantPage(PAGE_DEFAULT);

    dispatch(getDealParticipantListData({
      dealId,
      filters: resultNewFilters,
    }));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (resultNewFilters) {
      Object.entries(resultNewFilters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem || valueItem === false) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, dealParticipantCurrentFilters, dealId, queryTab]);

  const dealParticipantSetFilterValue: Function = useCallback((
    filterType: string, value: string,
  ): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = { ...dealParticipantCurrentFilters, [filterType]: selectedValue };

    setDealParticipantCurrentFilters(newFilters);

    return newFilters;
  }, [dealParticipantCurrentFilters]);

  const onDealParticipantDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = dealParticipantSetFilterValue(filterType, null);

    onDealParticipantApplyFilters(newFilters);
  }, [locationQueryParams, dealParticipantCurrentFilters, dealId, queryTab]);

  const onDealParticipantCommentAttachmentFileViewClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, attachmentFile: FileItem, dealParticipantName: string,
  ): void => {
    if (dealId && event.button === POINTER_KEY_PRIMARY && attachmentFile && attachmentFile.name
      && dealParticipantName) {
      dispatch(viewDealFile({
        dealId,
        details: `Viewed / downloaded attachment file ${attachmentFile.name} `
          + `of deal participant ${dealParticipantName}`,
      }));
    }

    event.stopPropagation();
  }, [dealId]);

  const checkIsDealParticipantActive: Function = useCallback((dealParticipantId: number): boolean => (
    isSmallScreen || (dealParticipantActiveIds ? dealParticipantActiveIds.includes(dealParticipantId) : false)
  ), [dealParticipantActiveIds, isSmallScreen]);

  const onDealScheduleChangeTableColumns: Function = useCallback((
    tableColumnSettings: TableField[],
    isDefault?: boolean,
  ): void => {
    setDealScheduleTableColumnSettings(tableColumnSettings);

    if (!isDefault) {
      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_DEAL_SCHEDULE_TASK_LIST,
        tableColumnSettings,
      }));
    }
  }, []);

  const onDealSchedulesChange: Function = useCallback((
    dealScheduleId: number, dealScheduleTaskItems: DealScheduleTask[],
    dealScheduleTaskLinkItems: DealScheduleTaskLink[],
  ): void => {
    const formKeyDealSchedule: string = FormService.getIndexedFormFieldKey(dealScheduleId);

    setDealScheduleIsTaskDataChanged(true);
    changeForm(`dealData.dealSchedulesData[${formKeyDealSchedule}].tasks`, dealScheduleTaskItems);
    changeForm(`dealData.dealSchedulesData[${formKeyDealSchedule}].links`, dealScheduleTaskLinkItems);
  }, []);

  const onDealScheduleZoomChange: Function = useCallback((zoomValue: number): void => {
    dispatch(setDealScheduleZoom(zoomValue));
  }, []);

  const openDealScheduleModal: Function = useCallback((): void => {
    setIsDealScheduleModalOpen(true);
  }, []);

  const closeDealScheduleModal: Function = useCallback((): void => {
    setIsDealScheduleModalOpen(false);
    setCreateModalErrorMessage(null);

    onCancelForm();
  }, [
    dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onDealScheduleModalCancel: Function = useCallback((): void => {
    closeDealScheduleModal();
  }, []);

  const onDealScheduleCreateClick: Function = useCallback((): void => {
    openDealScheduleModal();
  }, [isEditMode, dealSchedulesData]);

  const onDealScheduleSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onDealScheduleDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealScheduleActiveId || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealSchedule({
        dealScheduleId: dealScheduleActiveId,
        dealId,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealScheduleDeleteConfirmModalOpen(false);

        setDealScheduleActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealScheduleActiveId, dealId]);

  const openDealScheduleDeleteConfirmModal: Function = useCallback((dealScheduleId: number): void => {
    setDealScheduleActiveId(dealScheduleId);
    setIsDealScheduleDeleteConfirmModalOpen(true);
  }, []);

  const closeDealScheduleDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealScheduleDeleteConfirmModalOpen(false);
    setDealScheduleActiveId(null);
  }, []);

  const openDealFileModal: Function = useCallback((
    dealFileItem: DealFile,
    dealFileGroupItem: DealFileGroup,
    isNew?: boolean,
  ): void => {
    if (dealFileItem) {
      setDealFileData({ ...dealFileItem });
    }

    if (dealFileGroupItem) {
      setDealFileGroupData({ ...dealFileGroupItem });
    }

    if (isNew) {
      setIsNewRow(true);
    }

    setIsDealFileModalOpen(true);
  }, [isEditMode]);

  const closeDealFileModal: Function = useCallback((): void => {
    setDealFileData(null);
    setDealFileGroupData(null);
    setIsDealFileModalOpen(false);
    setIsNewRow(false);

    if (isEditMode) {
      setDealFileInlineSaveId(FormService.FORM_KEY_NEW);
      setDealFileGroupInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    }
  }, [isEditMode, dealFileActiveIds]);

  const closeDealFileGroupModal: Function = useCallback((): void => {
    setIsDealFileGroupModalOpen(false);
    setIsNewDealFileGroup(false);
  }, []);

  const openDealFileNewModal: Function = useCallback((): void => {
    setDealFileData(null);
    setDealFileGroupData(null);
    openDealFileModal(null, null, true);
  }, []);

  const openDealFileExportModal: Function = useCallback((): void => {
    let dealFileActiveIdList: number[] = [];
    if (dealFileRowsSelected) {
      Object.keys(dealFileRowsSelected).forEach((dealFileGroupIndex: string): void => {
        const dealFileItemIds: number[] = dealFileRowsSelected[dealFileGroupIndex];

        dealFileActiveIdList = [...dealFileActiveIdList, ...dealFileItemIds];
      });
    }
    setDealFileActiveIds(dealFileActiveIdList);

    setDealFileIsExportModalOpen(true);
  }, [dealFileRowsSelected]);

  const closeDealFileExportModal: Function = useCallback((): void => {
    setDealFileIsExportModalOpen(false);

    setDealFileActiveIds([]);

    closeMessage();
  }, []);

  const onDealFileModalExportSuccess: Function = useCallback((): void => {
    onCancelForm();

    setStatusMessage('Exported successfully');
    setIsOpenMessage(true);
  }, [
    dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onDealFileAddNewItems: Function = useCallback((dealFileGroupId: number, newItems: DealFileInput[]): void => {
    const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);

    newItems.forEach((dealFile: DealFileInput): void => {
      const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFile.id as number);

      changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].groupId`, dealFileGroupId);
      changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].file`, null);
      changeForm(
        `dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].name`,
        dealFile.name || null,
      );
    });

    setDealFileNewItems({
      ...dealFileNewItems,
      [dealFileGroupId]: [...((dealFileNewItems && dealFileNewItems[dealFileGroupId]) || []), ...newItems],
    });
  }, [dealFileNewItems]);

  const onDealFileCreateClick: Function = useCallback((): void => {
    openDealFileNewModal();
  }, []);

  const onDealFileChange: Function = useCallback((file: File, dealFileGroupId: number, dealFileId: number): void => {
    const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);
    const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileId);

    setDealFileFiles({
      ...dealFileFiles,
      [dealFileGroupId]: {
        ...dealFileFiles[dealFileGroupId] || {},
        [dealFileId]: file,
      },
    });

    changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].file`, file);

    if (isNewDeal && isDealFileAnalyzed) {
      dispatch(resetDealFileAnalyzed());
    }
  }, [dealFileFiles, isNewDeal, isDealFileAnalyzed]);

  const onDealFilesChange: Function = useCallback((
    dealFileGroupId: number, dealFileIdList: number[], fileList: File[],
  ): void => {
    const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);

    const newFileMap: Record<string, File> = fileList.reduce((
      fileMap: Record<string, File>, file: File, fileIndex: number,
    ): Record<string, File> => {
      const resultFileMap: Record<string, File> = fileMap;

      resultFileMap[dealFileIdList[fileIndex]] = file;

      return resultFileMap;
    }, {});

    setDealFileFiles({
      ...dealFileFiles,
      [dealFileGroupId]: {
        ...dealFileFiles[dealFileGroupId] || {},
        ...newFileMap,
      },
    });

    fileList.forEach((file: File, fileIndex: number): void => {
      const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileIdList[fileIndex]);

      changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].file`, file);
    });
  }, [dealFileFiles]);

  const onDealFileAnalyzeClick: Function = useCallback((file: File): void => {
    if (isDealFileAnalyzed || !file || !FileService.checkFileExtension(file, FILE_EXTENSION_LIST_ANALYZABLE)) {
      return;
    }

    dispatch(getDealDataByFile({
      file,
    }))
  }, [isDealFileAnalyzed]);

  const processDealFileGroupsChange: Function = useCallback((groupedFileList: DirectoryFileGroup[]): void => {
    const newDealFileFiles: Record<string, Record<string, File>> = {};
    const newDealFileNewItems: Record<string, DealFileInput[]> = {};

    groupedFileList.forEach((fileGroupItem: DirectoryFileGroup, fileGroupIndex: number): void => {
      const fileList: File[] = fileGroupItem.files;
      const newDealFileGroupId: number = StructureService.getNewItemId(dealFileNewGroupItems) + fileGroupIndex;
      const newDealFileId: number = StructureService.getNewItemId();
      const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(newDealFileGroupId);
      const dealFileIdList: number[] = fileList.map((fileItem: File, fileIndex: number): number => (
        newDealFileId + fileIndex
      ));

      const newDealFileItems: DealFileInput[] = fileList.map((fileItem: File, fileIndex: number): DealFileInput => ({
        id: newDealFileId + fileIndex,
        groupId: newDealFileGroupId as unknown as number,
        name: fileItem.name ? FormatService.formatCleanFileName(fileItem.name) : '',
        file: null,
      }));
      newDealFileNewItems[newDealFileGroupId] = [
        ...((dealFileNewItems && dealFileNewItems[newDealFileGroupId]) || []),
        ...newDealFileItems,
      ];
      newDealFileFiles[newDealFileGroupId] = fileList.reduce((
        fileMap: Record<string, File>, fileItem: File, fileIndex: number,
      ): Record<string, File> => {
        const resultFileMap: Record<string, File> = fileMap;

        resultFileMap[dealFileIdList[fileIndex]] = fileItem;

        return resultFileMap;
      }, {});

      changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}]`, {
        name: fileGroupItem.name,
        files: {},
      });
      newDealFileItems.forEach((dealFile: DealFileInput): void => {
        const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFile.id as number);

        changeForm(
          `dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].groupId`,
          newDealFileGroupId,
        );

        if (dealFile.name) {
          changeForm(
            `dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].name`,
            dealFile.name,
          );
        }
      });
      fileList.forEach((fileItem: File, fileIndex: number): void => {
        const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileIdList[fileIndex]);

        changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].file`, fileItem);
      });
    });

    const newDealFileNewGroupItems: DealFileGroupInput[] = groupedFileList.map((
      fileGroupItem: DirectoryFileGroup, fileGroupIndex: number,
    ): DealFileGroupInput => ({
      id: StructureService.getNewItemId(dealFileNewGroupItems) + fileGroupIndex,
      name: fileGroupItem.name,
      files: [],
    }));
    setDealFileNewGroupItems([...dealFileNewGroupItems, ...newDealFileNewGroupItems]);
    setDealFileExpandedGroups([
      ...dealFileExpandedGroups,
      ...(groupedFileList.map((fileGroupItem: DirectoryFileGroup, fileGroupIndex: number): number => (
        dealFileExpandedGroups.length + fileGroupIndex + 1
      ))),
    ]);
    setDealFileFiles({
      ...dealFileFiles,
      ...newDealFileFiles,
    });
    setDealFileNewItems({
      ...dealFileNewItems,
      ...newDealFileNewItems,
    });
    setDealFileGroupActiveIds([
      ...dealFileGroupActiveIds,
      ...newDealFileNewGroupItems.map((dealFileGroupItem: DealFileGroupInput): number | null => (
        dealFileGroupItem.id || null
      )).filter((dealFileGroupId: number | null): boolean => !!dealFileGroupId),
    ]);
  }, [dealFileNewGroupItems, dealFileNewItems, dealFileGroupActiveIds, dealFileFiles]);

  const onDealFileDelete: Function = useCallback((dealFileGroupId: number, dealFileId: number): void => {
    const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);
    const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileId);

    const newDealFileFiles: Record<string, Record<string, File>> = dealFileFiles;

    if (newDealFileFiles[dealFileGroupId]) {
      delete newDealFileFiles[dealFileGroupId][dealFileId];
      setDealFileFiles({ ...newDealFileFiles });
      changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].file`, null);
    }
  }, [dealFileFiles]);

  const openDealFileGroupModal: Function = useCallback((): void => {
    setIsDealFileGroupModalOpen(true);
    setIsNewDealFileGroup(true);
  }, []);

  const onDealFileInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealFileId: number,
  ): void => {
    setDealFileActiveIds([...(dealFileActiveIds || []), dealFileId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealFileActiveIds]);

  const onDealFileInlineGroupEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealFileGroupId: number,
  ): void => {
    setDealFileGroupActiveIds([...(dealFileGroupActiveIds || []), dealFileGroupId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealFileGroupActiveIds]);

  const onDealFileGroupCreateClick: Function = useCallback((): void => {
    openDealFileGroupModal();
  }, [isEditMode, dealFilesData, dealFileExpandedGroups]);

  const onDealFileItemDeleteClick: Function = useCallback((dealFileId: number, dealFileGroupId: number): void => {
    const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);
    const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileId);

    if (dealFileNewItems[dealFileGroupId]) {
      dealFileNewItems[dealFileGroupId].forEach((dealFile: DealFileInput, dealFileItemIndex: number): void => {
        if (dealFile && dealFile.id === dealFileId) {
          delete dealFileNewItems[dealFileGroupId][dealFileItemIndex];
        }
      });
      setDealFileNewItems({
        ...dealFileNewItems,
        [dealFileGroupId]: dealFileNewItems[dealFileGroupId].filter((
          dealFileItem: DealFileInput,
        ): boolean => !!dealFileItem),
      });

      onDealFileDelete(dealFileGroupId, dealFileId);

      changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}]`, null);
    }
  }, [dealFileNewItems, dealFileFiles]);

  const onDealFileGroupDeleteClick: Function = useCallback((dealFileGroupId: number): void => {
    const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);

    if (dealFileNewGroupItems && dealFileNewGroupItems.length) {
      dealFileNewGroupItems.forEach((dealFileGroupItem: DealFileGroupInput, dealFileGroupItemIndex: number): void => {
        if (dealFileGroupItem && dealFileGroupItem.id === dealFileGroupId) {
          delete dealFileNewGroupItems[dealFileGroupItemIndex];
        }
      });
      setDealFileNewGroupItems([...dealFileNewGroupItems.filter((
        dealFileGroupItem: DealFileGroupInput,
      ): boolean => !!dealFileGroupItem)]);
    }

    if (dealFileNewItems[dealFileGroupId]) {
      delete dealFileNewItems[dealFileGroupId];
      setDealFileNewItems({ ...dealFileNewItems });
    }

    changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}]`, null);
  }, [dealFileNewGroupItems, dealFileNewItems]);

  const onDealFileCloseNewGroupForm: Function = useCallback((dealFileId: number, dealFileGroupId: number): void => {
    const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);
    const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileId);

    changeForm(`dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}].group`, null);
  }, []);

  const openDealFileDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealFileId: number,
  ): void => {
    setDealFileActiveIds(dealFileId ? [dealFileId] : (dealFileRowsSelected && Object.values(dealFileRowsSelected)
      .reduce((resultDealFileItemIds: number[], dealFileItemIds: number[]): number[] => ([
        ...resultDealFileItemIds,
        ...dealFileItemIds,
      ]), [])));
    setIsDealFileDeleteConfirmModalOpen(true);
  }, [dealFileRowsSelected]);

  const openDealFileGroupDeleteConfirmModal: Function = useCallback((dealFileGroupId: number): void => {
    setDealFileGroupActiveId(dealFileGroupId);
    setIsDealFileGroupDeleteConfirmModalOpen(true);
  }, []);

  const closeDealFileDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealFileDeleteConfirmModalOpen(false);
    setDealFileActiveIds([]);
    setDealFileRowsSelected(dealFileRowsSelectedDefault);
  }, []);

  const closeDealFileGroupDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDealFileGroupDeleteConfirmModalOpen(false);
    setDealFileGroupActiveId(null);
  }, []);

  const onDealFileDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealFileActiveIds || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealFileList({
        dealFileIds: dealFileActiveIds,
        dealId,
        page: dealFilePage || null,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealFileDeleteConfirmModalOpen(false);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    dealFileActiveIds, dealId, dealFilePage, dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault,
    dealCapitalizationSourceItemActiveIds, dealCapitalizationUseItemActiveIds, dealTaskActiveIds,
    dealValuationFileActiveIds, dealParticipantActiveIds, dealActivityActiveIds,
    dealCapitalizationSourceItemInlineSaveId, dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId,
    dealTaskInlineSaveId, queryTab, dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId,
    dealPhotoListDefault, isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onDealFileGroupDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealFileGroupActiveId || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealFileGroup({
        dealFileGroupId: dealFileGroupActiveId,
        dealId,
        page: dealFilePage || null,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDealFileGroupDeleteConfirmModalOpen(false);

        setDealFileGroupActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealFileGroupActiveId, dealId, dealFilePage]);

  const openDealFileExpansionGroups: Function = useCallback((
    event?: React.MouseEvent<Element, MouseEvent>, isDefault?: boolean,
  ): void => {
    if ((isDefault && dealFilesData.length <= DEAL_GROUP_DEFAULT_COLLAPSE_LIMIT) || !isDefault) {
      setDealFileExpandedGroups(dealFilesData && dealFilesData.length
        ? dealFilesData.map((dealFileGroupItem: DealFileGroup, dealFileGroupIndex: number): number => (
          dealFileGroupIndex + 1
        )) : []);
    }
  }, [dealFilesData]);

  const closeDealFileExpansionGroups: Function = useCallback((): void => {
    setDealFileExpandedGroups([]);
  }, []);

  const dealFileExpansionGroupChange: Function = useCallback((groupIndex?: number): void => {
    if (groupIndex && dealFileExpandedGroups) {
      if (dealFileExpandedGroups.includes(groupIndex)) {
        delete dealFileExpandedGroups[dealFileExpandedGroups.indexOf(groupIndex)];
        setDealFileExpandedGroups([
          ...dealFileExpandedGroups.filter((expandedGroup: number): boolean => !!expandedGroup),
        ]);
      } else {
        setDealFileExpandedGroups([...dealFileExpandedGroups, groupIndex]);
      }
    }
  }, [dealFileExpandedGroups]);

  const onDealFileSelectRow: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, rowGroupId: number, rowId: number,
    selectedRows: Record<string, number[]>, onSelectRows?: Function,
  ): void => {
    if (onSelectRows) {
      onSelectRows({
        ...selectedRows,
        [rowGroupId]: StructureService.selectRow(selectedRows, rowId, rowGroupId),
      });
    }

    setDealFileAllRowsSelected(false);

    event.stopPropagation();
  }, []);

  const onDealFileSelectRows: Function = useCallback((selectedRows: Record<string, number[]>): void => {
    setDealFileRowsSelected(selectedRows);
  }, []);

  const onDealFileLoadBatchData: Function = useCallback((allRows?: boolean, onSuccess?: Function): void => {
    dispatch(getDealFileListData({
      dealId,
      page: dealFilePage + PAGE_LIST_DEFAULT,
      allRows: allRows || false,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
    setDealFilePage(dealFilePage + PAGE_LIST_DEFAULT);
  }, [dealId, dealFilePage]);

  const onDealFileExpansionGroupChange: Function = useCallback((
    event: React.SyntheticEvent, groupIndex?: number, withLoading?: boolean,
  ): void => {
    if (withLoading) {
      onDealFileLoadBatchData(true, (): void => {
        dealFileExpansionGroupChange(groupIndex);
      });
    } else {
      dealFileExpansionGroupChange(groupIndex);
    }

    if (event) {
      event.stopPropagation();
    }
  }, [dealFileExpandedGroups, dealId, dealFilePage]);

  const onDealFileGroupsChange: Function = useCallback((groupedFileList: DirectoryFileGroup[]): void => {
    if (dealFilesTotal > dealFilesLoadedTotal) {
      onDealFileLoadBatchData(true, (): void => {
        processDealFileGroupsChange(groupedFileList);
      });
    } else {
      processDealFileGroupsChange(groupedFileList);
    }
  }, [
    dealFilesData, dealFileNewGroupItems, dealFileNewItems, dealFileExpandedGroups,
    dealFilesTotal, dealFilesLoadedTotal,
  ]);

  const onDealFileLongClick: Function = useCallback((): void => {
    dispatch(toggleSelectMode(true));
  }, []);

  const onDealFileViewClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealFileFile: FileItem, dealFileGroupName: string,
  ): void => {
    if (dealId && dealFileFile && dealFileFile.name && dealFileGroupName && event.button === POINTER_KEY_PRIMARY) {
      dispatch(viewDealFile({
        dealId,
        details: `Viewed / downloaded deal file ${dealFileFile.name} of group ${dealFileGroupName}`,
      }));
    }
  }, [dealId]);

  const openDealTaskAttachmentFileChatModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealChatFileItem: DealChatFile,
    dealTaskId: number,
  ): void => {
    setIsDealTaskAttachmentFileChatModalOpen(true);
    setDealFileChatData({
      ...dealChatFileItem,
      entityId: dealTaskId,
    });
  }, []);

  const closeDealTaskAttachmentFileChatModal: Function = useCallback((): void => {
    setIsDealTaskAttachmentFileChatModalOpen(false);
    setDealFileChatModalErrorMessage(null);
  }, []);

  const openDealFileChatModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealChatFileItem: DealChatFile,
  ): void => {
    setIsDealFileChatModalOpen(true);
    setDealFileChatData({ ...dealChatFileItem });
  }, []);

  const closeDealFileChatModal: Function = useCallback((): void => {
    setIsDealFileChatModalOpen(false);
    setDealFileChatModalErrorMessage(null);
  }, []);

  const onDealFileChatModalCancel: Function = useCallback((): void => {
    dispatch(resetDealFileChatResponseText());
    setDealFileChatData(null);
  }, []);

  const onDealTaskAttachmentFileChatSendQuery: Function = useCallback((
    attachmentId: number | null, query: string, dealTaskId: number | null,
  ): void => {
    if (!dealTaskId || !attachmentId || !query) {
      return;
    }

    dispatch(getDealTaskAttachmentTextData({
      dealTaskId,
      attachmentId,
      query,
    }, (): void => {
      setDealFileChatModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception) {
        setDealFileChatModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const onDealFileChatSendQuery: Function = useCallback((dealFileId: number | null, query: string): void => {
    if (!dealFileId || !query) {
      return;
    }

    dispatch(getDealFileTextData({
      dealFileId,
      query,
    }, (): void => {
      setDealFileChatModalErrorMessage(null);
    }, (exception: ServerError): void => {
      if (exception) {
        setDealFileChatModalErrorMessage(exception.message);
      }
    }));
  }, []);

  const checkIsDealFileActive: Function = useCallback((dealFileId: number): boolean => (
    isSmallScreen || (dealFileActiveIds ? dealFileActiveIds.includes(dealFileId) : false)
  ), [dealFileActiveIds, isSmallScreen]);

  const checkIsDealFileGroupActive: Function = useCallback((dealFileId: number): boolean => (
    isSmallScreen || (dealFileGroupActiveIds ? dealFileGroupActiveIds.includes(dealFileId) : false)
  ), [dealFileGroupActiveIds, isSmallScreen]);

  const getDealActivityListOptions: Function = useCallback((
    pageOption: number, limitOption: number, sortOption?: SortParams, filtersOption?: Record<string, FilterValue>,
  ): RequestOptions => {
    const filtersOptionParams: Record<string, FilterValue> = { ...filtersOption };
    const categoryOption: string | null = (filtersOptionParams && filtersOptionParams[FILTER_TYPE_CATEGORY]
      && filtersOptionParams[FILTER_TYPE_CATEGORY].toString()) || null;

    delete filtersOptionParams[FILTER_TYPE_CATEGORY];

    let options: RequestOptions = { dealId, page: pageOption + 1, limit: limitOption };

    options = sortOption ? { ...options, sort: sortOption } : options;
    options = categoryOption ? { ...options, category: categoryOption } : options;
    options = filtersOptionParams ? { ...options, filters: { ...filtersOptionParams } } : options;

    return options;
  }, [dealId]);

  const onDealActivityResetFilters: Function = useCallback((): void => {
    setDealActivityCurrentFilters({ ...dealActivityFilters });
  }, [dealActivityFilters]);

  const onDealActivityApplyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || dealActivityCurrentFilters;

    setDealActivityFilters(resultNewFilters);
    setDealActivityPage(PAGE_DEFAULT);

    dispatch(getActivityListData(
      getDealActivityListOptions(PAGE_DEFAULT, dealActivityLimit, dealActivitySortParams, resultNewFilters),
    ));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryTab) {
      queryParams[QUERY_PARAM_TAB] = queryTab;
    }

    if (resultNewFilters) {
      Object.entries(resultNewFilters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem || valueItem === false) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, dealActivityCurrentFilters, dealActivityLimit, dealActivitySortParams, queryTab, dealId]);

  const dealActivitySetFilterValue: Function = useCallback((
    filterType: string, value: string,
  ): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = { ...dealActivityCurrentFilters, [filterType]: selectedValue };

    setDealActivityCurrentFilters(newFilters);

    return newFilters;
  }, [dealActivityCurrentFilters]);

  const onDealActivityDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = dealActivitySetFilterValue(filterType, null);

    onDealActivityApplyFilters(newFilters);
  }, [locationQueryParams, dealActivityCurrentFilters]);

  const onDealActivityPageChange: Function = useCallback((pageValue: number): void => {
    setDealActivityPage(pageValue);

    if (dealId) {
      dispatch(getActivityListData(
        getDealActivityListOptions(pageValue, dealActivityLimit, dealActivitySortParams, dealActivityFilters),
      ));
    }
  }, [dealId, dealActivityPage, dealActivityLimit, dealActivitySortParams, dealActivityFilters]);

  const onDealActivityLimitChange: Function = useCallback((limitValue: number): void => {
    setDealActivityPage(PAGE_DEFAULT);
    setDealActivityLimit(limitValue);

    if (dealId) {
      dispatch(getActivityListData(
        getDealActivityListOptions(PAGE_DEFAULT, limitValue, dealActivitySortParams, dealActivityFilters),
      ));
    }
  }, [dealId, dealActivityPage, dealActivityLimit, dealActivitySortParams, dealActivityFilters]);

  const onDealActivitySortParamsChange: Function = useCallback((field: string, type: SortType): void => {
    const sortParamsValue: SortParams = {
      field,
      type,
    };

    setDealActivitySortParams(sortParamsValue);

    if (dealId) {
      dispatch(getActivityListData(
        getDealActivityListOptions(dealActivityPage, dealActivityLimit, sortParamsValue, dealActivityFilters),
      ));
    }
  }, [dealId, dealActivityPage, dealActivityLimit, dealActivityFilters]);

  const onDealActivityChangeTableColumns: Function = useCallback((
    tableColumnSettings: TableField[],
    isDefault?: boolean,
  ): void => {
    setDealActivityTableColumnSettings(tableColumnSettings);

    if (!isDefault) {
      dispatch(saveTableColumnSettings({
        tableName: TABLE_NAME_ACTIVITY_LIST_DEAL,
        tableColumnSettings,
      }));
    }
  }, []);

  const openDealActivityModal: Function = useCallback((
    activityItem: Activity, isCommentActive?: boolean, isNew?: boolean,
  ): void => {
    setDealActivityItemData({ ...activityItem });

    if (isCommentActive) {
      setDealActivityIsCommentFormActive(isCommentActive);
    }

    if (isNew) {
      setIsNewRow(isNew);
    }

    setIsDealActivityModalOpen(true);
  }, []);

  const openDealTaskModalById: Function = useCallback((dealTaskId: number): void => {
    if (dealTasksData) {
      dealTasksData.forEach((dealTaskGroupItem: DealTaskGroup): void => {
        if (dealTaskGroupItem.tasks && dealTaskGroupItem.tasks.length) {
          dealTaskGroupItem.tasks.forEach((dealTaskItem: DealTask): void => {
            if (dealTaskItem.id === dealTaskId && !isDealTaskModalOpen) {
              openDealTaskModal(dealTaskItem, dealTaskGroupItem);
            }

            if (dealTaskItem.subTasks && dealTaskItem.subTasks.length) {
              dealTaskItem.subTasks.forEach((dealSubTaskItem: DealTask): void => {
                if (dealSubTaskItem.id === dealTaskId && !isDealTaskModalOpen) {
                  openDealTaskModal(dealSubTaskItem, dealTaskGroupItem);
                }
              });
            }
          });
        }
      });
    }
  }, [dealTasksData, isDealTaskModalOpen]);

  const openDealParticipantModalById: Function = useCallback((dealParticipantId: number): void => {
    if (dealParticipantsData) {
      dealParticipantsData.forEach((dealParticipantItem: DealParticipant): void => {
        if (dealParticipantItem.id === dealParticipantId && !isDealParticipantModalOpen) {
          openDealParticipantModal(dealParticipantItem);
        }
      });
    }
  }, [dealParticipantsData, isDealParticipantModalOpen]);

  const openDealActivityDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealActivityId: number,
  ): void => {
    setDealActivityActiveIds(dealActivityId ? [dealActivityId] : dealActivityRowsSelected);
    setIsDealActivityDeleteConfirmModalOpen(true);
  }, [dealActivityRowsSelected]);

  const closeDealActivityDeleteConfirmModal: Function = useCallback((): void => {
    setIsDealActivityDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
    setDealActivityActiveIds([]);
    setDealActivityRowsSelected([]);
    setDealActivityAllRowsSelected(false);
  }, []);

  const onDealActivityDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealActivityActiveIds || !dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteActivityList({
        activityIds: dealActivityActiveIds,
        isListItem: true,
        ...getDealActivityListOptions(dealActivityPage, dealActivityLimit, dealActivitySortParams, dealActivityFilters),
      }, (response: ActionResponse): void => {
        setIsDealActivityDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    dealActivityActiveIds, dealId, dealActivityPage, dealActivityLimit, dealActivitySortParams, dealActivityFilters,
    dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const closeDealActivityModal: Function = useCallback((): void => {
    setDealActivityItemData(null);
    setIsDealActivityModalOpen(false);
    setIsNewRow(false);

    if (isEditMode) {
      setDealActivityInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    }
  }, [isEditMode, dealActivityActiveIds]);

  const onDealActivitySubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const openDealActivityNewModal: Function = useCallback((): void => {
    setDealActivityItemData(null);
    openDealActivityModal(null, false, true);
  }, []);

  const onDealActivityAddComment: Function = useCallback((
    activityId: number, comment: string, attachmentFiles: File[], mentionContactIds: number[],
    linkedEntities: CommentEntityInput[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachmentFiles, activityId, mentionContactIds, linkedEntities,
    }, (): void => {
      if (onSuccess) {
        onSuccess();
      }
    }));
  }, []);

  const onDealActivityUpdateComment: Function = useCallback((
    activityId: number, comment: string, attachments: AttachmentInput[], attachmentFiles: File[], commentId: number,
    mentionContactIds: number[], onSuccess?: Function,
  ): void => {
    dispatch(saveActivityComment({
      comment, attachments, attachmentFiles, activityId, commentId, mentionContactIds,
    }, (): void => {
      if (onSuccess) {
        onSuccess(commentId);
      }
    }));
  }, []);

  const onDealActivityDeleteComment: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityId: number, commentId: number,
  ): void => {
    dispatch(deleteActivityComment({
      activityId,
      commentId,
    }));

    event.stopPropagation();
  }, []);

  const dealActivityOnOpenNewDealSourceForm: Function = useCallback((activityId: number): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`dealData.activityListData[${formKeyActivityItem}].dealSource`, {
      name: null,
    });
    setDealActivityIsNewDealSourceFormVisible(true);
  }, []);

  const dealActivityOnCloseNewDealSourceForm: Function = useCallback((activityId: number): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`dealData.activityListData[${formKeyActivityItem}].dealSource`, null);
    setDealActivityIsNewDealSourceFormVisible(false);
  }, []);

  const dealActivityOnOpenNewDealContactForm: Function = useCallback((activityId: number): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`dealData.activityListData[${formKeyActivityItem}].dealContact`, {
      firstName: null,
      lastName: null,
    });
    setDealActivityIsNewDealContactFormVisible(true);
  }, []);

  const dealActivityOnCloseNewDealContactForm: Function = useCallback((activityId: number): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`dealData.activityListData[${formKeyActivityItem}].dealContact`, null);
    setDealActivityIsNewDealContactFormVisible(false);
  }, []);

  const onDealActivityFieldAddNewItem: Function = useCallback((activityFieldItem: ActivityFieldInput): void => {
    setDealActivityFieldNewItems({
      ...dealActivityFieldNewItems,
      [dealActivityId]: [...(dealActivityFieldNewItems[dealActivityId] || []), activityFieldItem],
    });
  }, [dealActivityFieldNewItems, dealActivityId]);

  const onDealActivityFieldDeleteClick: Function = useCallback((activityId: number, activityFieldId: number): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);
    const formKeyActivityFieldItem: string = FormService.getIndexedFormFieldKey(activityFieldId);

    if (dealActivityFieldNewItems && dealActivityFieldNewItems[activityId]
      && dealActivityFieldNewItems[activityId].length) {
      dealActivityFieldNewItems[activityId].forEach((
        activityFieldItem: ActivityFieldInput, activityFieldIndex: number,
      ): void => {
        if (activityFieldItem && activityFieldItem.id === activityFieldId) {
          delete dealActivityFieldNewItems[activityId][activityFieldIndex];
        }
      });
      setDealActivityFieldNewItems({
        ...dealActivityFieldNewItems,
        [activityId]: [...dealActivityFieldNewItems[activityId]
          .filter((activityFieldItem: ActivityField): boolean => !!activityFieldItem)],
      });
    }

    changeForm(`dealData.activityListData[${formKeyActivityItem}].options[${formKeyActivityFieldItem}]`, null);
  }, [dealActivityFieldNewItems]);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const onDealActivityTagsChange: Function = useCallback((activityId: number, tags: Tag[]): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    setDealActivityTags({ ...dealActivityTags, [activityId]: tags });
    changeForm(`dealData.activityListData[${formKeyActivityItem}].tags`, tags);
  }, [dealActivityTags]);

  const onDealActivityDealContactsChange: Function = useCallback((activityId: number, contacts: Contact[]): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`dealData.activityListData[${formKeyActivityItem}].dealContactIds`, contacts);
  }, []);

  const onDealActivityDealContactGroupsChange: Function = useCallback((
    activityId: number, contactGroupValues: ContactGroup[],
  ): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`dealData.activityListData[${formKeyActivityItem}].dealContact.groupIds`, contactGroupValues);
  }, []);

  const onDealActivityDealContactPhoneChange: Function = useCallback((
    activityId: number, phoneValue?: string,
  ): void => {
    const formKeyActivityItem: string = FormService.getIndexedFormFieldKey(activityId);

    changeForm(`dealData.activityListData[${formKeyActivityItem}].dealContact.phone`, phoneValue);
  }, []);

  const onDealActivityTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onDealActivityCreateClick: Function = useCallback((): void => {
    openDealActivityNewModal();
  }, []);

  const onDealActivityLongClick: Function = useCallback((): void => {
    dispatch(toggleSelectMode(true));
  }, []);

  const onDealActivitySelectAllRows: Function = useCallback((event: React.ChangeEvent): void => {
    const isSelected: boolean = (event.target as HTMLInputElement).checked;
    setDealActivityRowsSelected(isSelected
      ? dealActivityList.map((activityItem: Activity): number => activityItem.id) : []);
    setDealActivityAllRowsSelected(!dealActivityAllRowsSelected);
  }, [dealActivityList, dealActivityAllRowsSelected]);

  const onDealActivitySelectRow: Function = useCallback((rowId: number): void => {
    setDealActivityRowsSelected(StructureService.selectRow(dealActivityRowsSelected, rowId));
    setDealActivityAllRowsSelected(false);
  }, [dealActivityRowsSelected]);

  const openDealActivityStatusChangeModal: Function = useCallback((
    event: React.SyntheticEvent, activityIds: number[],
  ): void => {
    setDealActivityActiveIds(activityIds || dealActivityRowsSelected);
    setDealActivityIsStatusChangeModalOpen(true);
  }, [dealActivityRowsSelected]);

  const closeDealActivityStatusModal: Function = useCallback((): void => {
    setStatusModalErrorMessage(null);
    setDealActivityIsStatusChangeModalOpen(false);

    setDealActivityActiveIds([]);

    closeMessage();
  }, []);

  const onDealActivityInlineEditModeButtonClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityId: number,
  ): void => {
    setDealActivityActiveIds([...(dealActivityActiveIds || []), activityId]);

    if (!isEditMode) {
      dispatch(toggleEditMode(true));
    }

    event.stopPropagation();
  }, [isEditMode, dealActivityActiveIds]);

  const onDealActivityStatusModalSaveClick: Function = useCallback((status: string): Promise<ActionCallback> | null => {
    if (!dealActivityActiveIds || !dealActivityActiveIds.length || !status) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveActivityStatusList({
        activityStatusListForm: {
          newStatus: status,
          activityIds: dealActivityActiveIds,
        },
        dealId,
      }, (response: ActionResponse): void => {
        setDealActivityIsStatusChangeModalOpen(false);
        setStatusModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setStatusModalErrorMessage(exception.message);
        }
      }));
    });
  }, [
    dealActivityActiveIds, dealId, dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault,
    dealCapitalizationSourceItemActiveIds, dealCapitalizationUseItemActiveIds, dealTaskActiveIds,
    dealValuationFileActiveIds, dealParticipantActiveIds, dealFileActiveIds, dealActivityActiveIds,
    dealCapitalizationSourceItemInlineSaveId, dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId,
    dealTaskInlineSaveId, queryTab, dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId,
    dealPhotoListDefault, isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const applyDealActivityUrlFilters: Function = useCallback((): Record<string, FilterValue> => {
    const filterValues: Record<string, FilterValue> = { ...dealActivityFilters };

    if (queryFilterCategory) {
      filterValues[FILTER_TYPE_CATEGORY] = queryFilterCategory;
    }

    if (queryFilterBrokerId) {
      filterValues[FILTER_TYPE_BROKER_ID] = queryFilterBrokerId;
    }

    if (queryFilterLenderId) {
      filterValues[FILTER_TYPE_LENDER_ID] = queryFilterLenderId;
    }

    if (queryFilterInvestorId) {
      filterValues[FILTER_TYPE_INVESTOR_ID] = queryFilterInvestorId;
    }

    if (queryFilterDealContactId) {
      filterValues[FILTER_TYPE_DEAL_CONTACT_ID] = queryFilterDealContactId;
    }

    if (queryFilterType) {
      filterValues[FILTER_TYPE_TYPE] = queryFilterType;
    }

    setDealActivityFilters(filterValues);

    return filterValues;
  }, [
    dealActivityFilters, queryFilterCategory, queryFilterBrokerId, queryFilterLenderId,
    queryFilterInvestorId, queryFilterDealContactId, queryFilterType,
  ]);

  const checkIsDealActivityActive: Function = useCallback((activityId: number): boolean => (
    dealActivityActiveIds ? dealActivityActiveIds.includes(activityId) : false
  ), [dealActivityActiveIds]);

  const closeAllModals: Function = useCallback((): void => {
    if (isDealCapitalizationSourcesModalOpen) {
      closeDealCapitalizationSourcesModal();
    }

    if (isDealCapitalizationUsesModalOpen) {
      closeDealCapitalizationUsesModal();
    }

    if (isDealTermGroupModalOpen) {
      closeDealTermGroupModal();
    }

    if (isDealValuationFileModalOpen) {
      closeDealValuationFileModal();
    }

    if (isDealTaskModalOpen) {
      closeDealTaskModal();
    }

    if (isDealTaskGroupModalOpen) {
      closeDealTaskGroupModal();
    }

    if (isDealParticipantModalOpen) {
      closeDealParticipantModal();
    }

    if (isDealParticipantListModalOpen) {
      closeDealParticipantListModal();
    }

    if (isDealFileModalOpen) {
      closeDealFileModal();
    }

    if (isDealFileGroupModalOpen) {
      closeDealFileGroupModal();
    }

    if (isDealActivityModalOpen) {
      closeDealActivityModal();
    }
  }, [
    isDealCapitalizationSourcesModalOpen, isDealCapitalizationUsesModalOpen, isDealTermGroupModalOpen,
    isDealValuationFileModalOpen, isDealTaskModalOpen, isDealTaskGroupModalOpen, isDealParticipantModalOpen,
    isDealParticipantListModalOpen, isDealFileModalOpen, isDealFileGroupModalOpen, isDealActivityModalOpen,
  ]);

  const processDealPhoto: Function = useCallback((dealPhotoItem: DealPhoto, isNew?: boolean): DealPhotoInput => {
    const resultDealPhotoItem: FormValueItem = dealPhotoItem;

    if (dealPhotoFiles[resultDealPhotoItem.id]) {
      resultDealPhotoItem.file = dealPhotoFiles[resultDealPhotoItem.id];
    }

    if (resultDealPhotoItem.id && isNew) {
      delete resultDealPhotoItem.id;
    }

    return resultDealPhotoItem;
  }, [dealPhotoFiles]);

  const processDealAssetUseBreakdownItem: Function = useCallback((
    dealAssetUseBreakdownItem: FormValueItem,
  ): DealAssetUseBreakdownItemInput => {
    const resultDealAssetUseBreakdownItem: FormValueItem = dealAssetUseBreakdownItem;

    resultDealAssetUseBreakdownItem.value = resultDealAssetUseBreakdownItem.value
      ? FormatService.formatCleanNumber(resultDealAssetUseBreakdownItem.value, true, true) : 0;

    return resultDealAssetUseBreakdownItem;
  }, []);

  const processDealCapitalizationItem: Function = useCallback((
    dealCapitalizationItem: FormValueItem,
  ): DealCapitalizationItemInput => {
    const resultDealCapitalizationItem: FormValueItem = dealCapitalizationItem;

    resultDealCapitalizationItem.atClosingAmount = resultDealCapitalizationItem.atClosingAmount
      ? FormatService.formatCleanNumber(resultDealCapitalizationItem.atClosingAmount, false, true) : 0;
    resultDealCapitalizationItem.futureAmount = resultDealCapitalizationItem.futureAmount
      ? FormatService.formatCleanNumber(resultDealCapitalizationItem.futureAmount, false, true) : 0;
    resultDealCapitalizationItem.amount = resultDealCapitalizationItem.amount
      ? FormatService.formatCleanNumber(resultDealCapitalizationItem.amount, false, true) : 0;
    resultDealCapitalizationItem.pricing = resultDealCapitalizationItem.pricing
      ? FormatService.formatCleanNumber(resultDealCapitalizationItem.pricing, true, true) : 0;

    return resultDealCapitalizationItem;
  }, []);

  const processDealMetricsCapitalStackItem: Function = useCallback((
    dealMetricsCapitalStackItem: FormValueItem,
  ): DealMetricsCapitalStackItemInput => {
    const resultDealMetricsCapitalStackItem: FormValueItem = dealMetricsCapitalStackItem;

    resultDealMetricsCapitalStackItem.value = resultDealMetricsCapitalStackItem.value
      ? FormatService.formatCleanNumber(resultDealMetricsCapitalStackItem.value) : 0;

    return resultDealMetricsCapitalStackItem;
  }, []);

  const processDealHistoricalValuationsItem: Function = useCallback((
    dealHistoricalValuationsItem: FormValueItem,
  ): DealHistoricalValuationsItemInput => {
    const resultDealHistoricalValuationsItem: FormValueItem = dealHistoricalValuationsItem;

    resultDealHistoricalValuationsItem.value = dealHistoricalValuationsItem.value
      ? FormatService.formatCleanNumber(dealHistoricalValuationsItem.value) : 0;

    return resultDealHistoricalValuationsItem;
  }, []);

  const processDealTask: Function = useCallback((dealTaskItem: FormValueItem): DealTaskInput => {
    const resultDealTaskItem: FormValueItem = dealTaskItem;

    resultDealTaskItem.parentId = resultDealTaskItem.parentId || null;
    resultDealTaskItem.contactId = resultDealTaskItem.contactId || null;
    resultDealTaskItem.accessLevel = resultDealTaskItem.accessLevel || null;
    resultDealTaskItem.dueDate = resultDealTaskItem.dueDate
      ? DateService.formatDate(resultDealTaskItem.dueDate) : null;
    resultDealTaskItem.reviewContactIds = resultDealTaskItem.reviewContactIds
    && resultDealTaskItem.reviewContactIds.length
      ? resultDealTaskItem.reviewContactIds.map((contactItem: Contact): number => (
        contactItem.id
      )) : null;

    if (resultDealTaskItem.group) {
      if (Object.keys(resultDealTaskItem.group).length) {
        delete resultDealTaskItem.groupId;
      } else {
        delete resultDealTaskItem.group;
      }
    }

    if (resultDealTaskItem.subTasks && Object.keys(resultDealTaskItem.subTasks).length) {
      const resultSubTasks: DealTaskInput[] = [];

      Object.keys(resultDealTaskItem.subTasks).forEach((dealSubTaskDataIndex: string): void => {
        const dealSubTaskItem: DealTaskInput = { ...resultDealTaskItem.subTasks[dealSubTaskDataIndex] };

        resultSubTasks.push(processDealTask(dealSubTaskItem));
      });

      resultDealTaskItem.subTasks = resultSubTasks;
    }

    return resultDealTaskItem;
  }, []);

  const processDealParticipant: Function = useCallback((
    dealParticipantItem: FormValueItem, isNewItem?: boolean,
  ): DealParticipantInput => {
    const resultDealParticipantItem: FormValueItem = dealParticipantItem;

    const dealParticipantItemId: number = isNewItem ? FormService.FORM_KEY_NEW : dealParticipantItem.id;

    resultDealParticipantItem.accessLevel = resultDealParticipantItem.accessLevel || null;

    if (dealParticipantNdaFiles && (dealParticipantNdaFiles[dealParticipantItemId]
      || dealParticipantNdaFiles[dealParticipantItemId] === null)) {
      if (dealParticipantNdaFiles[dealParticipantItemId]) {
        resultDealParticipantItem.ndaFile = dealParticipantNdaFiles[dealParticipantItemId];
      }

      if (dealParticipantNdaFiles[dealParticipantItemId] === null) {
        resultDealParticipantItem.ndaFile = null;
      }
    } else {
      delete resultDealParticipantItem.ndaFile;
    }

    return resultDealParticipantItem;
  }, [dealParticipantNdaFiles]);

  const processDealValuationFile: Function = useCallback((
    dealValuationFileGroupItem: FormValueItem, dealValuationFileItem: FormValueItem, isNewItem?: boolean,
    newItemIndex?: string,
  ): DealFileInput => {
    const resultDealValuationFileItem: FormValueItem = dealValuationFileItem;

    const dealValuationFileItemId: number = isNewItem ? (newItemIndex || FormService.FORM_KEY_NEW)
      : dealValuationFileItem.id;
    const dealValuationFileGroupItemId: number = isNewItem ? FormService.FORM_KEY_NEW : dealValuationFileGroupItem.id;

    if (dealValuationFileFiles[dealValuationFileGroupItemId]
      && dealValuationFileFiles[dealValuationFileGroupItemId][dealValuationFileItemId]) {
      resultDealValuationFileItem.file = dealValuationFileFiles[dealValuationFileGroupItemId][dealValuationFileItemId];
    }

    if (resultDealValuationFileItem.group) {
      delete resultDealValuationFileItem.groupId;
    }

    return resultDealValuationFileItem;
  }, [dealValuationFileFiles]);

  const processDealFile: Function = useCallback((
    dealFileGroupItem: FormValueItem, dealFileItem: FormValueItem, isNewItem?: boolean, newItemIndex?: string,
    isNewGroup?: boolean, newGroupIndex?: string,
  ): DealFileInput => {
    const resultDealFileItem: FormValueItem = dealFileItem;

    const dealFileItemId: number = isNewItem ? (newItemIndex || FormService.FORM_KEY_NEW) : dealFileItem.id;
    const dealFileGroupItemId: number = isNewItem ? (newGroupIndex || FormService.FORM_KEY_NEW) : dealFileGroupItem.id;

    resultDealFileItem.accessLevel = dealFileItem.accessLevel || null;

    if (dealFileFiles[dealFileGroupItemId] && dealFileFiles[dealFileGroupItemId][dealFileItemId]) {
      resultDealFileItem.file = dealFileFiles[dealFileGroupItemId][dealFileItemId];
    }

    if (dealFileItem.group) {
      if (Object.keys(dealFileItem.group).length) {
        delete resultDealFileItem.groupId;
      } else {
        delete resultDealFileItem.group;
      }
    }

    return dealFileItem;
  }, [dealFileFiles]);

  const processDealActivityItem: Function = useCallback((dealActivityItem: FormValueItem): ActivityInput => {
    const resultDealActivityItem: FormValueItem = dealActivityItem;

    delete resultDealActivityItem.deal;

    resultDealActivityItem.dealId = dealId;

    if (resultDealActivityItem.dealSource && Object.keys(resultDealActivityItem.dealSource).length) {
      delete resultDealActivityItem.dealSourceId;
    } else {
      delete resultDealActivityItem.dealSource;
      resultDealActivityItem.dealSourceId = resultDealActivityItem.dealSourceId || null;
    }

    if (resultDealActivityItem.dealContact && Object.keys(resultDealActivityItem.dealContact).length) {
      resultDealActivityItem.dealContact = { ...resultDealActivityItem.dealContact };
      resultDealActivityItem.dealContact.companyId = resultDealActivityItem.dealContact.companyId || null;
      resultDealActivityItem.dealContact.phone = resultDealActivityItem.dealContact.phone
      && FormatService.isValidPhoneNumber(resultDealActivityItem.dealContact.phone, true)
        ? FormatService.formatPhoneValue(resultDealActivityItem.dealContact.phone) : null;
      resultDealActivityItem.dealContact.groupIds = resultDealActivityItem.dealContact.groupIds
      && resultDealActivityItem.dealContact.groupIds.length
        ? resultDealActivityItem.dealContact.groupIds
          .map((contactGroupItem: ContactGroup): number => contactGroupItem.id) : [];
    } else {
      delete resultDealActivityItem.dealContact;
    }

    resultDealActivityItem.sponsorId = resultDealActivityItem.sponsorId || null;
    resultDealActivityItem.brokerId = resultDealActivityItem.brokerId || null;
    resultDealActivityItem.lenderId = resultDealActivityItem.lenderId || null;
    resultDealActivityItem.dealContactIds = resultDealActivityItem.dealContactIds
    && resultDealActivityItem.dealContactIds.length
      ? resultDealActivityItem.dealContactIds.map((contactItem: Contact): number => contactItem.id) : [];
    resultDealActivityItem.amount = resultDealActivityItem.amount
      ? FormatService.formatCleanNumber(resultDealActivityItem.amount) : 0;
    resultDealActivityItem.size = resultDealActivityItem.size
      ? FormatService.formatCleanNumber(resultDealActivityItem.size) : 0;
    resultDealActivityItem.ltv = resultDealActivityItem.ltv
      ? FormatService.formatCleanNumber(resultDealActivityItem.ltv, true) : 0;
    resultDealActivityItem.purchasePrice = resultDealActivityItem.purchasePrice
      ? FormatService.formatCleanNumber(resultDealActivityItem.purchasePrice) : 0;
    resultDealActivityItem.capitalExpenditure = resultDealActivityItem.capitalExpenditure
      ? FormatService.formatCleanNumber(resultDealActivityItem.capitalExpenditure) : 0;
    resultDealActivityItem.oneYearNoi = resultDealActivityItem.oneYearNoi
      ? FormatService.formatCleanNumber(resultDealActivityItem.oneYearNoi) : 0;
    resultDealActivityItem.yieldOnCost = resultDealActivityItem.yieldOnCost
      ? FormatService.formatCleanNumber(resultDealActivityItem.yieldOnCost) : 0;
    resultDealActivityItem.debtProceeds = resultDealActivityItem.debtProceeds
      ? FormatService.formatCleanNumber(resultDealActivityItem.debtProceeds) : 0;
    resultDealActivityItem.requiredEquityAmount = resultDealActivityItem.requiredEquityAmount
      ? FormatService.formatCleanNumber(resultDealActivityItem.requiredEquityAmount) : 0;
    resultDealActivityItem.interestLevel = resultDealActivityItem.interestLevel || null;
    resultDealActivityItem.ltc = resultDealActivityItem.ltc
      ? FormatService.formatCleanNumber(resultDealActivityItem.ltc, true) : 0;
    resultDealActivityItem.equityProviderId = resultDealActivityItem.equityProviderId || null;
    resultDealActivityItem.investorId = resultDealActivityItem.investorId || null;
    resultDealActivityItem.additionalPartyId = resultDealActivityItem.additionalPartyId || null;
    resultDealActivityItem.tags = resultDealActivityItem.tags && resultDealActivityItem.tags.length
      ? resultDealActivityItem.tags.map((tagItem: Tag): string => tagItem.name) : [];

    const activityOptions: ActivityField[] = [];
    if (resultDealActivityItem.options && Object.keys(resultDealActivityItem.options).length) {
      Object.keys(resultDealActivityItem.options).forEach((activityFieldIndex: string): void => {
        activityOptions.push({ ...resultDealActivityItem.options[activityFieldIndex] });
      });
    }
    resultDealActivityItem.options = activityOptions;

    return resultDealActivityItem;
  }, [dealId]);

  const inlineCancel: Function = useCallback((): void => {
    const dealCapitalizationSourceItemId: number | null = dealCapitalizationSourceItemInlineSaveId;
    const dealCapitalizationSourceGroupId: number | null = dealCapitalizationSourceGroupInlineSaveId;
    const dealCapitalizationUseItemId: number | null = dealCapitalizationUseItemInlineSaveId;
    const dealCapitalizationUseGroupId: number | null = dealCapitalizationUseGroupInlineSaveId;
    const dealValuationFileId: number | null = dealValuationFileInlineSaveId;
    const dealValuationFileGroupId: number | null = dealValuationFileGroupInlineSaveId;
    const dealTaskId: number | null = dealTaskInlineSaveId;
    const dealTaskGroupId: number | null = dealTaskGroupInlineSaveId;
    const dealParticipantId: number | null = dealParticipantInlineSaveId;
    const dealFileId: number | null = dealFileInlineSaveId;
    const dealFileGroupId: number | null = dealFileGroupInlineSaveId;
    const dealActivityId: number | null = dealActivityInlineSaveId;

    const formKeyDealCapitalizationSourceGroup: string | undefined = dealCapitalizationSourceGroupId
      ? FormService.getIndexedFormFieldKey(dealCapitalizationSourceGroupId) : undefined;
    const formKeyDealCapitalizationSourceItem: string | undefined = dealCapitalizationSourceItemId
      ? FormService.getIndexedFormFieldKey(dealCapitalizationSourceItemId) : undefined;
    const formKeyDealCapitalizationUseGroup: string | undefined = dealCapitalizationUseGroupId
      ? FormService.getIndexedFormFieldKey(dealCapitalizationUseGroupId) : undefined;
    const formKeyDealCapitalizationUseItem: string | undefined = dealCapitalizationUseItemId
      ? FormService.getIndexedFormFieldKey(dealCapitalizationUseItemId) : undefined;
    const formKeyDealValuationFile: string | undefined = dealValuationFileId
      ? FormService.getIndexedFormFieldKey(dealValuationFileId) : undefined;
    const formKeyDealValuationGroup: string | undefined = dealValuationFileGroupId
      ? FormService.getIndexedFormFieldKey(dealValuationFileGroupId) : undefined;
    const formKeyDealTask: string | undefined = dealTaskId
      ? FormService.getIndexedFormFieldKey(dealTaskId) : undefined;
    const formKeyDealTaskGroup: string | undefined = dealTaskGroupId
      ? FormService.getIndexedFormFieldKey(dealTaskGroupId) : undefined;
    const formKeyDealParticipant: string | undefined = dealParticipantId
      ? FormService.getIndexedFormFieldKey(dealParticipantId) : undefined;
    const formKeyDealFile: string | undefined = dealFileId
      ? FormService.getIndexedFormFieldKey(dealFileId) : undefined;
    const formKeyDealFileGroup: string | undefined = dealFileGroupId
      ? FormService.getIndexedFormFieldKey(dealFileGroupId) : undefined;
    const formKeyActivityItem: string | undefined = dealActivityId
      ? FormService.getIndexedFormFieldKey(dealActivityId) : undefined;

    if (dealCapitalizationSourceItemId !== null && dealCapitalizationSourceGroupId !== null) {
      const category: string = DEAL_CAPITALIZATION_CATEGORY_SOURCES;

      if (formKeyDealCapitalizationSourceGroup && formKeyDealCapitalizationSourceItem
        && initialFormValues && initialFormValues.dealData
        && initialFormValues.dealData.dealCapitalizationData
        && initialFormValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationSourceGroup]
        && initialFormValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationSourceGroup]
          .items[formKeyDealCapitalizationSourceItem]) {
        changeForm(
          `dealData.dealCapitalizationData.${category}[${formKeyDealCapitalizationSourceGroup}]`
          + `.items[${formKeyDealCapitalizationSourceItem}]`,
          initialFormValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationSourceGroup]
            .items[formKeyDealCapitalizationSourceItem],
        );
      }

      if (dealCapitalizationSourceItemActiveIds
        && dealCapitalizationSourceItemActiveIds[dealCapitalizationSourceGroupId]) {
        const newDealCapitalizationSourceItemActiveIds: Record<string, number[]> = {
          ...dealCapitalizationSourceItemActiveIds,
        };
        const activeItemIndex: number = newDealCapitalizationSourceItemActiveIds[dealCapitalizationSourceGroupId]
          .findIndex((dealCapitalizationSourceItemActiveId: number): boolean => (
            dealCapitalizationSourceItemActiveId === dealCapitalizationSourceItemId
          ));

        if (activeItemIndex !== FormatService.INDEX_NOT_FOUND) {
          StructureService.deleteListItemByIndex(
            newDealCapitalizationSourceItemActiveIds[dealCapitalizationSourceGroupId],
            activeItemIndex,
          );
          setDealCapitalizationSourceItemActiveIds(newDealCapitalizationSourceItemActiveIds);
        }
      }

      if (dealCapitalizationSourceNewItems[dealCapitalizationSourceGroupId]) {
        const dealCapitalizationSourceNewItemIndex: number = (
          dealCapitalizationSourceNewItems[dealCapitalizationSourceGroupId]
            .findIndex((dealCapitalizationItem: DealCapitalizationItemInput): boolean => (
              dealCapitalizationItem.id === dealCapitalizationSourceItemId
            ))
        );

        if (dealCapitalizationSourceNewItemIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealCapitalizationSourceNewItems: DealCapitalizationItemInput[] = [
            ...dealCapitalizationSourceNewItems[dealCapitalizationSourceGroupId],
          ];

          StructureService.deleteListItemByIndex(
            newDealCapitalizationSourceNewItems,
            dealCapitalizationSourceNewItemIndex,
          );
          setDealCapitalizationSourceNewItems({
            ...dealCapitalizationSourceNewItems,
            [dealCapitalizationSourceGroupId]: newDealCapitalizationSourceNewItems,
          });
        }
      }
    }

    if (dealCapitalizationUseItemId !== null && dealCapitalizationUseGroupId !== null) {
      const category: string = DEAL_CAPITALIZATION_CATEGORY_USES;

      if (formKeyDealCapitalizationUseGroup && formKeyDealCapitalizationUseItem
        && initialFormValues && initialFormValues.dealData && initialFormValues.dealData.dealCapitalizationData
        && initialFormValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationUseGroup]
        && initialFormValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationUseGroup]
          .items[formKeyDealCapitalizationUseItem]) {
        changeForm(
          `dealData.dealCapitalizationData.${category}[${formKeyDealCapitalizationUseGroup}]`
          + `.items[${formKeyDealCapitalizationUseItem}]`,
          initialFormValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationUseGroup]
            .items[formKeyDealCapitalizationUseItem],
        );
      }

      if (dealCapitalizationUseItemActiveIds
        && dealCapitalizationUseItemActiveIds[dealCapitalizationUseGroupId]) {
        const newDealCapitalizationUseItemActiveIds: Record<string, number[]> = {
          ...dealCapitalizationUseItemActiveIds,
        };
        const activeItemIndex: number = newDealCapitalizationUseItemActiveIds[dealCapitalizationUseGroupId]
          .findIndex((dealCapitalizationUseItemActiveId: number): boolean => (
            dealCapitalizationUseItemActiveId === dealCapitalizationUseItemId
          ));

        if (activeItemIndex !== FormatService.INDEX_NOT_FOUND) {
          StructureService.deleteListItemByIndex(
            newDealCapitalizationUseItemActiveIds[dealCapitalizationUseGroupId],
            activeItemIndex,
          );
          setDealCapitalizationUseItemActiveIds(newDealCapitalizationUseItemActiveIds);
        }
      }

      if (dealCapitalizationUseNewItems[dealCapitalizationUseGroupId]) {
        const dealCapitalizationUseNewItemIndex: number = dealCapitalizationUseNewItems[dealCapitalizationUseGroupId]
          .findIndex((dealCapitalizationItem: DealCapitalizationItemInput): boolean => (
            dealCapitalizationItem.id === dealCapitalizationUseItemId
          ));

        if (dealCapitalizationUseNewItemIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealCapitalizationUseNewItems: DealCapitalizationItemInput[] = [
            ...dealCapitalizationUseNewItems[dealCapitalizationUseGroupId],
          ];

          StructureService.deleteListItemByIndex(
            newDealCapitalizationUseNewItems,
            dealCapitalizationUseNewItemIndex,
          );
          setDealCapitalizationUseNewItems({
            ...dealCapitalizationUseNewItems,
            [dealCapitalizationUseGroupId]: newDealCapitalizationUseNewItems,
          });
        }
      }
    }

    if (dealValuationFileId !== null && dealValuationFileGroupId !== null) {
      if (formKeyDealValuationGroup && formKeyDealValuationFile
        && initialFormValues && initialFormValues.dealData && initialFormValues.dealData.dealValuationFilesData
        && initialFormValues.dealData.dealValuationFilesData[formKeyDealValuationGroup]
        && initialFormValues.dealData.dealValuationFilesData[formKeyDealValuationGroup]
          .files[formKeyDealValuationFile]) {
        changeForm(
          `dealData.dealValuationFilesData[${formKeyDealValuationGroup}].files[${formKeyDealValuationFile}]`,
          initialFormValues.dealData.dealValuationFilesData[formKeyDealValuationGroup].files[formKeyDealValuationFile],
        );
      }

      const dealValuationFileActiveIdIndex: number = dealValuationFileActiveIds
        .findIndex((dealValuationFileItemActiveId: number): boolean => (
          dealValuationFileItemActiveId === dealValuationFileId
        ));

      if (dealValuationFileActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
        const newDealValuationFileActiveIds: number[] = [...dealValuationFileActiveIds];
        StructureService.deleteListItemByIndex(newDealValuationFileActiveIds, dealValuationFileActiveIdIndex);
        setDealValuationFileActiveIds(newDealValuationFileActiveIds);
      }

      if (dealValuationFileFiles[dealValuationFileGroupId]
        && dealValuationFileFiles[dealValuationFileGroupId][dealValuationFileId]) {
        const newValuationDealFileFiles: Record<string, Record<string, DealFile>> = { ...dealValuationFileFiles };
        delete newValuationDealFileFiles[dealValuationFileGroupId][dealValuationFileId];
        setDealValuationFileFiles(newValuationDealFileFiles);
      }

      if (dealValuationFileNewItems[dealValuationFileGroupId]) {
        const dealValuationFileNewItemIndex: number = dealValuationFileNewItems[dealValuationFileGroupId]
          .findIndex((dealValuationFileItem: DealFileInput): boolean => (
            dealValuationFileItem.id === dealValuationFileId
          ));

        if (dealValuationFileNewItemIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealValuationFileNewItems: DealFileInput[] = [
            ...dealValuationFileNewItems[dealValuationFileGroupId],
          ];

          StructureService.deleteListItemByIndex(newDealValuationFileNewItems, dealValuationFileNewItemIndex);
          setDealValuationFileNewItems({
            ...dealValuationFileNewItems,
            [dealValuationFileGroupId]: newDealValuationFileNewItems,
          });
        }
      }
    }

    if (dealTaskId !== null && dealTaskGroupId !== null) {
      if (formKeyDealTaskGroup && formKeyDealTask && initialFormValues && initialFormValues.dealData
        && initialFormValues.dealData.dealTasksData
        && initialFormValues.dealData.dealTasksData[formKeyDealTaskGroup]
        && initialFormValues.dealData.dealTasksData[formKeyDealTaskGroup].tasks[formKeyDealTask]) {
        changeForm(
          `dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`,
          initialFormValues.dealData.dealTasksData[formKeyDealTaskGroup].tasks[formKeyDealTask],
        );
      }

      if (dealTaskId !== FormService.FORM_KEY_NEW) {
        const dealTaskActiveIdIndex: number = dealTaskActiveIds.findIndex((dealTaskItemActiveId: number): boolean => (
          dealTaskItemActiveId === dealTaskId
        ));

        if (dealTaskActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealTaskActiveIds: number[] = [...dealTaskActiveIds];
          StructureService.deleteListItemByIndex(newDealTaskActiveIds, dealTaskActiveIdIndex);
          setDealTaskActiveIds(newDealTaskActiveIds);
        }
      }

      if (dealTaskNewItems[dealTaskGroupId]) {
        const dealTaskNewItemIndex: number = dealTaskNewItems[dealTaskGroupId]
          .findIndex((dealTaskItem: DealTaskInput): boolean => (
            dealTaskItem.id === dealTaskId
          ));

        if (dealTaskNewItemIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealTaskNewItems: DealTaskInput[] = [...dealTaskNewItems[dealTaskGroupId]];

          StructureService.deleteListItemByIndex(newDealTaskNewItems, dealTaskNewItemIndex);
          setDealTaskNewItems({
            ...dealTaskNewItems,
            [dealTaskGroupId]: newDealTaskNewItems,
          });
        }
      }
    }

    if (dealParticipantId !== null) {
      if (formKeyDealParticipant && initialFormValues && initialFormValues.dealData
        && initialFormValues.dealData.dealParticipantsData
        && initialFormValues.dealData.dealParticipantsData[formKeyDealParticipant]) {
        changeForm(
          `dealData.dealParticipantsData[${formKeyDealParticipant}]`,
          initialFormValues.dealData.dealParticipantsData[formKeyDealParticipant],
        );
      }

      if (dealParticipantId !== FormService.FORM_KEY_NEW) {
        const dealParticipantActiveIdIndex: number = dealParticipantActiveIds
          .findIndex((dealParticipantItemActiveId: number): boolean => (
            dealParticipantItemActiveId === dealParticipantId
          ));

        if (dealParticipantActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealParticipantActiveIds: number[] = [...dealParticipantActiveIds];

          StructureService.deleteListItemByIndex(newDealParticipantActiveIds, dealParticipantActiveIdIndex);
          setDealParticipantActiveIds(newDealParticipantActiveIds);
        }
      }

      if (dealParticipantNdaFiles[dealParticipantId]) {
        const newDealParticipantNdaFiles: Record<string, File> = { ...dealParticipantNdaFiles };

        delete newDealParticipantNdaFiles[dealParticipantId];
        setDealParticipantNdaFiles(newDealParticipantNdaFiles);
      }

      if (dealParticipantNewItems) {
        const dealParticipantNewItemIndex: number = dealParticipantNewItems
          .findIndex((dealParticipantItem: DealParticipantInput): boolean => (
            dealParticipantItem.id === dealParticipantId
          ));

        if (dealParticipantNewItemIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealParticipantNewItems: DealParticipantInput[] = [...dealParticipantNewItems];

          StructureService.deleteListItemByIndex(newDealParticipantNewItems, dealParticipantNewItemIndex);
          setDealParticipantNewItems(newDealParticipantNewItems);
        }
      }
    }

    if (dealFileId !== null && dealFileGroupId !== null) {
      if (formKeyDealFileGroup && formKeyDealFile && initialFormValues && initialFormValues.dealData
        && initialFormValues.dealData.dealFilesData
        && initialFormValues.dealData.dealFilesData[formKeyDealFileGroup].files[formKeyDealFile]) {
        changeForm(
          `dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}]`,
          initialFormValues.dealData.dealFilesData[formKeyDealFileGroup].files[formKeyDealFile],
        );
      }

      if (dealFileId !== FormService.FORM_KEY_NEW) {
        const dealFileActiveIdIndex: number = dealFileActiveIds.findIndex((dealFileItemActiveId: number): boolean => (
          dealFileItemActiveId === dealFileId
        ));

        if (dealFileActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealFileActiveIds: number[] = [...dealFileActiveIds];
          StructureService.deleteListItemByIndex(newDealFileActiveIds, dealFileActiveIdIndex);
          setDealFileActiveIds(newDealFileActiveIds);
        }
      }

      if (dealFileFiles[dealFileGroupId] && dealFileFiles[dealFileGroupId][dealFileId]) {
        const newDealFileFiles: Record<string, Record<string, File>> = { ...dealFileFiles };

        delete newDealFileFiles[dealFileGroupId][dealFileId];
        setDealFileFiles(newDealFileFiles);
      }

      if (dealFileNewItems[dealFileGroupId]) {
        const dealFileNewItemIndex: number = dealFileNewItems[dealFileGroupId]
          .findIndex((dealFileItem: DealFileInput): boolean => (
            dealFileItem.id === dealFileId
          ));

        if (dealFileNewItemIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealFileNewItems: DealFileInput[] = [...dealFileNewItems[dealFileGroupId]];
          StructureService.deleteListItemByIndex(newDealFileNewItems, dealFileNewItemIndex);
          setDealFileNewItems({
            ...dealFileNewItems,
            [dealFileGroupId]: newDealFileNewItems,
          });
        }
      }
    }

    if (dealActivityId !== null) {
      if (formKeyActivityItem && initialFormValues && initialFormValues.dealData
        && initialFormValues.dealData.activityListData
        && initialFormValues.dealData.activityListData[formKeyActivityItem]) {
        changeForm(
          `dealData.activityListData[${formKeyActivityItem}]`,
          initialFormValues.dealData.activityListData[formKeyActivityItem],
        );
      }

      if (dealActivityId !== FormService.FORM_KEY_NEW) {
        const dealActivityActiveIdIndex: number = dealActivityActiveIds
          .findIndex((dealActivityItemActiveId: number): boolean => (
            dealActivityItemActiveId === dealActivityId
          ));

        if (dealActivityActiveIdIndex !== FormatService.INDEX_NOT_FOUND) {
          const newDealActivityActiveIds: number[] = [...dealActivityActiveIds];
          StructureService.deleteListItemByIndex(newDealActivityActiveIds, dealActivityActiveIdIndex);
          setDealActivityActiveIds(newDealActivityActiveIds);
        }
      }

      if (dealActivityFieldNewItems[dealActivityId]) {
        const newDealActivityFieldNewItems: Record<string, ActivityField[]> = { ...dealActivityFieldNewItems };

        delete newDealActivityFieldNewItems[dealActivityId];
        setDealActivityFieldNewItems(newDealActivityFieldNewItems);
      }
    }

    resetInlineIds();
  }, [
    initialFormValues, dealCapitalizationSourceItemActiveIds, dealCapitalizationUseItemActiveIds, dealTaskActiveIds,
    dealValuationFileActiveIds, dealParticipantActiveIds, dealFileActiveIds, dealFileGroupActiveIds,
    dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId, dealCapitalizationSourceGroupInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealCapitalizationUseGroupInlineSaveId, dealValuationFileInlineSaveId,
    dealTaskGroupInlineSaveId, dealTaskInlineSaveId, dealParticipantInlineSaveId, dealFileInlineSaveId,
    dealFileGroupInlineSaveId, dealActivityInlineSaveId, dealValuationFileFiles, dealFileFiles,
    dealParticipantNdaFiles, dealActivityFieldNewItems, dealCapitalizationSourceNewItems,
    dealCapitalizationUseNewItems, dealValuationFileNewItems, dealTaskNewItems, dealParticipantNewItems,
    dealFileNewItems, dealFileNewGroupItems,
  ]);

  const openDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onDeleteClick: Function = useCallback((): void => {
    openDeleteConfirmModal();
  }, []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!dealId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteDealList({
        dealIds: [dealId],
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        setTimeout((): void => {
          navigate({
            pathname: ROUTE_DEAL_LIST,
          });
        }, TIMEOUT_SHORT);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const formData: FormValueItem = {
      ...values.dealData,
    };
    if (!Object.keys(formData).length) {
      return null;
    }

    const dealCommonFormData: FormValueItem = formData.dealCommonData ? { ...formData.dealCommonData } : {};
    if (Object.keys(dealCommonFormData).length) {
      dealCommonFormData.id = dealCommonFormData.id || dealId || null;
      dealCommonFormData.asset = dealCommonFormData.asset ? { ...dealCommonFormData.asset } : {};

      if (isNewDeal) {
        dealCommonFormData.asset.name = '';
      }

      if (Object.keys(dealCommonFormData.asset).length
        && (dealCommonFormData.asset.name || dealCommonFormData.asset.name === '')) {
        if (dealCommonFormData.asset.name === '' && dealCommonFormData.name) {
          dealCommonFormData.asset.name = dealCommonFormData.name;
        }

        delete dealCommonFormData.asset.streetAddress;
        delete dealCommonFormData.asset.location;
        delete dealCommonFormData.asset.postalCode;

        dealCommonFormData.asset.sellerIds = dealCommonFormData.asset.sellerIds
        && dealCommonFormData.asset.sellerIds.length
          ? dealCommonFormData.asset.sellerIds.map((contactItem: Contact): number => contactItem.id) : [];
        dealCommonFormData.asset.types = dealCommonFormData.asset.types && dealCommonFormData.asset.types.length
          ? dealCommonFormData.asset.types.map((assetTypeItem: AssetType): number => assetTypeItem.id) : [];
        dealCommonFormData.asset.size = dealCommonFormData.asset.size
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.size) : 0;
        dealCommonFormData.asset.loanAmount = dealCommonFormData.asset.loanAmount
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.loanAmount) : 0;
        dealCommonFormData.asset.amount = dealCommonFormData.asset.amount
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.amount) : 0;
        dealCommonFormData.asset.value = dealCommonFormData.asset.value
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.value) : 0;
        dealCommonFormData.asset.cost = dealCommonFormData.asset.cost
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.cost) : 0;
        dealCommonFormData.asset.currentNoi = dealCommonFormData.asset.currentNoi
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.currentNoi) : 0;
        dealCommonFormData.asset.ttmNoi = dealCommonFormData.asset.ttmNoi
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.ttmNoi) : 0;
        dealCommonFormData.asset.runRateNoi = dealCommonFormData.asset.runRateNoi
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.runRateNoi) : 0;
        dealCommonFormData.asset.forwardNoi = dealCommonFormData.asset.forwardNoi
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.forwardNoi) : 0;
        dealCommonFormData.asset.maturityDate = dealCommonFormData.asset.maturityDate
          ? DateService.formatDate(dealCommonFormData.asset.maturityDate) : null;
        dealCommonFormData.asset.portfolioInvestedEquity = dealCommonFormData.asset.portfolioInvestedEquity
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.portfolioInvestedEquity) : 0;
        dealCommonFormData.asset.leveredIrr = dealCommonFormData.asset.leveredIrr
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.leveredIrr, true) : 0;
        dealCommonFormData.asset.equityMultiple = dealCommonFormData.asset.equityMultiple
          ? FormatService.formatCleanNumber(dealCommonFormData.asset.equityMultiple, true) : 0;

        if (dealAssetPhotoFile) {
          dealCommonFormData.asset.mainPhoto = dealAssetPhotoFile;
        }

        const assetOptions: AssetFieldInput[] = [];
        if (dealCommonFormData.asset.options && Object.keys(dealCommonFormData.asset.options).length) {
          Object.keys(dealCommonFormData.asset.options).forEach((assetFieldIndex: string): void => {
            assetOptions.push({ ...dealCommonFormData.asset.options[assetFieldIndex] });
          });
        }
        dealCommonFormData.asset.options = assetOptions;
      } else {
        dealCommonFormData.assetId = dealCommonFormData.assetId || null;
        dealCommonFormData.portfolioId = (dealCommonFormData.asset && dealCommonFormData.asset.portfolioId) || null;
        delete dealCommonFormData.asset;
      }

      const dealSponsors: ContactInput[] = (dealCommonFormData.sponsors
        && Object.values(dealCommonFormData.sponsors)) || [];
      delete dealCommonFormData.sponsors;
      if (dealSponsors && dealSponsors.length) {
        dealCommonFormData.sponsors = dealSponsors.filter((dealSponsor: ContactInput): boolean => !!dealSponsor);
      }

      const dealBrokers: ContactInput[] = (dealCommonFormData.brokers
        && Object.values(dealCommonFormData.brokers)) || [];
      delete dealCommonFormData.brokers;
      if (dealBrokers && dealBrokers.length) {
        dealCommonFormData.brokers = dealBrokers.filter((dealBroker: ContactInput): boolean => !!dealBroker);
      }

      const dealLenders: ContactInput[] = (dealCommonFormData.lenders
        && Object.values(dealCommonFormData.lenders)) || [];
      delete dealCommonFormData.lenders;
      if (dealLenders && dealLenders.length) {
        dealCommonFormData.lenders = dealLenders.filter((dealLender: ContactInput): boolean => !!dealLender);
      }

      dealCommonFormData.sponsorIds = dealCommonFormData.sponsorIds && dealCommonFormData.sponsorIds.length
        ? dealCommonFormData.sponsorIds.map((contactItem: Contact): number => contactItem.id) : [];

      if (dealCommonFormData.sponsorContactIds && dealCommonFormData.sponsorContactIds.length) {
        dealCommonFormData.sponsorIds = [
          ...dealCommonFormData.sponsorIds,
          ...(dealCommonFormData.sponsorContactIds.map((contactItem: Contact): number => contactItem.id)),
        ];
      }

      delete dealCommonFormData.sponsorContactIds;

      dealCommonFormData.brokerIds = dealCommonFormData.brokerIds && dealCommonFormData.brokerIds.length
        ? dealCommonFormData.brokerIds.map((contactItem: Contact): number => contactItem.id) : [];

      if (dealCommonFormData.brokerContactIds && dealCommonFormData.brokerContactIds.length) {
        dealCommonFormData.brokerIds = [
          ...dealCommonFormData.brokerIds,
          ...(dealCommonFormData.brokerContactIds.map((contactItem: Contact): number => contactItem.id)),
        ];
      }

      delete dealCommonFormData.brokerContactIds;

      dealCommonFormData.lenderIds = dealCommonFormData.lenderIds && dealCommonFormData.lenderIds.length
        ? dealCommonFormData.lenderIds.map((contactItem: Contact): number => contactItem.id) : [];

      if (dealCommonFormData.lenderContactIds && dealCommonFormData.lenderContactIds.length) {
        dealCommonFormData.lenderIds = [
          ...dealCommonFormData.lenderIds,
          ...(dealCommonFormData.lenderContactIds.map((contactItem: Contact): number => contactItem.id)),
        ];
      }

      delete dealCommonFormData.lenderContactIds;

      dealCommonFormData.type = dealCommonFormData.type || null;
      dealCommonFormData.amount = dealCommonFormData.amount
        ? FormatService.formatCleanNumber(dealCommonFormData.amount) : 0;
      dealCommonFormData.loanAmount = dealCommonFormData.loanAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.loanAmount) : 0;
      dealCommonFormData.zfaAmount = dealCommonFormData.zfaAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.zfaAmount) : 0;
      dealCommonFormData.gsfAmount = dealCommonFormData.gsfAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.gsfAmount) : 0;
      dealCommonFormData.nsfAmount = dealCommonFormData.nsfAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.nsfAmount) : 0;
      dealCommonFormData.unitsAmount = dealCommonFormData.unitsAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.unitsAmount) : 0;
      dealCommonFormData.keysAmount = dealCommonFormData.keysAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.keysAmount) : 0;
      dealCommonFormData.acresAmount = dealCommonFormData.acresAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.acresAmount) : 0;
      dealCommonFormData.lotsfAmount = dealCommonFormData.lotsfAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.lotsfAmount) : 0;
      dealCommonFormData.projectedNoiAmount = dealCommonFormData.projectedNoiAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.projectedNoiAmount) : 0;
      dealCommonFormData.appraisedValueAmount = dealCommonFormData.appraisedValueAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.appraisedValueAmount) : 0;
      dealCommonFormData.irrAmount = dealCommonFormData.irrAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.irrAmount, true, true) : 0;
      dealCommonFormData.yieldToCostAmount = dealCommonFormData.yieldToCostAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.yieldToCostAmount, true, true) : 0;
      dealCommonFormData.askingPrice = dealCommonFormData.askingPrice
        ? FormatService.formatCleanNumber(dealCommonFormData.askingPrice) : 0;
      dealCommonFormData.equityRequired = dealCommonFormData.equityRequired
        ? FormatService.formatCleanNumber(dealCommonFormData.equityRequired) : 0;
      dealCommonFormData.estimatedTdc = dealCommonFormData.estimatedTdc
        ? FormatService.formatCleanNumber(dealCommonFormData.estimatedTdc) : 0;
      dealCommonFormData.equityAmount = dealCommonFormData.equityAmount
        ? FormatService.formatCleanNumber(dealCommonFormData.equityAmount) : 0;
      dealCommonFormData.totalFunded = dealCommonFormData.totalFunded
        ? FormatService.formatCleanNumber(dealCommonFormData.totalFunded) : 0;
      dealCommonFormData.totalUnfunded = dealCommonFormData.totalUnfunded
        ? FormatService.formatCleanNumber(dealCommonFormData.totalUnfunded) : 0;
      dealCommonFormData.bidDate = dealCommonFormData.bidDate
        ? DateService.formatDate(dealCommonFormData.bidDate) : null;
      dealCommonFormData.bidDateReminderOffset = dealCommonFormData.bidDateReminderOffset
        ? FormatService.formatCleanNumber(dealCommonFormData.bidDateReminderOffset, false, true) : null;
      dealCommonFormData.receivedDate = dealCommonFormData.receivedDate
        ? DateService.formatDate(dealCommonFormData.receivedDate) : null;
      dealCommonFormData.closedDate = dealCommonFormData.closedDate
        ? DateService.formatDate(dealCommonFormData.closedDate) : null;

      if (dealCommonFormData.newDealActivity) {
        dealCommonFormData.newDealActivity = {
          ...dealCommonFormData.newDealActivity,
        };
        dealCommonFormData.newDealActivity.dealContactIds = dealCommonFormData.newDealActivity.dealContactIds
        && dealCommonFormData.newDealActivity.dealContactIds.length
          ? dealCommonFormData.newDealActivity.dealContactIds.map((contactItem: Contact): number => contactItem.id)
          : [];
      }

      const dealAllocationItems: DealAllocationItemInput[] = [];
      if (dealCommonFormData.allocationItems && Object.keys(dealCommonFormData.allocationItems).length) {
        Object.keys(dealCommonFormData.allocationItems).forEach((dealAllocationItemIndex: string): void => {
          const dealAllocationItem: FormValueItem = (
            dealCommonFormData.allocationItems[dealAllocationItemIndex]
          );

          if (dealAllocationItem) {
            dealAllocationItems.push({
              ...dealAllocationItem,
              value: FormatService.formatCleanNumber(dealAllocationItem.value, true, true),
            });
          }
        });
      }
      dealCommonFormData.allocationItems = dealAllocationItems;
    }

    let dealPhotosFormData: DealPhotoInput[] | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealPhotosFormData = dealPhotoList && dealPhotoList.length
        ? dealPhotoList.map((dealPhotoItem: DealPhoto): DealPhotoInput => ({
          id: dealPhotoItem.id,
          link: dealPhotoItem.link,
          file: null,
        })) : [];

      if (dealPhotoNewItems) {
        dealPhotoNewItems.forEach((dealPhotoItem: DealPhotoInput): void => {
          const dealPhotoData: DealPhotoInput = processDealPhoto({ ...dealPhotoItem }, true);

          if (dealPhotosFormData && (dealPhotoData.file || dealPhotoData.link)) {
            dealPhotosFormData.push(dealPhotoData);
          }
        });
      }
    }

    let dealActivityListFormData: ActivityInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_DEAL_ACTIVITY) || isNewDeal) {
      dealActivityListFormData = [];

      if (dealActivityInlineSaveId !== FormService.FORM_KEY_NEW) {
        delete formData.activityListData[formKeyNew];
      }

      Object.keys(formData.activityListData).forEach((dealActivityItemIndex: string): void => {
        const dealActivityItem: FormValueItem = {
          ...formData.activityListData[dealActivityItemIndex],
        };

        if (dealActivityListFormData && dealActivityItem && (Object.keys(dealActivityItem).length
          || (dealActivityInlineSaveId && dealActivityInlineSaveId === dealActivityItem.id)
          || (!dealActivityInlineSaveId && checkIsDealActivityActive(dealActivityItem.id)))) {
          dealActivityListFormData.push(processDealActivityItem(dealActivityItem));
        }
      });
    }

    let dealBuildingDetailsFormData: DealBuildingDetailsDataInput | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealBuildingDetailsFormData = {
        assetUseBreakdown: [],
        assetUseBreakdownHeaders: [],
      };

      Object.keys(formData.dealBuildingDetailsData.assetUseBreakdown).forEach((groupIndex): void => {
        if (formData.dealBuildingDetailsData.assetUseBreakdown[groupIndex]
          && formData.dealBuildingDetailsData.assetUseBreakdown[groupIndex].assetTypeId) {
          const dealBuildingDetailsGroupItem: DealAssetUseBreakdownItemInput = {
            assetTypeId: formData.dealBuildingDetailsData.assetUseBreakdown[groupIndex].assetTypeId,
            breakdownProperties: [],
          };

          Object.keys(formData.dealBuildingDetailsData.assetUseBreakdownHeaders).forEach((itemIndex: string): void => {
            const dealBuildingDetailsItem: DealAssetUseBreakdownPropertyInput = (
              formData.dealBuildingDetailsData.assetUseBreakdown[groupIndex]
              && formData.dealBuildingDetailsData.assetUseBreakdown[groupIndex].breakdownProperties
              && formData.dealBuildingDetailsData.assetUseBreakdown[groupIndex].breakdownProperties[itemIndex]
            ) || { value: '' };

            if (formData.dealBuildingDetailsData.assetUseBreakdownHeaders[itemIndex] && dealBuildingDetailsItem
              && Object.keys(dealBuildingDetailsItem).length) {
              dealBuildingDetailsGroupItem.breakdownProperties.push(
                processDealAssetUseBreakdownItem({ ...dealBuildingDetailsItem }),
              );
            }
          });

          if (dealBuildingDetailsFormData && dealBuildingDetailsFormData.assetUseBreakdown) {
            dealBuildingDetailsFormData.assetUseBreakdown.push(dealBuildingDetailsGroupItem);
          }
        }
      });

      Object.keys(formData.dealBuildingDetailsData.assetUseBreakdownHeaders).forEach((itemIndex: string): void => {
        if (dealBuildingDetailsFormData && dealBuildingDetailsFormData.assetUseBreakdownHeaders
          && formData.dealBuildingDetailsData.assetUseBreakdownHeaders[itemIndex]) {
          dealBuildingDetailsFormData.assetUseBreakdownHeaders.push({
            ...formData.dealBuildingDetailsData.assetUseBreakdownHeaders[itemIndex],
          });
        }
      });
    }

    let dealInvestmentReturnsFormData: DealInvestmentReturnsItemInput[] | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealInvestmentReturnsFormData = [];
      Object.keys(formData.dealInvestmentReturnsData).forEach((dealInvestmentReturnsDataIndex: string): void => {
        const dealInvestmentReturnsItem: FormValueItem = {
          ...formData.dealInvestmentReturnsData[dealInvestmentReturnsDataIndex],
        };

        if (dealInvestmentReturnsFormData && dealInvestmentReturnsFormData && dealInvestmentReturnsItem
          && Object.keys(dealInvestmentReturnsItem).length) {
          dealInvestmentReturnsFormData.push({ ...dealInvestmentReturnsItem });
        }
      });
    }

    let dealMilestonesFormData: DealMilestonesItemInput[] | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealMilestonesFormData = [];

      Object.keys(formData.dealMilestonesData).forEach((dealMilestonesDataIndex: string): void => {
        const dealMilestonesItem: FormValueItem = {
          ...formData.dealMilestonesData[dealMilestonesDataIndex],
        };

        if (dealMilestonesFormData && dealMilestonesItem && Object.keys(dealMilestonesItem).length) {
          dealMilestonesItem.value = dealMilestonesItem.value
            ? DateService.formatDate(dealMilestonesItem.value) : null;
          dealMilestonesItem.reminderOffset = dealMilestonesItem.reminderOffset
            ? FormatService.formatCleanNumber(dealMilestonesItem.reminderOffset, false, true) : null;

          if (dealMilestonesItem.value) {
            dealMilestonesFormData.push(dealMilestonesItem);
          }
        }
      });
    }

    let dealExternalLinksFormData: DealExternalLinksItemInput[] | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealExternalLinksFormData = [];

      Object.keys(formData.dealExternalLinksData).forEach((dealExternalLinksDataIndex: string): void => {
        const dealExternalLinksItem: FormValueItem = {
          ...formData.dealExternalLinksData[dealExternalLinksDataIndex],
        };

        if (dealExternalLinksFormData && dealExternalLinksItem && Object.keys(dealExternalLinksItem).length) {
          dealExternalLinksFormData.push({ ...dealExternalLinksItem });
        }
      });
    }

    let dealContactsFormData: DealContactGroupInput[] | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealContactsFormData = [];

      Object.keys(formData.dealContactsData).forEach((dealContactsDataGroupIndex: string): void => {
        if (dealContactsFormData && formData.dealContactsData[dealContactsDataGroupIndex]) {
          const dealContactsDataGroupItem: DealContactGroupInput = {
            name: formData.dealContactsData[dealContactsDataGroupIndex].name,
            contacts: [],
          };

          if (formData.dealContactsData[dealContactsDataGroupIndex].contacts) {
            Object.keys(formData.dealContactsData[dealContactsDataGroupIndex].contacts).forEach(
              (dealContactsDataIndex): void => {
                const dealContactsDataItem: DealContactInput = {
                  ...formData.dealContactsData[dealContactsDataGroupIndex].contacts[dealContactsDataIndex],
                };

                if (dealContactsDataItem && Object.keys(dealContactsDataItem).length) {
                  dealContactsDataGroupItem.contacts.push(dealContactsDataItem);
                }
              },
            );
          }

          dealContactsFormData.push(dealContactsDataGroupItem);
        }
      });
    }

    let dealCustomSectionsFormData: DealSectionInput[] | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealCustomSectionsFormData = [];

      Object.keys(formData.dealCustomSectionsData).forEach((dealCustomSectionIndex: string): void => {
        if (dealCustomSectionsFormData && formData.dealCustomSectionsData[dealCustomSectionIndex]) {
          const dealCustomSectionData: DealSectionInput = {
            id: formData.dealCustomSectionsData[dealCustomSectionIndex].id,
            name: formData.dealCustomSectionsData[dealCustomSectionIndex].name,
            fields: [],
          };

          if (formData.dealCustomSectionsData[dealCustomSectionIndex].fields) {
            Object.keys(formData.dealCustomSectionsData[dealCustomSectionIndex].fields).forEach(
              (dealCustomSectionFieldIndex): void => {
                const dealSectionFieldDataItem: JsonObject = {
                  ...formData.dealCustomSectionsData[dealCustomSectionIndex].fields[dealCustomSectionFieldIndex],
                };

                if (dealSectionFieldDataItem && Object.keys(dealSectionFieldDataItem).length) {
                  const valueType: string = dealSectionFieldDataItem.type;
                  const value: string | Date | null = dealSectionFieldDataItem.value || null;

                  let resultValue: string | null = null;
                  if (valueType === FIELD_TYPE_TEXT || valueType === FIELD_TYPE_TEXT_MULTILINE
                    || valueType === FIELD_TYPE_DROPDOWN) {
                    resultValue = value as string || null;
                  }
                  if (valueType === FIELD_TYPE_NUMBER) {
                    resultValue = value ? FormatService.formatCleanNumber(value as string).toString() : null;
                  }
                  if (valueType === FIELD_TYPE_NUMBER_DOUBLE || valueType === FIELD_TYPE_NUMBER_PERCENT
                    || valueType === FIELD_TYPE_NUMBER_CURRENCY) {
                    resultValue = value ? FormatService.formatCleanNumber(value as string, true).toString() : null;
                  }
                  if (valueType === FIELD_TYPE_DATE) {
                    resultValue = value ? DateService.formatDate(value as unknown as Date) as string : null;
                  }
                  if (valueType === FIELD_TYPE_BOOLEAN) {
                    resultValue = value ? value.toString() : null;
                  }

                  const dealCustomSectionFieldData: DealSectionFieldInput = {
                    id: dealSectionFieldDataItem.id,
                    name: dealSectionFieldDataItem.name,
                    type: dealSectionFieldDataItem.type,
                    value: resultValue,
                  };

                  if (valueType === FIELD_TYPE_DROPDOWN) {
                    dealCustomSectionFieldData.listType = dealSectionFieldDataItem.listType;
                  }

                  dealCustomSectionData.fields.push(dealCustomSectionFieldData);
                }
              },
            );
          }

          dealCustomSectionsFormData.push(dealCustomSectionData);
        }
      });
    }

    let dealCustomListTypesFormData: FieldListTypeInput[] | null = null;
    if (((queryTab && queryTab === TAB_NAME_SUMMARY) || !queryTab) || isNewDeal) {
      dealCustomListTypesFormData = [];

      Object.keys(formData.dealCustomListTypesData).forEach((dealCustomListTypeIndex: string): void => {
        if (dealCustomListTypesFormData && formData.dealCustomListTypesData[dealCustomListTypeIndex]) {
          const dealCustomListTypeData: FieldListTypeInput = {
            id: formData.dealCustomListTypesData[dealCustomListTypeIndex].id,
            name: formData.dealCustomListTypesData[dealCustomListTypeIndex].name,
            title: formData.dealCustomListTypesData[dealCustomListTypeIndex].title,
            options: [],
          };

          if (formData.dealCustomListTypesData[dealCustomListTypeIndex].options) {
            Object.keys(formData.dealCustomListTypesData[dealCustomListTypeIndex].options).forEach(
              (dealCustomListTypeOptionIndex): void => {
                const dealCustomListTypeOptionItemData: JsonObject = {
                  ...formData.dealCustomListTypesData[dealCustomListTypeIndex].options[dealCustomListTypeOptionIndex],
                };

                if (dealCustomListTypeOptionItemData && Object.keys(dealCustomListTypeOptionItemData).length) {
                  dealCustomListTypeData.options.push({
                    id: dealCustomListTypeOptionItemData.id,
                    name: dealCustomListTypeOptionItemData.name,
                    title: dealCustomListTypeOptionItemData.title,
                  });
                }
              },
            );
          }

          dealCustomListTypesFormData.push(dealCustomListTypeData);
        }
      });
    }

    let dealCapitalizationFormData: DealCapitalizationDataInput | null = null;
    if (((queryTab && (queryTab === TAB_NAME_SUMMARY || queryTab === TAB_NAME_CAPITALIZATION)) || !queryTab)
      || isNewDeal) {
      dealCapitalizationFormData = {
        sources: [],
        uses: [],
      };

      const newDealCapitalizationSourceGroup: FormValueItem = {
        ...formData.dealCapitalizationData.sources[formKeyNew],
      };
      const newDealCapitalizationSourceItem: FormValueItem = newDealCapitalizationSourceGroup
        && { ...newDealCapitalizationSourceGroup.items[formKeyNew] };

      if (dealCapitalizationFormData) {
        Object.keys(formData.dealCapitalizationData.sources).forEach((dealCapitalizationGroupIndex: string): void => {
          const dealCapitalizationGroup: FormValueItem = {
            ...formData.dealCapitalizationData.sources[dealCapitalizationGroupIndex],
          };

          if (dealCapitalizationGroupIndex !== formKeyNew) {
            const dealCapitalizationGroupItem: DealCapitalizationGroupInput = {
              id: dealCapitalizationGroup.id,
              name: dealCapitalizationGroup.name,
              isSystem: dealCapitalizationGroup.isSystem || false,
              items: [],
            };

            if (dealCapitalizationGroup.items && Object.keys(dealCapitalizationGroup.items).length) {
              Object.keys(dealCapitalizationGroup.items).forEach((dealCapitalizationItemIndex: string): void => {
                const dealCapitalizationItem: DealCapitalizationItemInput = {
                  ...dealCapitalizationGroup.items[dealCapitalizationItemIndex],
                };

                if (dealCapitalizationItem && Object.keys(dealCapitalizationItem).length) {
                  dealCapitalizationGroupItem.items.push(
                    processDealCapitalizationItem({ ...dealCapitalizationGroup.items[dealCapitalizationItemIndex] }),
                  );
                }
              });
            }

            if (dealCapitalizationFormData && dealCapitalizationFormData.sources) {
              dealCapitalizationFormData.sources.push(dealCapitalizationGroupItem);
            }
          }
        });
      }

      if (newDealCapitalizationSourceItem && newDealCapitalizationSourceItem.type
        && newDealCapitalizationSourceItem.group) {
        const newDealCapitalizationGroupItem: DealCapitalizationGroup | null = dealCapitalizationGroupList
          .find((dealCapitalizationGroupItem: DealCapitalizationGroup): boolean => (
            dealCapitalizationGroupItem.name === newDealCapitalizationSourceItem.group
          )) || null;

        if (newDealCapitalizationGroupItem) {
          const dealCapitalizationGroupIndex: number = dealCapitalizationFormData.sources
            .findIndex((dealCapitalizationGroupItem: DealCapitalizationGroupInput): boolean => (
              dealCapitalizationGroupItem.name === newDealCapitalizationGroupItem.title
            ));

          if (dealCapitalizationGroupIndex !== FormatService.INDEX_NOT_FOUND) {
            delete newDealCapitalizationSourceItem.group;

            dealCapitalizationFormData.sources[dealCapitalizationGroupIndex].items.push(
              processDealCapitalizationItem({ ...newDealCapitalizationSourceItem }),
            );
          }
        }
      }

      const newDealCapitalizationUseGroup: FormValueItem = {
        ...formData.dealCapitalizationData.uses[formKeyNew],
      };
      const newDealCapitalizationUseItem: FormValueItem = newDealCapitalizationUseGroup
        && { ...newDealCapitalizationUseGroup.items[formKeyNew] };

      Object.keys(formData.dealCapitalizationData.uses).forEach((dealCapitalizationGroupIndex: string): void => {
        const dealCapitalizationGroup: FormValueItem = {
          ...formData.dealCapitalizationData.uses[dealCapitalizationGroupIndex],
        };

        if (dealCapitalizationGroupIndex !== formKeyNew) {
          const dealCapitalizationGroupItem: DealCapitalizationGroupInput = {
            id: dealCapitalizationGroup.id,
            name: dealCapitalizationGroup.name,
            isSystem: dealCapitalizationGroup.isSystem || false,
            items: [],
          };

          if (dealCapitalizationGroup.items && Object.keys(dealCapitalizationGroup.items).length) {
            Object.keys(dealCapitalizationGroup.items).forEach((dealCapitalizationItemIndex: string): void => {
              const dealCapitalizationItem: DealCapitalizationItemInput = {
                ...dealCapitalizationGroup.items[dealCapitalizationItemIndex],
              };

              if (dealCapitalizationItem && Object.keys(dealCapitalizationItem).length) {
                dealCapitalizationGroupItem.items.push(
                  processDealCapitalizationItem(dealCapitalizationGroup.items[dealCapitalizationItemIndex]),
                );
              }
            });
          }

          if (dealCapitalizationFormData && dealCapitalizationFormData.uses) {
            dealCapitalizationFormData.uses.push(dealCapitalizationGroupItem);
          }
        }
      });

      if (newDealCapitalizationUseItem && newDealCapitalizationUseItem.type && newDealCapitalizationUseItem.group) {
        const newDealCapitalizationGroupItem: DealCapitalizationGroup | null = dealCapitalizationGroupList
          .find((dealCapitalizationGroupItem: DealCapitalizationGroup): boolean => (
            dealCapitalizationGroupItem.name === newDealCapitalizationUseItem.group
          )) || null;

        if (newDealCapitalizationGroupItem) {
          const dealCapitalizationGroupIndex: number = dealCapitalizationFormData.uses
            .findIndex((dealCapitalizationGroupItem: DealCapitalizationGroupInput): boolean => (
              dealCapitalizationGroupItem.name === newDealCapitalizationGroupItem.title
            ));

          if (dealCapitalizationGroupIndex !== FormatService.INDEX_NOT_FOUND) {
            delete newDealCapitalizationUseItem.group;

            dealCapitalizationFormData.uses[dealCapitalizationGroupIndex].items.push(
              processDealCapitalizationItem({ ...newDealCapitalizationUseItem }),
            );
          }
        }
      }
    }

    let dealCapitalizationFileFormData: DealCapitalizationFileDataInput | null = null;
    if (((queryTab && queryTab === TAB_NAME_CAPITALIZATION) || isNewDeal) && (dealCapitalizationFileFile
      || dealIsDealCapitalizationFileDeleted)) {
      dealCapitalizationFileFormData = {
        file: dealCapitalizationFileFile || null,
      };
    }

    let dealMetricsCapitalStackFormData: DealMetricsCapitalStackItemInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_CAPITALIZATION) || isNewDeal) {
      dealMetricsCapitalStackFormData = [];

      Object.keys(formData.dealMetricsCapitalStackData).forEach((dealMetricsCapitalStackDataIndex: string): void => {
        const dealMetricsCapitalStackItem: FormValueItem = {
          ...formData.dealMetricsCapitalStackData[dealMetricsCapitalStackDataIndex],
        };

        if (dealMetricsCapitalStackFormData && dealMetricsCapitalStackItem
          && Object.keys(dealMetricsCapitalStackItem).length) {
          dealMetricsCapitalStackFormData.push(processDealMetricsCapitalStackItem(dealMetricsCapitalStackItem));
        }
      });
    }

    let dealMetricsStatisticsFormData: DealMetricsStatisticsGroupInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_CAPITALIZATION) || isNewDeal) {
      dealMetricsStatisticsFormData = [];

      Object.keys(formData.dealMetricsStatisticsData).forEach((groupIndex: string): void => {
        if (dealMetricsStatisticsFormData && formData.dealMetricsStatisticsData[groupIndex]) {
          const dealMetricsStatisticsGroupItem: DealMetricsStatisticsGroupInput = {
            name: formData.dealMetricsStatisticsData[groupIndex].name,
            values: [],
          };

          Object.keys(formData.dealMetricsStatisticsData[groupIndex].values).forEach((itemIndex: string): void => {
            const dealMetricsStatisticsItem: DealMetricsStatisticsItemInput = (
              formData.dealMetricsStatisticsData[groupIndex].values[itemIndex]
            );

            dealMetricsStatisticsGroupItem.values.push({
              value: dealMetricsStatisticsItem.value || '',
            });
          });

          dealMetricsStatisticsFormData.push(dealMetricsStatisticsGroupItem);
        }
      });
    }

    let dealEquityOrganizationChartFormData: DealEquityOrganizationChartInput | null = null;
    if ((queryTab && queryTab === TAB_NAME_CAPITALIZATION) || isNewDeal) {
      dealEquityOrganizationChartFormData = {
        file: null,
      };

      if (dealEquityOrganizationChartFormData) {
        if (dealEquityOrganizationChartFile) {
          dealEquityOrganizationChartFormData.file = dealEquityOrganizationChartFile;
        }

        if (dealIsEquityOrganizationChartDeleted) {
          dealEquityOrganizationChartFormData.file = null;
        }
      }
    }

    let dealTermsFormData: DealTermGroupInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_DEAL_TERMS) || isNewDeal) {
      dealTermsFormData = [];

      if (isNewDealTermGroup) {
        delete formData.dealTermsData[formKeyNew].terms;

        dealTermsFormData.push(formData.dealTermsData[formKeyNew]);
      } else {
        delete formData.dealTermsData[formKeyNew];

        Object.keys(formData.dealTermsData).forEach((dealTermGroupDataIndex: string): void => {
          if (dealTermsFormData && formData.dealTermsData[dealTermGroupDataIndex]) {
            const dealTermGroupItem: DealTermGroupInput = {
              id: formData.dealTermsData[dealTermGroupDataIndex].id,
              name: formData.dealTermsData[dealTermGroupDataIndex].name,
              terms: [],
            };

            Object.keys(formData.dealTermsData[dealTermGroupDataIndex].terms).forEach((dealTermDataIndex): void => {
              const dealTermItem: FormValueItem = {
                ...formData.dealTermsData[dealTermGroupDataIndex].terms[dealTermDataIndex],
              };

              if (dealTermItem && Object.keys(dealTermItem).length && dealTermItem.name && dealTermItem.content) {
                dealTermGroupItem.terms.push(dealTermItem);
              }
            });

            dealTermsFormData.push(dealTermGroupItem);
          }
        });
      }
    }

    let dealHistoricalValuationsFormData: DealHistoricalValuationsItemInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_PRO_FORMA) || isNewDeal) {
      dealHistoricalValuationsFormData = [];

      Object.keys(formData.dealHistoricalValuationsData).forEach((dealHistoricalValuationsDataIndex: string): void => {
        const dealHistoricalValuationsItem: FormValueItem = {
          ...formData.dealHistoricalValuationsData[dealHistoricalValuationsDataIndex],
        };

        if (dealHistoricalValuationsFormData && dealHistoricalValuationsItem
          && Object.keys(dealHistoricalValuationsItem).length) {
          dealHistoricalValuationsFormData.push(processDealHistoricalValuationsItem({
            ...dealHistoricalValuationsItem,
            name: dealHistoricalValuationsItem.name.trim(),
            value: dealHistoricalValuationsItem.value ? dealHistoricalValuationsItem.value.trim() : null,
          }));
        }
      });
    }

    let dealTopLevelMetricsFormData: DealTopLevelMetricsItemInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_PRO_FORMA) || isNewDeal) {
      dealTopLevelMetricsFormData = [];

      Object.keys(formData.dealTopLevelMetricsData).forEach((dealTopLevelMetricsDataIndex: string): void => {
        const dealTopLevelMetricsItem: FormValueItem = {
          ...formData.dealTopLevelMetricsData[dealTopLevelMetricsDataIndex],
        };

        if (dealTopLevelMetricsFormData && dealTopLevelMetricsItem && Object.keys(dealTopLevelMetricsItem).length) {
          dealTopLevelMetricsFormData.push(dealTopLevelMetricsItem);
        }
      });
    }

    let dealValuationFilesFormData: DealFileGroupInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_PRO_FORMA) || isNewDeal) {
      dealValuationFilesFormData = [];

      delete formData.dealValuationFilesData[formKeyNew];

      Object.keys(formData.dealValuationFilesData).forEach((dealValuationFileGroupDataIndex: string): void => {
        const dealValuationFileGroup: FormValueItem = {
          ...formData.dealValuationFilesData[dealValuationFileGroupDataIndex],
        };
        const dealValuationFileGroupFileActiveIds: number[] = dealValuationFileGroup && dealValuationFileGroup.files
        && Object.keys(dealValuationFileGroup.files).length
          ? Object.values(dealValuationFileGroup.files)
            .filter((dealValuationFileItem: FormValueItem): boolean => !!dealValuationFileItem)
            .map((dealValuationFileItem: FormValueItem): number | null => dealValuationFileItem.id || null)
            .filter((dealValuationFileId: number | null): boolean => (
              checkIsDealValuationFileActive(dealValuationFileId) || !dealValuationFileId
            )) as number[] : [];

        if (dealValuationFilesFormData && dealValuationFileGroup && (dealValuationFileGroupFileActiveIds.length || (
          (dealValuationFileGroupInlineSaveId && dealValuationFileGroupInlineSaveId === dealValuationFileGroup.id)
          || !dealValuationFileInlineSaveId))) {
          const dealValuationFileGroupItem: DealFileGroupInput = {
            id: !isNewDeal ? dealValuationFileGroup.id : null,
            name: dealValuationFileGroup.name,
            files: [],
          };

          Object.keys(dealValuationFileGroup.files).forEach((dealValuationFileDataIndex: string): void => {
            const dealValuationFileItem: DealFileInput = {
              ...dealValuationFileGroup.files[dealValuationFileDataIndex],
            };
            if (dealValuationFileGroupItem.files && dealValuationFileItem
              && Object.keys(dealValuationFileItem).length && dealValuationFileItem.name
              && ((dealValuationFileInlineSaveId && dealValuationFileInlineSaveId === dealValuationFileItem.id)
                || (!dealValuationFileInlineSaveId && checkIsDealValuationFileActive(dealValuationFileItem.id))
                || !dealValuationFileItem.id)) {
              dealValuationFileGroupItem.files.push(processDealValuationFile(
                dealValuationFileGroupItem,
                dealValuationFileItem,
                !dealValuationFileItem.id,
                dealValuationFileDataIndex,
              ));
            }
          });

          dealValuationFilesFormData.push(dealValuationFileGroupItem);
        }
      });
    }

    let dealTasksFormData: DealTaskGroupInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_DUE_DILIGENCE) || isNewDeal) {
      dealTasksFormData = [];

      if (isNewDealTaskGroup) {
        delete formData.dealTasksData[formKeyNew].tasks;

        dealTasksFormData.push(formData.dealTasksData[formKeyNew]);
      } else {
        if (dealTaskGroupInlineSaveId !== FormService.FORM_KEY_NEW) {
          delete formData.dealTasksData[formKeyNew];
        }

        Object.keys(formData.dealTasksData).forEach((dealTaskGroupDataIndex: string): void => {
          if (formData.dealTasksData[dealTaskGroupDataIndex]) {
            const dealTaskGroup: FormValueItem = {
              ...formData.dealTasksData[dealTaskGroupDataIndex],
            };
            const dealTaskGroupTaskActiveIds: number[] = dealTaskGroup && dealTaskGroup.tasks
            && Object.keys(dealTaskGroup.tasks).length
              ? Object.values(dealTaskGroup.tasks).filter((dealTaskItem: FormValueItem): boolean => !!dealTaskItem)
                .map((dealTaskItem: FormValueItem): number | null => dealTaskItem.id || null)
                .filter((dealTaskId: number | null): boolean => (
                  checkIsDealTaskActive(dealTaskId) || !dealTaskId
                )) as number[] : [];

            if (dealTasksFormData && dealTaskGroup && (dealTaskGroupTaskActiveIds.length
              || (dealTaskGroupInlineSaveId && dealTaskGroupInlineSaveId === dealTaskGroup.id)
              || (!dealTaskGroupInlineSaveId && checkIsDealTaskGroupActive(dealTaskGroup.id))
              || !dealTaskGroup.id || !dealId)) {
              const dealTaskGroupItem: DealTaskGroupInput = {
                id: dealTaskGroup.id,
                name: dealTaskGroup.name,
                tasks: [],
              };

              Object.keys(dealTaskGroup.tasks).forEach((dealTaskDataIndex: string): void => {
                const dealTaskItem: DealTaskInput = { ...dealTaskGroup.tasks[dealTaskDataIndex] };

                if (dealTaskItem && Object.keys(dealTaskItem).length && dealTaskItem.name
                  && ((dealTaskInlineSaveId && dealTaskInlineSaveId === dealTaskItem.id)
                    || (!dealTaskInlineSaveId && checkIsDealTaskActive(dealTaskItem.id))
                    || !dealTaskItem.id || !dealId)) {
                  dealTaskGroupItem.tasks.push(processDealTask(dealTaskItem));
                }
              });

              dealTasksFormData.push(dealTaskGroupItem);
            }
          }
        });
      }
    }

    let dealParticipantsFormData: DealParticipantInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_PARTICIPANTS) || isNewDeal) {
      dealParticipantsFormData = [];

      if (dealParticipantInlineSaveId !== FormService.FORM_KEY_NEW) {
        delete formData.dealParticipantsData[formKeyNew];
      }

      Object.keys(formData.dealParticipantsData).forEach((dealParticipantDataIndex: string): void => {
        const dealParticipantItem: FormValueItem = {
          ...formData.dealParticipantsData[dealParticipantDataIndex],
        };

        if (dealParticipantsFormData && dealParticipantItem && Object.keys(dealParticipantItem).length
          && ((dealParticipantInlineSaveId && dealParticipantInlineSaveId === dealParticipantItem.id)
            || (!dealParticipantInlineSaveId && checkIsDealParticipantActive(dealParticipantItem.id))
            || !dealParticipantItem.id || !dealId)) {
          dealParticipantsFormData.push(processDealParticipant(dealParticipantItem));
        }
      });
    }

    let dealFilesFormData: DealFileGroupInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_DEAL_FILES) || isNewDeal) {
      dealFilesFormData = [];

      if (isNewDealFileGroup) {
        delete formData.dealFilesData[formKeyNew].files;

        dealFilesFormData.push(formData.dealFilesData[formKeyNew]);
      } else {
        if (dealFileGroupInlineSaveId !== FormService.FORM_KEY_NEW) {
          delete formData.dealFilesData[formKeyNew];
        }

        Object.keys(formData.dealFilesData).forEach((dealFileGroupDataIndex: string): void => {
          if (formData.dealFilesData[dealFileGroupDataIndex]) {
            const dealFileGroup: FormValueItem = {
              ...formData.dealFilesData[dealFileGroupDataIndex],
            };
            const dealFileGroupFileActiveIds: number[] = dealFileGroup && dealFileGroup.files
            && Object.keys(dealFileGroup.files).length
              ? Object.values(dealFileGroup.files).filter((dealFileItem: FormValueItem): boolean => !!dealFileItem)
                .map((dealFileItem: FormValueItem): number | null => dealFileItem.id || null)
                .filter((dealFileId: number | null): boolean => (
                  checkIsDealFileActive(dealFileId) || !dealFileId
                )) as number[] : [];

            if (dealFilesFormData && dealFileGroup && (dealFileGroupFileActiveIds.length
              || (dealFileGroupInlineSaveId && dealFileGroupInlineSaveId === dealFileGroup.id)
              || (!dealFileGroupInlineSaveId && checkIsDealFileGroupActive(dealFileGroup.id)) || !dealId)) {
              const dealFileGroupItem: DealFileGroupInput = {
                id: dealFileGroup.id,
                name: dealFileGroup.name,
                files: [],
              };

              Object.keys(dealFileGroup.files).forEach((dealFileDataIndex: string): void => {
                const dealFileItem: DealFileInput = {
                  ...dealFileGroup.files[dealFileDataIndex],
                };

                if (dealFileGroupItem.files && dealFileItem && Object.keys(dealFileItem).length && dealFileItem.name
                  && ((dealFileInlineSaveId && dealFileInlineSaveId === dealFileItem.id)
                    || (!dealFileInlineSaveId && checkIsDealFileActive(dealFileItem.id)) || !dealFileItem.id
                    || !dealId)) {
                  dealFileGroupItem.files.push(processDealFile(
                    dealFileGroupItem,
                    dealFileItem,
                    !dealFileItem.id,
                    dealFileDataIndex,
                    !dealFileGroupItem.id,
                    dealFileGroupDataIndex,
                  ));
                }
              });

              dealFilesFormData.push(dealFileGroupItem);
            }
          }
        });
      }
    }

    let dealSchedulesFormData: DealScheduleInput[] | null = null;
    if ((queryTab && queryTab === TAB_NAME_SCHEDULE) || isNewDeal) {
      dealSchedulesFormData = [];
      delete formData.dealSchedulesData[formKeyNew];

      Object.keys(formData.dealSchedulesData).forEach((dealScheduleDataIndex: string): void => {
        const dealScheduleItem: FormValueItem = {
          ...formData.dealSchedulesData[dealScheduleDataIndex],
        };

        if (dealSchedulesFormData && dealScheduleItem && Object.keys(dealScheduleItem).length) {
          dealSchedulesFormData.push({
            id: dealScheduleItem.id,
            name: dealScheduleItem.name,
            tasks: dealScheduleItem.tasks && dealScheduleItem.tasks.length
              ? dealScheduleItem.tasks.map((dealScheduleTaskItem: FormValueItem): DealScheduleTaskInput => ({
                id: dealScheduleTaskItem.id,
                name: dealScheduleTaskItem.name,
                notes: dealScheduleTaskItem.notes || null,
                parentId: dealScheduleTaskItem.parentId || null,
                progress: dealScheduleTaskItem.progress || ChartService.getGanttChartProgressDefault(),
                startedAt: dealScheduleTaskItem.startedAt || null,
                assignedTo: dealScheduleTaskItem.assignedTo || null,
                color: dealScheduleTaskItem.color || null,
                duration: dealScheduleTaskItem.duration || null,
                order: dealScheduleTaskItem.order,
              })) : [],
            links: dealScheduleItem.links && dealScheduleItem.links.length
              ? dealScheduleItem.links.map((
                dealScheduleTaskLinkItem: FormValueItem,
              ): DealScheduleTaskLinkInput => ({
                id: dealScheduleTaskLinkItem.id,
                sourceId: dealScheduleTaskLinkItem.sourceId || null,
                targetId: dealScheduleTaskLinkItem.targetId || null,
                type: dealScheduleTaskLinkItem.type || null,
              })) : [],
          });
        }
      });
    }

    const isInlineSave: boolean = dealCapitalizationSourceItemInlineSaveId !== null
      || dealCapitalizationUseItemInlineSaveId !== null || dealValuationFileInlineSaveId !== null
      || dealTaskInlineSaveId !== null || dealParticipantInlineSaveId !== null || dealFileInlineSaveId !== null
      || dealActivityInlineSaveId !== null;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealData({
        dealForm: {
          dealCommonData: dealCommonFormData,
          dealPhotosData: dealPhotosFormData,
          dealBuildingDetailsData: dealBuildingDetailsFormData,
          dealInvestmentReturnsData: dealInvestmentReturnsFormData,
          dealMilestonesData: dealMilestonesFormData,
          dealExternalLinksData: dealExternalLinksFormData,
          dealContactsData: dealContactsFormData,
          dealCustomSectionsData: dealCustomSectionsFormData,
          dealCustomListTypesData: dealCustomListTypesFormData,
          dealCapitalizationData: dealCapitalizationFormData,
          dealCapitalizationFileData: dealCapitalizationFileFormData,
          dealEquityOrganizationChartData: dealEquityOrganizationChartFormData,
          dealMetricsCapitalStackData: dealMetricsCapitalStackFormData,
          dealMetricsStatisticsData: dealMetricsStatisticsFormData,
          dealHistoricalValuationsData: dealHistoricalValuationsFormData,
          dealTopLevelMetricsData: dealTopLevelMetricsFormData,
          dealValuationFilesData: dealValuationFilesFormData,
          dealTasksData: dealTasksFormData,
          dealFilesData: dealFilesFormData,
          dealTermsData: dealTermsFormData,
          dealSchedulesData: dealSchedulesFormData,
          dealParticipantsData: dealParticipantsFormData,
          dealActivityListData: dealActivityListFormData,
        },
        dealId,
        template: queryTemplate || null,
        isInline: isInlineSave,
      }, (response: ActionResponse): void => {
        if (isInlineSave) {
          dispatch(getDealData({
            dealId,
            tab: queryTab || TAB_NAME_SUMMARY,
            filters: {
              dealTaskListFilters: DEAL_TASK_DEFAULT_FILTERS,
              dealParticipantListFilters: DEAL_PARTICIPANT_DEFAULT_FILTERS,
              activityListFilters: DEAL_ACTIVITY_DEFAULT_FILTERS,
            },
            page: (queryTab && dealTabPageMap[queryTab]) || null,
            limit: (queryTab && dealTabLimitMap[queryTab]) || null,
            allRows: true,
          }, (): void => {
            inlineCancel();
          }, (exception: ServerError): void => {
            if (exception) {
              setErrorMessage(exception.message);
            }
          }));
          closeAllModals();
        } else {
          closeAllModals();
          setIsFormProcessed(true);
          dispatch(toggleModalEditMode(false));
          dispatch(toggleEditMode(false));
          resetInlineIds();
        }

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        resetInlineIds();

        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    isSmallScreen, dealAssetPhotoFile, dealId, queryTab, isNewDeal, dealPhotoList, dealPhotoNewItems,
    dealEquityOrganizationChartFile, dealIsEquityOrganizationChartDeleted, dealId, queryTemplate,
    dealPhotoFiles, dealValuationFileFiles, dealFileFiles, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealTaskGroupActiveIds, dealValuationFileActiveIds,
    dealParticipantActiveIds, dealFileActiveIds, dealFileGroupActiveIds, dealActivityActiveIds,
    dealCapitalizationSourceItemInlineSaveId, dealCapitalizationSourceGroupInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealCapitalizationUseGroupInlineSaveId, dealValuationFileInlineSaveId,
    dealTaskGroupInlineSaveId, dealTaskInlineSaveId, dealParticipantInlineSaveId, dealFileInlineSaveId,
    dealFileGroupInlineSaveId, dealActivityInlineSaveId, dealActivityFieldNewItems, dealCapitalizationSourceNewItems,
    dealCapitalizationUseNewItems, dealValuationFileNewItems, dealTaskNewItems, dealParticipantNewItems,
    dealParticipantNdaFiles, dealFileNewItems, dealFileNewGroupItems, dealTaskPage, dealFilePage, dealActivityPage,
    dealParticipantPage, isNewDealTermGroup, isNewDealTaskGroup, isNewDealFileGroup,
    isDealCapitalizationSourcesModalOpen, isDealCapitalizationUsesModalOpen, dealCapitalizationFileFile, formKeyNew,
  ]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSmallScreen, dealAssetPhotoFile, queryTab, isNewDeal, dealPhotoList, dealPhotoNewItems,
    dealEquityOrganizationChartFile, dealIsEquityOrganizationChartDeleted, dealId, queryTemplate,
    dealPhotoFiles, dealValuationFileFiles, dealFileFiles, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealTaskGroupActiveIds, dealValuationFileActiveIds,
    dealParticipantActiveIds, dealFileActiveIds, dealFileGroupActiveIds, dealActivityActiveIds,
    dealCapitalizationSourceItemInlineSaveId, dealCapitalizationSourceGroupInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealCapitalizationUseGroupInlineSaveId, dealValuationFileInlineSaveId,
    dealTaskGroupInlineSaveId, dealTaskInlineSaveId, dealParticipantInlineSaveId, dealFileInlineSaveId,
    dealFileGroupInlineSaveId, dealActivityInlineSaveId, dealActivityFieldNewItems, dealCapitalizationSourceNewItems,
    dealCapitalizationUseNewItems, dealValuationFileNewItems, dealTaskNewItems, dealParticipantNewItems,
    dealParticipantNdaFiles, dealFileNewItems, dealFileNewGroupItems, dealTaskPage, dealFilePage, dealActivityPage,
    dealParticipantPage, isNewDealTermGroup, isNewDealTaskGroup, isNewDealFileGroup,
    isDealCapitalizationSourcesModalOpen, isDealCapitalizationUsesModalOpen, dealCapitalizationFileFile, formKeyNew,
    formValues, withFormErrors,
  ]);

  const onDealCapitalizationSourceInlineSave: Function = useCallback((
    dealCapitalizationGroupId: number, dealCapitalizationItemId: number,
  ): void => {
    setDealCapitalizationSourceGroupInlineSaveId(dealCapitalizationGroupId);
    setDealCapitalizationSourceItemInlineSaveId(dealCapitalizationItemId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onDealCapitalizationUseInlineSave: Function = useCallback((
    dealCapitalizationGroupId: number, dealCapitalizationItemId: number,
  ): void => {
    setDealCapitalizationUseGroupInlineSaveId(dealCapitalizationGroupId);
    setDealCapitalizationUseItemInlineSaveId(dealCapitalizationItemId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onDealValuationFileInlineSave: Function = useCallback((
    dealValuationFileGroupId: number, dealValuationFileId: number,
  ): void => {
    setDealValuationFileGroupInlineSaveId(dealValuationFileGroupId);
    setDealValuationFileInlineSaveId(dealValuationFileId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onDealTaskInlineSave: Function = useCallback((dealTaskGroupId: number, dealTaskId: number): void => {
    setDealTaskGroupInlineSaveId(dealTaskGroupId);
    setDealTaskInlineSaveId(dealTaskId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onDealParticipantInlineSave: Function = useCallback((dealParticipantId: number): void => {
    setDealParticipantInlineSaveId(dealParticipantId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onDealFileInlineSave: Function = useCallback((dealFileGroupId: number, dealFileId: number): void => {
    setDealFileGroupInlineSaveId(dealFileGroupId);
    setDealFileInlineSaveId(dealFileId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onDealActivityInlineSave: Function = useCallback((dealActivityId: number): void => {
    setDealActivityInlineSaveId(dealActivityId);
    setInlineSaveIsConfirm(true);
  }, []);

  const onDealCapitalizationSourceInlineCancel: Function = useCallback((
    dealCapitalizationGroupId: number, dealCapitalizationItemId: number,
  ): void => {
    setDealCapitalizationSourceGroupInlineSaveId(dealCapitalizationGroupId);
    setDealCapitalizationSourceItemInlineSaveId(dealCapitalizationItemId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onDealCapitalizationUseInlineCancel: Function = useCallback((
    dealCapitalizationGroupId: number, dealCapitalizationItemId: number,
  ): void => {
    setDealCapitalizationUseGroupInlineSaveId(dealCapitalizationGroupId);
    setDealCapitalizationUseItemInlineSaveId(dealCapitalizationItemId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onDealValuationFileInlineCancel: Function = useCallback((
    dealValuationFileGroupId: number, dealValuationFileId: number,
  ): void => {
    setDealValuationFileGroupInlineSaveId(dealValuationFileGroupId);
    setDealValuationFileInlineSaveId(dealValuationFileId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onDealTaskInlineCancel: Function = useCallback((dealTaskGroupId: number, dealTaskId: number): void => {
    setDealTaskGroupInlineSaveId(dealTaskGroupId);
    setDealTaskInlineSaveId(dealTaskId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onDealParticipantInlineCancel: Function = useCallback((dealParticipantId: number): void => {
    setDealParticipantInlineSaveId(dealParticipantId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onDealFileInlineCancel: Function = useCallback((dealFileGroupId: number, dealFileId: number): void => {
    setDealFileGroupInlineSaveId(dealFileGroupId);
    setDealFileInlineSaveId(dealFileId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onDealActivityInlineCancel: Function = useCallback((dealActivityId: number): void => {
    setDealActivityInlineSaveId(dealActivityId);
    setInlineSaveIsConfirm(false);
  }, []);

  const onSubTabChange: Function = useCallback((): void => {
    dispatch(toggleEditMode(false));
  }, []);

  const dealTaskCustomListOptions: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <EditIcon />,
      text: 'Update Tasks',
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTaskIds: number[]): void => {
        openDealTaskListModal(event, dealTaskIds);
      },
      permission: DEAL_TASK_EDIT,
    },
  ], [dealTaskRowsSelected]);

  const dealTaskCustomActionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <SaveTemplateIcon />,
      text: 'Save As Template',
      onClick: (): void => {
        openDealTaskTemplateSaveModal();
      },
      permission: DEAL_TEMPLATE_CREATE,
      isHidden: dealTasksData && !dealTasksData.length,
    },
    {
      id: 2,
      icon: <LoadTemplateIcon />,
      text: 'Load From Template',
      onClick: (): void => {
        openDealTaskTemplateLoadModal();
      },
      permission: DEAL_TASK_GROUP_CREATE,
      isHidden: (dealTasksData && !!dealTasksData.length) || (dealTaskTemplateList && !dealTaskTemplateList.length),
    },
  ], [dealTasksData, dealTaskTemplateList]);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      permission: DEAL_SUMMARY_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_SUMMARY,
      component: (
        <DealTab
          isLimited
          withDelete={!isNewDeal}
          isDraft={isDraft}
          isEmpty={isSummaryTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaveDisabled={isSaveDisabled}
          withNoMultiLine={isSmallScreen}
          withTabSettings={!isNewDeal}
          formError={formError}
          tabName={TAB_NAME_SUMMARY}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          deletePermission={DEAL_DELETE}
          editPermissions={EDIT_PERMISSION_LIST_SUMMARY}
          customizePermission={DEAL_COMMON_EDIT}
          onTabSettingsChange={onDealTabSettingsChange}
          onSubmitForm={onSubmitForm}
          onCancelForm={onCancelForm}
          onDeleteClick={onDeleteClick}
          component={(
            <Fragment>
              {
                (isEditMode || isNewDeal) && (
                  <DealCommonData
                    isNewDeal={isNewDeal}
                    dealData={dealCommonData}
                    dealStatusList={filteredDealStatusList}
                    contactList={contactList}
                    contactCompanyList={contactCompanyList}
                    contactPersonList={contactPersonList}
                    contactPersonInternalList={contactPersonInternalList}
                    contactTypeList={contactTypeList}
                    portfolioList={portfolioList}
                    dealHiddenFieldList={dealHiddenFieldList}
                    onSponsorsChange={onDealSponsorsChange}
                    onBrokersChange={onDealBrokersChange}
                    onLendersChange={onDealLendersChange}
                    onNewDealActivityDealContactsChange={onDealNewDealActivityDealContactsChange}
                    onOpenNewSponsorForm={onDealSponsorOpenNewContactForm}
                    onOpenNewBrokerForm={onDealBrokerOpenNewContactForm}
                    onOpenNewLenderForm={onDealLenderOpenNewContactForm}
                    onCloseNewSponsorForm={onDealSponsorCloseNewContactForm}
                    onCloseNewBrokerForm={onDealBrokerCloseNewContactForm}
                    onCloseNewLenderForm={onDealLenderCloseNewContactForm}
                    onSponsorContactsChange={onDealSponsorContactsChange}
                    onBrokerContactsChange={onDealBrokerContactsChange}
                    onLenderContactsChange={onDealLenderContactsChange}
                    onContactPersonSearch={onContactPersonSearch}
                    onContactPersonSearchClose={onContactPersonSearchClose}
                    onContactPersonInternalSearch={onContactPersonInternalSearch}
                    onContactPersonInternalSearchClose={onContactPersonInternalSearchClose}
                    onContactCompanySearch={onContactCompanySearch}
                    onContactCompanySearchClose={onContactCompanySearchClose}
                  />
                )
              }
              <FlexContainer isSpread withNoAlign isColumn={isSmallScreen}>
                <WidgetRow isColumn={!isSmallScreen}>
                  <CheckAccess permissions={dealBuildingDetailsListViewPermissions}>
                    <DealPropertyDetails
                      isEditMode={isEditMode}
                      isNewDeal={isNewDeal}
                      isSimpleView={isNewDeal}
                      dealData={dealCommonData}
                      dealAssetData={dealAsset || (dealCommonData && dealCommonData.asset)}
                      dealBuildingDetailsData={dealBuildingDetailsData}
                      dealAssetUseBreakdownValues={formValues.dealData && formValues.dealData.dealBuildingDetailsData
                        && formValues.dealData.dealBuildingDetailsData.assetUseBreakdown}
                      assetList={assetList}
                      portfolioList={portfolioList}
                      assetTypeList={assetTypeList}
                      assetStatusList={assetStatusList}
                      contactCompanyList={contactCompanyList}
                      measureUnitList={measureUnitList}
                      assetOwnershipStructureTypeList={assetOwnershipStructureTypeList}
                      dealHiddenFieldList={dealHiddenFieldList}
                      dealAssetPhotoFile={dealAssetPhotoFile}
                      dealAssetIsPhotoDeleted={dealAssetIsPhotoDeleted}
                      dealAssetNewFieldItems={dealPropertyDetailsDealAssetNewFieldItems}
                      dealAssetUseBreakdownNewItems={dealAssetUseBreakdownNewItems}
                      dealAssetUseBreakdownNewFieldItems={dealAssetUseBreakdownNewFieldItems}
                      dealAssetUseBreakdownDefaultProperties={dealAssetUseBreakdownDefaultProperties}
                      onFileChange={onDealPropertyDetailsAssetFileChange}
                      onFileDelete={onDealPropertyDetailsAssetFileDelete}
                      onAssetTypesChange={onDealPropertyDetailsAssetTypesChange}
                      onAssetSellersChange={onDealPropertyDetailsAssetSellersChange}
                      onAssetChange={onDealPropertyDetailsAssetChange}
                      onAddNewAsset={onDealPropertyDetailsAddNewAsset}
                      onCancelNewAsset={onDealPropertyDetailsCancelNewAsset}
                      onAddNewItem={onDealPropertyDetailsAddNewItem}
                      onDeleteClick={onDealPropertyDetailsDeleteClick}
                      onAddAssetType={onDealPropertyDetailsAddAssetType}
                      onAssetUseBreakdownAddNewItem={onDealBuildingDetailsAssetUseBreakdownAddNewItem}
                      onAssetUseBreakdownDeleteClick={onDealBuildingDetailsAssetUseBreakdownDeleteClick}
                      onAssetUseBreakdownAddNewField={onDealBuildingDetailsAssetUseBreakdownAddNewField}
                      onAssetUseBreakdownDeleteField={onDealBuildingDetailsAssetUseBreakdownDeleteField}
                      onAssetUseBreakdownAssetTypeChange={onDealBuildingDetailsAssetUseBreakdownAssetTypeChange}
                      onAddressSearchCompleted={onDealPropertyDetailsAddressSearchCompleted}
                      onAddressFieldKeyDown={onDealPropertyDetailsFieldKeyDown}
                      onEditModeButtonClick={onEditModeButtonClick}
                      onDealAssetSearch={onDealAssetSearch}
                      onDealAssetSearchClose={onDealAssetSearchClose}
                      onContactCompanySearch={onContactCompanySearch}
                      onContactCompanySearchClose={onContactCompanySearchClose}
                    />
                  </CheckAccess>
                  {
                    checkIsSectionVisible(DEAL_TEMPLATE_KEY_DEAL_OVERVIEW) && (
                      <DealDescription
                        isEditMode={isEditMode}
                        isNewDeal={isNewDeal}
                        dealData={dealCommonData}
                        dealParticipantsData={dealParticipantsData}
                        onDescriptionChange={onDealDescriptionFieldChange}
                        onContactClick={onContactClick}
                        onEditModeButtonClick={onEditModeButtonClick}
                      />
                    )
                  }
                  {
                    checkIsSectionVisible(DEAL_TEMPLATE_KEY_DEAL_EXTERNAL_LINKS) && (
                      <DealExternalLinks
                        isEditMode={isEditMode}
                        isNewDeal={isNewDeal}
                        dealExternalLinksData={dealExternalLinksData}
                        dealExternalLinksNewItems={dealExternalLinksNewItems}
                        onAddNewItem={onDealExternalLinksAddNewItem}
                        onDeleteItem={onDealExternalLinksDeleteItem}
                        onEditModeButtonClick={onEditModeButtonClick}
                      />
                    )
                  }
                  {
                    checkIsSectionVisible(DEAL_TEMPLATE_KEY_DEAL_PHOTOS) && (
                      <CheckAccess permissions={dealPhotoListViewPermissions}>
                        <DealPhotos
                          isEditMode={isEditMode}
                          isNewDeal={isNewDeal}
                          dealPhotosData={dealPhotosData}
                          dealPhotoFiles={dealPhotoFiles}
                          dealPhotoNewItems={dealPhotoNewItems}
                          dealData={dealCommonData}
                          dealPhotoList={dealPhotoList}
                          onFilesChange={onDealPhotosFilesChange}
                          onFileDelete={onDealPhotosFileDelete}
                          onAddNewItem={onDealPhotoAddNewItem}
                          onDeleteDealPhoto={onDeleteDealPhoto}
                          onDealPhotoListChange={onDealPhotoListChange}
                          onEditModeButtonClick={onEditModeButtonClick}
                        />
                      </CheckAccess>
                    )
                  }
                  {
                    isNewDeal && checkIsSectionVisible(DEAL_TEMPLATE_KEY_DEAL_FILES) && (
                      <CheckAccess permissions={dealFileGroupListViewPermissions}>
                        <Widget isFullWidth isFullHeight defaultWidgetTitle={DEAL_FILES_WIDGET_TITLE_DEFAULT}>
                          <DealFiles
                            isNewDeal
                            isSimple
                            isEditMode={isEditMode}
                            isCustomizeMode={isCustomizeMode}
                            isDealFileAnalyzed={isDealFileAnalyzed}
                            dealId={dealId}
                            dealFilesData={dealFilesData}
                            dealFileFiles={dealFileFiles}
                            dealFileNewItems={dealFileNewItems}
                            dealFileNewGroupItems={dealFileNewGroupItems}
                            accessLevelList={accessLevelList}
                            expandedGroups={dealFileExpandedGroups}
                            queryDealFileId={queryDealFileId}
                            onFileChange={onDealFileChange}
                            onFileDelete={onDealFileDelete}
                            onFilesChange={onDealFilesChange}
                            onAddNewItems={onDealFileAddNewItems}
                            onDeleteClick={onDealFileItemDeleteClick}
                            onDeleteGroupClick={onDealFileGroupDeleteClick}
                            onOpenModal={openDealFileModal}
                            onOpenDeleteConfirmModal={openDealFileDeleteConfirmModal}
                            onOpenDeleteGroupConfirmModal={openDealFileGroupDeleteConfirmModal}
                            onExpansionGroupChange={onDealFileExpansionGroupChange}
                            onDealFileAnalyzeClick={onDealFileAnalyzeClick}
                          />
                        </Widget>
                      </CheckAccess>
                    )
                  }
                  {
                    checkIsSectionVisible(DEAL_TEMPLATE_KEY_DEAL_ALLOCATION) && (isEditMode || isNewDeal
                      || (dealAllocationItems && !!Object.keys(dealAllocationItems).length)) && (
                      <DealAllocation
                        isEditMode={isEditMode}
                        isNewDeal={isNewDeal}
                        dealAllocationItems={dealAllocationItems}
                        dealAllocationNewItemIds={dealAllocationNewItemIds}
                        portfolioList={portfolioList}
                        hiddenPortfolioList={hiddenPortfolioList}
                        onEditModeButtonClick={onEditModeButtonClick}
                        onAddNewDealAllocationItem={onAddNewDealAllocationItem}
                        onDealAllocationPortfolioChange={onDealAllocationPortfolioChange}
                        onDeleteDealAllocationItemClick={onDeleteDealAllocationItemClick}
                      />
                    )
                  }
                  {
                    !isEditMode && !isNewDeal && dealStatusChangeListData && !!dealStatusChangeListData.length && (
                      <DealStatusChanges dealStatusChangeListData={dealStatusChangeListData} />
                    )
                  }
                  {
                    dealCustomSectionsData && !!dealCustomSectionsData.length
                    && dealCustomSectionsData.map((dealCustomSectionData: DealSection): ReactElement => (
                      <DealCustomSection
                        isEditMode={isEditMode}
                        isNewDeal={isNewDeal}
                        key={dealCustomSectionData.id}
                        dealCustomSectionData={dealCustomSectionData}
                        pricingTypeList={pricingTypeList}
                        measureUnitList={measureUnitList}
                        dealCustomListTypeList={dealCustomListTypeList}
                        onEditModeButtonClick={onEditModeButtonClick}
                      />
                    ))
                  }
                </WidgetRow>
                <WidgetRow isColumn={!isSmallScreen}>
                  <CheckAccess permissions={dealCapitalizationViewPermissions}>
                    <DealInvestmentDetails
                      isSaving={isFormSaving}
                      isCustomizeMode={isCustomizeMode}
                      isEditMode={isEditMode}
                      isNewDeal={isNewDeal}
                      isSaveDisabled={isSaveDisabled}
                      dealData={dealCommonData}
                      dealTypeList={dealTypeList}
                      pricingTypeList={pricingTypeList}
                      dealLoanTypeList={dealLoanTypeList}
                      dealHiddenFieldList={dealHiddenFieldList}
                      dealBuildingDetailsData={dealBuildingDetailsData}
                      dealCapitalizationTypeList={dealCapitalizationTypeList}
                      dealCapitalizationGroupList={dealCapitalizationGroupList}
                      dealCapitalizationSourcesData={dealCapitalizationSourcesData}
                      dealCapitalizationSourcesSummaryData={dealCapitalizationSourcesSummaryData}
                      dealCapitalizationSourceNewItems={dealCapitalizationSourceNewItems}
                      dealCapitalizationSourcesFormValues={dealCapitalizationSourcesFormValues}
                      dealInvestmentReturnsData={dealInvestmentReturnsData}
                      dealInvestmentReturnsNewItems={dealInvestmentReturnsNewItems}
                      onEditModeButtonClick={onEditModeButtonClick}
                      onDealCapitalizationSourceAddNewItem={onDealCapitalizationSourceAddNewItem}
                      onDealCapitalizationSourceDeleteClick={onDealCapitalizationSourceDeleteClick}
                      onDealInvestmentReturnsAddNewItem={onDealInvestmentReturnsAddNewItem}
                      onDealInvestmentReturnsDeleteClick={onDealInvestmentReturnsDeleteClick}
                    />
                  </CheckAccess>
                  {
                    checkIsSectionVisible(DEAL_TEMPLATE_KEY_DEAL_MILESTONES) && (
                      <DealMilestones
                        isEditMode={isEditMode}
                        isNewDeal={isNewDeal}
                        dealData={dealCommonData}
                        dealMilestonesData={dealMilestonesData}
                        dealMilestonesNewItems={dealMilestonesNewItems}
                        onAddNewItem={onDealMilestonesAddNewItem}
                        onDeleteClick={onDealMilestonesDeleteClick}
                        onEditModeButtonClick={onEditModeButtonClick}
                      />
                    )
                  }
                  {
                    checkIsSectionVisible(DEAL_TEMPLATE_KEY_DEAL_CONTACTS) && (
                      <CheckAccess permissions={dealContactsGroupListViewPermissions}>
                        <DealContacts
                          isEditMode={isEditMode}
                          isNewDeal={isNewDeal}
                          dealContactsData={dealContactsData}
                          dealContactNewItems={dealContactNewItems}
                          dealContactNewGroupItems={dealContactNewGroupItems}
                          onAddNewItem={onDealContactAddNewItem}
                          onDeleteClick={onDealContactDeleteClick}
                          onDeleteGroupClick={onDealContactGroupDeleteClick}
                          onAddNewGroupItem={onDealContactGroupAddNewItem}
                          onEditModeButtonClick={onEditModeButtonClick}
                        />
                      </CheckAccess>
                    )
                  }
                </WidgetRow>
              </FlexContainer>
            </Fragment>
          )}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_CAPITALIZATION,
      title: 'Capitalization',
      permission: DEAL_CAPITALIZATION_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_DEAL_CAPITALIZATION,
      component: (
        <DealTab
          isLimited
          isExportDisabled
          withColumnSettings
          isDraft={isDraft}
          isEmpty={isCapitalizationTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isCustomizable={isCustomizable}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaveDisabled={isSaveDisabled}
          withNoMultiLine={isSmallScreen}
          formError={formError}
          tabName={TAB_NAME_CAPITALIZATION}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          tabImportFormatList={FORMAT_LIST_IMPORT_DEAL_TABLE}
          tabExportFormatList={FORMAT_LIST_EXPORT_DEAL_TABLE}
          editPermissions={EDIT_PERMISSION_LIST_DEAL_CAPITALIZATION}
          importPermission={DEAL_CAPITALIZATION_IMPORT}
          exportPermission={DEAL_CAPITALIZATION_EXPORT}
          createButtonTitle={createButtonTitleDealCapitalization}
          onCreateButtonClick={onDealCapitalizationSourceItemCreateClick}
          createAdditionalItemButtonTitle={createAdditionalItemButtonTitleDealCapitalization}
          onCreateAdditionalItemButtonClick={onDealCapitalizationUseItemCreateClick}
          createPermission={DEAL_SOURCES_ITEM_CREATE}
          createAdditionalItemPermission={DEAL_USES_ITEM_CREATE}
          tableFields={dealCapitalizationTableFieldLabels}
          tableFieldGroups={dealCapitalizationTableFieldGroups}
          tableColumnSettings={dealCapitalizationTableColumnSettings}
          onTableColumnsChange={onDealCapitalizationTableColumnsChange}
          onExportSuccess={onExportSuccess}
          onImportSuccess={onImportSuccess}
          onSubmitForm={onSubmitForm}
          onCancelForm={onCancelForm}
          component={(
            <Fragment>
              {
                (isEditMode || isNewDeal) && (
                  <DealCapitalizationCommonData />
                )
              }
              <div>
                <CheckAccess permissions={dealSourcesListViewPermissions}>
                  <DealCapitalization
                    isEditMode={isEditMode}
                    isNewDeal={isNewDeal}
                    isSaving={isFormSaving}
                    isCustomizeMode={isCustomizeMode}
                    isSaveDisabled={isSaveDisabled}
                    dealCapitalizationCategory={DEAL_CAPITALIZATION_CATEGORY_SOURCES}
                    dealCapitalizationData={dealCapitalizationSourcesData}
                    dealCapitalizationSummaryData={dealCapitalizationSourcesSummaryData}
                    dealCapitalizationNewItems={dealCapitalizationSourceNewItems}
                    dealCapitalizationTypeList={dealCapitalizationTypeList}
                    dealCapitalizationGroupList={dealCapitalizationGroupList}
                    dealCapitalizationItemActiveIds={dealCapitalizationSourceItemActiveIds}
                    dealCapitalizationItemInlineSaveId={dealCapitalizationSourceItemInlineSaveId}
                    dealCapitalizationGroupInlineSaveId={dealCapitalizationSourceGroupInlineSaveId}
                    rowIndexFrom={ROW_INDEX_FROM_DEFAULT}
                    onOpenModal={openDealCapitalizationSourcesModal}
                    groupTitle="Source"
                    currentUser={currentUser}
                    onAddNewItem={onDealCapitalizationSourceAddNewItem}
                    onDeleteClick={onDealCapitalizationSourceDeleteClick}
                    createItemPermission={DEAL_SOURCES_ITEM_CREATE}
                    editItemPermission={DEAL_SOURCES_ITEM_EDIT}
                    deleteItemPermission={DEAL_SOURCES_ITEM_DELETE}
                    tableColumnSettings={dealCapitalizationTableColumnSettings}
                    onTableColumnsChange={onDealCapitalizationTableColumnsChange}
                    onInlineEditModeButtonClick={onDealCapitalizationSourceInlineEditModeButtonClick}
                    onInlineCancel={onDealCapitalizationSourceInlineCancel}
                    onInlineSave={onDealCapitalizationSourceInlineSave}
                  />
                </CheckAccess>
                <CheckAccess permissions={dealUsesListViewPermissions}>
                  <DealCapitalization
                    isEditMode={isEditMode}
                    isNewDeal={isNewDeal}
                    isSaving={isFormSaving}
                    isCustomizeMode={isCustomizeMode}
                    isSaveDisabled={isSaveDisabled}
                    dealCapitalizationCategory={DEAL_CAPITALIZATION_CATEGORY_USES}
                    dealCapitalizationData={dealCapitalizationUsesData}
                    dealCapitalizationSummaryData={dealCapitalizationUsesSummaryData}
                    dealCapitalizationNewItems={dealCapitalizationUseNewItems}
                    dealCapitalizationTypeList={dealCapitalizationTypeList}
                    dealCapitalizationGroupList={dealCapitalizationGroupList}
                    dealCapitalizationItemActiveIds={dealCapitalizationUseItemActiveIds}
                    dealCapitalizationItemInlineSaveId={dealCapitalizationUseItemInlineSaveId}
                    dealCapitalizationGroupInlineSaveId={dealCapitalizationUseGroupInlineSaveId}
                    rowIndexFrom={dealCapitalizationSourcesTotal + dealCapitalizationSourcesNewItemsTotal}
                    groupTitle="Use"
                    currentUser={currentUser}
                    createItemPermission={DEAL_USES_ITEM_CREATE}
                    editItemPermission={DEAL_USES_ITEM_EDIT}
                    deleteItemPermission={DEAL_USES_ITEM_DELETE}
                    tableColumnSettings={dealCapitalizationTableColumnSettings}
                    onOpenModal={openDealCapitalizationUsesModal}
                    onAddNewItem={onDealCapitalizationUseAddNewItem}
                    onDeleteClick={onDealCapitalizationUseDeleteClick}
                    onTableColumnsChange={onDealCapitalizationTableColumnsChange}
                    onInlineEditModeButtonClick={onDealCapitalizationUseInlineEditModeButtonClick}
                    onInlineCancel={onDealCapitalizationUseInlineCancel}
                    onInlineSave={onDealCapitalizationUseInlineSave}
                  />
                </CheckAccess>
                <DealCapitalizationFile
                  isEditMode={isEditMode}
                  isNewDeal={isNewDeal}
                  isDealCapitalizationFileDeleted={dealIsDealCapitalizationFileDeleted}
                  dealCapitalizationFileData={dealCapitalizationFileData}
                  dealCapitalizationFileFile={dealCapitalizationFileFile}
                  onFileChange={dealCapitalizationFileFileChange}
                  onFileDelete={dealCapitalizationFileFileDelete}
                  onFileViewClick={onDealCapitalizationFileViewClick}
                />
              </div>
              <WidgetRow>
                <CheckAccess permissions={dealEquityOrganizationChartViewPermissions}>
                  <DealEquityOrganizationChart
                    isEditMode={isEditMode}
                    isNewDeal={isNewDeal}
                    isDealEquityOrganizationChartDeleted={dealIsEquityOrganizationChartDeleted}
                    dealEquityOrganizationChartData={dealEquityOrganizationChartData}
                    dealEquityOrganizationChartFile={dealEquityOrganizationChartFile}
                    onFileChange={onDealEquityOrganizationChartChange}
                    onFileDelete={onDealEquityOrganizationChartDelete}
                    onEditModeButtonClick={onEditModeButtonClick}
                  />
                </CheckAccess>
                <CheckAccess permissions={dealMetricsViewPermissions}>
                  <DealMetrics
                    isEditMode={isEditMode}
                    isNewDeal={isNewDeal}
                    dealMetricsStatisticsData={dealMetricsStatisticsData}
                    dealMetricsCapitalStackData={dealMetricsCapitalStackData}
                    dealMetricsStatisticsNewItems={dealMetricsStatisticsNewItems}
                    onAddNewItem={onDealMetricsStatisticsAddNewItem}
                    onDeleteClick={onDealMetricsStatisticsDeleteClick}
                    onEditModeButtonClick={onEditModeButtonClick}
                  />
                </CheckAccess>
              </WidgetRow>
              {
                isCapitalizationTabEmpty && (
                  <NoContentBlock>
                    No capitalization data
                  </NoContentBlock>
                )
              }
            </Fragment>
          )}
        />
      ),
    },
    {
      id: 3,
      name: TAB_NAME_PRO_FORMA,
      title: 'Pro Forma',
      permission: DEAL_PRO_FORMA_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_PRO_FORMA,
      component: (
        <DealTab
          isLimited
          isExportDisabled
          isDraft={isDraft}
          isEmpty={isProFormaTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaveDisabled={isSaveDisabled}
          isCustomizeMode={isCustomizeMode}
          withNoMultiLine={isSmallScreen}
          formError={formError}
          tabName={TAB_NAME_PRO_FORMA}
          tabExportFormatList={FORMAT_LIST_EXPORT_DEAL_FILES_TABLE}
          editPermissions={EDIT_PERMISSION_LIST_PRO_FORMA}
          exportPermission={DEAL_PRO_FORMA_EXPORT}
          createButtonTitle={createButtonTitleProForma}
          createPermission={DEAL_VALUATION_FILE_CREATE}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onExportSuccess={onExportSuccess}
          onCreateButtonClick={onDealValuationFileCreateClick}
          component={(
            <Fragment>
              <WidgetRow>
                <CheckAccess permissions={dealHistoricalValuationsListViewPermissions}>
                  <DealHistoricalValuations
                    isEditMode={isEditMode}
                    isNewDeal={isNewDeal}
                    dealHistoricalValuationsData={dealHistoricalValuationsData}
                    dealHistoricalValuationsNewItems={dealHistoricalValuationsNewItems}
                    onAddNewItem={onDealHistoricalValuationsAddNewItem}
                    onDeleteClick={onDealHistoricalValuationsDeleteClick}
                    onEditModeButtonClick={onEditModeButtonClick}
                  />
                </CheckAccess>
                <CheckAccess permissions={dealTopLevelMetricsListViewPermissions}>
                  <DealTopLevelMetrics
                    isEditMode={isEditMode}
                    isNewDeal={isNewDeal}
                    dealTopLevelMetricsData={dealTopLevelMetricsData}
                    dealTopLevelMetricsNewItems={dealTopLevelMetricsNewItems}
                    onAddNewItem={onDealTopLevelMetricsAddNewItem}
                    onDeleteClick={onDealTopLevelMetricsDeleteClick}
                    onEditModeButtonClick={onEditModeButtonClick}
                  />
                </CheckAccess>
              </WidgetRow>
              <CheckAccess permissions={dealValuationFileGroupListViewPermissions}>
                <DealValuationFiles
                  isVisible={isDealValuationFilesVisible}
                  isEditMode={isEditMode}
                  isNewDeal={isNewDeal}
                  isRightDrawerOpen={isRightDrawerOpen}
                  isSaveDisabled={isSaveDisabled}
                  isSaving={isFormSaving}
                  dealValuationFilesData={dealValuationFilesData}
                  dealValuationFileFiles={dealValuationFileFiles}
                  dealValuationFileNewItems={dealValuationFileNewItems}
                  dealValuationFileActiveIds={dealValuationFileActiveIds}
                  dealValuationFileInlineSaveId={dealValuationFileInlineSaveId}
                  dealValuationFileGroupInlineSaveId={dealValuationFileGroupInlineSaveId}
                  queryDealFileId={queryDealFileId}
                  onInlineSave={onDealValuationFileInlineSave}
                  onInlineCancel={onDealValuationFileInlineCancel}
                  onOpenModal={openDealValuationFileModal}
                  onAddNewItem={onDealValuationFileAddNewItem}
                  onDeleteClick={onDealValuationFileItemDeleteClick}
                  onFileChange={onDealValuationFileChange}
                  onFileDelete={onDealValuationFileDelete}
                  onFileViewClick={onDealValuationFileViewClick}
                  onOpenDeleteConfirmModal={openDealValuationFileDeleteConfirmModal}
                  onInlineEditModeButtonClick={onDealValuationFileInlineEditModeButtonClick}
                />
              </CheckAccess>
              {
                isProFormaTabEmpty && (
                  <NoContentBlock>
                    No pro forma data
                  </NoContentBlock>
                )
              }
            </Fragment>
          )}
        />
      ),
    },
    {
      id: 4,
      name: TAB_NAME_DUE_DILIGENCE,
      title: 'Due Diligence',
      permission: DEAL_DUE_DILIGENCE_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_DUE_DILIGENCE,
      component: (
        <DealTab
          isTableExport
          isCustomizable
          withGroups
          withSelectAll
          withSelectMode
          withFilters
          isDraft={isDraft}
          isEmpty={isDueDiligenceTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isExportMode={isExportMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaveDisabled={isSaveDisabled}
          isExportDisabled={isExportDisabled}
          isGroupExpanded={isDealTaskGroupExpanded}
          withAllFiltersEmpty={dealTaskAllFiltersEmpty}
          withAllRowsSelected={dealTaskAllRowsSelected}
          formError={formError}
          tabName={TAB_NAME_DUE_DILIGENCE}
          tabImportFormatList={FORMAT_LIST_IMPORT_DEAL_TABLE}
          tabExportFormatList={FORMAT_LIST_EXPORT_DEAL_FILES_TABLE}
          createButtonTitle={createButtonTitleDueDiligence}
          onCreateButtonClick={onDealTaskCreateClick}
          createGroupButtonTitle={createGroupButtonTitleDueDiligence}
          customListOptions={dealTaskCustomListOptions}
          customActionMenuItems={dealTaskCustomActionMenuItems}
          editPermissions={EDIT_PERMISSION_LIST_DUE_DILIGENCE}
          customizePermission={DEAL_TASK_GROUP_EDIT}
          importPermission={DEAL_DUE_DILIGENCE_IMPORT}
          exportPermission={DEAL_DUE_DILIGENCE_EXPORT}
          changeStatusPermission={DEAL_TASK_STATUS_EDIT}
          deletePermission={DEAL_TASK_DELETE}
          createPermission={DEAL_TASK_CREATE}
          createGroupPermission={DEAL_TASK_GROUP_CREATE}
          rowsTotal={dealTasksTotal}
          rowsSelectedTotal={dealTasksSelectedTotal}
          selectedRows={dealTaskRowsSelected}
          filters={dealTaskFilters}
          currentFilters={dealTaskCurrentFilters}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onSelectAllRows={onDealTaskSelectAllRows}
          onApplyFilters={onDealTaskApplyFilters}
          onResetFilters={onDealTaskResetFilters}
          onExpandAll={openDealTaskExpansionGroups}
          onCollapseAll={closeDealTaskExpansionGroups}
          onExportClick={openDealTaskExportModal}
          onDeleteClick={openDealTaskDeleteConfirmModal}
          onStatusChangeClick={openDealTaskStatusChangeModal}
          onExportSuccess={onExportSuccess}
          onImportSuccess={onImportSuccess}
          onCreateGroupButtonClick={onDealTaskGroupCreateClick}
          component={(
            <CheckAccess permissions={dealTaskGroupListViewPermissions}>
              <DealTasks
                isEditMode={isEditMode}
                isSelectMode={isSelectMode}
                isExportMode={isExportMode}
                isCustomizeMode={isCustomizeMode}
                isNewDeal={isNewDeal}
                isDealTaskModalOpen={isDealTaskModalOpen}
                isLoading={isDealTasksLoading}
                isSaveDisabled={isSaveDisabled}
                isSaving={isFormSaving}
                withAllRowsSelected={dealTaskAllRowsSelected}
                withAllFiltersEmpty={dealTaskAllFiltersEmpty}
                dealId={dealId}
                dealTasksData={dealTasksData}
                dealTasksTotal={dealTasksTotal}
                dealTaskNewItems={dealTaskNewItems}
                dealTaskPlainList={dealTaskPlainList}
                dealTaskContactPersonList={dealTaskContactPersonList}
                dealTaskCollapsedParents={dealTaskCollapsedParents}
                accessLevelList={accessLevelList}
                dealTaskStatusList={dealTaskStatusList}
                selectedRows={dealTaskRowsSelected}
                expandedGroups={dealTaskExpandedGroups}
                dealTaskActiveIds={dealTaskActiveIds}
                dealTaskGroupActiveIds={dealTaskGroupActiveIds}
                dealTaskInlineSaveId={dealTaskInlineSaveId}
                dealTaskGroupInlineSaveId={dealTaskGroupInlineSaveId}
                onInlineSave={onDealTaskInlineSave}
                onInlineCancel={onDealTaskInlineCancel}
                onOpenModal={openDealTaskModal}
                onLongClick={onDealTaskLongClick}
                onInlineEditModeButtonClick={onDealTaskInlineEditModeButtonClick}
                onInlineGroupEditModeButtonClick={onDealTaskInlineGroupEditModeButtonClick}
                onDealTaskContactPersonSearch={onDealTaskContactPersonSearch}
                onDealTaskContactPersonSearchClose={onDealTaskContactPersonSearchClose}
                onSwitchParentRow={onDealTaskSwitchParentRow}
                onOpenDeleteConfirmModal={openDealTaskDeleteConfirmModal}
                onOpenDeleteGroupConfirmModal={openDealTaskGroupDeleteConfirmModal}
                onOpenDealTaskListModal={openDealTaskListModal}
                onOpenDealTaskStatusChangeModal={openDealTaskStatusChangeModal}
                onExpansionGroupChange={onDealTaskExpansionGroupChange}
                onOpenDealFileChatModal={openDealTaskAttachmentFileChatModal}
                onLoadBatchData={onDealTaskLoadBatchData}
                onSelectRows={onDealTaskSelectRows}
                onSelectRow={onDealTaskSelectRow}
                onSelectAllGroupRows={onDealTaskSelectAllGroupRows}
                onAddNewItem={onDealTaskAddNewItem}
                onDeleteClick={onDealTaskItemDeleteClick}
                onCommentAttachmentFileViewClick={onDealTaskCommentAttachmentFileViewClick}
              />
            </CheckAccess>
          )}
          filterComponent={(
            <DealTasksFilters
              filters={dealTaskFilters}
              currentFilters={dealTaskCurrentFilters}
              currentUser={currentUser}
              filterDealTaskContactList={filterDealTaskContactList}
              dealTaskStatusList={dealTaskStatusList}
              onSetFilterValue={dealTaskSetFilterValue}
            />
          )}
          filterPanelComponent={!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode && (
            <FilterPanel
              filters={dealTaskFilters}
              filterOptionMap={DEAL_TASK_FILTER_OPTION_MAP}
              filterKeyMap={DEAL_TASK_FILTER_KEY_MAP}
              filterLabelMap={DEAL_TASK_FILTER_LABEL_MAP}
              filterLabelKeyMap={DEAL_TASK_FILTER_LABEL_KEY_MAP}
              defaultLabelMap={DEAL_TASK_FILTER_DEFAULT_LABEL_MAP}
              onDeleteFilterValue={onDealTaskDeleteFilterValue}
            />
          )}
        />
      ),
    },
    {
      id: 5,
      name: TAB_NAME_DEAL_FILES,
      title: 'Deal Files',
      permission: DEAL_FILES_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_DEAL_FILES,
      component: (
        <DealTab
          withGroups
          isCustomizable
          withSelectAll
          withSelectMode
          withStaticEdit
          isTableExport
          isDraft={isDraft}
          isEmpty={isDealFilesTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isExportMode={isExportMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaveDisabled={isSaveDisabled}
          isExportDisabled={isExportDisabled}
          isGroupExpanded={isDealFileGroupExpanded}
          withNoMultiLine={isSmallScreen}
          withAllRowsSelected={dealFileAllRowsSelected}
          formError={formError}
          tabName={TAB_NAME_DEAL_FILES}
          tabExportFormatList={FORMAT_LIST_EXPORT_DEAL_FILES_TABLE}
          createButtonTitle={createButtonTitleDealFiles}
          createGroupButtonTitle={createGroupButtonTitleDealFiles}
          editPermissions={EDIT_PERMISSION_LIST_DEAL_FILES}
          customizePermission={DEAL_FILE_GROUP_EDIT}
          exportPermission={DEAL_FILES_EXPORT}
          deletePermission={DEAL_FILE_DELETE}
          createPermission={DEAL_FILE_CREATE}
          createGroupPermission={DEAL_FILE_GROUP_CREATE}
          rowsTotal={dealFilesTotal}
          rowsSelectedTotal={dealFilesSelectedTotal}
          selectedRows={dealFileRowsSelected}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onExportSuccess={onExportSuccess}
          onSelectAllRows={onDealFileSelectAllRows}
          onExportClick={openDealFileExportModal}
          onDeleteClick={openDealFileDeleteConfirmModal}
          onExpandAll={openDealFileExpansionGroups}
          onCollapseAll={closeDealFileExpansionGroups}
          onCreateButtonClick={onDealFileCreateClick}
          onCreateGroupButtonClick={onDealFileGroupCreateClick}
          component={(
            <CheckAccess permissions={dealFileGroupListViewPermissions}>
              <DealFiles
                isLoading={isDealFilesLoading}
                isEditMode={isEditMode}
                isSelectMode={isSelectMode}
                isExportMode={isExportMode}
                isCustomizeMode={isCustomizeMode}
                isNewDeal={isNewDeal}
                isSaveDisabled={isSaveDisabled}
                isSaving={isFormSaving}
                isDealFileChatModalOpen={isDealFileChatModalOpen}
                withAllRowsSelected={dealFileAllRowsSelected}
                dealId={dealId}
                dealFilesData={dealFilesData}
                dealFilesTotal={dealFilesTotal}
                dealFileFiles={dealFileFiles}
                dealFileNewItems={dealFileNewItems}
                dealFileNewGroupItems={dealFileNewGroupItems}
                dealFilesLoadedTotal={dealFilesLoadedTotal}
                dealFileActiveIds={dealFileActiveIds}
                dealFileGroupActiveIds={dealFileGroupActiveIds}
                dealFileInlineSaveId={dealFileInlineSaveId}
                dealFileGroupInlineSaveId={dealFileGroupInlineSaveId}
                accessLevelList={accessLevelList}
                selectedRows={dealFileRowsSelected}
                expandedGroups={dealFileExpandedGroups}
                queryDealFileId={queryDealFileId}
                onLongClick={onDealFileLongClick}
                onLoadBatchData={onDealFileLoadBatchData}
                onExpansionGroupChange={onDealFileExpansionGroupChange}
                onInlineEditModeButtonClick={onDealFileInlineEditModeButtonClick}
                onInlineGroupEditModeButtonClick={onDealFileInlineGroupEditModeButtonClick}
                onInlineSave={onDealFileInlineSave}
                onInlineCancel={onDealFileInlineCancel}
                onSelectRows={onDealFileSelectRows}
                onSelectRow={onDealFileSelectRow}
                onSelectAllGroupRows={onDealFileSelectAllGroupRows}
                onDeleteClick={onDealFileItemDeleteClick}
                onDeleteGroupClick={onDealFileGroupDeleteClick}
                onOpenModal={openDealFileModal}
                onFileChange={onDealFileChange}
                onFileDelete={onDealFileDelete}
                onFilesChange={onDealFilesChange}
                onFileGroupsChange={onDealFileGroupsChange}
                onFileViewClick={onDealFileViewClick}
                onAddNewItems={onDealFileAddNewItems}
                onOpenDeleteConfirmModal={openDealFileDeleteConfirmModal}
                onOpenDeleteGroupConfirmModal={openDealFileGroupDeleteConfirmModal}
                onOpenDealFileChatModal={openDealFileChatModal}
              />
            </CheckAccess>
          )}
        />
      ),
    },
    {
      id: 6,
      name: TAB_NAME_DEAL_TERMS,
      title: 'Deal Terms',
      permission: DEAL_TERMS_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_DEAL_TERMS,
      component: (
        <DealTab
          isExportDisabled
          withGroups
          isDraft={isDraft}
          isEmpty={isDealTermsTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isGroupExpanded={isDealTermGroupExpanded}
          isSaveDisabled={isSaveDisabled}
          withNoMultiLine={isSmallScreen}
          formError={formError}
          tabName={TAB_NAME_DEAL_TERMS}
          tabImportFormatList={FORMAT_LIST_IMPORT_DEAL_TABLE}
          tabExportFormatList={FORMAT_LIST_EXPORT_DEAL_TABLE}
          createGroupButtonTitle={createGroupButtonTitleDealTerms}
          editPermissions={EDIT_PERMISSION_LIST_DEAL_TERMS}
          customizePermission={DEAL_TERM_GROUP_EDIT}
          importPermission={DEAL_TERMS_IMPORT}
          exportPermission={DEAL_TERMS_EXPORT}
          createPermission={DEAL_TERM_CREATE}
          createGroupPermission={DEAL_TERM_GROUP_CREATE}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onExpandAll={openDealTermExpansionGroups}
          onCollapseAll={closeDealTermExpansionGroups}
          onExportSuccess={onExportSuccess}
          onImportSuccess={onImportSuccess}
          onCreateGroupButtonClick={onDealTermCreateGroupClick}
          component={(
            <CheckAccess permissions={dealTermGroupListViewPermissions}>
              <DealTerms
                isEditMode={isEditMode}
                isCustomizeMode={isCustomizeMode}
                isNewDeal={isNewDeal}
                dealId={dealId}
                dealTermsData={dealTermsData}
                dealTermNewItems={dealTermNewItems}
                dealTermNewGroupItems={dealTermNewGroupItems}
                expandedGroups={dealTermExpandedGroups}
                onAddNewItem={onDealTermAddNewItem}
                onDeleteClick={onDealTermItemDeleteClick}
                onDeleteGroupClick={onDealTermGroupDeleteClick}
                onOpenDeleteConfirmModal={openDealTermDeleteConfirmModal}
                onOpenDeleteGroupConfirmModal={openDealTermGroupDeleteConfirmModal}
                onExpansionGroupChange={onDealTermExpansionGroupChange}
              />
            </CheckAccess>
          )}
        />
      ),
    },
    {
      id: 7,
      name: TAB_NAME_SCHEDULE,
      title: 'Schedule',
      permission: DEAL_SCHEDULE_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_SCHEDULE,
      component: (
        <DealTab
          withColumnSettings
          isDraft={isDraft}
          isEmpty={isScheduleTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isCustomizable={isCustomizable}
          isSaveDisabled={isSaveDisabled}
          withNoMultiLine={isSmallScreen}
          formError={formError}
          tabName={TAB_NAME_SCHEDULE}
          createPermission={DEAL_SCHEDULE_CREATE}
          editPermissions={EDIT_PERMISSION_LIST_SCHEDULE}
          createButtonTitle={createButtonTitleDealSchedule}
          tableFields={dealScheduleTableFieldLabels}
          tableFieldGroups={dealScheduleTableFieldGroups}
          tableColumnSettings={dealScheduleTableColumnSettings}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCreateButtonClick={onDealScheduleCreateClick}
          onTableColumnsChange={onDealScheduleChangeTableColumns}
          component={(
            <div>
              <CheckAccess permissions={dealScheduleTaskListViewPermissions}>
                <DealSchedules
                  isEditMode={isEditMode}
                  isNewDeal={isNewDeal}
                  isRightDrawerOpen={isRightDrawerOpen}
                  isVisible={isDealSchedulesVisible}
                  tabIndex={subTabIndex}
                  dealSchedulesData={dealSchedulesData}
                  dealId={dealId}
                  dealScheduleZoom={dealScheduleZoom}
                  tableColumnSettings={dealScheduleTableColumnSettings}
                  currentUser={currentUser}
                  onChange={onDealSchedulesChange}
                  onZoomChange={onDealScheduleZoomChange}
                  onTabChange={onSubTabChange}
                  onTableColumnsChange={onDealScheduleChangeTableColumns}
                  onBeforeTabChange={onBeforeTabChange}
                  onSetTabIndex={setSubTabIndex}
                  onDeleteClick={openDealScheduleDeleteConfirmModal}
                />
              </CheckAccess>
            </div>
          )}
        />
      ),
    },
    {
      id: 8,
      name: TAB_NAME_PARTICIPANTS,
      title: 'Participants',
      permission: DEAL_PARTICIPANTS_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_PARTICIPANTS,
      component: (
        <DealTab
          withFilters
          withSelectAll
          withSelectMode
          isExportDisabled
          withColumnSettings
          isDraft={isDraft}
          isEmpty={isDealParticipantsTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isCustomizable={isCustomizable}
          isSaveDisabled={isSaveDisabled}
          withNoMultiLine={isSmallScreen}
          withAllRowsSelected={dealParticipantAllRowsSelected}
          withAllFiltersEmpty={dealParticipantAllFiltersEmpty}
          formError={formError}
          tabName={TAB_NAME_PARTICIPANTS}
          tabImportFormatList={FORMAT_LIST_IMPORT_DEAL_TABLE}
          tabExportFormatList={FORMAT_LIST_EXPORT_DEAL_TABLE}
          createButtonTitle={createButtonTitleDealParticipants}
          createAdditionalItemButtonTitle={createAdditionalItemButtonTitleDealParticipants}
          editPermissions={EDIT_PERMISSION_LIST_PARTICIPANTS}
          importPermission={DEAL_PARTICIPANTS_IMPORT}
          exportPermission={DEAL_PARTICIPANTS_EXPORT}
          deletePermission={DEAL_PARTICIPANT_DELETE}
          createPermission={DEAL_PARTICIPANT_CREATE}
          createAdditionalItemPermission={DEAL_PARTICIPANT_CREATE}
          rowsTotal={dealParticipantsTotal}
          rowsSelectedTotal={dealParticipantRowsSelected.length}
          selectedRows={dealParticipantRowsSelected}
          filters={dealParticipantFilters}
          currentFilters={dealParticipantCurrentFilters}
          tableFields={dealParticipantTableFieldLabels}
          tableFieldGroups={dealParticipantsTableFieldGroups}
          tableColumnSettings={dealParticipantTableColumnSettings}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCreateButtonClick={onDealParticipantCreateClick}
          onCreateAdditionalItemButtonClick={onDealParticipantListCreateClick}
          onDeleteClick={openDealParticipantDeleteConfirmModal}
          onSelectAllRows={onDealParticipantSelectAllRows}
          onApplyFilters={onDealParticipantApplyFilters}
          onResetFilters={onDealParticipantResetFilters}
          onTableColumnsChange={onDealParticipantChangeTableColumns}
          onExportSuccess={onExportSuccess}
          onImportSuccess={onImportSuccess}
          component={(
            <CheckAccess permissions={dealParticipantGroupListViewPermissions}>
              <DealParticipants
                isCustomizeMode={isCustomizeMode}
                isEditMode={isEditMode}
                isNewDeal={isNewDeal}
                isSelectMode={isSelectMode}
                isSaveDisabled={isSaveDisabled}
                isSaving={isFormSaving}
                withAllRowsSelected={dealParticipantAllRowsSelected}
                currentUser={currentUser}
                dealParticipantsData={dealParticipantsData}
                dealParticipantsTotal={dealParticipantsTotal}
                dealParticipantNewItems={dealParticipantNewItems}
                dealParticipantGroupList={dealParticipantGroupList}
                dealParticipantNdaFiles={dealParticipantNdaFiles}
                dealParticipantActiveIds={dealParticipantActiveIds}
                dealParticipantInlineSaveId={dealParticipantInlineSaveId}
                accessLevelList={accessLevelList}
                contactPersonList={contactPersonList}
                contactNotParticipantList={contactNotParticipantList}
                page={dealParticipantPage}
                limit={dealParticipantLimit}
                sortParams={dealParticipantSortParams}
                dealId={dealId}
                filters={dealParticipantFilters}
                selectedRows={dealParticipantRowsSelected}
                tableColumnSettings={dealParticipantTableColumnSettings}
                onLongClick={onDealParticipantLongClick}
                onOpenModal={openDealParticipantModal}
                onPageChange={onDealParticipantPageChange}
                onLimitChange={onDealParticipantLimitChange}
                onSortParamsChange={onDealParticipantSortParamsChange}
                onFileViewClick={onDealParticipantFileViewClick}
                onNdaFileChange={onDealParticipantNdaFileChange}
                onNdaFileDelete={onDealParticipantNdaFileDelete}
                onSelectRow={onDealParticipantSelectRow}
                onContactClick={onContactClick}
                onTableColumnsChange={onDealParticipantChangeTableColumns}
                onAddNewItem={onDealParticipantAddNewItem}
                onDeleteClick={onDealParticipantItemDeleteClick}
                onOpenDeleteConfirmModal={openDealParticipantDeleteConfirmModal}
                onContactChange={onDealParticipantContactChange}
                onInlineEditModeButtonClick={onDealParticipantInlineEditModeButtonClick}
                onInlineSave={onDealParticipantInlineSave}
                onInlineCancel={onDealParticipantInlineCancel}
                onContactPersonSearch={onContactPersonSearch}
                onContactPersonSearchClose={onContactPersonSearchClose}
                onContactNotParticipantSearch={onContactNotParticipantSearch}
                onContactNotParticipantSearchClose={onContactNotParticipantSearchClose}
              />
            </CheckAccess>
          )}
          filterComponent={(
            <DealParticipantsFilters
              filters={dealParticipantFilters}
              currentFilters={dealParticipantCurrentFilters}
              filterDealParticipantCompanyList={filterDealParticipantCompanyList}
              filterDealParticipantAccessLevelList={filterDealParticipantAccessLevelList}
              onSetFilterValue={dealParticipantSetFilterValue}
            />
          )}
          filterPanelComponent={!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode && (
            <FilterPanel
              filters={dealParticipantFilters}
              filterOptionMap={DEAL_PARTICIPANT_FILTER_OPTION_MAP}
              filterKeyMap={DEAL_PARTICIPANT_FILTER_KEY_MAP}
              filterLabelMap={DEAL_PARTICIPANT_FILTER_LABEL_MAP}
              filterLabelKeyMap={DEAL_PARTICIPANT_FILTER_LABEL_KEY_MAP}
              onDeleteFilterValue={onDealParticipantDeleteFilterValue}
            />
          )}
        />
      ),
    },
    {
      id: 9,
      name: TAB_NAME_DEAL_ACTIVITY,
      title: 'Deal Activity',
      permission: ACTIVITY_LIST_VIEW,
      editPermissions: EDIT_PERMISSION_LIST_DEAL_ACTIVITY,
      component: (
        <DealTab
          isExportDisabled
          withFilters
          withSelectMode
          withSelectAll
          withColumnSettings
          isDraft={isDraft}
          isEmpty={isDealActivityTabEmpty}
          isSaving={isFormSaving}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isCustomizeMode={isCustomizeMode}
          isCustomizable={isCustomizable}
          isExportMode={isExportMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaveDisabled={isSaveDisabled}
          withNoMultiLine={isSmallScreen}
          withAllFiltersEmpty={dealActivityAllFiltersEmpty}
          withAllRowsSelected={dealActivityAllRowsSelected}
          formError={formError}
          tabName={TAB_NAME_DEAL_ACTIVITY}
          tabImportFormatList={FORMAT_LIST_IMPORT_ACTIVITY}
          tabExportFormatList={FORMAT_LIST_EXPORT_ACTIVITY}
          createButtonTitle={createButtonTitleDealActivity}
          editPermissions={EDIT_PERMISSION_LIST_DEAL_ACTIVITY}
          importPermission={ACTIVITY_LIST_IMPORT}
          exportPermission={ACTIVITY_LIST_EXPORT}
          changeStatusPermission={ACTIVITY_STATUS_EDIT}
          deletePermission={ACTIVITY_DELETE}
          createPermission={ACTIVITY_CREATE}
          rowsTotal={dealActivityTotal}
          rowsSelectedTotal={dealActivityRowsSelected.length}
          selectedRows={dealActivityRowsSelected}
          tableFields={dealActivityTableFieldLabels}
          tableFieldGroups={dealActivityTableFieldGroups}
          tableColumnSettings={dealActivityTableColumnSettings}
          filters={dealActivityFilters}
          currentFilters={dealActivityCurrentFilters}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onSelectAllRows={onDealActivitySelectAllRows}
          onTableColumnsChange={onDealActivityChangeTableColumns}
          onApplyFilters={onDealActivityApplyFilters}
          onResetFilters={onDealActivityResetFilters}
          onExportSuccess={onExportSuccess}
          onImportSuccess={onImportSuccess}
          onCreateButtonClick={onDealActivityCreateClick}
          onDeleteClick={openDealActivityDeleteConfirmModal}
          onStatusChangeClick={openDealActivityStatusChangeModal}
          component={(
            <CheckAccess permissions={activityListViewPermissions}>
              <ActivityListInternal
                isDealActivity
                isSaveDisabled={isSaveDisabled}
                isSaving={isFormSaving}
                withAllRowsSelected={dealActivityAllRowsSelected}
                withAllFiltersEmpty={dealActivityAllFiltersEmpty}
                dealId={dealId}
                tableName={TABLE_NAME_ACTIVITY_LIST_DEAL}
                page={dealActivityPage}
                limit={dealActivityLimit}
                sortParams={dealActivitySortParams}
                activityListData={dealActivityList}
                activityTotal={dealActivityTotal}
                activityListSummaryData={dealActivityListSummaryData}
                activityStatusList={activityStatusList}
                activityCategoryList={activityCategoryList}
                contactList={contactList}
                contactCompanyList={contactCompanyList}
                contactPersonList={contactPersonList}
                contactPersonInternalList={contactPersonInternalList}
                measureUnitList={measureUnitList}
                activityInterestLevelList={activityInterestLevelList}
                activityTypeList={activityTypeList}
                tagList={tagList}
                selectedRows={dealActivityRowsSelected}
                activityActiveIds={dealActivityActiveIds}
                inlineSaveActivityId={dealActivityInlineSaveId}
                tableColumnSettings={dealActivityTableColumnSettings}
                onSelectRow={onDealActivitySelectRow}
                onOpenModal={openDealActivityModal}
                onOpenDeleteConfirmModal={openDealActivityDeleteConfirmModal}
                onPageChange={onDealActivityPageChange}
                onLimitChange={onDealActivityLimitChange}
                onSortParamsChange={onDealActivitySortParamsChange}
                onTableColumnsChange={onDealActivityChangeTableColumns}
                onTagsChange={onDealActivityTagsChange}
                onTagClick={onDealActivityTagClick}
                onDealContactsChange={onDealActivityDealContactsChange}
                onLongClick={onDealActivityLongClick}
                onOpenStatusChangeModal={openDealActivityStatusChangeModal}
                onInlineSave={onDealActivityInlineSave}
                onInlineCancel={onDealActivityInlineCancel}
                onInlineEditModeButtonClick={onDealActivityInlineEditModeButtonClick}
                onActivityDealSearch={onActivityDealSearch}
                onActivityDealSearchClose={onActivityDealSearchClose}
                onContactSearch={onContactSearch}
                onContactSearchClose={onContactSearchClose}
                onContactPersonSearch={onContactPersonSearch}
                onContactPersonSearchClose={onContactPersonSearchClose}
                onContactPersonInternalSearch={onContactPersonInternalSearch}
                onContactPersonInternalSearchClose={onContactPersonInternalSearchClose}
                onContactCompanySearch={onContactCompanySearch}
                onContactCompanySearchClose={onContactCompanySearchClose}
                onTagSearch={onTagSearch}
                onTagSearchClose={onTagSearchClose}
              />
            </CheckAccess>
          )}
          filterComponent={(
            <ActivityListFilters
              isDealActivity
              filters={dealActivityFilters}
              currentFilters={dealActivityCurrentFilters}
              filterActivityCategoryList={filterActivityCategoryList}
              filterActivityTypeList={filterActivityTypeList}
              filterBrokerList={filterBrokerList}
              filterLenderList={filterLenderList}
              filterInvestorList={filterInvestorList}
              filterDealContactList={filterDealContactList}
              onSetFilterValue={dealActivitySetFilterValue}
            />
          )}
          filterPanelComponent={!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode && (
            <FilterPanel
              filters={dealActivityFilters}
              filterOptionMap={DEAL_ACTIVITY_FILTER_OPTION_MAP}
              filterKeyMap={DEAL_ACTIVITY_FILTER_KEY_MAP}
              filterLabelMap={DEAL_ACTIVITY_FILTER_LABEL_MAP}
              filterLabelKeyMap={DEAL_ACTIVITY_FILTER_LABEL_KEY_MAP}
              onDeleteFilterValue={onDealActivityDeleteFilterValue}
            />
          )}
        />
      ),
    },
    {
      id: 10,
      name: TAB_NAME_DEAL_COMMENTS,
      title: 'Deal Comments',
      permission: DEAL_COMMENTS_VIEW,
      component: (
        <DealTab
          isLimited
          withNoEditMode
          isDraft={isDraft}
          isNewDeal={isNewDeal}
          withNoMultiLine={isSmallScreen}
          tabName={TAB_NAME_DEAL_COMMENTS}
          dealId={dealId}
          tabSettings={extendedDealTabSettings}
          onTabSettingsChange={onDealTabSettingsChange}
          component={(
            <DealComments
              isSaving={isFormSaving}
              isEditMode={isEditMode}
              isRightDrawerOpen={isRightDrawerOpen}
              isCommentSaving={isDealParticipantCommentSaving}
              formError={formError}
              dealParticipantId={currentUserDealParticipantId}
              queryCommentId={queryCommentId}
              commentList={dealCommentsData}
              currentUser={currentUser}
              contactLinkedUserList={contactLinkedUserList}
              tagList={tagList}
              onAddComment={onDealParticipantAddComment}
              onUpdateComment={onDealParticipantUpdateComment}
              onDeleteComment={onDealParticipantDeleteComment}
              onCommentEntityLinkClick={onCommentEntityLinkClick}
              onCommentAttachmentFileViewClick={onDealParticipantCommentAttachmentFileViewClick}
            />
          )}
        />
      ),
    },
  ];

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }
    if (isExportMode) {
      dispatch(toggleExportMode(false));
    }
    if (isSelectMode) {
      dispatch(toggleSelectMode(false));
    }
    if (isCustomizeMode) {
      dispatch(toggleCustomizeMode(false));
    }
    if (isModalEditMode) {
      dispatch(toggleModalEditMode(false));
    }

    dispatch(getCommentListData(getOptions(true)));
    dispatch(getNotificationListData(getOptions(true)));

    return true;
  }, [isFormChanged, isEditMode, isExportMode, isSelectMode, isCustomizeMode, isModalEditMode]);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    dispatch(getCommentListData(getOptions(true)));
    dispatch(getNotificationListData(getOptions(true)));

    onCancelForm(null, true);
  }, [
    dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId, queryTab,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged, formDealId, isNewDeal,
  ]);

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        if (queryTemplate) {
          queryParams[QUERY_PARAM_TEMPLATE] = queryTemplate;
        }

        navigate({
          pathname: !isNewDeal && dealId ? `${ROUTE_DEAL}/${dealId}` : `${ROUTE_DEAL}/${ROUTE_PARAM_ID_NEW}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [
    locationQueryParams, tabs, isDataLoaded, queryTab, queryTemplate, isNewDeal, dealId, formDealId,
    dealTaskRowsSelectedDefault, dealFileRowsSelectedDefault, dealCapitalizationSourceItemActiveIds,
    dealCapitalizationUseItemActiveIds, dealTaskActiveIds, dealValuationFileActiveIds, dealParticipantActiveIds,
    dealFileActiveIds, dealActivityActiveIds, dealCapitalizationSourceItemInlineSaveId,
    dealCapitalizationUseItemInlineSaveId, dealValuationFileInlineSaveId, dealTaskInlineSaveId,
    dealParticipantInlineSaveId, dealFileInlineSaveId, dealActivityInlineSaveId, dealPhotoListDefault,
    isDealPhotoListChanged,
  ]);

  const sendDealValuationFileForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const dealValuationFileGroupItemData: FormValueItem = {
      ...values.dealData.dealValuationFilesData[formKeyDealValuationFileGroup],
    };
    const dealValuationFileItemData: FormValueItem = {
      ...values.dealData.dealValuationFilesData[formKeyDealValuationFileGroup].files[formKeyDealValuationFile],
    };

    if (!dealValuationFileItemData || !dealValuationFileGroupItemData) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealValuationFile({
        dealValuationFile: (
          processDealValuationFile(
            dealValuationFileGroupItemData, dealValuationFileItemData, !formKeyDealValuationFile,
          )
        ),
        dealId,
      }, (response: ActionResponse): void => {
        closeDealValuationFileModal();

        fullResetForm();
        dispatch(toggleModalEditMode(false));

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    dealId, dealValuationFileFiles, dealValuationFileGroupData, dealValuationFileData, queryTab,
    isDealPhotoListChanged, dealPhotoListDefault, formKeyDealValuationFile, formKeyDealValuationFileGroup,
  ]);

  const onDealValuationFileSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealValuationFileForm(formValues);
    }
  }, [
    dealId, dealValuationFileFiles, dealValuationFileGroupData, dealValuationFileData, queryTab,
    isDealPhotoListChanged, dealPhotoListDefault, withFormErrors, formValues,
  ]);

  const sendDealTaskForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const dealTaskItemData: FormValueItem = {
      ...values.dealData.dealTasksData[formKeyDealTaskGroup].tasks[formKeyDealTask],
    };

    if (!dealTaskItemData) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealTask({
        dealTask: processDealTask(dealTaskItemData),
        dealId,
        filters: dealTaskFilters,
        page: dealTaskPage || null,
      }, (response: ActionResponse): void => {
        closeDealTaskModal();

        fullResetForm();
        dispatch(toggleModalEditMode(false));

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    dealId, dealTaskFilters, dealTaskGroupData, dealTaskData, dealTaskPage, queryTab, isDealPhotoListChanged,
    dealPhotoListDefault, formKeyDealTaskGroup, formKeyDealTask,
  ]);

  const onDealTaskSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealTaskForm(formValues);
    }
  }, [
    dealId, dealTaskFilters, dealTaskGroupData, dealTaskData, dealTaskPage, queryTab, isDealPhotoListChanged,
    dealPhotoListDefault, withFormErrors, formValues,
  ]);

  const onDealTaskSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const sendDealScheduleForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const dealScheduleItemData: FormValueItem = {
      ...values.dealData.dealSchedulesData[formKeyNew],
    };

    if (!dealScheduleItemData) {
      return null;
    }

    dealScheduleItemData.tasks = [];
    dealScheduleItemData.links = [];

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealSchedule({
        dealSchedule: dealScheduleItemData,
        dealId,
      }, (response: ActionResponse): void => {
        closeDealScheduleModal();

        fullResetForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setCreateModalErrorMessage(exception.message);
        }
      }));
    });
  }, [dealId, queryTab, isDealPhotoListChanged, dealPhotoListDefault, formKeyNew]);

  const sendDealParticipantForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const dealParticipantItemData: FormValueItem = {
      ...values.dealData.dealParticipantsData[formKeyDealParticipant],
    };

    if (!dealParticipantItemData) {
      return null;
    }

    if (dealParticipantItemData.user) {
      dealParticipantItemData.user = { ...dealParticipantItemData.user };

      if (!dealParticipantItemData.user.password || dealParticipantItemData.user.withSendInvitation) {
        delete dealParticipantItemData.user.password;
      }

      delete dealParticipantItemData.user.passwordConfirm;
      delete dealParticipantItemData.user.accountUserTypeGroup;

      dealParticipantItemData.user.phone = dealParticipantItemData.user.phone
      && FormatService.isValidPhoneNumber(dealParticipantItemData.user.phone, true)
        ? FormatService.formatPhoneValue(dealParticipantItemData.user.phone) : null;
      dealParticipantItemData.user.withSendInvitation = dealParticipantItemData.user.withSendInvitation || false;
      dealParticipantItemData.user.invitationMessage = dealParticipantItemData.user.invitationMessage || null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealParticipant({
        dealParticipant: dealParticipantItemData,
        dealId,
        ...getDealParticipantListOptions(
          dealParticipantPage,
          dealParticipantLimit,
          dealParticipantSortParams,
          dealParticipantFilters,
        ),
      }, (response: ActionResponse): void => {
        closeDealParticipantModal();

        fullResetForm();
        dispatch(toggleModalEditMode(false));

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    dealId, dealParticipantNdaFiles, dealParticipantData, dealParticipantPage, dealParticipantLimit,
    dealParticipantFilters, queryTab, isDealPhotoListChanged, dealPhotoListDefault, formKeyDealParticipant,
  ]);

  const onDealParticipantSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealParticipantForm(formValues);
    }
  }, [
    dealId, dealParticipantNdaFiles, dealParticipantData, dealParticipantPage, dealParticipantLimit,
    dealParticipantFilters, queryTab, isDealPhotoListChanged, dealPhotoListDefault, withFormErrors, formValues,
  ]);

  const onDealParticipantSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const sendDealFileForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const dealFileGroupItemData: FormValueItem = {
      ...values.dealData.dealFilesData[formKeyDealFileGroup],
    };
    const dealFileItemData: FormValueItem = {
      ...values.dealData.dealFilesData[formKeyDealFileGroup].files[formKeyDealFile],
    };

    if (!dealFileItemData || !dealFileGroupItemData) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveDealFile({
        dealFile: processDealFile(dealFileGroupItemData, dealFileItemData, !dealFileGroupId),
        dealId,
        page: dealFilePage || null,
      }, (response: ActionResponse): void => {
        closeDealFileModal();
        openDealFileExpansionGroups(undefined, true);

        fullResetForm();
        dispatch(toggleModalEditMode(false));

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    dealId, dealFileFiles, dealFileGroupData, dealFileData, dealFilePage, queryTab, isDealPhotoListChanged,
    dealPhotoListDefault, dealFileGroupId, formKeyDealFileGroup, formKeyDealFile,
  ]);

  const onDealFileSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealFileForm(formValues);
    }
  }, [
    dealId, dealFileFiles, dealFileGroupData, dealFileData, dealFilePage, queryTab, isDealPhotoListChanged,
    dealPhotoListDefault, withFormErrors, formValues,
  ]);

  const sendDealActivityForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const activityItemData: FormValueItem = {
      ...values.dealData.activityListData[formKeyNew],
    };

    if (!activityItemData) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveActivityListData({
        activityListForm: {
          activityList: [processDealActivityItem(activityItemData)],
        },
        ...getDealActivityListOptions(
          dealActivityPage, dealActivityLimit, dealActivitySortParams, dealActivityFilters,
        ),
      }, (response: ActionResponse): void => {
        closeDealActivityModal();

        fullResetForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [
    dealActivityPage, dealActivityLimit, dealActivitySortParams, dealActivityFilters, dealId, queryTab,
    isDealPhotoListChanged, dealPhotoListDefault, formKeyNew,
  ]);

  const onDealTermGroupCreate: Function = useCallback((): void => {
    onSubmitForm();
  }, [
    isSmallScreen, dealAssetPhotoFile, queryTab, isNewDeal, dealPhotoList, dealPhotoNewItems,
    dealEquityOrganizationChartFile, dealIsEquityOrganizationChartDeleted, dealId, queryTemplate,
    dealPhotoFiles, dealValuationFileFiles, dealFileFiles, dealParticipantNdaFiles, formValues, withFormErrors,
  ]);

  const onDealTermGroupModalCancel: Function = useCallback((): void => {
    closeDealTermGroupModal();

    fullResetForm();
  }, [queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onDealTermGroupSubmitCreateForm: Function = useCallback((onSubmitCallback: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onDealValuationFileCreate: Function = useCallback((): void => {
    if (dealId) {
      submitForm();

      if (!withFormErrors) {
        sendDealValuationFileForm(formValues);
      }
    }
  }, [
    dealId, dealValuationFileFiles, dealValuationFileGroupData, dealValuationFileData, queryTab,
    isDealPhotoListChanged, dealPhotoListDefault, withFormErrors, formValues,
  ]);

  const onDealValuationFileModalCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setDealValuationFileInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      closeDealValuationFileModal();

      if (!isNewDeal && !isEditMode) {
        fullResetForm();
      }

      dispatch(toggleModalEditMode(false));
    }
  }, [isNewDeal, isEditMode, dealValuationFileActiveIds, queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onDealTaskCreate: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealTaskForm(formValues);
    }
  }, [
    isEditMode, dealId, dealTaskFilters, dealTaskInlineSaveId, dealTaskGroupInlineSaveId,
    dealTaskActiveIds, dealTaskGroupActiveIds, dealTaskPage, queryTab, isDealPhotoListChanged,
    dealPhotoListDefault, withFormErrors, formValues,
  ]);

  const onDealTaskModalCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setDealTaskInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      closeDealTaskModal();

      if (!isNewDeal && !isEditMode) {
        fullResetForm();
      }

      dispatch(toggleModalEditMode(false));
    }
  }, [isNewDeal, isEditMode, dealTaskActiveIds, queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onDealTaskGroupCreate: Function = useCallback((): void => {
    onSubmitForm();
  }, [
    isSmallScreen, dealAssetPhotoFile, queryTab, isNewDeal, dealPhotoList, dealPhotoNewItems,
    dealEquityOrganizationChartFile, dealIsEquityOrganizationChartDeleted, dealId, queryTemplate,
    dealPhotoFiles, dealValuationFileFiles, dealFileFiles, dealParticipantNdaFiles, formValues, withFormErrors,
  ]);

  const onDealTaskGroupSubmitCreateForm: Function = useCallback((onSubmitCallback: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onDealTaskGroupModalCancel: Function = useCallback((): void => {
    closeDealTaskGroupModal();
    fullResetForm();
  }, [queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onDealScheduleCreate: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealScheduleForm(formValues);
    }
  }, [
    isEditMode, dealId, queryTab, isDealPhotoListChanged, dealPhotoListDefault, dealTaskRowsSelectedDefault,
    withFormErrors, formValues,
  ]);

  const onDealParticipantCreate: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealParticipantForm(formValues);
    }
  }, [
    isEditMode, dealId, dealParticipantNdaFiles, dealParticipantInlineSaveId, dealParticipantActiveIds,
    dealParticipantPage, dealParticipantLimit, queryTab, isDealPhotoListChanged, dealPhotoListDefault,
    dealTaskRowsSelectedDefault, withFormErrors, formValues,
  ]);

  const onDealParticipantModalCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setDealParticipantInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      closeDealParticipantModal();

      if (!isNewDeal && !isEditMode) {
        fullResetForm();
      }

      dispatch(toggleModalEditMode(false));
    }
  }, [isNewDeal, isEditMode, dealParticipantActiveIds, dealParticipantNdaFiles]);

  const onDealFileCreate: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealFileForm(formValues);
    }
  }, [
    isEditMode, dealId, dealFileFiles, dealFileInlineSaveId, dealFileGroupInlineSaveId, dealFileActiveIds,
    dealFileGroupActiveIds, dealFilePage, queryTab, isDealPhotoListChanged, dealPhotoListDefault,
    dealTaskRowsSelectedDefault, withFormErrors, formValues,
  ]);

  const onDealFileModalCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setDealFileInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      closeDealFileModal();

      if (!isNewDeal && !isEditMode) {
        fullResetForm();
      }

      dispatch(toggleModalEditMode(false));
    }
  }, [isNewDeal, isEditMode, dealFileActiveIds, queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onDealFileGroupCreate: Function = useCallback((): void => {
    onSubmitForm();
  }, [
    isSmallScreen, dealAssetPhotoFile, queryTab, isNewDeal, dealPhotoList, dealPhotoNewItems,
    dealEquityOrganizationChartFile, dealIsEquityOrganizationChartDeleted, dealId, queryTemplate,
    dealPhotoFiles, dealValuationFileFiles, dealFileFiles, formValues, withFormErrors,
  ]);

  const onDealFileGroupModalCancel: Function = useCallback((): void => {
    closeDealFileGroupModal();
    fullResetForm();
  }, [queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onDealFileSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onDealFileGroupSubmitCreateForm: Function = useCallback((onSubmitCallback?: Function): void => {
    submitForm();

    if (!withFormErrors && onSubmitCallback) {
      onSubmitCallback();
    }
  }, [withFormErrors]);

  const onDealActivityCreate: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendDealActivityForm(formValues);
    }
  }, [
    isEditMode, dealId, dealActivityPage, dealActivityLimit, dealActivitySortParams, dealActivityFilters,
    dealActivityInlineSaveId, dealActivityActiveIds, queryTab, isDealPhotoListChanged, dealPhotoListDefault,
    withFormErrors, formValues,
  ]);

  const onDealActivityCancel: Function = useCallback((): void => {
    if (isEditMode) {
      setDealActivityInlineSaveId(FormService.FORM_KEY_NEW);
      setInlineSaveIsConfirm(false);
    } else {
      closeDealActivityModal();

      if (!isNewDeal && !isEditMode) {
        fullResetForm();
      }
    }
  }, [isNewDeal, isEditMode, dealActivityActiveIds, queryTab, isDealPhotoListChanged, dealPhotoListDefault]);

  const onResetDealData: Function = useCallback((): void => {
    dispatch(resetDealData());
  }, []);

  const extendedTabs: TabItem[] = useMemo((): TabItem[] => (
    (dealTabSettings || currentDealTabSettings)
    && StructureService.extendOptions(tabs, dealTabSettings || currentDealTabSettings, true)
  ), [tabs, dealTabSettings, currentDealTabSettings]);

  const getOptions: Function = useCallback((noDealId?: boolean): RequestOptions => {
    let options: RequestOptions = {};

    options = dealId && !noDealId ? { ...options, dealId } : options;
    options = portfolioIdsFilter && portfolioIdsFilter.length
      ? { ...options, portfolioIds: portfolioIdsFilter } : options;
    options = portfolioGroupIdsFilter && portfolioGroupIdsFilter.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsFilter } : options;

    return options;
  }, [portfolioIdsFilter, portfolioGroupIdsFilter, dealId]);

  const onRefresh: Function = useCallback((): void => {
    if (dealId) {
      dispatch(getDealData({
        dealId,
        tab: queryTab || TAB_NAME_SUMMARY,
        filters: {
          dealTaskListFilters: dealTaskFilters,
          dealParticipantListFilters: dealParticipantFilters,
          activityListFilters: dealActivityFilters,
        },
        page: (queryTab && dealTabPageMap[queryTab]) || null,
        limit: (queryTab && dealTabLimitMap[queryTab]) || null,
        allRows: true,
      }, null, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }
  }, [dealId, queryTab, dealTaskFilters, dealParticipantFilters, dealActivityFilters]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_DEAL}/${dealId}`,
            search: FormatService.formatQueryParams(queryParams),
          });

          setTabIndex(lastTabIndex);
        }

        setLastTabIndex(null);
      }

      if (lastSubTabIndex && lastSubTabIndex >= TAB_INDEX_DEFAULT) {
        setSubTabIndex(lastSubTabIndex);

        setLastSubTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
      setLastSubTabIndex(null);
    }
  }, [lastLocation]);

  useEffect((): void => {
    if (dealTermsData) {
      openDealTermExpansionGroups(true);
    }

    if (dealTasksData) {
      openDealTaskExpansionGroups(undefined, true);
    }

    if (dealFilesData) {
      openDealFileExpansionGroups(undefined, true);
    }

    if (queryDealTaskId && isDataLoaded && formDealId === dealId) {
      openDealTaskModalById(queryDealTaskId);
    }

    if (queryDealParticipantId && isDataLoaded && formDealId === dealId) {
      openDealParticipantModalById(queryDealParticipantId);
    }

    if (dealPhotoListDefault) {
      setDealPhotoList([...(dealPhotoListDefault || [])]);
    }
  }, [isDataLoaded]);

  useEffect((): void => {
    if (dealTasksData) {
      openDealTaskExpansionGroups(undefined, true);
    }
  }, [dealTaskGroupList]);

  useEffect((): void => {
    const initialDealAssetUseBreakdownDefaultProperties: DealAssetUseBreakdownProperty[] = [];

    if (dealBuildingDetailsData && dealBuildingDetailsData.assetUseBreakdownHeaders) {
      dealBuildingDetailsData.assetUseBreakdownHeaders.forEach((
        dealAssetUseBreakdownHeader: DealAssetUseBreakdownProperty,
      ): void => {
        initialDealAssetUseBreakdownDefaultProperties.push({
          id: dealAssetUseBreakdownHeader.id,
          name: dealAssetUseBreakdownHeader.name,
          value: '',
        });
      });
    }

    setDealAssetUseBreakdownDefaultProperties(initialDealAssetUseBreakdownDefaultProperties);
  }, [dealBuildingDetailsData, dealAssetUseBreakdownNewFieldItems]);

  useEffect((): void => {
    if (dealCommonData) {
      const initialDealAllocationItems: Record<string, DealAllocationItem> = {};
      if (dealCommonData.allocationItems && dealCommonData.allocationItems.length) {
        dealCommonData.allocationItems.forEach((dealAllocationItem: DealAllocationItem): void => {
          initialDealAllocationItems[dealAllocationItem.id] = {
            id: dealAllocationItem.id,
            portfolioId: dealAllocationItem.portfolio ? dealAllocationItem.portfolio.id : null,
            portfolio: dealAllocationItem.portfolio || null,
            value: dealAllocationItem.value,
          };
        });
      }
      setDealAllocationItems(initialDealAllocationItems);
    }
  }, [dealCommonData, isEditMode]);

  useEffect((): void => {
    if (dealActivityList && dealActivityItemData) {
      dealActivityList.forEach((activityItem: Activity): void => {
        if (activityItem.id === dealActivityItemData.id) {
          setDealActivityItemData({ ...activityItem });
        }
      });
    }
  }, [dealActivityNewCommentList]);

  useEffect((): void => {
    if (dealParticipantsData && dealParticipantData) {
      dealParticipantsData.forEach((dealParticipantItem: DealParticipant): void => {
        if (dealParticipantItem.id === dealParticipantData.id) {
          setDealParticipantData({ ...dealParticipantItem });
        }
      });
    }
  }, [dealParticipantNewCommentList]);

  useEffect((): void => {
    if (dealTasksData && dealTaskData) {
      dealTasksData.forEach((dealTaskGroupItem: DealTaskGroup): void => {
        if (dealTaskGroupItem.tasks && dealTaskGroupItem.tasks.length) {
          dealTaskGroupItem.tasks.forEach((dealTaskItem: DealTask): void => {
            if (dealTaskItem.id === dealTaskData.id) {
              setDealTaskData({ ...dealTaskItem });
            }

            if (dealTaskItem.subTasks && dealTaskItem.subTasks.length) {
              dealTaskItem.subTasks.forEach((dealSubTaskItem: DealTask): void => {
                if (dealSubTaskItem.id === dealTaskData.id) {
                  setDealTaskData({ ...dealSubTaskItem });
                }
              });
            }
          });
        }
      });
    }
  }, [dealTaskNewCommentList, dealTasksData]);

  useEffect((): void => {
    if (isFormProcessed) {
      setIsFormProcessed(false);

      if (isNewDeal && newDealId) {
        fullResetForm();
      } else {
        dispatch(getDealData({
          dealId,
          tab: queryTab || TAB_NAME_SUMMARY,
          filters: {
            dealTaskListFilters: DEAL_TASK_DEFAULT_FILTERS,
            dealParticipantListFilters: DEAL_PARTICIPANT_DEFAULT_FILTERS,
            activityListFilters: DEAL_ACTIVITY_DEFAULT_FILTERS,
          },
          page: (queryTab && dealTabPageMap[queryTab]) || null,
          limit: (queryTab && dealTabLimitMap[queryTab]) || null,
          allRows: true,
        }, (): void => {
          fullResetForm();
        }, (exception: ServerError): void => {
          if (exception) {
            setErrorMessage(exception.message);
          }
        }));
      }
    }
  }, [isFormProcessed]);

  useEffect((): void => {
    if (newDealId && !isNewDeal) {
      dispatch(resetNewDealId());
    }
  }, [newDealId, isNewDeal]);

  useEffect((): ReactUseEffectCleanUp => {
    if (isDataLoaded) {
      onResetDealData();
    }

    dispatch(getDealData({
      dealId,
      template: queryTemplate || null,
      tab: queryTab || TAB_NAME_SUMMARY,
      filters: {
        dealTaskListFilters: applyDealTaskUrlFilters(),
        dealParticipantListFilters: applyDealParticipantUrlFilters(),
        activityListFilters: applyDealActivityUrlFilters(),
      },
    }, undefined, (exception: ServerError): void => {
      if (exception) {
        setErrorMessage(exception.message);
      }
    }));

    return (): void => {
      onResetDealData();
    };
  }, [dealId, queryTab]);

  useEffect((): void => {
    onResetDealPhotos();
  }, [isDealPhotoListChanged, dealPhotoListDefault]);

  useEffect((): void => {
    if (tabIndex === null) {
      setTabIndex(TAB_INDEX_DEFAULT);
    }
  }, [tabIndex]);

  useEffect((): void => {
    if (dealAssetId) {
      dealPropertyDetailsResetAssetForm(dealAsset);

      setDealAssetAddress((dealAsset && dealAsset.address) || null);
    }
  }, [dealAssetId]);

  useEffect((): void => {
    if (dealTasksData) {
      const initialRowsSelectedDefault: Record<string, number[]> = {};
      const initialRowsSelected: Record<string, number[]> = {};
      dealTasksData.forEach((dealTaskGroupItem: DealTaskGroup): void => {
        initialRowsSelectedDefault[dealTaskGroupItem.id] = [];
        initialRowsSelected[dealTaskGroupItem.id] = (
          !!dealTaskRowsSelected && dealTaskRowsSelected[dealTaskGroupItem.id]
        ) || [];
      });
      setDealTaskRowsSelectedDefault(initialRowsSelectedDefault);
      setDealTaskRowsSelected(initialRowsSelected);
    }
  }, [dealTasksData]);

  useEffect((): void => {
    if (dealFilesData) {
      const initialRowsSelectedDefault: Record<string, number[]> = {};
      const initialRowsSelected: Record<string, number[]> = {};
      dealFilesData.forEach((dealFileGroupItem: DealFileGroup): void => {
        initialRowsSelectedDefault[dealFileGroupItem.id] = [];
        initialRowsSelected[dealFileGroupItem.id] = (
          dealFileRowsSelected && dealFileRowsSelected[dealFileGroupItem.id]
        ) || [];
      });
      setDealFileRowsSelectedDefault(initialRowsSelectedDefault);
      setDealFileRowsSelected(initialRowsSelected);
    }
  }, [dealFilesData]);

  useEffect((): void => {
    if (dealTabSettings && dealTabSettings.length) {
      setCurrentDealTabSettings(dealTabSettings);
    }
  }, [dealTabSettings]);

  useEffect((): void => {
    if (inlineSaveIsConfirm !== null && (dealCapitalizationSourceItemInlineSaveId !== null
      || dealCapitalizationUseItemInlineSaveId !== null || dealValuationFileInlineSaveId !== null
      || dealTaskInlineSaveId !== null || dealParticipantInlineSaveId !== null || dealFileInlineSaveId !== null
      || dealActivityInlineSaveId !== null)) {
      if (inlineSaveIsConfirm) {
        onSubmitForm();
      } else {
        onCancelForm();
      }
    }
  }, [inlineSaveIsConfirm]);

  useEffect((): void => {
    if (withServerErrors && inlineSaveIsConfirm !== null) {
      resetInlineIds();
    }
  }, [withServerErrors]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (newDealId && isNewDeal && isFormDataPristine) {
    return <Navigate to={`${ROUTE_DEAL}/${newDealId}`} />;
  }

  if (!dealId && !isNewDeal) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (errorMessage) {
    return (
      <PageError message={errorMessage} />
    );
  }

  return (
    <CheckAccess permissions={dealViewPermissions}>
      <Form id={FORM_ID_DEAL} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode || isNewDeal} onRefresh={onRefresh}>
          <ContentHeader
            title={(
              (!isEditMode && !isNewDeal) ? (
                <FlexContainer isInline>
                  <FlexContainer>
                    {pageTitle}
                  </FlexContainer>
                  {
                    !isNewDeal && dealName && (
                      <CheckAccess permissions={dealStatusEditPermissions}>
                        {
                          !isSmallScreen && (
                            <Fragment>
                              <PageTitleSeparator />
                              <DealStatusDropdown
                                dealStatusList={commonDealStatusList}
                                dealRejectReasonList={dealRejectReasonList}
                                currentStatus={currentDealStatus}
                                currentDealRejectReason={currentDealRejectReason}
                                errorMessage={dealStatusErrorMessage}
                                onStatusChange={onDealStatusChange}
                              />
                            </Fragment>
                          )
                        }
                      </CheckAccess>
                    )
                  }
                </FlexContainer>
              ) : pageTitle
            )}
          />
          <PageTabs
            withTabTopOffset
            isDisabled={isFormChanged}
            isCompressed={isRightDrawerOpen}
            isLoading={!isDataLoaded}
            withNoTabSwitcher={isNewDeal}
            tabs={extendedTabs}
            currentTab={!isNewDeal ? queryTab : TAB_NAME_SUMMARY}
            onSetTabIndex={setTabIndex}
            onTabChange={onTabChange}
            onBeforeTabChange={onBeforeTabChange}
          />
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <DealCapitalizationModal
          isOpen={isDealCapitalizationSourcesModalOpen || isDealCapitalizationUsesModalOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isModalEditMode={isModalEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isNewEntity={isNewRow}
          isSimpleView={isNewDeal}
          dealCapitalizationCategory={isDealCapitalizationSourcesModalOpen
            ? DEAL_CAPITALIZATION_CATEGORY_SOURCES : DEAL_CAPITALIZATION_CATEGORY_USES}
          dealCapitalizationItemData={isDealCapitalizationSourcesModalOpen
            ? dealCapitalizationSourcesItemData : dealCapitalizationUsesItemData}
          dealCapitalizationGroupData={isDealCapitalizationSourcesModalOpen
            ? dealCapitalizationSourcesGroupData : dealCapitalizationUsesGroupData}
          groupTitle={isDealCapitalizationSourcesModalOpen ? 'Source' : 'Use'}
          editPermission={isDealCapitalizationSourcesModalOpen ? DEAL_SOURCES_ITEM_EDIT : DEAL_USES_ITEM_EDIT}
          dealCapitalizationTypeList={dealCapitalizationTypeList}
          dealCapitalizationGroupList={dealCapitalizationGroupList}
          onCancel={onDealCapitalizationModalCancel}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onCloseModal={isDealCapitalizationSourcesModalOpen ? closeDealCapitalizationSourcesModal
            : closeDealCapitalizationUsesModal}
          onSwitchModalEditMode={onSwitchModalEditMode}
        />
        <DealTermGroupModal
          isOpen={isDealTermGroupModalOpen}
          isSaveDisabled={isSaveDisabled}
          isSaving={isFormSaving}
          saveFormDeps={saveFormDeps}
          onCloseModal={closeDealTermGroupModal}
          onCreate={onDealTermGroupCreate}
          onCancel={onDealTermGroupModalCancel}
          onSubmitCreateForm={onDealTermGroupSubmitCreateForm}
        />
        <DealValuationFileModal
          isOpen={isDealValuationFileModalOpen}
          isNewEntity={isNewRow}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isModalEditMode={isModalEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaveDisabled={isSaveDisabled}
          isSaving={isFormSaving}
          dealValuationFileData={dealValuationFileData}
          dealValuationFileGroupData={dealValuationFileGroupData}
          dealValuationFileGroupList={dealValuationFileGroupList}
          dealValuationFileFile={(dealValuationFileFiles[dealValuationFileGroupId]
            && dealValuationFileFiles[dealValuationFileGroupId][dealValuationFileId])}
          saveFormDeps={saveFormDeps}
          onCloseModal={closeDealValuationFileModal}
          onFileChange={onDealValuationFileChange}
          onFileDelete={onDealValuationFileDelete}
          onFileViewClick={onDealValuationFileViewClick}
          onCreate={onDealValuationFileCreate}
          onCancel={onDealValuationFileModalCancel}
          onSwitchModalEditMode={onSwitchModalEditMode}
          onCancelForm={onCancelForm}
          onSubmitForm={onDealValuationFileSubmitForm}
        />
        <DealTaskModal
          isNewEntity={isNewRow}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isModalEditMode={isModalEditMode}
          isCommentSaving={isDealTaskCommentSaving}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isOpen={isDealTaskModalOpen}
          isCommentFormActive={dealTaskIsCommentFormActive}
          queryCommentId={queryCommentId}
          dealTaskData={dealTaskData}
          dealTaskGroupData={dealTaskGroupData}
          dealTaskPlainList={dealTaskPlainList}
          dealTaskContactPersonList={dealTaskContactPersonList}
          dealTaskGroupList={dealTaskGroupList}
          dealTaskStatusList={dealTaskStatusList}
          accessLevelList={accessLevelList}
          currentUser={currentUser}
          contactLinkedUserList={contactLinkedUserList}
          tagList={tagList}
          saveFormDeps={saveFormDeps}
          onCloseModal={closeDealTaskModal}
          onCreate={onDealTaskCreate}
          onCancel={onDealTaskModalCancel}
          onAddComment={onDealTaskAddComment}
          onUpdateComment={onDealTaskUpdateComment}
          onDeleteComment={onDealTaskDeleteComment}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
          onStatusChange={onDealTaskStatusChange}
          onReviewButtonClick={onDealTaskReviewButtonClick}
          onCommentAttachmentFileViewClick={onDealTaskCommentAttachmentFileViewClick}
          onCloseNewGroupForm={onDealTaskCloseNewGroupForm}
          onDealTaskGroupChange={onDealTaskGroupChange}
          onDealTaskReviewContactsChange={onDealTaskReviewContactsChange}
          onSwitchModalEditMode={onSwitchModalEditMode}
          onCancelForm={onCancelForm}
          onSubmitForm={onDealTaskSubmitForm}
          onSubmitCreateForm={onDealTaskSubmitCreateForm}
          onDealTaskContactPersonSearch={onDealTaskContactPersonSearch}
          onDealTaskContactPersonSearchClose={onDealTaskContactPersonSearchClose}
          onOpenDealFileChatModal={openDealTaskAttachmentFileChatModal}
        />
        <DealTaskGroupModal
          isOpen={isDealTaskGroupModalOpen}
          isSaveDisabled={isSaveDisabled}
          isSaving={isFormSaving}
          saveFormDeps={saveFormDeps}
          onCloseModal={closeDealTaskGroupModal}
          onCancel={onDealTaskGroupModalCancel}
          onCreate={onDealTaskGroupCreate}
          onSubmitCreateForm={onDealTaskGroupSubmitCreateForm}
        />
        <DealScheduleModal
          isOpen={isDealScheduleModalOpen}
          isSaveDisabled={isSaveDisabled}
          isSaving={isFormSaving}
          errorMessage={createModalErrorMessage}
          saveFormDeps={saveFormDeps}
          onCloseModal={closeDealScheduleModal}
          onCancel={onDealScheduleModalCancel}
          onCreate={onDealScheduleCreate}
          onSubmitCreateForm={onDealScheduleSubmitCreateForm}
        />
        <DealTaskListModal
          isOpen={dealTaskIsListModalOpen}
          isSaving={isDealTaskListSaving}
          contactList={dealTaskContactPersonList}
          errorMessage={dealTaskListModalErrorMessage}
          onCloseModal={closeDealTaskListModal}
          onSaveForm={onDealTaskListSave}
          onChange={onDealTaskListChange}
        />
        <DealTaskTemplateSaveModal
          isOpen={dealTaskIsTemplateSaveModalOpen}
          isSaving={isDealTaskListSaving}
          dealTasksData={dealTasksData}
          errorMessage={dealTaskTemplateSaveModalErrorMessage}
          onCloseModal={closeDealTaskTemplateSaveModal}
          onSaveForm={onDealTaskTemplateSave}
        />
        <DealTaskTemplateLoadModal
          isOpen={dealTaskIsTemplateLoadModalOpen}
          dealTaskTemplateList={dealTaskTemplateList}
          errorMessage={dealTaskTemplateLoadModalErrorMessage}
          onCloseModal={closeDealTaskTemplateLoadModal}
          onConfirm={onDealTaskTemplateLoadData}
        />
        <DealParticipantModal
          isNewEntity={isNewRow}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isModalEditMode={isModalEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isCommentSaving={isDealParticipantCommentSaving}
          isSaving={isFormSaving}
          isOpen={isDealParticipantModalOpen}
          isSaveDisabled={isSaveDisabled}
          isCommentFormActive={dealParticipantIsCommentFormActive}
          queryCommentId={queryCommentId}
          currentUser={currentUser}
          dealId={dealId}
          dealParticipantData={dealParticipantData}
          ndaFile={dealParticipantNdaFiles[dealParticipantId]}
          dealParticipantGroupList={dealParticipantGroupList}
          accessLevelList={accessLevelList}
          roleList={roleList}
          accountUserTypeList={accountUserTypeList}
          contactPersonList={contactPersonList}
          contactNotParticipantList={contactNotParticipantList}
          contactLinkedUserList={contactLinkedUserList}
          tagList={tagList}
          saveFormDeps={saveFormDeps}
          onNdaFileChange={onDealParticipantNdaFileChange}
          onNdaFileDelete={onDealParticipantNdaFileDelete}
          onCloseModal={closeDealParticipantModal}
          onCreate={onDealParticipantCreate}
          onCancel={onDealParticipantModalCancel}
          onSwitchModalEditMode={onSwitchModalEditMode}
          onCancelForm={onCancelForm}
          onSubmitForm={onDealParticipantSubmitForm}
          onSubmitCreateForm={onDealParticipantSubmitCreateForm}
          onContactClick={onContactClick}
          onAddComment={onDealParticipantAddComment}
          onUpdateComment={onDealParticipantUpdateComment}
          onDeleteComment={onDealParticipantDeleteComment}
          onFileViewClick={onDealParticipantFileViewClick}
          onContactChange={onDealParticipantContactChange}
          onCommentAttachmentFileViewClick={onDealParticipantCommentAttachmentFileViewClick}
          onSwitchNewUserFormActive={onDealParticipantSwitchNewUserFormActive}
          onAccountUserTypeGroupChange={onDealParticipantAccountUserTypeGroupChange}
          onNewUserSendInvitationCheckboxChange={onDealParticipantNewUserSendInvitationCheckboxChange}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onContactNotParticipantSearch={onContactNotParticipantSearch}
          onContactNotParticipantSearchClose={onContactNotParticipantSearchClose}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
        />
        <DealParticipantListModal
          isSaving={isDealParticipantListSaving}
          isOpen={isDealParticipantListModalOpen}
          dealId={dealId}
          dealParticipantGroupList={dealParticipantGroupList}
          accessLevelList={accessLevelList}
          contactNotParticipantList={contactNotParticipantList}
          errorMessage={dealParticipantListModalErrorMessage}
          saveFormDeps={saveFormDeps}
          onChange={onDealParticipantListChange}
          onCloseModal={closeDealParticipantListModal}
          onSaveForm={onDealParticipantListSaveForm}
          onContactNotParticipantSearch={onContactNotParticipantSearch}
          onContactNotParticipantSearchClose={onContactNotParticipantSearchClose}
        />
        <DealFileModal
          isNewEntity={isNewRow}
          isEditMode={isEditMode}
          isNewDeal={isNewDeal}
          isModalEditMode={isModalEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isOpen={isDealFileModalOpen}
          isDealFileAnalyzed={isDealFileAnalyzed}
          dealFileData={dealFileData}
          dealFileGroupData={dealFileGroupData}
          dealFileGroupList={dealFileGroupList}
          dealFileFile={(dealFileFiles[dealFileGroupId] && dealFileFiles[dealFileGroupId][dealFileId])}
          accessLevelList={accessLevelList}
          saveFormDeps={saveFormDeps}
          onCloseModal={closeDealFileModal}
          onFileChange={onDealFileChange}
          onFileDelete={onDealFileDelete}
          onFileViewClick={onDealFileViewClick}
          onCreate={onDealFileCreate}
          onCancel={onDealFileModalCancel}
          onCloseNewGroupForm={onDealFileCloseNewGroupForm}
          onSwitchModalEditMode={onSwitchModalEditMode}
          onCancelForm={onCancelForm}
          onSubmitForm={onDealFileSubmitForm}
          onSubmitCreateForm={onDealFileSubmitCreateForm}
          onDealFileAnalyzeClick={onDealFileAnalyzeClick}
        />
        <DealFileGroupModal
          isOpen={isDealFileGroupModalOpen}
          isSaveDisabled={isSaveDisabled}
          isSaving={isFormSaving}
          saveFormDeps={saveFormDeps}
          onCreate={onDealFileGroupCreate}
          onCancel={onDealFileGroupModalCancel}
          onCloseModal={closeDealFileGroupModal}
          onSubmitCreateForm={onDealFileGroupSubmitCreateForm}
        />
        <DealFileChatModal
          isOpen={isDealTaskAttachmentFileChatModalOpen}
          isResponseLoading={isDealFileChatResponseLoading}
          fileData={dealFileChatData}
          errorMessage={dealFileChatModalErrorMessage}
          responseText={dealFileChatResponseText}
          onCancel={onDealFileChatModalCancel}
          onCloseModal={closeDealTaskAttachmentFileChatModal}
          onSendQuery={onDealTaskAttachmentFileChatSendQuery}
        />
        <DealFileChatModal
          isOpen={isDealFileChatModalOpen}
          isResponseLoading={isDealFileChatResponseLoading}
          fileData={dealFileChatData}
          errorMessage={dealFileChatModalErrorMessage}
          responseText={dealFileChatResponseText}
          onCancel={onDealFileChatModalCancel}
          onCloseModal={closeDealFileChatModal}
          onSendQuery={onDealFileChatSendQuery}
        />
        <ActivityModal
          isDealActivity
          isEditMode={isEditMode}
          isSaving={isFormSaving}
          isOpen={isDealActivityModalOpen}
          isSaveDisabled={isSaveDisabled}
          isCommentFormActive={dealActivityIsCommentFormActive}
          isNewActivity={isNewRow}
          isNewDealSourceFormVisible={dealActivityIsNewDealSourceFormVisible}
          isNewDealContactFormVisible={dealActivityIsNewDealContactFormVisible}
          isCommentSaving={isDealActivityCommentSaving}
          withFixedFooter={isNewRow}
          dealId={dealId}
          currentUser={currentUser}
          activityData={dealActivityItemData}
          activityStatusList={activityStatusList}
          activityCategoryList={activityCategoryList}
          activityTypeList={activityTypeList}
          activityFieldNewItems={dealActivityFieldNewItems[dealActivityId]}
          contactList={contactList}
          contactCompanyList={contactCompanyList}
          contactPersonList={contactPersonList}
          contactPersonInternalList={contactPersonInternalList}
          contactGroupList={contactGroupList}
          measureUnitList={measureUnitList}
          activityInterestLevelList={activityInterestLevelList}
          tagList={tagList}
          contactLinkedUserList={contactLinkedUserList}
          newCommentList={dealActivityNewCommentList}
          saveFormDeps={saveFormDeps}
          onCreate={onDealActivityCreate}
          onCancel={onDealActivityCancel}
          onCloseModal={closeDealActivityModal}
          onSubmitCreateForm={onDealActivitySubmitCreateForm}
          onAddComment={onDealActivityAddComment}
          onUpdateComment={onDealActivityUpdateComment}
          onDeleteComment={onDealActivityDeleteComment}
          onOpenNewDealSourceForm={dealActivityOnOpenNewDealSourceForm}
          onCloseNewDealSourceForm={dealActivityOnCloseNewDealSourceForm}
          onOpenNewDealContactForm={dealActivityOnOpenNewDealContactForm}
          onCloseNewDealContactForm={dealActivityOnCloseNewDealContactForm}
          onFieldDeleteClick={onDealActivityFieldDeleteClick}
          onAddNewFieldItem={onDealActivityFieldAddNewItem}
          onTagsChange={onDealActivityTagsChange}
          onDealContactsChange={onDealActivityDealContactsChange}
          onDealContactGroupsChange={onDealActivityDealContactGroupsChange}
          onDealContactPhoneChange={onDealActivityDealContactPhoneChange}
          onActivityDealSearch={onActivityDealSearch}
          onActivityDealSearchClose={onActivityDealSearchClose}
          onDealAssetSearch={onDealAssetSearch}
          onDealAssetSearchClose={onDealAssetSearchClose}
          onContactSearch={onContactSearch}
          onContactSearchClose={onContactSearchClose}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
        />
        <ExportModal
          isOpen={dealTaskIsExportModalOpen}
          withExportAll={dealTaskAllRowsSelected}
          dealId={dealId}
          exportTab={queryTab}
          exportEntityType={IMPORT_EXPORT_ENTITY_TYPE_DEAL_TASK_LIST}
          entityIds={dealTaskActiveIds}
          exportFormatList={FORMAT_LIST_EXPORT_DEAL_FILES_TABLE}
          onSuccess={onDealTaskModalExportSuccess}
          onCloseModal={closeDealTaskExportModal}
        />
        <ExportModal
          isOpen={dealFileIsExportModalOpen}
          withExportAll={dealFileAllRowsSelected}
          dealId={dealId}
          entityIds={dealFileActiveIds}
          exportTab={queryTab}
          exportEntityType={IMPORT_EXPORT_ENTITY_TYPE_DEAL_FILE_LIST}
          exportFormatList={FORMAT_LIST_EXPORT_DEAL_FILES_TABLE}
          onSuccess={onDealFileModalExportSuccess}
          onCloseModal={closeDealFileExportModal}
        />
        <StatusModal
          isOpen={dealTaskIsStatusChangeModalOpen}
          isSaving={isDealTaskListSaving}
          statusList={dealTaskStatusList}
          errorMessage={statusModalErrorMessage}
          itemActiveIds={dealTaskActiveIds}
          onSaveForm={onDealTaskStatusModalSaveClick}
          onCloseModal={closeDealTaskStatusModal}
        />
        <StatusModal
          isOpen={dealActivityIsStatusChangeModalOpen}
          isSaving={isDealActivityListSaving}
          statusList={activityStatusList}
          errorMessage={statusModalErrorMessage}
          itemActiveIds={dealActivityActiveIds}
          onCloseModal={closeDealActivityStatusModal}
          onSaveForm={onDealActivityStatusModalSaveClick}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealTermDeleteConfirmModalOpen}
          title={dealTermDeleteConfirmModalTitle}
          text={dealTermDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealTermDeleteConfirm}
          onCloseModal={closeDealTermDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealTermGroupDeleteConfirmModalOpen}
          title={dealTermGroupDeleteConfirmModalTitle}
          text={dealTermGroupDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealTermGroupDeleteConfirm}
          onCloseModal={closeDealTermGroupDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealValuationFileDeleteConfirmModalOpen}
          title={dealValuationFileDeleteConfirmModalTitle}
          text={dealValuationFileDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealValuationFileDeleteConfirm}
          onCloseModal={closeDealValuationFileDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealTaskDeleteConfirmModalOpen}
          title={dealTaskDeleteConfirmModalTitle}
          text={dealTaskDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealTaskDeleteConfirm}
          onCloseModal={closeDealTaskDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealTaskGroupDeleteConfirmModalOpen}
          title={dealTaskGroupDeleteConfirmModalTitle}
          text={dealTaskGroupDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealTaskGroupDeleteConfirm}
          onCloseModal={closeDealTaskGroupDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealScheduleDeleteConfirmModalOpen}
          title={dealScheduleDeleteConfirmModalTitle}
          text={dealScheduleDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealScheduleDeleteConfirm}
          onCloseModal={closeDealScheduleDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealParticipantDeleteConfirmModalOpen}
          title={dealParticipantDeleteConfirmModalTitle}
          text={dealParticipantDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealParticipantDeleteConfirm}
          onCloseModal={closeDealParticipantDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealFileDeleteConfirmModalOpen}
          title={dealFileDeleteConfirmModalTitle}
          text={dealFileDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealFileDeleteConfirm}
          onCloseModal={closeDealFileDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealFileGroupDeleteConfirmModalOpen}
          title={dealFileGroupDeleteConfirmModalTitle}
          text={dealFileGroupDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealFileGroupDeleteConfirm}
          onCloseModal={closeDealFileGroupDeleteConfirmModal}
        />
        <ConfirmModal
          isOpen={isDealActivityDeleteConfirmModalOpen}
          title={dealActivityDeleteConfirmModalTitle}
          text={dealActivityDeleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDealActivityDeleteConfirm}
          onCloseModal={closeDealActivityDeleteConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onCloseModal={closeLeaveConfirmModal}
          onConfirm={onLeaveConfirm}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    deal,
    asset,
    assetList,
    activityList,
    controls,
    dealTasks,
    dealFiles,
    dealParticipants,
    dealScheduleTasks,
    rightDrawer,
    activity,
    portfolioFilter,
  } = reducers;

  const formKeyNew: string = FormService.getIndexedFormFieldKey(FormService.FORM_KEY_NEW);

  const dealCommonData: Deal = deal.dealData && deal.dealData.dealCommonData;
  const dealAsset: Asset = assetList.assetData && assetList.assetData.asset;
  const assetData: Asset = dealAsset || (dealCommonData && dealCommonData.asset);
  const portfolioData: Portfolio = dealCommonData && dealCommonData.portfolio;
  const dealCustomSectionsData: DealSection[] = deal.dealData && deal.dealData.dealCustomSectionsData;
  const dealCustomListTypeList: FieldListType[] = deal.dealData && deal.dealData.dealCustomListTypeList;
  const dealPhotosData: DealPhoto[] = deal.dealData && deal.dealData.dealPhotosData;
  const dealBuildingDetailsData: DealBuildingDetailsData = deal.dealData && deal.dealData.dealBuildingDetailsData;
  const dealInvestmentReturnsData: DealInvestmentReturnsItem[] = deal.dealData
    && deal.dealData.dealInvestmentReturnsData;
  const dealMilestonesData: DealMilestonesItem[] = deal.dealData && deal.dealData.dealMilestonesData;
  const dealExternalLinksData: DealExternalLinksItem[] = deal.dealData && deal.dealData.dealExternalLinksData;
  const dealContactsData: DealContactGroup[] = deal.dealData && deal.dealData.dealContactsData;
  const dealCapitalizationSourcesData: DealCapitalizationGroup[] = deal.dealData && deal.dealData.dealCapitalizationData
    && deal.dealData.dealCapitalizationData.sources;
  const dealCapitalizationUsesData: DealCapitalizationGroup[] = deal.dealData && deal.dealData.dealCapitalizationData
    && deal.dealData.dealCapitalizationData.uses;
  const dealEquityOrganizationChartData: DealEquityOrganizationChartData = deal.dealData
    && deal.dealData.dealEquityOrganizationChartData;
  const dealMetricsCapitalStackData: DealMetricsCapitalStackItem[] = deal.dealData
    && deal.dealData.dealMetricsCapitalStackData;
  const dealMetricsStatisticsData: DealMetricsStatisticsGroup[] = deal.dealData
    && deal.dealData.dealMetricsStatisticsData;
  const dealTermsData: DealTermGroup[] = deal.dealData && deal.dealData.dealTermsData;
  const dealHistoricalValuationsData: DealHistoricalValuationsItem[] = deal.dealData
    && deal.dealData.dealHistoricalValuationsData;
  const dealTopLevelMetricsData: DealTopLevelMetricsItem[] = deal.dealData && deal.dealData.dealTopLevelMetricsData;
  const dealValuationFilesData: DealFileGroup[] = deal.dealData && deal.dealData.dealValuationFilesData;
  const dealTasksData: DealTaskGroup[] = deal.dealData && deal.dealData.dealTasksData;
  const dealParticipantsData: DealParticipant[] = deal.dealData && deal.dealData.dealParticipantsData;
  const dealFilesData: DealFileGroup[] = deal.dealData && deal.dealData.dealFilesData;
  const dealSchedulesData: DealSchedule[] = deal.dealData && deal.dealData.dealSchedulesData;
  const dealActivityList: Activity[] = deal.dealData && deal.dealData.dealActivityList;

  if (dealCommonData) {
    initialValues.dealData = {
      dealCommonData: {
        id: dealCommonData.id,
        name: dealCommonData.name || null,
        type: dealCommonData.type || null,
        status: dealCommonData.status && dealCommonData.status !== DEAL_STATUS_DRAFT
          ? dealCommonData.status : DEAL_STATUS_INITIAL,
        description: dealCommonData.description,
        amount: FormatService.formatNumber(dealCommonData.amount),
        receivedDate: dealCommonData.receivedDate ? DateService.parseDate(dealCommonData.receivedDate)
          : DateService.parseDate(DateService.getDateNow()),
        closedDate: dealCommonData.closedDate ? DateService.parseDate(dealCommonData.closedDate) : null,
        bidDate: dealCommonData.bidDate ? DateService.parseDate(dealCommonData.bidDate) : '',
        bidDateWithReminder: dealCommonData.bidDateWithReminder,
        bidDateReminderOffset: dealCommonData.bidDateReminderOffset
          ? FormatService.formatNumber(dealCommonData.bidDateReminderOffset.toString(), true) : null,
        loanAmount: FormatService.formatNumber(dealCommonData.loanAmount),
        zfaAmount: FormatService.formatNumber(dealCommonData.zfaAmount, true),
        gsfAmount: FormatService.formatNumber(dealCommonData.gsfAmount, true),
        nsfAmount: FormatService.formatNumber(dealCommonData.nsfAmount, true),
        unitsAmount: FormatService.formatNumber(dealCommonData.unitsAmount, true),
        keysAmount: FormatService.formatNumber(dealCommonData.keysAmount, true),
        acresAmount: FormatService.formatNumber(dealCommonData.acresAmount, true),
        lotsfAmount: FormatService.formatNumber(dealCommonData.lotsfAmount, true),
        dealTermValue: dealCommonData.dealTermValue,
        projectedNoiAmount: FormatService.formatNumber(dealCommonData.projectedNoiAmount),
        appraisedValueAmount: FormatService.formatNumber(dealCommonData.appraisedValueAmount),
        irrAmount: FormatService.formatNumber(dealCommonData.irrAmount, true),
        yieldToCostAmount: FormatService.formatNumber(dealCommonData.yieldToCostAmount, true),
        dealStructure: dealCommonData.dealStructure,
        rfp: dealCommonData.rfp,
        askingPrice: FormatService.formatNumber(dealCommonData.askingPrice),
        equityRequired: FormatService.formatNumber(dealCommonData.equityRequired),
        estimatedTdc: FormatService.formatNumber(dealCommonData.estimatedTdc),
        loanType: dealCommonData.loanType,
        extensionConditions: dealCommonData.extensionConditions,
        equityAmount: FormatService.formatNumber(dealCommonData.equityAmount),
        totalFunded: FormatService.formatNumber(dealCommonData.totalFunded),
        totalUnfunded: FormatService.formatNumber(dealCommonData.totalUnfunded),
        assetId: dealCommonData.asset ? dealCommonData.asset.id : null,
        sponsorIds: dealCommonData.sponsors && dealCommonData.sponsors.length
          ? dealCommonData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
            .map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
        sponsorContactIds: dealCommonData.sponsors && dealCommonData.sponsors.length
          ? dealCommonData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
            .map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
        brokerIds: dealCommonData.brokers && dealCommonData.brokers.length
          ? dealCommonData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
            .map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
        brokerContactIds: dealCommonData.brokers && dealCommonData.brokers.length
          ? dealCommonData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
            .map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
        lenderIds: dealCommonData.lenders && dealCommonData.lenders.length
          ? dealCommonData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY)
            .map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
        lenderContactIds: dealCommonData.lenders && dealCommonData.lenders.length
          ? dealCommonData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON)
            .map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
        asset: {
          id: assetData ? assetData.id : null,
          name: assetData ? assetData.name : '',
          status: assetData ? assetData.status : ASSET_STATUS_UNREALIZED,
          size: assetData ? FormatService.formatNumber(assetData.size) : null,
          sizeType: assetData && assetData.sizeType ? assetData.sizeType.name : null,
          loanAmount: assetData ? FormatService.formatNumber(assetData.loanAmount) : null,
          amount: assetData ? FormatService.formatNumber(assetData.amount) : null,
          value: assetData ? FormatService.formatNumber(assetData.value) : null,
          parcelNumber: assetData ? assetData.parcelNumber : null,
          submarket: assetData ? assetData.submarket : null,
          lotSize: assetData ? assetData.lotSize : null,
          portfolioId: assetData && assetData.portfolio && assetData.portfolio.id
            ? assetData.portfolio.id : null,
          streetAddress: assetData ? assetData.streetAddress : null,
          address: assetData ? assetData.address : null,
          addressDetails: assetData ? assetData.addressDetails : null,
          location: assetData ? assetData.location : null,
          postalCode: assetData ? assetData.postalCode : null,
          showInPortfolio: assetData ? assetData.showInPortfolio : false,
          assetOwnershipStructure: assetData ? assetData.assetOwnershipStructure : null,
          affordableUnits: assetData ? assetData.affordableUnits : null,
          asOfRightZoning: assetData ? assetData.asOfRightZoning : false,
          cost: assetData ? FormatService.formatNumber(assetData.cost) : null,
          occupancy: assetData ? assetData.occupancy : null,
          currentNoi: assetData ? FormatService.formatNumber(assetData.currentNoi) : null,
          ttmNoi: assetData ? FormatService.formatNumber(assetData.ttmNoi) : null,
          runRateNoi: assetData ? FormatService.formatNumber(assetData.runRateNoi) : null,
          forwardNoi: assetData ? FormatService.formatNumber(assetData.forwardNoi) : null,
          maturityDate: (assetData && (assetData.maturityDate
            ? DateService.parseDate(assetData.maturityDate) : '')) || null,
          sellerIds: assetData && assetData.sellers && assetData.sellers.length
            ? assetData.sellers.map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              name: contactItem.name,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
          portfolioInvestedEquity: assetData ? FormatService.formatNumber(assetData.portfolioInvestedEquity) : null,
          leveredIrr: assetData ? FormatService.formatNumber(assetData.leveredIrr, true) : null,
          equityMultiple: assetData ? FormatService.formatNumber(assetData.equityMultiple, true) : null,
          yardiAssetId: assetData ? assetData.yardiAssetId : null,
          types: assetData ? assetData.types : [],
          options: {},
          mainPhoto: null,
        },
        sponsors: {},
        brokers: {},
        lenders: {},
        allocationItems: {},
      },
    };

    if (dealCommonData.allocationItems && dealCommonData.allocationItems.length) {
      dealCommonData.allocationItems.forEach((dealAllocationItem: DealAllocationItem): void => {
        const formKeyDealAllocationItem: string = FormService.getIndexedFormFieldKey(dealAllocationItem.id);

        initialValues.dealData.dealCommonData.allocationItems[formKeyDealAllocationItem] = {
          id: dealAllocationItem.id,
          portfolioId: dealAllocationItem.portfolio ? dealAllocationItem.portfolio.id : null,
          value: dealAllocationItem.value
            ? FormatService.formatNumber(dealAllocationItem.value, true, undefined, true) : null,
        };
      });
    }
  } else {
    initialValues.dealData = {
      dealCommonData: {
        receivedDate: DateService.parseDate(DateService.getDateNow()),
      },
    };
  }

  if (assetData) {
    if (assetData.options && assetData.options.length) {
      assetData.options.forEach((assetFieldItem: AssetField): void => {
        const formKeyAssetField: string = FormService.getIndexedFormFieldKey(assetFieldItem.id);

        initialValues.dealData.dealCommonData.asset.options[formKeyAssetField] = {
          name: assetFieldItem.name,
          value: assetFieldItem.value,
        };
      });
    }
  } else if (portfolioData) {
    initialValues.dealData.dealCommonData.asset.portfolioId = portfolioData.id;
  }

  if (dealPhotosData) {
    initialValues.dealData.dealPhotosData = {};

    dealPhotosData.forEach((dealPhotoItem: DealPhoto): void => {
      const formKeyDealPhoto: string = FormService.getIndexedFormFieldKey(dealPhotoItem.id);

      initialValues.dealData.dealPhotosData[formKeyDealPhoto] = {
        id: dealPhotoItem.id,
      };
    });
  }

  if (dealBuildingDetailsData) {
    initialValues.dealData.dealBuildingDetailsData = {
      assetUseBreakdown: {},
      assetUseBreakdownHeaders: {},
    };

    if (dealBuildingDetailsData) {
      dealBuildingDetailsData.assetUseBreakdown.forEach((
        assetUseBreakdownGroupItem: DealAssetUseBreakdownItem,
      ): void => {
        const formKeyAssetUseBreakdownGroupItem: string = FormService.getIndexedFormFieldKey(
          assetUseBreakdownGroupItem.id,
        );

        initialValues.dealData.dealBuildingDetailsData.assetUseBreakdown[formKeyAssetUseBreakdownGroupItem] = {
          assetTypeId: assetUseBreakdownGroupItem.assetType && assetUseBreakdownGroupItem.assetType.id,
          breakdownProperties: {},
        };
        assetUseBreakdownGroupItem.breakdownProperties.forEach((
          assetUseBreakdownItemProperty: DealAssetUseBreakdownProperty,
        ): void => {
          const formKeyAssetUseBreakdownItemProperty: string = FormService.getIndexedFormFieldKey(
            assetUseBreakdownItemProperty.id,
          );

          initialValues.dealData.dealBuildingDetailsData.assetUseBreakdown[formKeyAssetUseBreakdownGroupItem]
            .breakdownProperties[formKeyAssetUseBreakdownItemProperty] = {
            value: assetUseBreakdownItemProperty.value,
          };
        });
      });
      dealBuildingDetailsData.assetUseBreakdownHeaders.forEach((
        assetUseBreakdownTotalItem: DealAssetUseBreakdownHeader,
      ): void => {
        const formKeyAssetUseBreakdownTotalItem: string = FormService.getIndexedFormFieldKey(
          assetUseBreakdownTotalItem.id,
        );

        initialValues.dealData.dealBuildingDetailsData.assetUseBreakdownHeaders[formKeyAssetUseBreakdownTotalItem] = {
          name: assetUseBreakdownTotalItem.name,
        };
      });
    }
  }

  if (dealInvestmentReturnsData) {
    initialValues.dealData.dealInvestmentReturnsData = {};

    dealInvestmentReturnsData.forEach((dealInvestmentReturnsItem: DealInvestmentReturnsItem): void => {
      const formDealInvestmentReturnsItem: string = FormService.getIndexedFormFieldKey(
        dealInvestmentReturnsItem.id,
      );

      initialValues.dealData.dealInvestmentReturnsData[formDealInvestmentReturnsItem] = {
        name: dealInvestmentReturnsItem.name,
        value: dealInvestmentReturnsItem.value,
      };
    });
  }

  if (dealMilestonesData) {
    initialValues.dealData.dealMilestonesData = {};

    dealMilestonesData.forEach((dealMilestonesItem: DealMilestonesItem): void => {
      const formKeyDealMilestonesItem: string = FormService.getIndexedFormFieldKey(dealMilestonesItem.id);

      initialValues.dealData.dealMilestonesData[formKeyDealMilestonesItem] = {
        id: dealMilestonesItem.id,
        name: dealMilestonesItem.name,
        value: dealMilestonesItem.value ? DateService.parseDate(dealMilestonesItem.value)
          : DateService.parseDate(DateService.getDateNow()),
        withReminder: dealMilestonesItem.withReminder,
        reminderOffset: dealMilestonesItem.reminderOffset
          ? FormatService.formatNumber(dealMilestonesItem.reminderOffset.toString(), true) : null,
      };
    });
  }

  if (dealExternalLinksData) {
    initialValues.dealData.dealExternalLinksData = {};

    dealExternalLinksData.forEach((dealExternalLinksItem: DealExternalLinksItem): void => {
      const formKeyDealExternalLinksItem: string = FormService.getIndexedFormFieldKey(
        dealExternalLinksItem.id,
      );

      initialValues.dealData.dealExternalLinksData[formKeyDealExternalLinksItem] = {
        id: dealExternalLinksItem.id,
        name: dealExternalLinksItem.name,
        link: dealExternalLinksItem.link,
        description: dealExternalLinksItem.description,
      };
    });
  }

  if (dealContactsData) {
    initialValues.dealData.dealContactsData = {};

    dealContactsData.forEach((dealContactsGroupItem: DealContactGroup): void => {
      const formKeyDealContactsGroupItem: string = FormService.getIndexedFormFieldKey(
        dealContactsGroupItem.id,
      );

      initialValues.dealData.dealContactsData[formKeyDealContactsGroupItem] = {
        name: dealContactsGroupItem.name,
        contacts: {},
      };

      if (dealContactsGroupItem.contacts && dealContactsGroupItem.contacts.length) {
        dealContactsGroupItem.contacts.forEach((dealContactsItem: DealContact): void => {
          const formKeyDealContactsItem: string = FormService.getIndexedFormFieldKey(dealContactsItem.id);

          initialValues.dealData.dealContactsData[formKeyDealContactsGroupItem].contacts[formKeyDealContactsItem] = {
            name: dealContactsItem.name,
            value: dealContactsItem.value,
          };
        });
      }
    });
  }

  if (dealCustomSectionsData) {
    initialValues.dealData.dealCustomSectionsData = {};

    dealCustomSectionsData.forEach((dealCustomSectionData: DealSection): void => {
      const formKeyDealCustomSectionData: string = FormService.getIndexedFormFieldKey(
        dealCustomSectionData.id,
      );

      initialValues.dealData.dealCustomSectionsData[formKeyDealCustomSectionData] = {
        id: dealCustomSectionData.id,
        name: dealCustomSectionData.name,
        fields: {},
      };

      if (dealCustomSectionData.fields && dealCustomSectionData.fields.length) {
        dealCustomSectionData.fields.forEach((dealCustomSectionFieldData: DealSectionField): void => {
          const formKeyDealCustomSectionFieldData: string = FormService.getIndexedFormFieldKey(
            dealCustomSectionFieldData.id,
          );
          let value: string | boolean | Date | null;

          switch (dealCustomSectionFieldData.type) {
            case FIELD_TYPE_NUMBER:
              value = dealCustomSectionFieldData.value
                ? FormatService.formatNumber(dealCustomSectionFieldData.value as string) : null;
              break;
            case FIELD_TYPE_NUMBER_DOUBLE:
            case FIELD_TYPE_NUMBER_PERCENT:
            case FIELD_TYPE_NUMBER_CURRENCY:
              value = dealCustomSectionFieldData.value
                ? FormatService.formatNumber(dealCustomSectionFieldData.value as string, true) : null;
              break;
            case FIELD_TYPE_DATE:
              value = dealCustomSectionFieldData.value
                ? DateService.parseDate(dealCustomSectionFieldData.value as string) : '';
              break;
            case FIELD_TYPE_BOOLEAN:
              value = dealCustomSectionFieldData.value || false;
              break;
            default:
              value = dealCustomSectionFieldData.value || null;
          }

          const resultDealCustomSectionFieldData: DealSectionField = {
            id: dealCustomSectionFieldData.id,
            name: dealCustomSectionFieldData.name,
            type: dealCustomSectionFieldData.type,
            value: value || null,
          };

          if (dealCustomSectionFieldData.type === FIELD_TYPE_DROPDOWN) {
            resultDealCustomSectionFieldData.listType = dealCustomSectionFieldData.listType;
          }

          initialValues.dealData.dealCustomSectionsData[formKeyDealCustomSectionData]
            .fields[formKeyDealCustomSectionFieldData] = resultDealCustomSectionFieldData;
        });
      }
    });
  }

  if (dealCustomListTypeList) {
    initialValues.dealData.dealCustomListTypesData = {};

    dealCustomListTypeList.forEach((dealCustomListTypeData: FieldListType): void => {
      const formKeyDealCustomListTypeData: string = FormService.getIndexedFormFieldKey(
        dealCustomListTypeData.id,
      );

      initialValues.dealData.dealCustomListTypesData[formKeyDealCustomListTypeData] = {
        id: dealCustomListTypeData.id,
        name: dealCustomListTypeData.name,
        title: dealCustomListTypeData.title,
        options: {},
      };

      if (dealCustomListTypeData.options && dealCustomListTypeData.options.length) {
        dealCustomListTypeData.options.forEach((dealCustomListTypeOptionData: FieldListTypeOption): void => {
          const formKeyDealCustomListTypeOptionData: string = FormService.getIndexedFormFieldKey(
            dealCustomListTypeOptionData.id,
          );

          initialValues.dealData.dealCustomListTypesData[formKeyDealCustomListTypeData]
            .options[formKeyDealCustomListTypeOptionData] = {
            id: dealCustomListTypeOptionData.id,
            name: dealCustomListTypeOptionData.name,
            title: dealCustomListTypeOptionData.title,
          };
        });
      }
    });
  }

  if (dealCapitalizationSourcesData || dealCapitalizationUsesData) {
    initialValues.dealData.dealCapitalizationData = {
      [DEAL_CAPITALIZATION_CATEGORY_SOURCES]: {
        [formKeyNew]: {
          name: null,
          items: {
            [formKeyNew]: {
              id: FormService.FORM_KEY_NEW,
              group: DEAL_CAPITALIZATION_GROUP_DEBT,
              type: null,
              name: null,
            },
          },
        },
      },
      [DEAL_CAPITALIZATION_CATEGORY_USES]: {
        [formKeyNew]: {
          name: null,
          items: {
            [formKeyNew]: {
              id: FormService.FORM_KEY_NEW,
              group: DEAL_CAPITALIZATION_GROUP_COSTS,
              type: null,
              name: null,
            },
          },
        },
      },
    };
    if (dealCapitalizationSourcesData) {
      const category: string = DEAL_CAPITALIZATION_CATEGORY_SOURCES;

      dealCapitalizationSourcesData.forEach((dealCapitalizationGroup: DealCapitalizationGroup): void => {
        const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
          dealCapitalizationGroup.id,
        );

        initialValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationGroup] = {
          name: dealCapitalizationGroup.name,
          isSystem: dealCapitalizationGroup.isSystem,
          items: {},
        };

        if (dealCapitalizationGroup.items && dealCapitalizationGroup.items.length) {
          dealCapitalizationGroup.items.forEach((dealCapitalizationItem: DealCapitalizationItem): void => {
            const formKeyDealCapitalizationItem: string = FormService.getIndexedFormFieldKey(
              dealCapitalizationItem.id,
            );
            const initialDealCapitalizationSourcesData: Record<string, FormValueItem> = (
              initialValues.dealData.dealCapitalizationData[DEAL_CAPITALIZATION_CATEGORY_SOURCES]
            );

            initialDealCapitalizationSourcesData[formKeyDealCapitalizationGroup]
              .items[formKeyDealCapitalizationItem] = {
              type: dealCapitalizationItem.type ? dealCapitalizationItem.type.name : null,
              name: dealCapitalizationItem.name,
              notes: dealCapitalizationItem.notes,
              pricing: dealCapitalizationItem.pricing,
              pricingType: dealCapitalizationItem.pricingType && dealCapitalizationItem.pricingType.name,
              atClosingAmount: FormatService.formatNumber(dealCapitalizationItem.atClosingAmount),
              futureAmount: FormatService.formatNumber(dealCapitalizationItem.futureAmount),
              amount: FormatService.formatNumber(dealCapitalizationItem.amount),
            };
            initialValues.dealData.dealCapitalizationData[DEAL_CAPITALIZATION_CATEGORY_SOURCES] = {
              ...initialDealCapitalizationSourcesData,
            };
          });
        }
      });
    }

    if (dealCapitalizationUsesData) {
      const category: string = DEAL_CAPITALIZATION_CATEGORY_USES;

      dealCapitalizationUsesData.forEach((dealCapitalizationGroup: DealCapitalizationGroup): void => {
        const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
          dealCapitalizationGroup.id,
        );

        initialValues.dealData.dealCapitalizationData[category][formKeyDealCapitalizationGroup] = {
          name: dealCapitalizationGroup.name,
          isSystem: dealCapitalizationGroup.isSystem,
          items: {},
        };

        if (dealCapitalizationGroup.items && dealCapitalizationGroup.items.length) {
          dealCapitalizationGroup.items.forEach((dealCapitalizationItem: DealCapitalizationItem): void => {
            const formKeyDealCapitalizationItem: string = FormService.getIndexedFormFieldKey(
              dealCapitalizationItem.id,
            );
            const initialDealCapitalizationUsesData: Record<string, FormValueItem> = (
              initialValues.dealData.dealCapitalizationData[DEAL_CAPITALIZATION_CATEGORY_USES]
            );

            initialDealCapitalizationUsesData[formKeyDealCapitalizationGroup].items[formKeyDealCapitalizationItem] = {
              type: dealCapitalizationItem.type ? dealCapitalizationItem.type.name : null,
              name: dealCapitalizationItem.name,
              notes: dealCapitalizationItem.notes,
              pricing: dealCapitalizationItem.pricing,
              pricingType: dealCapitalizationItem.pricingType && dealCapitalizationItem.pricingType.name,
              atClosingAmount: FormatService.formatNumber(dealCapitalizationItem.atClosingAmount),
              futureAmount: FormatService.formatNumber(dealCapitalizationItem.futureAmount),
              amount: FormatService.formatNumber(dealCapitalizationItem.amount),
            };
            initialValues.dealData.dealCapitalizationData[DEAL_CAPITALIZATION_CATEGORY_USES] = {
              ...initialDealCapitalizationUsesData,
            };
          });
        }
      });
    }
  }

  if (dealEquityOrganizationChartData) {
    initialValues.dealData.dealEquityOrganizationChartData = {
      file: dealEquityOrganizationChartData.link,
    };
  }

  if (dealMetricsCapitalStackData) {
    initialValues.dealData.dealMetricsCapitalStackData = {};

    dealMetricsCapitalStackData.forEach((dealMetricsCapitalStackItem: DealMetricsCapitalStackItem): void => {
      const formKeyDealMetricsCapitalStackItem: string = FormService.getIndexedFormFieldKey(
        dealMetricsCapitalStackItem.id,
      );

      initialValues.dealData.dealMetricsCapitalStackData[formKeyDealMetricsCapitalStackItem] = {
        name: dealMetricsCapitalStackItem.name,
        value: FormatService.formatNumber(dealMetricsCapitalStackItem.value),
      };
    });
  }

  if (dealMetricsStatisticsData) {
    initialValues.dealData.dealMetricsStatisticsData = {};

    dealMetricsStatisticsData.forEach((dealMetricsGroupItem: DealMetricsStatisticsGroup): void => {
      const formKeyDealMetricsGroup: string = FormService.getIndexedFormFieldKey(
        dealMetricsGroupItem.id,
      );

      initialValues.dealData.dealMetricsStatisticsData[formKeyDealMetricsGroup] = {
        name: dealMetricsGroupItem.name,
        values: {},
      };
      dealMetricsGroupItem.values.forEach((dealMetricsItem: DealMetricsStatisticsItem): void => {
        const formKeyDealMetricsItem: string = FormService.getIndexedFormFieldKey(dealMetricsItem.id);

        initialValues.dealData.dealMetricsStatisticsData[formKeyDealMetricsGroup].values[formKeyDealMetricsItem] = {
          value: dealMetricsItem.value,
        };
      });
    });
  }

  if (dealTermsData) {
    initialValues.dealData.dealTermsData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        terms: {},
      },
    };
    dealTermsData.forEach((dealTermGroupItem: DealTermGroup): void => {
      const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroupItem.id);

      if (!initialValues.dealData.dealTermsData[formKeyDealTermGroup]) {
        initialValues.dealData.dealTermsData[formKeyDealTermGroup] = {
          id: dealTermGroupItem.id,
          name: dealTermGroupItem.name,
          terms: {},
        };
      }
      dealTermGroupItem.terms.forEach((dealTermItem: DealTerm): void => {
        const formKeyDealTermItemId: string = FormService.getIndexedFormFieldKey(dealTermItem.id);

        initialValues.dealData.dealTermsData[formKeyDealTermGroup].terms[formKeyDealTermItemId] = {
          id: dealTermItem.id,
          name: dealTermItem.name,
          groupId: dealTermGroupItem.id,
          content: dealTermItem.content,
        };
      });
    });
  }

  if (dealHistoricalValuationsData) {
    initialValues.dealData.dealHistoricalValuationsData = {};

    dealHistoricalValuationsData.forEach((dealHistoricalValuationsItem: DealHistoricalValuationsItem): void => {
      const formKeyDealHistoricalValuationsItem: string = FormService.getIndexedFormFieldKey(
        dealHistoricalValuationsItem.id,
      );

      initialValues.dealData.dealHistoricalValuationsData[formKeyDealHistoricalValuationsItem] = {
        name: dealHistoricalValuationsItem.name,
        value: FormatService.formatNumber(dealHistoricalValuationsItem.value),
      };
    });
  }

  if (dealTopLevelMetricsData) {
    initialValues.dealData.dealTopLevelMetricsData = {};

    dealTopLevelMetricsData.forEach((dealTopLevelMetricsItem: DealTopLevelMetricsItem): void => {
      const formKeyDealTopLevelMetricsItem: string = FormService.getIndexedFormFieldKey(
        dealTopLevelMetricsItem.id,
      );

      initialValues.dealData.dealTopLevelMetricsData[formKeyDealTopLevelMetricsItem] = {
        name: dealTopLevelMetricsItem.name,
        value: dealTopLevelMetricsItem.value,
      };
    });
  }

  if (dealValuationFilesData) {
    initialValues.dealData.dealValuationFilesData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        files: {},
      },
    };
    dealValuationFilesData.forEach((dealValuationFileGroupItem: DealFileGroup): void => {
      const formKeyDealValuationFileGroupItem: string = FormService.getIndexedFormFieldKey(
        dealValuationFileGroupItem.id,
      );

      initialValues.dealData.dealValuationFilesData[formKeyDealValuationFileGroupItem] = {
        id: dealValuationFileGroupItem.id,
        name: dealValuationFileGroupItem.name,
        files: {},
      };

      if (dealValuationFileGroupItem.files && dealValuationFileGroupItem.files.length) {
        dealValuationFileGroupItem.files.forEach((dealValuationFileItem: DealFile): void => {
          const formKeyDealValuationFileItem: string = FormService.getIndexedFormFieldKey(
            dealValuationFileItem.id,
          );

          initialValues.dealData.dealValuationFilesData[formKeyDealValuationFileGroupItem]
            .files[formKeyDealValuationFileItem] = {
            id: dealValuationFileItem.id,
            groupId: dealValuationFileGroupItem.id,
            name: dealValuationFileItem.file ? dealValuationFileItem.file.name : null,
            file: null,
          };
        });
      }
    });
  }

  if (dealTasksData) {
    initialValues.dealData.dealTasksData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        tasks: {
          [formKeyNew]: {
            id: FormService.FORM_KEY_NEW,
            name: null,
            dueDate: DateService.parseDate(DateService.getDateNow()),
            group: {},
          },
        },
      },
    };
    dealTasksData.forEach((dealTaskGroupItem: DealTaskGroup): void => {
      const formKeyDealTaskGroupItem: string = FormService.getIndexedFormFieldKey(dealTaskGroupItem.id);

      initialValues.dealData.dealTasksData[formKeyDealTaskGroupItem] = {
        id: dealTaskGroupItem.id,
        name: dealTaskGroupItem.name,
        tasks: {},
      };
      dealTaskGroupItem.tasks.forEach((dealTaskItem: DealTask): void => {
        const formKeyDealTaskItem: string = FormService.getIndexedFormFieldKey(dealTaskItem.id);

        initialValues.dealData.dealTasksData[formKeyDealTaskGroupItem].tasks[formKeyDealTaskItem] = {
          id: dealTaskItem.id,
          groupId: dealTaskGroupItem.id,
          parentId: dealTaskItem.parentId,
          name: dealTaskItem.name,
          dueDate: dealTaskItem.dueDate ? DateService.parseDate(dealTaskItem.dueDate)
            : DateService.parseDate(DateService.getDateNow()),
          contactId: dealTaskItem.contact ? dealTaskItem.contact.id : null,
          status: dealTaskItem.status,
          accessLevel: dealTaskItem.accessLevel ? dealTaskItem.accessLevel.name : null,
          reviewContactIds: dealTaskItem.reviews && dealTaskItem.reviews.length
            ? dealTaskItem.reviews.map((dealTaskReviewItem: DealTaskReview): Contact | null => (
              dealTaskReviewItem.contact.id ? ({
                id: dealTaskReviewItem.contact.id,
                name: dealTaskReviewItem.contact.name,
                fullName: dealTaskReviewItem.contact.fullName,
                photo: dealTaskReviewItem.contact.photo,
                photoThumbnail: dealTaskReviewItem.contact.photoThumbnail,
              }) : null),
            ).filter((dealTaskReviewContact: Contact | null): boolean => !!dealTaskReviewContact) : [],
          group: {},
        };

        if (dealTaskItem.subTasks && dealTaskItem.subTasks.length) {
          dealTaskItem.subTasks.forEach((dealSubTaskItem: DealTask): void => {
            const formKeyDealSubTaskItem: string = FormService.getIndexedFormFieldKey(dealSubTaskItem.id);

            if (dealCommonData.id) {
              initialValues.dealData.dealTasksData[formKeyDealTaskGroupItem].tasks[formKeyDealSubTaskItem] = {
                id: dealSubTaskItem.id,
                groupId: dealTaskGroupItem.id,
                parentId: dealSubTaskItem.parentId,
                name: dealSubTaskItem.name,
                dueDate: dealSubTaskItem.dueDate ? DateService.parseDate(dealSubTaskItem.dueDate)
                  : DateService.parseDate(DateService.getDateNow()),
                contactId: dealSubTaskItem.contact ? dealSubTaskItem.contact.id : null,
                status: dealSubTaskItem.status,
                accessLevel: dealSubTaskItem.accessLevel ? dealSubTaskItem.accessLevel.name : null,
                group: {},
              };
            } else {
              if (!initialValues.dealData.dealTasksData[formKeyDealTaskGroupItem].tasks[formKeyDealTaskItem].subTasks) {
                initialValues.dealData.dealTasksData[formKeyDealTaskGroupItem].tasks[formKeyDealTaskItem].subTasks = {}
              }

              initialValues.dealData.dealTasksData[formKeyDealTaskGroupItem].tasks[formKeyDealTaskItem]
                .subTasks[formKeyDealSubTaskItem] = {
                id: dealSubTaskItem.id,
                groupId: dealTaskGroupItem.id,
                parentId: dealSubTaskItem.parentId,
                name: dealSubTaskItem.name,
                dueDate: dealSubTaskItem.dueDate ? DateService.parseDate(dealSubTaskItem.dueDate)
                  : DateService.parseDate(DateService.getDateNow()),
                contactId: dealSubTaskItem.contact ? dealSubTaskItem.contact.id : null,
                status: dealSubTaskItem.status,
                accessLevel: dealSubTaskItem.accessLevel ? dealSubTaskItem.accessLevel.name : null,
                group: {},
              };
            }
          });
        }
      });
    });
  }

  if (dealParticipantsData) {
    initialValues.dealData.dealParticipantsData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
      },
    };
    dealParticipantsData.forEach((dealParticipantItem: DealParticipant): void => {
      const formKeyDealParticipantItem: string = FormService.getIndexedFormFieldKey(dealParticipantItem.id);

      initialValues.dealData.dealParticipantsData[formKeyDealParticipantItem] = {
        id: dealParticipantItem.id,
        name: dealParticipantItem.name,
        groupId: dealParticipantItem.group ? dealParticipantItem.group.id : null,
        contactId: dealParticipantItem.contact ? dealParticipantItem.contact.id : null,
        accessLevel: dealParticipantItem.accessLevel ? dealParticipantItem.accessLevel.name : null,
        isApproved: dealParticipantItem.isApproved,
        ndaFile: dealParticipantItem.ndaFile || null,
      };
    });
  }

  if (dealFilesData) {
    initialValues.dealData.dealFilesData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        files: {
          [formKeyNew]: {
            id: FormService.FORM_KEY_NEW,
            name: null,
            file: null,
            group: {},
          },
        },
      },
    };
    dealFilesData.forEach((dealFileGroupItem: DealFileGroup): void => {
      const formKeyDealFileGroupItem: string = FormService.getIndexedFormFieldKey(dealFileGroupItem.id);

      initialValues.dealData.dealFilesData[formKeyDealFileGroupItem] = {
        id: dealFileGroupItem.id,
        name: dealFileGroupItem.name,
        files: {},
      };

      if (dealFileGroupItem.files && dealFileGroupItem.files.length) {
        dealFileGroupItem.files.forEach((dealFileItem: DealFile): void => {
          const formKeyDealFileItem: string = FormService.getIndexedFormFieldKey(dealFileItem.id);

          initialValues.dealData.dealFilesData[formKeyDealFileGroupItem].files[formKeyDealFileItem] = {
            id: dealFileItem.id,
            groupId: dealFileGroupItem.id,
            name: dealFileItem.file ? dealFileItem.file.name : null,
            accessLevel: dealFileItem.accessLevel ? dealFileItem.accessLevel.name : null,
            file: null,
            group: {},
          };
        });
      }
    });
  }

  if (dealSchedulesData) {
    initialValues.dealData.dealSchedulesData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        name: null,
        tasks: {},
        links: {},
      },
    };
    dealSchedulesData.forEach((dealScheduleItem: DealSchedule): void => {
      const formKeyDealScheduleItem: string = FormService.getIndexedFormFieldKey(dealScheduleItem.id);

      initialValues.dealData.dealSchedulesData[formKeyDealScheduleItem] = {
        id: dealScheduleItem.id,
        name: dealScheduleItem.name,
        tasks: dealScheduleItem.tasks && dealScheduleItem.tasks.length
          ? dealScheduleItem.tasks.map((dealScheduleTaskItem: DealScheduleTask): DealScheduleTask => ({
            id: dealScheduleTaskItem.id,
            name: dealScheduleTaskItem.name,
            notes: dealScheduleTaskItem.notes || null,
            parentId: dealScheduleTaskItem.parentId || null,
            progress: dealScheduleTaskItem.progress || ChartService.getGanttChartProgressDefault(),
            startedAt: dealScheduleTaskItem.startedAt || null,
            assignedTo: dealScheduleTaskItem.assignedTo || null,
            color: dealScheduleTaskItem.color || null,
            duration: dealScheduleTaskItem.duration || null,
            order: dealScheduleTaskItem.order || null,
          })) : [],
        links: dealScheduleItem.links && dealScheduleItem.links.length
          ? dealScheduleItem.links.map((dealScheduleTaskLinkItem: DealScheduleTaskLink): DealScheduleTaskLink => ({
            id: dealScheduleTaskLinkItem.id,
            sourceId: dealScheduleTaskLinkItem.sourceId,
            targetId: dealScheduleTaskLinkItem.targetId,
            type: dealScheduleTaskLinkItem.type,
          })) : [],
      };
    });
  }

  if (dealActivityList) {
    initialValues.dealData.activityListData = {
      [formKeyNew]: {
        id: FormService.FORM_KEY_NEW,
        status: ACTIVITY_STATUS_INITIAL,
        category: ACTIVITY_CATEGORY_CAPITAL_RAISE,
        type: ACTIVITY_TYPE_DEBT,
        dealId: null,
        deal: {},
        dealSource: {},
        dealContact: {},
      },
    };
    dealActivityList.forEach((activityItem: Activity): void => {
      const formKeyActivity: string = FormService.getIndexedFormFieldKey(activityItem.id);

      initialValues.dealData.activityListData[formKeyActivity] = {
        id: activityItem.id,
        status: activityItem.status || ACTIVITY_STATUS_INITIAL,
        type: activityItem.type || ACTIVITY_TYPE_DEBT,
        category: activityItem.category,
        dealId: activityItem.deal ? activityItem.deal.id : null,
        dealSourceId: activityItem.dealSource ? activityItem.dealSource.id : null,
        sponsorId: activityItem.sponsor ? activityItem.sponsor.id : null,
        brokerId: activityItem.broker ? activityItem.broker.id : null,
        lenderId: activityItem.lender ? activityItem.lender.id : null,
        dealContactIds: activityItem.dealContacts && activityItem.dealContacts.length
          ? activityItem.dealContacts : [],
        amount: FormatService.formatNumber(activityItem.amount),
        pricing: activityItem.pricing,
        size: FormatService.formatNumber(activityItem.size),
        sizeType: activityItem.sizeType ? activityItem.sizeType.name : null,
        ltv: FormatService.formatNumber(activityItem.ltv, true),
        purchasePrice: FormatService.formatNumber(activityItem.purchasePrice),
        capitalExpenditure: FormatService.formatNumber(activityItem.capitalExpenditure),
        oneYearNoi: FormatService.formatNumber(activityItem.oneYearNoi),
        yieldOnCost: FormatService.formatNumber(activityItem.yieldOnCost),
        debtProceeds: FormatService.formatNumber(activityItem.debtProceeds),
        requiredEquityAmount: FormatService.formatNumber(activityItem.requiredEquityAmount),
        interestLevel: activityItem.interestLevel,
        ltc: FormatService.formatNumber(activityItem.ltc, true),
        equityProviderId: activityItem.equityProvider ? activityItem.equityProvider.id : null,
        investorId: activityItem.investor ? activityItem.investor.id : null,
        lenderRole: activityItem.lenderRole,
        maxAmount: activityItem.maxAmount,
        loanRecourse: activityItem.loanRecourse,
        additionalPartyId: activityItem.additionalParty ? activityItem.additionalParty.id : null,
        additionalPartyDetails: activityItem.additionalPartyDetails,
        tags: activityItem.tags,
        options: {},
        deal: {},
        dealSource: {},
        dealContact: {},
      };

      if (activityItem.options && activityItem.options.length) {
        activityItem.options.forEach((activityFieldItem: ActivityField): void => {
          const formKeyActivityField: string = FormService.getIndexedFormFieldKey(activityFieldItem.id);

          initialValues.dealData.activityListData[formKeyActivity].options[formKeyActivityField] = {
            name: activityFieldItem.name,
            value: activityFieldItem.value,
          };
        });
      }

      if (activityItem.category === ACTIVITY_CATEGORY_NEW_DEAL) {
        initialValues.dealData.dealCommonData.newDealActivity = {
          id: activityItem.id,
          dealContactIds: activityItem.dealContacts && activityItem.dealContacts.length
            ? activityItem.dealContacts.map((contactItem: Contact): Contact => ({
              id: contactItem.id,
              firstName: contactItem.firstName,
              lastName: contactItem.lastName,
              fullName: contactItem.fullName,
              photo: contactItem.photo,
              photoThumbnail: contactItem.photoThumbnail,
            })) : [],
        };
      }
    });
  }

  return {
    currentUser: app.baseData && app.baseData.currentUser,
    isDataLoaded: !!deal.dealData,
    isEditMode: controls.isEditMode,
    isExportMode: controls.isExportMode,
    isSelectMode: controls.isSelectMode,
    isCustomizeMode: controls.isCustomizeMode,
    isModalEditMode: controls.isModalEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isDealTasksLoading: dealTasks.isLoading,
    isDealFilesLoading: dealFiles.isLoading,
    isDealTaskListSaving: dealTasks.isFormSaving,
    isDealParticipantListSaving: dealParticipants.isFormSaving,
    isDealActivityListSaving: activityList.isFormSaving,
    isDealFileChatResponseLoading: deal.isDealFileChatResponseLoading,
    dealFileChatResponseText: deal.dealFileChatResponseText,
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
    newDealId: deal.newDealId,
    dealAsset: asset.assetData && asset.assetData.asset ? asset.assetData.asset : null,
    isDealTaskCommentSaving: dealTasks.isCommentSaving,
    isDealParticipantCommentSaving: dealParticipants.isCommentSaving,
    isDealActivityCommentSaving: activity.isCommentSaving,
    isDealFileAnalyzed: deal.isDealFileAnalyzed,
    dealTaskNewCommentList: dealTasks.commentList,
    dealParticipantNewCommentList: dealParticipants.commentList,
    dealActivityNewCommentList: activityList.commentList,
    dealScheduleZoom: dealScheduleTasks.zoom,
    dealCommonData: deal.dealData && deal.dealData.dealCommonData,
    dealPhotosData: deal.dealData && deal.dealData.dealPhotosData,
    dealMainPhotoData: deal.dealData && deal.dealData.dealMainPhotoData,
    dealBuildingDetailsData: deal.dealData && deal.dealData.dealBuildingDetailsData,
    dealInvestmentReturnsData: deal.dealData && deal.dealData.dealInvestmentReturnsData,
    dealMilestonesData: deal.dealData && deal.dealData.dealMilestonesData,
    dealExternalLinksData: deal.dealData && deal.dealData.dealExternalLinksData,
    dealContactsData: deal.dealData && deal.dealData.dealContactsData,
    dealCustomSectionsData: deal.dealData && deal.dealData.dealCustomSectionsData,
    dealStatusChangeListData: deal.dealData && deal.dealData.dealStatusChangeListData,
    dealCapitalizationFileData: deal.dealData && deal.dealData.dealCapitalizationFileData,
    dealEquityOrganizationChartData: deal.dealData && deal.dealData.dealEquityOrganizationChartData,
    dealMetricsCapitalStackData: deal.dealData && deal.dealData.dealMetricsCapitalStackData,
    dealMetricsStatisticsData: deal.dealData && deal.dealData.dealMetricsStatisticsData,
    dealTermsData: deal.dealData && deal.dealData.dealTermsData,
    dealHistoricalValuationsData: deal.dealData && deal.dealData.dealHistoricalValuationsData,
    dealCapitalizationSourcesData: deal.dealData && deal.dealData.dealCapitalizationData
      && deal.dealData.dealCapitalizationData.sources,
    dealCapitalizationSourcesSummaryData: deal.dealData && deal.dealData.dealCapitalizationData
      && deal.dealData.dealCapitalizationData.sourcesSummary,
    dealCapitalizationUsesData: deal.dealData && deal.dealData.dealCapitalizationData
      && deal.dealData.dealCapitalizationData.uses,
    dealCapitalizationUsesSummaryData: deal.dealData && deal.dealData.dealCapitalizationData
      && deal.dealData.dealCapitalizationData.usesSummary,
    dealTopLevelMetricsData: deal.dealData && deal.dealData.dealTopLevelMetricsData,
    dealValuationFilesData: deal.dealData && deal.dealData.dealValuationFilesData,
    dealValuationFileGroupList: deal.dealData && deal.dealData.dealValuationFileGroupList,
    dealTasksData: deal.dealData && deal.dealData.dealTasksData,
    dealTasksTotal: deal.dealData && deal.dealData.dealTasksTotal,
    dealTaskPlainList: deal.dealData && deal.dealData.dealTaskPlainList,
    dealTaskGroupList: deal.dealData && deal.dealData.dealTaskGroupList,
    dealTaskStatusList: deal.dealData && deal.dealData.dealTaskStatusList,
    dealTaskContactPersonList: deal.dealData && deal.dealData.dealTaskContactPersonList,
    dealParticipantsData: deal.dealData && deal.dealData.dealParticipantsData,
    dealParticipantsTotal: deal.dealData && deal.dealData.dealParticipantsTotal,
    dealParticipantGroupList: deal.dealData && deal.dealData.dealParticipantGroupList,
    dealSchedulesData: deal.dealData && deal.dealData.dealSchedulesData,
    dealFilesData: deal.dealData && deal.dealData.dealFilesData,
    dealFileGroupList: deal.dealData && deal.dealData.dealFileGroupList,
    dealFilesTotal: deal.dealData && deal.dealData.dealFilesTotal,
    dealCommentsData: deal.dealData && deal.dealData.dealCommentsData,
    dealParticipantId: deal.dealData && deal.dealData.dealParticipantId,
    accessLevelList: deal.dealData && deal.dealData.accessLevelList,
    roleList: deal.dealData && deal.dealData.roleList,
    accountUserTypeList: deal.dealData && deal.dealData.accountUserTypeList,
    contactPersonList: deal.dealData && deal.dealData.contactPersonList,
    contactPersonInternalList: deal.dealData && deal.dealData.contactPersonInternalList,
    contactNotParticipantList: deal.dealData && deal.dealData.contactNotParticipantList,
    contactGroupList: deal.dealData && deal.dealData.contactGroupList,
    contactCompanyList: deal.dealData && deal.dealData.contactCompanyList,
    contactList: deal.dealData && deal.dealData.contactList,
    contactTypeList: deal.dealData && deal.dealData.contactTypeList,
    dealCustomListTypeList: deal.dealData && deal.dealData.dealCustomListTypeList,
    dealActivityList: deal.dealData && deal.dealData.dealActivityList,
    dealActivityTotal: deal.dealData && deal.dealData.dealActivityTotal,
    dealActivityListSummaryData: deal.dealData && deal.dealData.dealActivityListSummaryData,
    activityStatusList: deal.dealData && deal.dealData.activityStatusList,
    activityCategoryList: deal.dealData && deal.dealData.activityCategoryList,
    dealStatusList: deal.dealData && deal.dealData.dealStatusList,
    dealTypeList: deal.dealData && deal.dealData.dealTypeList,
    assetTypeList: deal.dealData && deal.dealData.assetTypeList,
    assetStatusList: deal.dealData && deal.dealData.assetStatusList,
    assetList: deal.dealData && deal.dealData.assetList,
    portfolioList: deal.dealData && deal.dealData.portfolioList,
    hiddenPortfolioList: deal.dealData && deal.dealData.hiddenPortfolioList,
    measureUnitList: deal.dealData && deal.dealData.measureUnitList,
    pricingTypeList: deal.dealData && deal.dealData.pricingTypeList,
    activityInterestLevelList: deal.dealData && deal.dealData.activityInterestLevelList,
    activityTypeList: deal.dealData && deal.dealData.activityTypeList,
    dealCapitalizationTypeList: deal.dealData && deal.dealData.dealCapitalizationTypeList,
    dealCapitalizationGroupList: deal.dealData && deal.dealData.dealCapitalizationGroupList,
    dealRejectReasonList: deal.dealData && deal.dealData.dealRejectReasonList,
    dealTaskTemplateList: deal.dealData && deal.dealData.dealTaskTemplateList,
    assetOwnershipStructureTypeList: deal.dealData && deal.dealData.assetOwnershipStructureTypeList,
    dealLoanTypeList: deal.dealData && deal.dealData.dealLoanTypeList,
    tagList: deal.dealData && deal.dealData.tagList,
    contactLinkedUserList: deal.dealData && deal.dealData.contactLinkedUserList,
    dealTabSettings: deal.dealData && deal.dealData.tabSettings,
    dealHiddenFieldList: deal.dealData && deal.dealData.dealHiddenFieldList,
    filterDealTaskContactList: deal.dealData && deal.dealData.dealTaskListFilters
      && deal.dealData.dealTaskListFilters.dealTaskContactList,
    filterDealParticipantCompanyList: deal.dealData && deal.dealData.dealParticipantListFilters
      && deal.dealData.dealParticipantListFilters.dealParticipantCompanyList,
    filterDealParticipantAccessLevelList: deal.dealData && deal.dealData.dealParticipantListFilters
      && deal.dealData.dealParticipantListFilters.dealParticipantAccessLevelList,
    filterActivityCategoryList: deal.dealData && deal.dealData.dealActivityListFilters
      && deal.dealData.dealActivityListFilters.activityCategoryList,
    filterActivityTypeList: deal.dealData && deal.dealData.dealActivityListFilters
      && deal.dealData.dealActivityListFilters.activityTypeList,
    filterBrokerList: deal.dealData && deal.dealData.dealActivityListFilters
      && deal.dealData.dealActivityListFilters.brokerList,
    filterLenderList: deal.dealData && deal.dealData.dealActivityListFilters
      && deal.dealData.dealActivityListFilters.lenderList,
    filterInvestorList: deal.dealData && deal.dealData.dealActivityListFilters
      && deal.dealData.dealActivityListFilters.investorList,
    filterDealContactList: deal.dealData && deal.dealData.dealActivityListFilters
      && deal.dealData.dealActivityListFilters.dealContactList,
  };
};

const DealForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ deal }: Reducers): ValidationErrorsMap => (
    deal.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ deal }: Reducers): ValidationErrorsMap => (
    deal.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_DEAL}
      component={Deal}
      validator={DealValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(DealForm);
